import * as i0 from '@angular/core';
import { Injectable, APP_INITIALIZER, Directive, Component, ChangeDetectionStrategy, Host, HostBinding, Input, NgModule, Optional, SkipSelf, Injector, ViewContainerRef, ViewChild, ContentChild, Pipe, ElementRef, Inject, EventEmitter, Output, HostListener, TemplateRef, Self } from '@angular/core';
import * as i1$1 from '@angular/common';
import { CommonModule, DOCUMENT } from '@angular/common';
import { distinctUntilChanged, pluck, takeUntil, map, filter } from 'rxjs/operators';
import { AvatarPrimitive } from '@slidebean/hydra/avatar';
import { createStitches, Styled as Styled$1 } from '@slidebean/stitches-angular';
import { blue, crimson, red, yellow, green, cyan, slate, violet, blackA, blueDark, crimsonDark, redDark, yellowDark, greenDark, cyanDark, slateDark, violetDark } from '@radix-ui/colors';
import { Subject, BehaviorSubject, merge, EMPTY, fromEvent } from 'rxjs';
import * as i1 from '@slidebean/hydra/media';
import { CoerceBooleanVariant, createCoercionDecoratorFactory, CoerceBoolean, coerceElement } from '@slidebean/hydra/coercion';
import { ComponentContentRef, TEXT_CONTENT_RENDERER } from '@slidebean/hydra/dynamic';
import { PopoverPrimitive, POPOVER_PROVIDERS } from '@slidebean/hydra/popover';
import { faArrowCircleRight, faArrowLeft, faArrowRight, faCalendar, faChartMixedUpCircleDollar, faCheckCircle, faCheck, faChevronDown, faChevronLeft, faChevronRight, faChevronUp, faCircleNotch, faExclamationCircle as faExclamationCircle$1, faExpandWide, faFolderPlus, faQuestionCircle, faUsers, faTimes, faLaptop, faFilePowerpoint, faFilePdf, faUserPlus, faPersonChalkboard } from '@fortawesome/pro-solid-svg-icons';
import { FaIconComponent, FontAwesomeModule, FaConfig } from '@fortawesome/angular-fontawesome';
import { didChangeAfterFirst } from '@slidebean/hydra/did-change';
import { faFastBackward, faChartColumn, faChartLine, faChartMixedUpCircleDollar as faChartMixedUpCircleDollar$1, faChartPie, faCircle, faExclamationCircle, faFont, faPlus, faQuestionCircle as faQuestionCircle$1, faDiagramProject, faTrademark, faSpinnerThird, faText, faHeading, faParagraph, faQuoteLeft, faLongArrowRight, faMinus, faPhotoVideo, faTaxi, faWaveform, faChartBar, faChartArea, faTriangle, faShapes, faSquare, faTable, faProjector, faLevelUp, faLevelDown, faPencilPaintbrush, faLink, faUsers as faUsers$1, faFolder, faFolderTimes, faCheckCircle as faCheckCircle$1, faTimeline, faDiagramVenn, faTally, faDotCircle, faFilter, faCircleDashed, faPause, faExchangeAlt, faMagicWandSparkles, faPalette, faTextHeight, faAlignLeft, faAlignCenter, faAlignRight, faFillDrip, faEmptySet, faBold, faItalic, faUnderline, faStrikethrough, faSuperscript, faSubscript, faListUl, faListOl, faUnlink, faTextSlash, faFaceLaughWink, faBringFront, faSendBack, faArrowUpToLine, faArrowDownToLine, faArrowLeftToLine, faArrowRightToLine, faSort, faPersonChalkboard as faPersonChalkboard$1, faObjectGroup, faTriangleExclamation, faInfoCircle, faTimesCircle, faScissors, faPaste, faHandPointUp } from '@fortawesome/pro-light-svg-icons';
import { TooltipPrimitive, TOOLTIP_PROVIDERS } from '@slidebean/hydra/tooltip';
import * as i1$2 from '@slidebean/hydra/arrow';
import { ArrowModule } from '@slidebean/hydra/arrow';
import * as i6 from '@slidebean/hydra/dialog';
import { BackdropPrimitive, DialogElement, DialogPrimitive, DialogService, DialogContext } from '@slidebean/hydra/dialog';
import * as i1$3 from '@slidebean/hydra/animator';
import * as i2 from '@angular/forms';
import { FormsModule, NgControl } from '@angular/forms';
import { TextInputPrimitive, Control, CheckInputPrimitive } from '@slidebean/hydra/forms';
import * as i1$4 from '@slidebean/hydra/check';
import { CheckComponent, CheckModule } from '@slidebean/hydra/check';
import { CenterModule } from '@slidebean/hydra/center';
import { AspectRatioModule } from '@slidebean/hydra/aspect-ratio';
import { VisuallyHiddenModule } from '@slidebean/hydra/visually-hidden';
import { FilePickerModule } from '@slidebean/hydra/file-picker';

function util(...properties) {
    return (value) => properties.reduce((final, cssProp) => ({
        ...final,
        [cssProp]: value,
    }), {});
}

function rename(name, scale) {
    return Object.values(scale).reduce((acc, value, i) => {
        acc[`${name}-${i + 1}`] = value;
        return acc;
    }, {});
}
function colorTokenKey(name, token) {
    return `${name}-${token}`;
}
function colorToken(name, key) {
    return `$${name}-${key}`;
}
/**
 * Creates token aliases for the provided color name
 * @param name the color name, it can be a semantic known color or an unknown name.
 * @param overrides
 * @returns
 */
function tokenize(name, overrides) {
    return {
        [colorTokenKey(name, 'lo-contrast')]: colorToken(name, 1),
        [colorTokenKey(name, 'hi-contrast')]: colorToken(name, 12),
        [colorTokenKey(name, 'foreground')]: colorToken(name, 11),
        [colorTokenKey(name, 'foreground-contrast')]: '$lo-contrast',
        [colorTokenKey(name, 'border-subtle')]: colorToken(name, 6),
        [colorTokenKey(name, 'border')]: colorToken(name, 7),
        [colorTokenKey(name, 'border-hover')]: colorToken(name, 8),
        [colorTokenKey(name, 'border-active')]: colorToken(name, 8),
        [colorTokenKey(name, 'subtle')]: colorToken(name, 3),
        [colorTokenKey(name, 'subtle-hover')]: colorToken(name, 4),
        [colorTokenKey(name, 'subtle-active')]: colorToken(name, 5),
        [colorTokenKey(name, 'solid')]: colorToken(name, 9),
        [colorTokenKey(name, 'solid-hover')]: colorToken(name, 10),
        [colorTokenKey(name, 'solid-active')]: colorToken(name, 11),
        ...(overrides ?? {}),
    };
}

const primary$1 = rename('primary', blue);
const accent$1 = rename('accent', crimson);
const danger$1 = rename('danger', red);
const warning$1 = rename('warning', yellow);
const success$1 = rename('success', green);
const info$1 = rename('info', cyan);
const neutral$1 = rename('neutral', slate);
const promote$1 = rename('promote', violet);
const overlay$1 = rename('overlay', blackA);
const colors$2 = {
    ...primary$1,
    'primary-3': 'hsl(223, 78%, 96%)', // #EFF3FD
    'primary-4': 'hsl(221, 79%, 95%)', // #E6EDFC
    'primary-5': 'hsl(222, 77%, 91%)', // #D8E2FA
    'primary-9': 'hsl(221, 80%, 55%)', // #316BE8
    'primary-10': 'hsl(221, 80%, 47%)', // #1854D7
    'primary-11': 'hsl(221, 80%, 40%)', // #1549B9
    ...accent$1,
    'accent-9': 'hsl(345, 95%, 62%)', // #FA4371
    'accent-10': 'hsl(345, 95%, 55%)', // #F91D54
    'accent-11': 'hsl(345, 95%, 44%)', // #DC063B
    ...danger$1,
    ...warning$1,
    ...success$1,
    ...info$1,
    ...neutral$1,
    'neutral-2': 'hsl(220, 18%, 97%)', // #F5F6F8
    'neutral-6': 'hsl(219, 18%, 78%)', // #BEC5D2
    'neutral-7': 'hsl(220, 18%, 67%)', // #9CA6BA
    'neutral-8': 'hsl(220, 18%, 57%)', // #7F8CA6
    'neutral-9': 'hsl(221, 18%, 49%)', // #677594
    'neutral-10': 'hsl(221, 18%, 41%)', // #55617B
    'neutral-11': 'hsl(221, 18%, 35%)', // #495369
    ...promote$1,
    ...overlay$1,
    'lo-contrast': '$white', // #FFFFFF
    'hi-contrast': '$black', // #292E3B
    'white': 'hsl(0, 0%, 100%)', // #FFFFFF
    'black': 'hsl(223, 18%, 20%)', // #292E3B
};

const colors$1 = {
    ...colors$2,
    ...tokenize('primary', {
        'primary-foreground': '$primary-10',
    }),
    ...tokenize('accent', { 'accent-foreground': '$accent-10' }),
    ...tokenize('neutral', {
        'neutral-foreground': '$neutral-9',
    }),
    ...tokenize('success'),
    ...tokenize('info'),
    ...tokenize('danger'),
    ...tokenize('warning', {
        'warning-solid-active': 'hsl(50, 100%, 41%)',
        'warning-foreground-contrast': '$warning-12',
    }),
    ...tokenize('promote'),
    none: 'transparent',
    panel: '$lo-contrast',
    canvas: '$neutral-2',
    backdrop: '$overlay-8',
    placeholder: '$neutral-9',
    foreground: '$hi-contrast',
    'foreground-subtle': '$neutral-7',
    'foreground-contrast': '$lo-contrast',
    'border-subtle': '$neutral-3',
    'border': '$primary-subtle',
    'border-hover': '$neutral-6',
    'border-active': '$neutral-6',
};

const primary = rename('primary', blueDark);
const accent = rename('accent', crimsonDark);
const danger = rename('danger', redDark);
const warning = rename('warning', yellowDark);
const success = rename('success', greenDark);
const info = rename('info', cyanDark);
const neutral = rename('neutral', slateDark);
const promote = rename('promote', violetDark);
const overlay = rename('overlay', blackA);
const colors = {
    ...primary,
    ...accent,
    ...danger,
    ...warning,
    ...success,
    ...info,
    ...neutral,
    ...promote,
    ...overlay,
    'lo-contrast': '$black',
    'hi-contrast': '$white',
    'white': 'hsl(0, 0%, 100%)', // #FFFFFF
    'black': 'hsl(0, 0%, 0%)', // #000000
};

const theme$1 = {
    colors: colors$1,
    space: {
        none: '0',
        xsmall: '0.25rem',
        small: '0.5rem',
        medium: '1rem',
        large: '1.5rem',
        xlarge: '3rem',
    },
    sizes: {
        1: '0.25rem',
        2: '0.5rem',
        3: '0.75rem',
        4: '1rem',
        5: '1.25rem',
        6: '1.5rem',
        8: '2rem',
        10: '2.5rem',
        12: '3rem',
        16: '4rem',
        20: '5rem',
        24: '6rem',
        32: '8rem',
        40: '10rem',
        48: '12rem',
        56: '14rem',
        64: '16rem',
        72: '18rem',
        80: '20rem',
        96: '24rem',
        full: '100%',
    },
    borderWidths: {
        none: '0',
        regular: '1px',
        thick: '2px',
    },
    radii: {
        none: '0',
        xsmall: '0.5rem',
        small: '0.75rem',
        medium: '1rem',
        large: '1.5rem',
        xlarge: '2rem',
        round: '50%',
        pill: '9999px',
    },
    shadows: {
        none: 'none',
        inner: 'inset 0px 5px 10px rgba(49, 107, 232, 0.1), inset 0px 1px 3px rgba(49, 107, 232, 0.08), inset 0px 0px 1px rgba(49, 107, 232, 0.04)',
        outline: '0 0 0 3px rgba(66, 153, 225, 0.5)',
        small: '0 .125rem .25rem rgba(0, 0, 0, 0.075)',
        medium: '0 .5rem 1rem rgba(0, 0, 0, 0.15)',
        large: '0 1rem 3rem rgba(0, 0, 0, 0.175)',
    },
    fonts: {
        body: '"Atlas Grotesk", "Helvetica Neue", Arial, sans-serif',
        heading: '"Biofolio Ultimate", "Atlas Grotesk", "Helvetica Neue", Arial, sans-serif',
        monospace: 'SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
    },
    lineHeights: {
        none: '1',
        xsmall: '$small',
        small: '$medium',
        medium: '1.4',
        large: '1.35',
        'heading-6': '$medium',
        'heading-5': '1.3',
        'heading-4': '$large',
        'heading-3': '$heading-5',
        'heading-2': '1.2',
        'heading-1': '$heading-2',
    },
    fontSizes: {
        xsmall: '0.75rem',
        small: '0.875rem',
        medium: '1rem',
        large: '1.125rem',
        'heading-6': '1.25rem',
        'heading-5': '1.375rem',
        'heading-4': '$heading-5',
        'heading-3': '1.625rem',
        'heading-2': '2rem',
        'heading-1': '2.5rem',
    },
    fontWeights: {
        light: '300',
        regular: '400',
        bold: '700',
        'heading-light': '200',
        'heading-regular': '400',
        'heading-bold': '600',
    },
    zIndices: {
        0: '0',
        1: '1000',
        2: '1020',
        3: '1030',
        4: '1040',
        5: '1050',
        6: '1060',
        7: '1070',
        dropdown: '$1',
        sticky: '$2',
        fixed: '$3',
        backdrop: '$4',
        modal: '$5',
        popover: '$6',
        tooltip: '$7',
        max: '9999',
        auto: 'auto',
    },
};

const stitches = createStitches({
    prefix: 'kp',
    theme: theme$1,
    media: {
        tablet: '(min-width: 768px)',
        desktop: '(min-width: 1024px)',
    },
    utils: {
        p: util('paddingTop', 'paddingRight', 'paddingBottom', 'paddingLeft'),
        pt: util('paddingTop'),
        pr: util('paddingRight'),
        pb: util('paddingBottom'),
        pl: util('paddingLeft'),
        px: util('paddingLeft', 'paddingRight'),
        py: util('paddingTop', 'paddingBottom'),
        m: util('marginTop', 'marginRight', 'marginBottom', 'marginLeft'),
        mt: util('marginTop'),
        mr: util('marginRight'),
        mb: util('marginBottom'),
        ml: util('marginLeft'),
        mx: util('marginLeft', 'marginRight'),
        my: util('marginTop', 'marginBottom'),
        bg: util('background'),
        w: util('width'),
        minW: util('minWidth'),
        maxW: util('maxWidth'),
        h: util('height'),
        minH: util('minHeight'),
        maxH: util('maxHeight'),
        size: util('width', 'height'),
        minSize: util('minWidth', 'minHeight'),
        maxSize: util('maxWidth', 'maxHeight'),
        inset: util('top', 'left', 'right', 'bottom'),
        insetX: util('left', 'right'),
        insetY: util('top', 'bottom'),
    },
});
const { Styled, css, theme, globalCss, keyframes, propsToCss, config, createTheme, } = stitches;

const media = config.media;
function resolveActive(state) {
    if (state.breakpoints[media.desktop])
        return 'desktop';
    if (state.breakpoints[media.tablet])
        return 'tablet';
    return 'initial';
}
class Breakpoints {
    constructor(observer) {
        this.destroy$ = new Subject();
        this.state$ = new BehaviorSubject({
            matches: false,
            breakpoints: {},
        });
        this.breakpoint$ = new BehaviorSubject('initial');
        this.active$ = this.breakpoint$.pipe(distinctUntilChanged());
        this.tablet$ = this.state$.pipe(pluck('breakpoints', media.tablet), distinctUntilChanged());
        this.desktop$ = this.state$.pipe(pluck('breakpoints', media.desktop), distinctUntilChanged());
        observer
            .observe(Object.values(media))
            .pipe(takeUntil(this.destroy$))
            .subscribe(state => {
            this.state$.next(state);
            this.breakpoint$.next(resolveActive(state));
        });
    }
    get active() {
        return this.breakpoint$.getValue();
    }
    get tablet() {
        return !!this.state$.getValue()?.breakpoints?.[media.tablet];
    }
    get desktop() {
        return !!this.state$.getValue()?.breakpoints?.[media.desktop];
    }
    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: Breakpoints, deps: [{ token: i1.BreakpointObserver }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: Breakpoints, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: Breakpoints, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: () => [{ type: i1.BreakpointObserver }] });

const register = globalCss({
    // Had to cast the type as any to avoid a typescript missmatch.
    // Stitches support @import as an array but the type expects a string.
    '@import': [
        'url("https://cdn.slidebean.com/fonts/atlas-grotesk/font.css?weight=300,400,700")',
        'url("https://cdn.slidebean.com/fonts/biofolio/font.css?weight=200,400,600")',
    ],
    body: {
        color: '$foreground',
        fontSize: '$medium',
        fontWeight: '$regular',
        fontFamily: '$body',
    },
    '*, *:after, *:before': {
        boxSizing: 'border-box',
    },
});
function registerGlobalStyles() {
    register();
}

function cartesianProduct(...factors) {
    return factors.reduce((results, entries) => results
        .map(result => entries.map(entry => result.concat([entry])))
        .reduce((subResults, result) => subResults.concat(result), []), [[]]);
}
function compoundVariants(fn, ...args) {
    return cartesianProduct(...args).map(value => fn(...value));
}

const GLOBAL_STYLES_INITIALIZER = {
    provide: APP_INITIALIZER,
    useFactory() {
        return async () => {
            registerGlobalStyles();
        };
    },
    multi: true,
};

var __decorate$3A = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
const $Avatar = css({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    overflow: 'hidden',
    lineHeight: '$none',
    pointerEvents: 'auto',
    borderRadius: '$round',
    color: '$primary-foreground',
    backgroundColor: '$primary-subtle',
    '& > *': { pointerEvents: 'none', userSelect: 'none' },
    '& > span': { display: 'inline-block' },
    '& > img': {
        width: '$full',
        height: '$full',
        objectFit: 'cover',
    },
    variants: {
        size: {
            xxsmall: {
                size: '2rem',
                minSize: '2rem',
                fontSize: '$small',
            },
            xsmall: {
                size: '2.5rem',
                minSize: '2.5rem',
                fontSize: '$small',
            },
            small: {
                size: '3rem',
                minSize: '3rem',
                fontSize: '$medium',
            },
            medium: {
                size: '3.5rem',
                minSize: '3.5rem',
                fontSize: '$large',
            },
            large: {
                size: '4.5rem',
                minSize: '4.5rem',
                fontSize: '$heading-2',
            },
            xlarge: {
                size: '5.5rem',
                minSize: '5.5rem',
                fontSize: '$heading-1',
            },
            xxlarge: {
                size: '6.5rem',
                minSize: '6.5rem',
                fontSize: '$heading-1',
            },
        },
    },
    defaultVariants: {
        size: 'medium',
    },
});
class AvatarBehavior extends AvatarPrimitive {
    static { this.FALLBACK_DELAY = 300; }
    getFallbackDelay() {
        return AvatarBehavior.FALLBACK_DELAY;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: AvatarBehavior, deps: null, target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.1.4", type: AvatarBehavior, selector: "kp-avatar", usesInheritance: true, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: AvatarBehavior, decorators: [{
            type: Directive,
            args: [{ selector: 'kp-avatar' }]
        }] });
/**
 * The Avatar component is used to represent users or organizations by displaying a profile picture or name initials.
 */
let AvatarComponent = class AvatarComponent {
    constructor(behavior) {
        this.behavior = behavior;
    }
    get isFallback$() {
        return this.behavior.content$.pipe(map(content => content === 'fallback'));
    }
    get isImage$() {
        return this.behavior.content$.pipe(map(content => content === 'image'));
    }
    get content() {
        if (!!this.fallback)
            return this.fallback;
        if (!this.alt)
            return this.alt;
        return this.alt
            .toUpperCase()
            .split(/\s+/)
            .map(s => s.substring(0, 1))
            .slice(0, 2)
            .join('');
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: AvatarComponent, deps: [{ token: AvatarBehavior, host: true }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: AvatarComponent, selector: "kp-avatar", inputs: { src: "src", alt: "alt", fallback: "fallback", size: "size" }, host: { properties: { "class": "this.class" } }, ngImport: i0, template: `
    <img *ngIf="isImage$ | async" [attr.src]="src" [attr.alt]="alt" />
    <span *ngIf="isFallback$ | async">{{ content }}</span>
  `, isInline: true, dependencies: [{ kind: "directive", type: i1$1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "pipe", type: i1$1.AsyncPipe, name: "async" }], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
};
AvatarComponent = __decorate$3A([
    Styled($Avatar)
], AvatarComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: AvatarComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-avatar',
                    template: `
    <img *ngIf="isImage$ | async" [attr.src]="src" [attr.alt]="alt" />
    <span *ngIf="isFallback$ | async">{{ content }}</span>
  `,
                    changeDetection: ChangeDetectionStrategy.OnPush,
                }]
        }], ctorParameters: () => [{ type: AvatarBehavior, decorators: [{
                    type: Host
                }] }], propDecorators: { class: [{
                type: HostBinding,
                args: ['class']
            }], src: [{
                type: Input
            }], alt: [{
                type: Input
            }], fallback: [{
                type: Input
            }], size: [{
                type: Input
            }] } });
const $AvatarGroup = css({
    display: 'flex',
    flexWrap: 'nowrap',
    flexDirection: 'row',
    alignItems: 'center',
    '& > *:nth-child(n+2)': {
        border: '$borderWidths$thick solid $colors$bg',
    },
    variants: {
        size: {
            xxsmall: { '& > * + *': { ml: '-$small' } },
            xsmall: { '& > * + *': { ml: '-$small' } },
            small: { '& > * + *': { ml: '-$medium' } },
            medium: { '& > * + *': { ml: '-$medium' } },
            large: { '& > * + *': { ml: '-$large' } },
            xlarge: { '& > * + *': { ml: '-$large' } },
            xxlarge: { '& > * + *': { ml: '-$large' } },
        },
    },
    defaultVariants: {
        size: 'medium',
    },
});
let AvatarGroupComponent = class AvatarGroupComponent {
    constructor() {
        this.content = [];
        this.max = 3;
    }
    get length() {
        return this.content?.length ?? 0;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: AvatarGroupComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: AvatarGroupComponent, selector: "kp-avatar-group", inputs: { content: "content", size: "size", max: "max" }, host: { properties: { "class": "this.class" } }, ngImport: i0, template: `
    <kp-avatar
      *ngFor="let x of content?.slice(0, max)"
      [src]="x?.src"
      [alt]="x?.alt"
      [fallback]="x?.fallback"
      [size]="size"
    >
    </kp-avatar>
    <kp-avatar
      *ngIf="length > max"
      [fallback]="'+' + (length - max)"
      [size]="size"
    >
    </kp-avatar>
  `, isInline: true, dependencies: [{ kind: "directive", type: i1$1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1$1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: AvatarBehavior, selector: "kp-avatar" }, { kind: "component", type: AvatarComponent, selector: "kp-avatar", inputs: ["src", "alt", "fallback", "size"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
};
AvatarGroupComponent = __decorate$3A([
    Styled($AvatarGroup)
], AvatarGroupComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: AvatarGroupComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-avatar-group',
                    template: `
    <kp-avatar
      *ngFor="let x of content?.slice(0, max)"
      [src]="x?.src"
      [alt]="x?.alt"
      [fallback]="x?.fallback"
      [size]="size"
    >
    </kp-avatar>
    <kp-avatar
      *ngIf="length > max"
      [fallback]="'+' + (length - max)"
      [size]="size"
    >
    </kp-avatar>
  `,
                    changeDetection: ChangeDetectionStrategy.OnPush,
                }]
        }], propDecorators: { class: [{
                type: HostBinding,
                args: ['class']
            }], content: [{
                type: Input
            }], size: [{
                type: Input
            }], max: [{
                type: Input
            }] } });
const DIRECTIVES$3z = [AvatarBehavior, AvatarComponent, AvatarGroupComponent];
class AvatarModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: AvatarModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: AvatarModule, declarations: [AvatarBehavior, AvatarComponent, AvatarGroupComponent], imports: [CommonModule], exports: [AvatarBehavior, AvatarComponent, AvatarGroupComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: AvatarModule, imports: [CommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: AvatarModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                    declarations: DIRECTIVES$3z,
                    exports: DIRECTIVES$3z,
                }]
        }] });

function shallowEqual(left, right) {
    if (left === right)
        return true;
    if (left == null ||
        typeof left !== 'object' ||
        right == null ||
        typeof right !== 'object') {
        return false;
    }
    const leftKeys = Object.keys(left);
    const rightKeys = Object.keys(right);
    if (leftKeys.length !== rightKeys.length)
        return false;
    for (let i = 0; i < leftKeys.length; i++) {
        const key = leftKeys[i];
        if (left[key] !== right[key] ||
            !Object.prototype.hasOwnProperty.call(right, key)) {
            return false;
        }
    }
    return true;
}
function equalsBackgroundStates(left, right) {
    if (left === right)
        return true;
    if (left == null || right == null)
        return false;
    return (shallowEqual(left.variant, right.variant) &&
        left.lightness === right.lightness);
}
class BackgroundContext {
    constructor(parent) {
        this.parent = parent;
        this.destroy$ = new Subject();
        this.state$ = new BehaviorSubject({
            variant: this.parent?.variant,
            lightness: this.parent?.lightness,
        });
        this.variant$ = this.state$.pipe(pluck('variant'), distinctUntilChanged());
        this.lightness$ = this.state$.pipe(pluck('lightness'), distinctUntilChanged());
        if (!!parent) {
            parent.variant$
                .pipe(filter(value => value !== this.variant), takeUntil(this.destroy$))
                .subscribe(variant => this.useVariant(variant));
            parent.lightness$
                .pipe(filter(value => value !== this.lightness), takeUntil(this.destroy$))
                .subscribe(lightness => this.useLightness(lightness));
        }
    }
    get variant() {
        return this.state$.getValue().variant;
    }
    get lightness() {
        return this.state$.getValue().lightness;
    }
    useVariant(variant) {
        if (typeof variant !== 'object') {
            this.useState(variant === 'none' || variant == null ? this.parent?.variant : variant, this.lightness);
            return;
        }
        const merged = Object.entries(variant).reduce((state, [key, value]) => {
            const parent = typeof this.parent?.variant === 'object'
                ? this.parent.variant[key]
                : this.parent?.variant;
            state[key] = value === 'none' || value == null ? parent : value;
            return state;
        }, typeof this.parent?.variant === 'object'
            ? { ...this.parent.variant }
            : {});
        this.useState(merged, this.lightness);
    }
    useLightness(lightness) {
        this.useState(this.variant, lightness);
    }
    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }
    useState(variant, lightness) {
        const state = { variant, lightness };
        if (equalsBackgroundStates(this.state$.getValue(), state))
            return;
        this.state$.next(state);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: BackgroundContext, deps: [{ token: BackgroundContext, optional: true, skipSelf: true }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: BackgroundContext }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: BackgroundContext, decorators: [{
            type: Injectable
        }], ctorParameters: () => [{ type: BackgroundContext, decorators: [{
                    type: Optional
                }, {
                    type: SkipSelf
                }] }] });
class BackgroundPrimitive {
    constructor(context) {
        this.context = context;
    }
    ngOnChanges(changes) {
        const [hasChanged, variant] = this.getVariantToUse(changes);
        if (!hasChanged)
            return;
        this.context.useVariant(variant);
    }
    asDark() {
        this.context.useLightness('dark');
    }
    asLight() {
        this.context.useLightness('light');
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: BackgroundPrimitive, deps: [{ token: BackgroundContext, host: true }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.1.4", type: BackgroundPrimitive, usesOnChanges: true, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: BackgroundPrimitive, decorators: [{
            type: Directive
        }], ctorParameters: () => [{ type: BackgroundContext, decorators: [{
                    type: Host
                }] }] });

var __decorate$3z = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
function space(property) {
    return {
        none: { [property]: '$none' },
        xsmall: { [property]: '$xsmall' },
        small: { [property]: '$small' },
        medium: { [property]: '$medium' },
        large: { [property]: '$large' },
        xlarge: { [property]: '$xlarge' },
    };
}
const $Box = css({
    display: 'block',
    variants: {
        padding: space('p'),
        paddingX: space('px'),
        paddingY: space('py'),
        paddingTop: space('pt'),
        paddingRight: space('pr'),
        paddingBottom: space('pb'),
        paddingLeft: space('pl'),
        radii: {
            none: { borderRadius: '$none' },
            small: { borderRadius: '$small' },
            medium: { borderRadius: '$medium' },
            large: { borderRadius: '$large' },
            round: { borderRadius: '$round' },
            pill: { borderRadius: '$pill' },
        },
        background: {
            none: { backgroundColor: '$none' },
            canvas: { backgroundColor: '$canvas' },
            panel: { backgroundColor: '$panel' },
            'primary-solid': { backgroundColor: '$primary-solid' },
            'accent-solid': { backgroundColor: '$accent-solid' },
            'neutral-solid': { backgroundColor: '$neutral-solid' },
            'success-solid': { backgroundColor: '$success-solid' },
            'info-solid': { backgroundColor: '$info-solid' },
            'warning-solid': { backgroundColor: '$warning-solid' },
            'danger-solid': { backgroundColor: '$danger-solid' },
            'promote-solid': { backgroundColor: '$promote-solid' },
            'primary-subtle': { backgroundColor: '$primary-subtle' },
            'accent-subtle': { backgroundColor: '$accent-subtle' },
            'neutral-subtle': { backgroundColor: '$neutral-subtle' },
            'success-subtle': { backgroundColor: '$success-subtle' },
            'info-subtle': { backgroundColor: '$info-subtle' },
            'warning-subtle': { backgroundColor: '$warning-subtle' },
            'danger-subtle': { backgroundColor: '$danger-subtle' },
            'promote-subtle': { backgroundColor: '$promote-subtle' },
        },
    },
});
class BoxBackgroundBehavior extends BackgroundPrimitive {
    getVariantToUse(changes) {
        const bg = changes['background'];
        const hasChanged = !!bg && bg.currentValue !== bg.previousValue;
        return [hasChanged, bg?.currentValue];
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: BoxBackgroundBehavior, deps: null, target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.1.4", type: BoxBackgroundBehavior, selector: "kp-box, div[kp-box], section[kp-box]", inputs: { background: "background" }, providers: [BackgroundContext], usesInheritance: true, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: BoxBackgroundBehavior, decorators: [{
            type: Directive,
            args: [{
                    selector: 'kp-box, div[kp-box], section[kp-box]',
                    providers: [BackgroundContext],
                }]
        }], propDecorators: { background: [{
                type: Input
            }] } });
let BoxComponent = class BoxComponent {
    constructor(behavior) {
        this.behavior = behavior;
    }
    get css() {
        return propsToCss({
            width: this.width,
            minWidth: this.minWidth,
            maxWidth: this.maxWidth,
            height: this.height,
            minHeight: this.minHeight,
            maxHeight: this.maxHeight,
            // There is an issue with the utils types, casting to any to avoid it.
            size: this.size,
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: BoxComponent, deps: [{ token: BoxBackgroundBehavior, host: true }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: BoxComponent, selector: "kp-box, div[kp-box], section[kp-box]", inputs: { background: "background", radii: "radii", padding: "padding", paddingX: "paddingX", paddingY: "paddingY", paddingTop: "paddingTop", paddingRight: "paddingRight", paddingBottom: "paddingBottom", paddingLeft: "paddingLeft", width: "width", minWidth: "minWidth", maxWidth: "maxWidth", height: "height", minHeight: "minHeight", maxHeight: "maxHeight", size: "size" }, host: { properties: { "class": "this.class" } }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
};
BoxComponent = __decorate$3z([
    Styled($Box)
], BoxComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: BoxComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-box, div[kp-box], section[kp-box]',
                    template: '<ng-content></ng-content>',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                }]
        }], ctorParameters: () => [{ type: BoxBackgroundBehavior, decorators: [{
                    type: Host
                }] }], propDecorators: { class: [{
                type: HostBinding,
                args: ['class']
            }], background: [{
                type: Input
            }], radii: [{
                type: Input
            }], padding: [{
                type: Input
            }], paddingX: [{
                type: Input
            }], paddingY: [{
                type: Input
            }], paddingTop: [{
                type: Input
            }], paddingRight: [{
                type: Input
            }], paddingBottom: [{
                type: Input
            }], paddingLeft: [{
                type: Input
            }], width: [{
                type: Input
            }], minWidth: [{
                type: Input
            }], maxWidth: [{
                type: Input
            }], height: [{
                type: Input
            }], minHeight: [{
                type: Input
            }], maxHeight: [{
                type: Input
            }], size: [{
                type: Input
            }] } });
const DIRECTIVES$3y = [BoxComponent, BoxBackgroundBehavior];
class BoxModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: BoxModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: BoxModule, declarations: [BoxComponent, BoxBackgroundBehavior], exports: [BoxComponent, BoxBackgroundBehavior] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: BoxModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: BoxModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$3y,
                    exports: DIRECTIVES$3y,
                }]
        }] });

var __decorate$3y = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
function tone$5(value) {
    const color = value === 'none'
        ? '$foreground'
        : value === 'subtle'
            ? '$foreground-subtle'
            : `$${value}-foreground`;
    return { color, '&:hover': { color } };
}
function compound$6(tone) {
    const color = tone === 'none' || tone === 'subtle'
        ? '$foreground-contrast'
        : `$${tone}-foreground-contrast`;
    return {
        contrast: true,
        tone,
        css: { color, '&:hover': { color } },
    };
}
const $Text = css({
    display: 'inline-block',
    fontFamily: '$body',
    variants: {
        tone: {
            none: tone$5('none'),
            primary: tone$5('primary'),
            accent: tone$5('accent'),
            danger: tone$5('danger'),
            warning: tone$5('warning'),
            success: tone$5('success'),
            info: tone$5('info'),
            neutral: tone$5('neutral'),
            subtle: tone$5('subtle'),
            promote: tone$5('promote'),
        },
        weight: {
            light: { fontWeight: '$light' },
            regular: { fontWeight: '$regular' },
            bold: { fontWeight: '$bold' },
        },
        size: {
            xsmall: {
                fontSize: '$xsmall',
                lineHeight: '$xsmall',
            },
            small: {
                fontSize: '$small',
                lineHeight: '$small',
            },
            medium: {
                fontSize: '$medium',
                lineHeight: '$medium',
            },
            large: {
                fontSize: '$large',
                lineHeight: '$large',
            },
        },
        contrast: {
            true: {
                color: '$foreground-contrast',
            },
        },
    },
    compoundVariants: [
        compound$6('none'),
        compound$6('primary'),
        compound$6('accent'),
        compound$6('danger'),
        compound$6('warning'),
        compound$6('info'),
        compound$6('success'),
        compound$6('neutral'),
        compound$6('subtle'),
        compound$6('promote'),
    ],
});
const mapping = {
    'canvas': '$foreground',
    'panel': '$foreground',
    'primary-solid': '$primary-foreground-contrast',
    'primary-subtle': '$primary-foreground',
    'accent-solid': '$accent-foreground-contrast',
    'accent-subtle': '$accent-foreground',
    'neutral-solid': '$neutral-foreground-contrast',
    'neutral-subtle': '$neutral-foreground',
    'success-solid': '$success-foreground-contrast',
    'success-subtle': '$success-foreground',
    'info-solid': '$info-foreground-contrast',
    'info-subtle': '$info-foreground',
    'warning-solid': '$warning-foreground-contrast',
    'warning-subtle': '$warning-foreground',
    'danger-solid': '$danger-foreground-contrast',
    'danger-subtle': '$danger-foreground',
    'promote-solid': '$promote-foreground-contrast',
    'promote-subtle': '$promote-foreground',
};
class TextContext {
    constructor(background) {
        this.background = background;
        this.background$ = !!this.background
            ? merge(this.background.variant$, this.background.lightness$)
            : EMPTY;
    }
    getContextualTone() {
        const background = this.background?.variant;
        if (!!background) {
            const variant = this.resolveBackgroundVariant(background);
            if (!!variant)
                return variant;
        }
        if (!!background &&
            typeof background === 'string' &&
            background in mapping) {
            return mapping[background];
        }
        const lightness = this.background?.lightness;
        return lightness === 'dark' ? '$foreground-contrast' : void 0;
    }
    resolveBackgroundVariant(variant) {
        if (typeof variant !== 'object')
            return mapping[variant];
        return Object.entries(variant).reduce((state, [key, value]) => {
            state[key] = !!value ? mapping[value] : value;
            return state;
        }, {});
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TextContext, deps: [{ token: BackgroundContext, optional: true }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TextContext }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TextContext, decorators: [{
            type: Injectable
        }], ctorParameters: () => [{ type: BackgroundContext, decorators: [{
                    type: Optional
                }] }] });
class ContextualTextPrimitive {
    constructor(context) {
        this.context = context;
        this.destroy$ = new Subject();
        if (!!this.context) {
            this.context.background$.pipe(takeUntil(this.destroy$)).subscribe(() => {
                if (!this.instance ||
                    !this.instance?.['ngOnChanges'] ||
                    !!this.instance?.['tone']) {
                    return;
                }
                this.instance['ngOnChanges']({});
            });
        }
    }
    for(instance) {
        this.instance = instance;
    }
    getContextualTone() {
        if (!this.context)
            return;
        return this.context.getContextualTone();
    }
    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ContextualTextPrimitive, deps: [{ token: TextContext, host: true }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.1.4", type: ContextualTextPrimitive, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ContextualTextPrimitive, decorators: [{
            type: Directive
        }], ctorParameters: () => [{ type: TextContext, decorators: [{
                    type: Host
                }] }] });
class TextBackgroundBehavior extends ContextualTextPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TextBackgroundBehavior, deps: null, target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.1.4", type: TextBackgroundBehavior, selector: "kp-text, span[kp-text], a[kp-text], kp-text-block, div[kp-text-block], p[kp-text-block], a[kp-text-block]", providers: [TextContext], usesInheritance: true, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TextBackgroundBehavior, decorators: [{
            type: Directive,
            args: [{
                    selector: 'kp-text, span[kp-text], a[kp-text], kp-text-block, div[kp-text-block], p[kp-text-block], a[kp-text-block]',
                    providers: [TextContext],
                }]
        }] });
let TextComponent = class TextComponent {
    constructor(behavior) {
        this.behavior = behavior;
        this.behavior.for(this);
    }
    get css() {
        return !this.tone && !this.contrast
            ? propsToCss({
                color: this.behavior.getContextualTone(),
            })
            : void 0;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TextComponent, deps: [{ token: TextBackgroundBehavior, host: true }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: TextComponent, selector: "kp-text, span[kp-text], a[kp-text]", inputs: { tone: "tone", weight: "weight", size: "size", contrast: "contrast" }, host: { properties: { "class": "this.class" } }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush, preserveWhitespaces: true }); }
};
__decorate$3y([
    CoerceBooleanVariant()
], TextComponent.prototype, "contrast", void 0);
TextComponent = __decorate$3y([
    Styled($Text)
], TextComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TextComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-text, span[kp-text], a[kp-text]',
                    template: '<ng-content></ng-content>',
                    preserveWhitespaces: true,
                    changeDetection: ChangeDetectionStrategy.OnPush,
                }]
        }], ctorParameters: () => [{ type: TextBackgroundBehavior, decorators: [{
                    type: Host
                }] }], propDecorators: { class: [{
                type: HostBinding,
                args: ['class']
            }], tone: [{
                type: Input
            }], weight: [{
                type: Input
            }], size: [{
                type: Input
            }], contrast: [{
                type: Input
            }] } });
const $TextBlock = css($Text, {
    display: 'block !important',
    variants: {
        alignment: {
            left: { textAlign: 'left' },
            center: { textAlign: 'center' },
            right: { textAlign: 'right' },
        },
        truncate: {
            true: {
                '& > span': {
                    display: 'block',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                },
            },
        },
        lineClamp: {
            true: {},
        },
        // HACK: adds variant type support.
        lines: {},
    },
    compoundVariants: [
        {
            truncate: true,
            lineClamp: true,
            css: {
                '& > span': {
                    display: '-webkit-box',
                    '-webkit-box-orient': 'vertical',
                    whiteSpace: 'initial',
                },
            },
        },
    ],
});
let TextBlockComponent = class TextBlockComponent {
    constructor(behavior) {
        this.behavior = behavior;
        this.behavior.for(this);
    }
    get lineClamp() {
        return !!this.clamp;
    }
    get css() {
        let style = {};
        if (!this.tone && !this.contrast) {
            style = propsToCss({ color: this.behavior.getContextualTone() });
        }
        if (this.lineClamp) {
            style['& > span'] = propsToCss({ '-webkit-line-clamp': this.clamp });
        }
        return Object.keys(style).length > 0 ? style : void 0;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TextBlockComponent, deps: [{ token: TextBackgroundBehavior, host: true }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: TextBlockComponent, selector: "kp-text-block, div[kp-text-block], p[kp-text-block], a[kp-text-block]", inputs: { tone: "tone", weight: "weight", size: "size", alignment: "alignment", contrast: "contrast", truncate: "truncate", clamp: "clamp" }, host: { properties: { "class": "this.class" } }, ngImport: i0, template: `
    <ng-template #content><ng-content></ng-content></ng-template>
    <ng-container *ngIf="!truncate">
      <ng-container *ngTemplateOutlet="content"></ng-container>
    </ng-container>
    <span *ngIf="truncate">
      <ng-container *ngTemplateOutlet="content"></ng-container>
    </span>
  `, isInline: true, dependencies: [{ kind: "directive", type: i1$1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1$1.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush, preserveWhitespaces: true }); }
};
__decorate$3y([
    CoerceBooleanVariant()
], TextBlockComponent.prototype, "contrast", void 0);
__decorate$3y([
    CoerceBooleanVariant()
], TextBlockComponent.prototype, "truncate", void 0);
TextBlockComponent = __decorate$3y([
    Styled($TextBlock)
], TextBlockComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TextBlockComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-text-block, div[kp-text-block], p[kp-text-block], a[kp-text-block]',
                    template: `
    <ng-template #content><ng-content></ng-content></ng-template>
    <ng-container *ngIf="!truncate">
      <ng-container *ngTemplateOutlet="content"></ng-container>
    </ng-container>
    <span *ngIf="truncate">
      <ng-container *ngTemplateOutlet="content"></ng-container>
    </span>
  `,
                    preserveWhitespaces: true,
                    changeDetection: ChangeDetectionStrategy.OnPush,
                }]
        }], ctorParameters: () => [{ type: TextBackgroundBehavior, decorators: [{
                    type: Host
                }] }], propDecorators: { class: [{
                type: HostBinding,
                args: ['class']
            }], tone: [{
                type: Input
            }], weight: [{
                type: Input
            }], size: [{
                type: Input
            }], alignment: [{
                type: Input
            }], contrast: [{
                type: Input
            }], truncate: [{
                type: Input
            }], clamp: [{
                type: Input
            }] } });
const DIRECTIVES$3x = [TextComponent, TextBlockComponent, TextBackgroundBehavior];
class TextModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TextModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: TextModule, declarations: [TextComponent, TextBlockComponent, TextBackgroundBehavior], imports: [CommonModule], exports: [TextComponent, TextBlockComponent, TextBackgroundBehavior] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TextModule, imports: [CommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TextModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                    declarations: DIRECTIVES$3x,
                    exports: DIRECTIVES$3x,
                }]
        }] });

var __decorate$3x = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
let TextHostComponent = class TextHostComponent {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TextHostComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: TextHostComponent, selector: "kp-text-host", host: { properties: { "class": "this.class" } }, providers: [TextBackgroundBehavior, TextContext], ngImport: i0, template: '<kp-text><ng-content></ng-content></kp-text>', isInline: true, dependencies: [{ kind: "component", type: TextComponent, selector: "kp-text, span[kp-text], a[kp-text]", inputs: ["tone", "weight", "size", "contrast"] }, { kind: "directive", type: TextBackgroundBehavior, selector: "kp-text, span[kp-text], a[kp-text], kp-text-block, div[kp-text-block], p[kp-text-block], a[kp-text-block]" }], changeDetection: i0.ChangeDetectionStrategy.OnPush, preserveWhitespaces: true }); }
};
TextHostComponent = __decorate$3x([
    Styled(css({ display: 'inline-block' }))
], TextHostComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TextHostComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-text-host',
                    template: '<kp-text><ng-content></ng-content></kp-text>',
                    preserveWhitespaces: true,
                    providers: [TextBackgroundBehavior, TextContext],
                    changeDetection: ChangeDetectionStrategy.OnPush,
                }]
        }], propDecorators: { class: [{
                type: HostBinding,
                args: ['class']
            }] } });
class KappaTextContentRenderer {
    constructor(resolver, injector, app, renderer, factory, view) {
        this.resolver = resolver;
        this.injector = injector;
        this.app = app;
        this.renderer = renderer;
        this.renderer = this.renderer ?? factory.createRenderer(null, null);
        this.injector = view?.injector ?? this.injector;
    }
    render(options) {
        const factory = this.resolver.resolveComponentFactory(TextHostComponent);
        const component = factory.create(this.resolveInjector(options), [
            [this.renderer.createText(options.value)],
        ]);
        this.app.attachView(component.hostView);
        return new ComponentContentRef(component, this.app);
    }
    resolveInjector(options) {
        const parent = options.injector ?? options?.container?.injector ?? this.injector;
        return !!options?.providers && !!options.providers.length
            ? Injector.create({
                providers: options.providers,
                parent,
            })
            : parent;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: KappaTextContentRenderer, deps: [{ token: i0.ComponentFactoryResolver }, { token: i0.Injector }, { token: i0.ApplicationRef }, { token: i0.Renderer2 }, { token: i0.RendererFactory2 }, { token: i0.ViewContainerRef, optional: true }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: KappaTextContentRenderer }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: KappaTextContentRenderer, decorators: [{
            type: Injectable
        }], ctorParameters: () => [{ type: i0.ComponentFactoryResolver }, { type: i0.Injector }, { type: i0.ApplicationRef }, { type: i0.Renderer2 }, { type: i0.RendererFactory2 }, { type: i0.ViewContainerRef, decorators: [{
                    type: Optional
                }] }] });
const TEXT_CONTENT_PROVIDERS = [
    {
        provide: TEXT_CONTENT_RENDERER,
        useClass: KappaTextContentRenderer,
    },
];
const DIRECTIVES$3w = [TextHostComponent];
class TextRendererModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TextRendererModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: TextRendererModule, declarations: [TextHostComponent], imports: [TextModule], exports: [TextModule, TextHostComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TextRendererModule, imports: [TextModule, TextModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TextRendererModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [TextModule],
                    declarations: DIRECTIVES$3w,
                    exports: [TextModule, ...DIRECTIVES$3w],
                }]
        }] });

var __decorate$3w = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
const $Heading = css(TextBlockComponent, {
    variants: {
        weight: {
            light: { fontWeight: '$heading-light' },
            regular: { fontWeight: '$heading-regular' },
            bold: { fontWeight: '$heading-bold' },
        },
        level: {
            '1': {
                fontFamily: '$heading',
                fontSize: '$heading-1',
                lineHeight: '$heading-1',
            },
            '2': {
                fontFamily: '$heading',
                fontSize: '$heading-2',
                lineHeight: '$heading-2',
            },
            '3': {
                fontFamily: '$heading',
                fontSize: '$heading-3',
                lineHeight: '$heading-3',
            },
            '4': {
                fontFamily: '$heading',
                fontSize: '$heading-4',
                lineHeight: '$heading-4',
            },
            '5': {
                fontFamily: '$body',
                fontSize: '$heading-5',
                lineHeight: '$heading-5',
            },
            '6': {
                fontFamily: '$body',
                fontSize: '$heading-6',
                lineHeight: '$heading-6',
            },
        },
    },
    defaultVariants: {
        weight: 'regular',
        level: '2',
    },
});
class ContextualHeadingPrimitive extends ContextualTextPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ContextualHeadingPrimitive, deps: null, target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.1.4", type: ContextualHeadingPrimitive, usesInheritance: true, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ContextualHeadingPrimitive, decorators: [{
            type: Directive
        }] });
class HeadingBackgroundBehavior extends ContextualHeadingPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: HeadingBackgroundBehavior, deps: null, target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.1.4", type: HeadingBackgroundBehavior, selector: "kp-heading, span[kp-heading], a[kp-heading]", providers: [TextContext], usesInheritance: true, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: HeadingBackgroundBehavior, decorators: [{
            type: Directive,
            args: [{
                    selector: 'kp-heading, span[kp-heading], a[kp-heading]',
                    providers: [TextContext],
                }]
        }] });
let HeadingComponent = class HeadingComponent {
    constructor(behavior) {
        this.behavior = behavior;
        this.behavior.for(this);
    }
    get lineClamp() {
        return !!this.clamp;
    }
    get css() {
        let style = {};
        if (!this.tone && !this.contrast) {
            style = propsToCss({ color: this.behavior.getContextualTone() });
        }
        if (this.lineClamp) {
            style['& > span'] = propsToCss({ '-webkit-line-clamp': this.clamp });
        }
        return Object.keys(style).length > 0 ? style : void 0;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: HeadingComponent, deps: [{ token: HeadingBackgroundBehavior, host: true }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: HeadingComponent, selector: "kp-heading, span[kp-heading], a[kp-heading]", inputs: { tone: "tone", weight: "weight", level: "level", alignment: "alignment", contrast: "contrast", truncate: "truncate", clamp: "clamp" }, host: { properties: { "class": "this.class" } }, ngImport: i0, template: `
    <ng-template #content><ng-content></ng-content></ng-template>
    <ng-container *ngIf="!truncate">
      <ng-container *ngTemplateOutlet="content"></ng-container>
    </ng-container>
    <span *ngIf="truncate">
      <ng-container *ngTemplateOutlet="content"></ng-container>
    </span>
  `, isInline: true, dependencies: [{ kind: "directive", type: i1$1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1$1.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush, preserveWhitespaces: true }); }
};
__decorate$3w([
    CoerceBooleanVariant()
], HeadingComponent.prototype, "contrast", void 0);
__decorate$3w([
    CoerceBooleanVariant()
], HeadingComponent.prototype, "truncate", void 0);
HeadingComponent = __decorate$3w([
    Styled($Heading)
], HeadingComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: HeadingComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-heading, span[kp-heading], a[kp-heading]',
                    template: `
    <ng-template #content><ng-content></ng-content></ng-template>
    <ng-container *ngIf="!truncate">
      <ng-container *ngTemplateOutlet="content"></ng-container>
    </ng-container>
    <span *ngIf="truncate">
      <ng-container *ngTemplateOutlet="content"></ng-container>
    </span>
  `,
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    preserveWhitespaces: true,
                }]
        }], ctorParameters: () => [{ type: HeadingBackgroundBehavior, decorators: [{
                    type: Host
                }] }], propDecorators: { class: [{
                type: HostBinding,
                args: ['class']
            }], tone: [{
                type: Input
            }], weight: [{
                type: Input
            }], level: [{
                type: Input
            }], alignment: [{
                type: Input
            }], contrast: [{
                type: Input
            }], truncate: [{
                type: Input
            }], clamp: [{
                type: Input
            }] } });
const DIRECTIVES$3v = [HeadingComponent, HeadingBackgroundBehavior];
class HeadingModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: HeadingModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: HeadingModule, declarations: [HeadingComponent, HeadingBackgroundBehavior], imports: [CommonModule], exports: [HeadingComponent, HeadingBackgroundBehavior] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: HeadingModule, imports: [CommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: HeadingModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                    declarations: DIRECTIVES$3v,
                    exports: DIRECTIVES$3v,
                }]
        }] });

var __decorate$3v = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
const $Spacer = css({
    display: 'block',
    flex: 1,
    justifySelf: 'stretch',
    alignSelf: 'stretch',
});
let SpacerComponent = class SpacerComponent {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: SpacerComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: SpacerComponent, selector: "kp-spacer", host: { properties: { "class": "this.class" } }, ngImport: i0, template: '', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
};
SpacerComponent = __decorate$3v([
    Styled($Spacer)
], SpacerComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: SpacerComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-spacer',
                    template: '',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                }]
        }], propDecorators: { class: [{
                type: HostBinding,
                args: ['class']
            }] } });
const DIRECTIVES$3u = [SpacerComponent];
class SpacerModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: SpacerModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: SpacerModule, declarations: [SpacerComponent], exports: [SpacerComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: SpacerModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: SpacerModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$3u,
                    exports: DIRECTIVES$3u,
                }]
        }] });

var __decorate$3u = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
function scaleWidth(scale) {
    return { flex: `0 0 ${scale * 100}%` };
}
const $Column = css({
    display: 'block',
    minWidth: 0,
    maxWidth: '$full',
    variants: {
        width: {
            fluid: { flex: '1 0 0%', width: '$full' },
            content: { flex: '0 0 auto', width: 'auto' },
            '1-2': scaleWidth(1 / 2),
            '1-3': scaleWidth(1 / 3),
            '2-3': scaleWidth(2 / 3),
            '1-4': scaleWidth(1 / 4),
            '3-4': scaleWidth(3 / 4),
            '1-5': scaleWidth(1 / 5),
            '2-5': scaleWidth(2 / 5),
            '3-5': scaleWidth(3 / 5),
            '4-5': scaleWidth(4 / 5),
        },
    },
    defaultVariants: {
        width: 'fluid',
    },
});
let ColumnComponent = class ColumnComponent {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ColumnComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: ColumnComponent, selector: "kp-column", inputs: { width: "width" }, host: { properties: { "class": "this.class" } }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
};
ColumnComponent = __decorate$3u([
    Styled($Column)
], ColumnComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ColumnComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-column',
                    template: '<ng-content></ng-content>',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                }]
        }], propDecorators: { class: [{
                type: HostBinding,
                args: ['class']
            }], width: [{
                type: Input
            }] } });
function createNonCollapsedStyle(selector, breakpoint, space, reverse, wrap) {
    return {
        flexWrap: wrap ? 'wrap' : 'nowrap',
        flexDirection: reverse ? 'row-reverse' : 'row',
        mx: wrap ? `calc($${space} * -0.5)` : '$none',
        mt: wrap ? `-$${space}` : '$none',
        [selector]: {
            px: `calc($${space} * 0.5)`,
            mt: wrap ? `$${space}` : '$none',
        },
        ['& > *:not(:only-child):first-child']: {
            pr: !wrap ? (reverse ? '$none' : `calc($${space} * 0.5)`) : void 0,
            pl: !wrap ? (reverse ? `calc($${space} * 0.5)` : '$none') : void 0,
            mt: breakpoint !== 'none' ? (wrap ? `$${space}` : '$none') : void 0,
        },
        ['& > *:not(:only-child):last-child']: {
            pr: !wrap ? (reverse ? `calc($${space} * 0.5)` : '$none') : void 0,
            pl: !wrap ? (reverse ? '$none' : `calc($${space} * 0.5)`) : void 0,
            mt: breakpoint !== 'none' ? (wrap ? `$${space}` : '$none') : void 0,
        },
    };
}
function compound$5(collapseBelow, space, reverse, wrap) {
    const selector = wrap ? '& > *' : '& > *:not(:first-child):not(:last-child)';
    if (collapseBelow === 'none') {
        return {
            collapseBelow,
            space,
            reverse,
            wrap,
            css: createNonCollapsedStyle(selector, collapseBelow, space, reverse, wrap),
        };
    }
    return {
        collapseBelow,
        space,
        reverse,
        wrap,
        css: {
            flexDirection: reverse ? 'column-reverse' : 'column',
            mx: wrap ? `calc($${space} * -0.5)` : '$none',
            mt: wrap ? `-$${space}` : '$none',
            [selector]: {
                px: wrap ? `calc($${space} * 0.5)` : '$none',
                mt: `$${space}`,
            },
            ['& > *:not(:only-child):first-child']: {
                px: wrap ? `calc($${space} * 0.5)` : '$none',
                mt: !wrap ? (reverse ? `$${space}` : '$none') : void 0,
            },
            ['& > *:not(:only-child):last-child']: {
                px: wrap ? `calc($${space} * 0.5)` : '$none',
                mt: !wrap ? (reverse ? '$none' : `$${space}`) : void 0,
            },
            [`@${collapseBelow}`]: createNonCollapsedStyle(selector, collapseBelow, space, reverse, wrap),
        },
    };
}
function flexAlignment$1(alignment) {
    if (alignment === 'left' || alignment === 'top')
        return 'flex-start';
    if (alignment === 'center')
        return 'center';
    if (alignment === 'right' || alignment === 'bottom')
        return 'flex-end';
    return void 0;
}
function compoundAlignment$1(alignmentX, alignmentY, collapseBelow) {
    if (alignmentX == null && alignmentX == null)
        return void 0;
    const style = {
        justifyContent: alignmentY == null
            ? flexAlignment$1(alignmentX)
            : flexAlignment$1(alignmentY),
        alignItems: alignmentX == null
            ? flexAlignment$1(alignmentY)
            : flexAlignment$1(alignmentX),
        [`@${collapseBelow}`]: {
            justifyContent: alignmentX == null
                ? flexAlignment$1(alignmentY)
                : flexAlignment$1(alignmentX),
            alignItems: alignmentY == null
                ? flexAlignment$1(alignmentX)
                : flexAlignment$1(alignmentY),
        },
    };
    return {
        alignmentX,
        alignmentY,
        collapseBelow,
        css: style,
    };
}
const $ColumnsContent = css({
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'nowrap',
    '& > *[truncate]': {
        minW: 0,
    },
    [`& > ${SpacerComponent}`]: {
        m: '$none !important',
        p: '$none !important',
    },
    variants: {
        space: {
            none: {},
            xsmall: {},
            small: {},
            medium: {},
            large: {},
            xlarge: {},
        },
        alignmentX: {
            left: { justifyContent: 'flex-start' },
            center: { justifyContent: 'center' },
            right: { justifyContent: 'flex-end' },
        },
        alignmentY: {
            top: { alignItems: 'flex-start' },
            center: { alignItems: 'center' },
            bottom: { alignItems: 'flex-end' },
        },
        collapseBelow: {
            none: {},
            tablet: {},
            desktop: {},
        },
        reverse: {
            true: {},
            false: {},
        },
        wrap: {
            true: {},
            false: {},
        },
    },
    defaultVariants: {
        space: 'none',
        collapseBelow: 'none',
        reverse: false,
        wrap: false,
    },
    compoundVariants: [
        ...compoundVariants(compound$5, ['none', 'tablet', 'desktop'], ['none', 'xsmall', 'small', 'medium', 'large', 'xlarge'], [true, false], [true, false]),
        ...compoundVariants(compoundAlignment$1, ['left', 'center', 'right', void 0], ['top', 'center', 'bottom', void 0], ['tablet', 'desktop']).filter(Boolean),
    ],
});
let ColumnsContentComponent = class ColumnsContentComponent {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ColumnsContentComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: ColumnsContentComponent, selector: "kp-columns-content", inputs: { space: "space", alignmentX: "alignmentX", alignmentY: "alignmentY", collapseBelow: "collapseBelow", wrap: "wrap", reverse: "reverse" }, host: { properties: { "class": "this.class" } }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
};
__decorate$3u([
    CoerceBooleanVariant()
], ColumnsContentComponent.prototype, "wrap", void 0);
__decorate$3u([
    CoerceBooleanVariant()
], ColumnsContentComponent.prototype, "reverse", void 0);
ColumnsContentComponent = __decorate$3u([
    Styled($ColumnsContent)
], ColumnsContentComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ColumnsContentComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-columns-content',
                    template: '<ng-content></ng-content>',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                }]
        }], propDecorators: { class: [{
                type: HostBinding,
                args: ['class']
            }], space: [{
                type: Input
            }], alignmentX: [{
                type: Input
            }], alignmentY: [{
                type: Input
            }], collapseBelow: [{
                type: Input
            }], wrap: [{
                type: Input
            }], reverse: [{
                type: Input
            }] } });
const $Columns = css({
    display: 'block',
    [`${ColumnsContentComponent}`]: {
        height: '$full',
    },
    variants: {
        wrap: {
            true: {
                overflow: 'hidden',
            },
            false: {},
        },
    },
    defaultVariants: {
        wrap: false,
    },
});
let ColumnsComponent = class ColumnsComponent {
    constructor() {
        this.space = 'none';
        this.collapseBelow = 'none';
        this.wrap = false;
        this.reverse = false;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ColumnsComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: ColumnsComponent, selector: "kp-columns", inputs: { space: "space", alignmentX: "alignmentX", alignmentY: "alignmentY", collapseBelow: "collapseBelow", wrap: "wrap", reverse: "reverse" }, host: { properties: { "class": "this.class" } }, ngImport: i0, template: `
    <kp-columns-content
      [space]="space"
      [alignmentX]="alignmentX"
      [alignmentY]="alignmentY"
      [collapseBelow]="collapseBelow"
      [wrap]="wrap"
      [reverse]="reverse"
    >
      <ng-content></ng-content>
    </kp-columns-content>
  `, isInline: true, dependencies: [{ kind: "component", type: ColumnsContentComponent, selector: "kp-columns-content", inputs: ["space", "alignmentX", "alignmentY", "collapseBelow", "wrap", "reverse"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
};
__decorate$3u([
    CoerceBooleanVariant()
], ColumnsComponent.prototype, "wrap", void 0);
__decorate$3u([
    CoerceBooleanVariant()
], ColumnsComponent.prototype, "reverse", void 0);
ColumnsComponent = __decorate$3u([
    Styled($Columns)
], ColumnsComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ColumnsComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-columns',
                    template: `
    <kp-columns-content
      [space]="space"
      [alignmentX]="alignmentX"
      [alignmentY]="alignmentY"
      [collapseBelow]="collapseBelow"
      [wrap]="wrap"
      [reverse]="reverse"
    >
      <ng-content></ng-content>
    </kp-columns-content>
  `,
                    changeDetection: ChangeDetectionStrategy.OnPush,
                }]
        }], propDecorators: { class: [{
                type: HostBinding,
                args: ['class']
            }], space: [{
                type: Input
            }], alignmentX: [{
                type: Input
            }], alignmentY: [{
                type: Input
            }], collapseBelow: [{
                type: Input
            }], wrap: [{
                type: Input
            }], reverse: [{
                type: Input
            }] } });
const DIRECTIVES$3t = [ColumnComponent, ColumnsComponent];
class ColumnsModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ColumnsModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: ColumnsModule, declarations: [ColumnComponent, ColumnsComponent, ColumnsContentComponent], imports: [CommonModule], exports: [ColumnComponent, ColumnsComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ColumnsModule, imports: [CommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ColumnsModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                    declarations: [...DIRECTIVES$3t, ColumnsContentComponent],
                    exports: DIRECTIVES$3t,
                }]
        }] });

var __decorate$3t = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
const $ContentBlock = css({
    w: '$full',
    variants: {
        display: {
            block: { display: 'block' },
            flex: { display: 'flex' },
        },
        width: {
            xsmall: { maxW: '480px' },
            small: { maxW: '660px' },
            medium: { maxW: '940px' },
            large: { maxW: '1280px' },
            xlarge: { maxW: '1400px' },
        },
        alignment: {
            left: { ml: '0' },
            right: { ml: 'auto' },
            center: { mx: 'auto' },
        },
    },
    defaultVariants: {
        display: 'block',
        alignment: 'center',
        width: 'large',
    },
});
let ContentBlockComponent = class ContentBlockComponent {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ContentBlockComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: ContentBlockComponent, selector: "kp-content-block", inputs: { display: "display", width: "width", alignment: "alignment" }, host: { properties: { "class": "this.class" } }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
};
ContentBlockComponent = __decorate$3t([
    Styled($ContentBlock)
], ContentBlockComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ContentBlockComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-content-block',
                    template: '<ng-content></ng-content>',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                }]
        }], propDecorators: { class: [{
                type: HostBinding,
                args: ['class']
            }], display: [{
                type: Input
            }], width: [{
                type: Input
            }], alignment: [{
                type: Input
            }] } });
let ContentBlockDirective = class ContentBlockDirective {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ContentBlockDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.1.4", type: ContentBlockDirective, selector: "[kp-content-block]", inputs: { display: ["kp-content-block", "display"], width: "width", alignment: "alignment" }, host: { properties: { "class": "this.class" } }, ngImport: i0 }); }
};
ContentBlockDirective = __decorate$3t([
    Styled($ContentBlock)
], ContentBlockDirective);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ContentBlockDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[kp-content-block]',
                }]
        }], propDecorators: { class: [{
                type: HostBinding,
                args: ['class']
            }], display: [{
                type: Input,
                args: ['kp-content-block']
            }], width: [{
                type: Input
            }], alignment: [{
                type: Input
            }] } });
const DIRECTIVES$3s = [ContentBlockComponent, ContentBlockDirective];
class ContentBlockModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ContentBlockModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: ContentBlockModule, declarations: [ContentBlockComponent, ContentBlockDirective], exports: [ContentBlockComponent, ContentBlockDirective] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ContentBlockModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ContentBlockModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$3s,
                    exports: DIRECTIVES$3s,
                }]
        }] });

var __decorate$3s = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
const $PopoverContent = css({
    display: 'block',
});
let PopoverContentComponent = class PopoverContentComponent {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: PopoverContentComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: PopoverContentComponent, selector: "kp-popover-content", host: { properties: { "class": "this.class" } }, ngImport: i0, template: `<ng-content></ng-content>`, isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
};
PopoverContentComponent = __decorate$3s([
    Styled($PopoverContent)
], PopoverContentComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: PopoverContentComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-popover-content',
                    template: `<ng-content></ng-content>`,
                    changeDetection: ChangeDetectionStrategy.OnPush,
                }]
        }], propDecorators: { class: [{
                type: HostBinding,
                args: ['class']
            }] } });
const $Popover = css({
    display: 'block',
    py: '1.375rem',
    boxShadow: '$small',
    backgroundColor: '$panel',
    borderRadius: '$small',
});
let PopoverComponent = class PopoverComponent {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: PopoverComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: PopoverComponent, selector: "kp-popover", host: { properties: { "class": "this.class" } }, ngImport: i0, template: `
    <kp-popover-content>
      <ng-content></ng-content>
    </kp-popover-content>
  `, isInline: true, dependencies: [{ kind: "component", type: PopoverContentComponent, selector: "kp-popover-content" }], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
};
PopoverComponent = __decorate$3s([
    Styled($Popover)
], PopoverComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: PopoverComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-popover',
                    template: `
    <kp-popover-content>
      <ng-content></ng-content>
    </kp-popover-content>
  `,
                    changeDetection: ChangeDetectionStrategy.OnPush,
                }]
        }], propDecorators: { class: [{
                type: HostBinding,
                args: ['class']
            }] } });
class PopoverBehavior extends PopoverPrimitive {
    createPopoverComponent(value) {
        return this.componentFactory.create(PopoverComponent, {
            content: { value },
        });
    }
    getOptions() {
        const options = super.getOptions();
        const modifiers = [
            {
                name: 'arrow',
                options: { element: void 0 },
            },
            { name: 'offset', options: { offset: [0, 4] } },
        ];
        options.modifiers = !!options.modifiers
            ? options.modifiers.concat(modifiers)
            : modifiers;
        return options;
    }
    getShowAnimation() {
        return {
            keyframes: [
                { opacity: 0, transform: 'translate3d(0, -8px, 0) scale(0.9)' },
                { opacity: 1, transform: 'translate3d(0, 0, 0) scale(1)' },
            ],
            options: { duration: 200, easing: 'ease-out' },
        };
    }
    getHideAnimation() {
        return {
            keyframes: [
                { opacity: 1, transform: 'translate3d(0, 0, 0) scale(1)' },
                { opacity: 0, transform: 'translate3d(0, -8px, 0) scale(0.9)' },
            ],
            options: { duration: 200, easing: 'ease-in' },
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: PopoverBehavior, deps: null, target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.1.4", type: PopoverBehavior, selector: "[kp-popover]", inputs: { content: ["kp-popover", "content"], trigger: ["kp-popover-trigger", "trigger"], placement: ["kp-popover-placement", "placement"] }, providers: [POPOVER_PROVIDERS, TEXT_CONTENT_PROVIDERS], exportAs: ["popover"], usesInheritance: true, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: PopoverBehavior, decorators: [{
            type: Directive,
            args: [{
                    selector: '[kp-popover]',
                    exportAs: 'popover',
                    providers: [POPOVER_PROVIDERS, TEXT_CONTENT_PROVIDERS],
                }]
        }], propDecorators: { content: [{
                type: Input,
                args: ['kp-popover']
            }], trigger: [{
                type: Input,
                args: ['kp-popover-trigger']
            }], placement: [{
                type: Input,
                args: ['kp-popover-placement']
            }] } });
class PopoverDirective {
    constructor(behavior) {
        this.behavior = behavior;
    }
    show() {
        return this.behavior.show();
    }
    hide() {
        return this.behavior.hide();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: PopoverDirective, deps: [{ token: PopoverBehavior, host: true }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.1.4", type: PopoverDirective, selector: "[kp-popover]", exportAs: ["popover"], ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: PopoverDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[kp-popover]',
                    exportAs: 'popover',
                }]
        }], ctorParameters: () => [{ type: PopoverBehavior, decorators: [{
                    type: Host
                }] }] });
class PopoverModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: PopoverModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: PopoverModule, declarations: [PopoverDirective, PopoverBehavior, PopoverContentComponent, PopoverComponent], imports: [TextRendererModule], exports: [PopoverDirective, PopoverBehavior] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: PopoverModule, imports: [TextRendererModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: PopoverModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [TextRendererModule],
                    declarations: [
                        PopoverDirective,
                        PopoverBehavior,
                        PopoverContentComponent,
                        PopoverComponent,
                    ],
                    exports: [PopoverDirective, PopoverBehavior],
                }]
        }] });

var __decorate$3r = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
const ICON = Symbol.for('kappa.icon');
function defineCustomIcon(prefix, definition) {
    return {
        prefix,
        iconName: definition.name,
        icon: [definition.width, definition.height, [], '', definition.svgPath],
    };
}
const CustomIconStyle = {
    path: { fillRule: 'evenodd', clipRule: 'evenodd' },
};
function coerceIcon(value) {
    const candidate = value;
    if (!candidate)
        return candidate;
    if (!!candidate[ICON])
        return candidate[ICON];
    if (!candidate.constructor)
        return candidate;
    const icon = candidate.constructor[ICON];
    return icon ?? candidate;
}
const CoerceIcon = createCoercionDecoratorFactory(coerceIcon);
const $Icon = css(TextComponent, {
    variants: {
        size: {
            small: { fontSize: '$small' },
            medium: { fontSize: '$medium' },
            large: { fontSize: '$large' },
            '1x': { fontSize: '1rem' },
            '2x': { fontSize: '2rem' },
            '3x': { fontSize: '3rem' },
            '4x': { fontSize: '4rem' },
            '5x': { fontSize: '5rem' },
            '6x': { fontSize: '6rem' },
            '7x': { fontSize: '7rem' },
            '8x': { fontSize: '8rem' },
            '9x': { fontSize: '9rem' },
            '10x': { fontSize: '10rem' },
        },
    },
});
function Icon(icon, css$) {
    return (component) => {
        if (!component)
            return component;
        if (!!icon)
            component[ICON] = icon;
        Styled(css($Icon, css$))(component);
        return component;
    };
}
const ICON_PROVIDERS = [
    { provide: TextContext, useClass: TextContext },
];
class IconPrimitive extends ContextualTextPrimitive {
    constructor(context) {
        super(context);
        this.icon = this.getIcon();
        this.for(this);
    }
    ngAfterContentInit() {
        if (!this.icon || !this.view)
            return;
        const component = this.view.createComponent(FaIconComponent);
        component.instance.icon = this.icon;
        component.instance.animation = this.spin ? 'spin' : void 0;
        component.instance.rotate = this.rotate;
        component.instance.flip = this.flip;
        component.instance.render();
        this.component = component;
    }
    ngOnChanges(changes) {
        if (!didChangeAfterFirst(changes['spin']) ||
            !didChangeAfterFirst(changes['rotate']) ||
            !didChangeAfterFirst(changes['flip']) ||
            !this.component)
            return;
        this.component.instance.animation = this.spin ? 'spin' : void 0;
        this.component.instance.rotate = this.rotate;
        this.component.instance.flip = this.flip;
        this.component.instance.render();
    }
    ngOnDestroy() {
        super.ngOnDestroy();
        if (!!this.component)
            this.component.destroy();
    }
    get css() {
        return !this.tone && !this.contrast
            ? propsToCss({
                color: this.getContextualTone(),
            })
            : void 0;
    }
    getIcon() {
        return coerceIcon(this);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: IconPrimitive, deps: [{ token: TextContext, host: true }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.1.4", type: IconPrimitive, inputs: { tone: "tone", size: "size", contrast: "contrast", spin: "spin", rotate: "rotate", flip: "flip" }, host: { properties: { "class": "this.class" } }, viewQueries: [{ propertyName: "view", first: true, predicate: ["icon"], descendants: true, read: ViewContainerRef, static: true }], usesInheritance: true, usesOnChanges: true, ngImport: i0 }); }
}
__decorate$3r([
    CoerceBoolean()
], IconPrimitive.prototype, "spin", void 0);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: IconPrimitive, decorators: [{
            type: Directive
        }], ctorParameters: () => [{ type: TextContext, decorators: [{
                    type: Host
                }] }], propDecorators: { class: [{
                type: HostBinding,
                args: ['class']
            }], view: [{
                type: ViewChild,
                args: ['icon', { static: true, read: ViewContainerRef }]
            }], tone: [{
                type: Input
            }], size: [{
                type: Input
            }], contrast: [{
                type: Input
            }], spin: [{
                type: Input
            }], rotate: [{
                type: Input
            }], flip: [{
                type: Input
            }] } });
let IconSpaceComponent = class IconSpaceComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: IconSpaceComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: IconSpaceComponent, selector: "kp-icon-space", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<div class="svg-inline--fa fa-w-12 fa-fw"></div>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
};
IconSpaceComponent = __decorate$3r([
    Styled($Icon)
], IconSpaceComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: IconSpaceComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-icon-space',
                    template: '<div class="svg-inline--fa fa-w-12 fa-fw"></div>',
                    providers: ICON_PROVIDERS,
                    changeDetection: ChangeDetectionStrategy.OnPush,
                }]
        }] });
let IconComponent = class IconComponent extends IconPrimitive {
    getIcon() {
        return coerceIcon(this.icon);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: IconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: IconComponent, selector: "kp-icon", inputs: { icon: "icon" }, providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon />', isInline: true }); }
};
__decorate$3r([
    CoerceIcon()
], IconComponent.prototype, "icon", void 0);
IconComponent = __decorate$3r([
    Styled(css($Icon, CustomIconStyle))
], IconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: IconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-icon',
                    template: '<ng-container #icon />',
                    providers: ICON_PROVIDERS,
                }]
        }], propDecorators: { icon: [{
                type: Input
            }] } });
const DIRECTIVES$3r = [IconSpaceComponent, IconComponent];
class IconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: IconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: IconModule, declarations: [IconSpaceComponent, IconComponent], exports: [IconSpaceComponent, IconComponent, FontAwesomeModule] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: IconModule, imports: [FontAwesomeModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: IconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$3r,
                    exports: [...DIRECTIVES$3r, FontAwesomeModule],
                }]
        }] });

const FA_CONFIG_INITIALIZER = {
    provide: APP_INITIALIZER,
    useFactory(fa) {
        return async () => {
            fa.fixedWidth = true;
        };
    },
    deps: [FaConfig],
    multi: true,
};

var __decorate$3q = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
let ArrowCircleRightIconComponent = class ArrowCircleRightIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ArrowCircleRightIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: ArrowCircleRightIconComponent, selector: "kp-arrow-circle-right-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
ArrowCircleRightIconComponent = __decorate$3q([
    Icon(faArrowCircleRight)
], ArrowCircleRightIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ArrowCircleRightIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-arrow-circle-right-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$3q = [ArrowCircleRightIconComponent];
class ArrowCircleRightIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ArrowCircleRightIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: ArrowCircleRightIconModule, declarations: [ArrowCircleRightIconComponent], exports: [ArrowCircleRightIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ArrowCircleRightIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ArrowCircleRightIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$3q,
                    exports: DIRECTIVES$3q,
                }]
        }] });

var __decorate$3p = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
let ArrowLeftIconComponent = class ArrowLeftIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ArrowLeftIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: ArrowLeftIconComponent, selector: "kp-arrow-left-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
ArrowLeftIconComponent = __decorate$3p([
    Icon(faArrowLeft)
], ArrowLeftIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ArrowLeftIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-arrow-left-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$3p = [ArrowLeftIconComponent];
class ArrowLeftIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ArrowLeftIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: ArrowLeftIconModule, declarations: [ArrowLeftIconComponent], exports: [ArrowLeftIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ArrowLeftIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ArrowLeftIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$3p,
                    exports: DIRECTIVES$3p,
                }]
        }] });

var __decorate$3o = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
let ArrowRightIconComponent = class ArrowRightIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ArrowRightIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: ArrowRightIconComponent, selector: "kp-arrow-right-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
ArrowRightIconComponent = __decorate$3o([
    Icon(faArrowRight)
], ArrowRightIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ArrowRightIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-arrow-right-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$3o = [ArrowRightIconComponent];
class ArrowRightIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ArrowRightIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: ArrowRightIconModule, declarations: [ArrowRightIconComponent], exports: [ArrowRightIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ArrowRightIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ArrowRightIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$3o,
                    exports: DIRECTIVES$3o,
                }]
        }] });

var __decorate$3n = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
const kpAward = defineCustomIcon('kp', {
    name: 'award',
    height: 22,
    width: 15,
    svgPath: 'M7.5 1.31134e-06C3.35786 5.87107e-07 -5.87108e-07 3.35787 -1.31134e-06 7.5C-1.72178e-06 9.84741 1.07843 11.9429 2.76688 13.3182L1.35923 20.8256C1.30881 21.0946 1.40884 21.3696 1.62024 21.5433C1.83165 21.717 2.12088 21.7618 2.37493 21.6602L7.33735 19.6752L12.2998 21.6602C12.5538 21.7618 12.843 21.717 13.0544 21.5433C13.2659 21.3696 13.3659 21.0946 13.3155 20.8256L11.9491 13.5384C13.7997 12.1727 15 9.9765 15 7.5C15 3.35787 11.6421 2.03558e-06 7.5 1.31134e-06ZM10.5739 14.3432C9.63581 14.7652 8.59532 15 7.5 15C6.28705 15 5.14135 14.7121 4.12752 14.2008L3.08506 19.7606L7.0588 18.1711C7.23761 18.0996 7.43708 18.0996 7.61589 18.1711L11.5896 19.7606L10.5739 14.3432Z',
});
let AwardIconComponent = class AwardIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: AwardIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: AwardIconComponent, selector: "kp-award-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
AwardIconComponent = __decorate$3n([
    Icon(kpAward, CustomIconStyle)
], AwardIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: AwardIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-award-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$3n = [AwardIconComponent];
class AwardIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: AwardIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: AwardIconModule, declarations: [AwardIconComponent], exports: [AwardIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: AwardIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: AwardIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$3n,
                    exports: DIRECTIVES$3n,
                }]
        }] });

var __decorate$3m = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
let BackwardIconComponent = class BackwardIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: BackwardIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: BackwardIconComponent, selector: "kp-backward-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
BackwardIconComponent = __decorate$3m([
    Icon(faFastBackward)
], BackwardIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: BackwardIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-backward-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$3m = [BackwardIconComponent];
class BackwardIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: BackwardIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: BackwardIconModule, declarations: [BackwardIconComponent], exports: [BackwardIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: BackwardIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: BackwardIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$3m,
                    exports: DIRECTIVES$3m,
                }]
        }] });

var __decorate$3l = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
const kpBlogsNews = defineCustomIcon('kp', {
    name: 'blogs-news',
    height: 48,
    width: 48,
    svgPath: 'M7.75 41.8Q6.25 41.8 5.225 40.775Q4.2 39.75 4.2 38.25V6.4L7.35 9.6L10.65 6.25L14 9.6L17.35 6.25L20.65 9.6L24 6.25L27.35 9.6L30.65 6.25L34 9.6L37.35 6.25L40.65 9.6L43.85 6.4V38.25Q43.85 39.75 42.8 40.775Q41.75 41.8 40.3 41.8ZM7.75 38.9H22.55V25.05H7.1V38.25Q7.1 38.55 7.275 38.725Q7.45 38.9 7.75 38.9ZM25.5 38.9H40.3Q40.6 38.9 40.775 38.725Q40.95 38.55 40.95 38.25V33.45H25.5ZM25.5 30.5H40.95V25.05H25.5ZM7.1 22.15H40.95V15.6H7.1Z',
});
let BlogsNewsIconComponent = class BlogsNewsIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: BlogsNewsIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: BlogsNewsIconComponent, selector: "kp-blogs-news-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
BlogsNewsIconComponent = __decorate$3l([
    Icon(kpBlogsNews, CustomIconStyle)
], BlogsNewsIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: BlogsNewsIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-blogs-news-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$3l = [BlogsNewsIconComponent];
class BlogsNewsIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: BlogsNewsIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: BlogsNewsIconModule, declarations: [BlogsNewsIconComponent], exports: [BlogsNewsIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: BlogsNewsIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: BlogsNewsIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$3l,
                    exports: DIRECTIVES$3l,
                }]
        }] });

var __decorate$3k = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
const kpBrushAlt = defineCustomIcon('kp', {
    name: 'brush-alt',
    height: 14,
    width: 18,
    svgPath: 'M2.82476 0.041748C1.56403 0.041748 0.541992 1.06378 0.541992 2.32451V7.33341V8.10033C0.541992 9.39508 1.2213 10.5948 2.33152 11.261L3.6996 12.0818C4.27137 12.4249 4.57515 13.0827 4.46553 13.7405L4.29483 14.7647C4.01618 16.4365 5.30543 17.9584 7.00033 17.9584C8.69524 17.9584 9.98449 16.4365 9.70583 14.7647L9.53516 13.7405C9.42549 13.0827 9.72933 12.4249 10.3011 12.0818L11.6692 11.261C12.7793 10.5948 13.4587 9.39508 13.4587 8.10033V7.33341V2.32451C13.4587 1.06378 12.4367 0.041748 11.1759 0.041748H7.83366H4.50033H2.82476ZM12.2087 6.70841V2.32451C12.2087 1.75413 11.7462 1.29175 11.1759 1.29175H8.45866V2.33341C8.45866 2.67859 8.17883 2.95841 7.83366 2.95841C7.48849 2.95841 7.20866 2.67859 7.20866 2.33341V1.29175H5.12533V4.00008C5.12533 4.34526 4.8455 4.62508 4.50033 4.62508C4.15515 4.62508 3.87533 4.34526 3.87533 4.00008V1.29175H2.82476C2.25438 1.29175 1.79199 1.75413 1.79199 2.32451V6.70841H12.2087ZM1.79199 7.95841H12.2087V8.10033C12.2087 8.956 11.7597 9.74891 11.026 10.1892L9.65791 11.01C8.64583 11.6172 8.10808 12.7817 8.30216 13.946L8.47283 14.9702C8.62449 15.8801 7.92283 16.7084 7.00033 16.7084C6.07783 16.7084 5.37616 15.8801 5.52783 14.9702L5.69849 13.946C5.89258 12.7817 5.35483 11.6172 4.34273 11.01L2.97464 10.1892C2.24093 9.74891 1.79199 8.956 1.79199 8.10033V7.95841Z',
});
let BrushAltIconComponent = class BrushAltIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: BrushAltIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: BrushAltIconComponent, selector: "kp-brush-alt-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
BrushAltIconComponent = __decorate$3k([
    Icon(kpBrushAlt, CustomIconStyle)
], BrushAltIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: BrushAltIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-brush-alt-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$3k = [BrushAltIconComponent];
class BrushAltIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: BrushAltIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: BrushAltIconModule, declarations: [BrushAltIconComponent], exports: [BrushAltIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: BrushAltIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: BrushAltIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$3k,
                    exports: DIRECTIVES$3k,
                }]
        }] });

var __decorate$3j = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
let CalendarIconComponent = class CalendarIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: CalendarIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: CalendarIconComponent, selector: "kp-calendar-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
CalendarIconComponent = __decorate$3j([
    Icon(faCalendar)
], CalendarIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: CalendarIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-calendar-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$3j = [CalendarIconComponent];
class CalendarIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: CalendarIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: CalendarIconModule, declarations: [CalendarIconComponent], exports: [CalendarIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: CalendarIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: CalendarIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$3j,
                    exports: DIRECTIVES$3j,
                }]
        }] });

var __decorate$3i = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
let ChartColumnIconComponent = class ChartColumnIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ChartColumnIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: ChartColumnIconComponent, selector: "kp-chart-column-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
ChartColumnIconComponent = __decorate$3i([
    Icon(faChartColumn)
], ChartColumnIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ChartColumnIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-chart-column-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$3i = [ChartColumnIconComponent];
class ChartColumnIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ChartColumnIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: ChartColumnIconModule, declarations: [ChartColumnIconComponent], exports: [ChartColumnIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ChartColumnIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ChartColumnIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$3i,
                    exports: DIRECTIVES$3i,
                }]
        }] });

var __decorate$3h = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
let ChartLineIconComponent = class ChartLineIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ChartLineIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: ChartLineIconComponent, selector: "kp-chart-line-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
ChartLineIconComponent = __decorate$3h([
    Icon(faChartLine)
], ChartLineIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ChartLineIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-chart-line-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$3h = [ChartLineIconComponent];
class ChartLineIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ChartLineIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: ChartLineIconModule, declarations: [ChartLineIconComponent], exports: [ChartLineIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ChartLineIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ChartLineIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$3h,
                    exports: DIRECTIVES$3h,
                }]
        }] });

var __decorate$3g = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
let ChartMixedUpCircleDollarIconComponent = class ChartMixedUpCircleDollarIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ChartMixedUpCircleDollarIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: ChartMixedUpCircleDollarIconComponent, selector: "kp-chart-mixed-up-circle-dollar-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
ChartMixedUpCircleDollarIconComponent = __decorate$3g([
    Icon(faChartMixedUpCircleDollar)
], ChartMixedUpCircleDollarIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ChartMixedUpCircleDollarIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-chart-mixed-up-circle-dollar-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$3g = [ChartMixedUpCircleDollarIconComponent];
class ChartMixedUpCircleDollarIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ChartMixedUpCircleDollarIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: ChartMixedUpCircleDollarIconModule, declarations: [ChartMixedUpCircleDollarIconComponent], exports: [ChartMixedUpCircleDollarIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ChartMixedUpCircleDollarIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ChartMixedUpCircleDollarIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$3g,
                    exports: DIRECTIVES$3g,
                }]
        }] });

var __decorate$3f = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
let ChartMixedUpCircleDollarAltIconComponent = class ChartMixedUpCircleDollarAltIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ChartMixedUpCircleDollarAltIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: ChartMixedUpCircleDollarAltIconComponent, selector: "kp-chart-mixed-up-circle-dollar-alt-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
ChartMixedUpCircleDollarAltIconComponent = __decorate$3f([
    Icon(faChartMixedUpCircleDollar$1)
], ChartMixedUpCircleDollarAltIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ChartMixedUpCircleDollarAltIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-chart-mixed-up-circle-dollar-alt-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$3f = [ChartMixedUpCircleDollarAltIconComponent];
class ChartMixedUpCircleDollarAltIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ChartMixedUpCircleDollarAltIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: ChartMixedUpCircleDollarAltIconModule, declarations: [ChartMixedUpCircleDollarAltIconComponent], exports: [ChartMixedUpCircleDollarAltIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ChartMixedUpCircleDollarAltIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ChartMixedUpCircleDollarAltIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$3f,
                    exports: DIRECTIVES$3f,
                }]
        }] });

var __decorate$3e = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
let ChartPieIconComponent = class ChartPieIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ChartPieIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: ChartPieIconComponent, selector: "kp-chart-pie-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
ChartPieIconComponent = __decorate$3e([
    Icon(faChartPie)
], ChartPieIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ChartPieIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-chart-pie-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$3e = [ChartPieIconComponent];
class ChartPieIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ChartPieIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: ChartPieIconModule, declarations: [ChartPieIconComponent], exports: [ChartPieIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ChartPieIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ChartPieIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$3e,
                    exports: DIRECTIVES$3e,
                }]
        }] });

var __decorate$3d = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
let CheckCircleIconComponent = class CheckCircleIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: CheckCircleIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: CheckCircleIconComponent, selector: "kp-check-circle-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
CheckCircleIconComponent = __decorate$3d([
    Icon(faCheckCircle)
], CheckCircleIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: CheckCircleIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-check-circle-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$3d = [CheckCircleIconComponent];
class CheckCircleIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: CheckCircleIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: CheckCircleIconModule, declarations: [CheckCircleIconComponent], exports: [CheckCircleIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: CheckCircleIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: CheckCircleIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$3d,
                    exports: DIRECTIVES$3d,
                }]
        }] });

var __decorate$3c = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
let CheckIconComponent = class CheckIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: CheckIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: CheckIconComponent, selector: "kp-check-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
CheckIconComponent = __decorate$3c([
    Icon(faCheck)
], CheckIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: CheckIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-check-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$3c = [CheckIconComponent];
class CheckIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: CheckIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: CheckIconModule, declarations: [CheckIconComponent], exports: [CheckIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: CheckIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: CheckIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$3c,
                    exports: DIRECTIVES$3c,
                }]
        }] });

var __decorate$3b = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
let ChevronDownIconComponent = class ChevronDownIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ChevronDownIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: ChevronDownIconComponent, selector: "kp-chevron-down-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
ChevronDownIconComponent = __decorate$3b([
    Icon(faChevronDown)
], ChevronDownIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ChevronDownIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-chevron-down-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$3b = [ChevronDownIconComponent];
class ChevronDownIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ChevronDownIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: ChevronDownIconModule, declarations: [ChevronDownIconComponent], exports: [ChevronDownIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ChevronDownIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ChevronDownIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$3b,
                    exports: DIRECTIVES$3b,
                }]
        }] });

var __decorate$3a = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
let ChevronLeftIconComponent = class ChevronLeftIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ChevronLeftIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: ChevronLeftIconComponent, selector: "kp-chevron-left-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
ChevronLeftIconComponent = __decorate$3a([
    Icon(faChevronLeft)
], ChevronLeftIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ChevronLeftIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-chevron-left-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$3a = [ChevronLeftIconComponent];
class ChevronLeftIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ChevronLeftIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: ChevronLeftIconModule, declarations: [ChevronLeftIconComponent], exports: [ChevronLeftIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ChevronLeftIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ChevronLeftIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$3a,
                    exports: DIRECTIVES$3a,
                }]
        }] });

var __decorate$39 = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
let ChevronRightIconComponent = class ChevronRightIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ChevronRightIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: ChevronRightIconComponent, selector: "kp-chevron-right-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
ChevronRightIconComponent = __decorate$39([
    Icon(faChevronRight)
], ChevronRightIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ChevronRightIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-chevron-right-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$39 = [ChevronRightIconComponent];
class ChevronRightIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ChevronRightIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: ChevronRightIconModule, declarations: [ChevronRightIconComponent], exports: [ChevronRightIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ChevronRightIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ChevronRightIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$39,
                    exports: DIRECTIVES$39,
                }]
        }] });

var __decorate$38 = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
let ChevronUpIconComponent = class ChevronUpIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ChevronUpIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: ChevronUpIconComponent, selector: "kp-chevron-up-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
ChevronUpIconComponent = __decorate$38([
    Icon(faChevronUp)
], ChevronUpIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ChevronUpIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-chevron-up-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$38 = [ChevronUpIconComponent];
class ChevronUpIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ChevronUpIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: ChevronUpIconModule, declarations: [ChevronUpIconComponent], exports: [ChevronUpIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ChevronUpIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ChevronUpIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$38,
                    exports: DIRECTIVES$38,
                }]
        }] });

var __decorate$37 = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
let CircleAltIconComponent = class CircleAltIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: CircleAltIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: CircleAltIconComponent, selector: "kp-circle-alt-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
CircleAltIconComponent = __decorate$37([
    Icon(faCircle)
], CircleAltIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: CircleAltIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-circle-alt-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$37 = [CircleAltIconComponent];
class CircleAltIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: CircleAltIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: CircleAltIconModule, declarations: [CircleAltIconComponent], exports: [CircleAltIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: CircleAltIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: CircleAltIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$37,
                    exports: DIRECTIVES$37,
                }]
        }] });

var __decorate$36 = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
let CircleNotchIconComponent = class CircleNotchIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: CircleNotchIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: CircleNotchIconComponent, selector: "kp-circle-notch-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
CircleNotchIconComponent = __decorate$36([
    Icon(faCircleNotch)
], CircleNotchIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: CircleNotchIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-circle-notch-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$36 = [CircleNotchIconComponent];
class CircleNotchIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: CircleNotchIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: CircleNotchIconModule, declarations: [CircleNotchIconComponent], exports: [CircleNotchIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: CircleNotchIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: CircleNotchIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$36,
                    exports: DIRECTIVES$36,
                }]
        }] });

var __decorate$35 = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
const kpClipboardCheck = defineCustomIcon('kp', {
    name: 'clipboard-check',
    height: 26,
    width: 20,
    svgPath: 'M16.0076 2.80329C15.6236 2.76848 15.1794 2.7564 14.6658 2.75221C14.3264 1.27131 13.0005 0.166656 11.4167 0.166656H8.58332C6.99947 0.166656 5.67362 1.27131 5.33419 2.75221C4.82055 2.7564 4.37643 2.76848 3.9924 2.80329C3.38605 2.85825 2.85625 2.97454 2.375 3.25239C1.80493 3.58153 1.33154 4.05492 1.00241 4.62499C0.724556 5.10624 0.608258 5.63604 0.553303 6.24239C0.499984 6.83068 0.499992 7.55996 0.500001 8.4642L0.500001 8.49999V18.5L0.500001 18.5549C0.49998 19.9225 0.499963 21.0248 0.616524 21.8917C0.73754 22.7918 0.996434 23.5497 1.59835 24.1516C2.20027 24.7536 2.95814 25.0124 3.85825 25.1335C4.72522 25.25 5.82754 25.25 7.19513 25.25H7.25H12.75H12.8049C14.1725 25.25 15.2748 25.25 16.1418 25.1335C17.0419 25.0124 17.7997 24.7536 18.4017 24.1516C19.0036 23.5497 19.2625 22.7918 19.3835 21.8917C19.5 21.0248 19.5 19.9224 19.5 18.5549V18.5V8.49999V8.46426C19.5 7.55999 19.5 6.83069 19.4467 6.24239C19.3917 5.63604 19.2755 5.10624 18.9976 4.62499C18.6685 4.05492 18.1951 3.58153 17.625 3.25239C17.1438 2.97454 16.614 2.85825 16.0076 2.80329ZM8.58332 2.16666C7.84694 2.16666 7.24999 2.76361 7.24999 3.49999C7.24999 4.23637 7.84694 4.83332 8.58332 4.83332H11.4167C12.153 4.83332 12.75 4.23637 12.75 3.49999C12.75 2.76361 12.153 2.16666 11.4167 2.16666H8.58332ZM14.481 10.9843C14.8578 11.3881 14.836 12.0209 14.4323 12.3977L9.43227 17.0644C9.04807 17.423 8.45184 17.423 8.06763 17.0644L6.19264 15.3144C5.78888 14.9375 5.76706 14.3048 6.1439 13.901C6.52073 13.4973 7.15352 13.4754 7.55727 13.8523L8.74995 14.9654L13.0676 10.9356C13.4714 10.5588 14.1042 10.5806 14.481 10.9843Z',
});
let ClipboardCheckIconComponent = class ClipboardCheckIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ClipboardCheckIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: ClipboardCheckIconComponent, selector: "kp-clipboard-check-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
ClipboardCheckIconComponent = __decorate$35([
    Icon(kpClipboardCheck, CustomIconStyle)
], ClipboardCheckIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ClipboardCheckIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-clipboard-check-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$35 = [ClipboardCheckIconComponent];
class ClipboardCheckIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ClipboardCheckIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: ClipboardCheckIconModule, declarations: [ClipboardCheckIconComponent], exports: [ClipboardCheckIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ClipboardCheckIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ClipboardCheckIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$35,
                    exports: DIRECTIVES$35,
                }]
        }] });

var __decorate$34 = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
const kpClipboard = defineCustomIcon('kp', {
    name: 'clipboard',
    height: 20,
    width: 16,
    svgPath: 'M4.87124 4.0886C4.28816 4.09004 4.05349 4.09828 3.86673 4.13832C3.00065 4.32401 2.3241 5.00057 2.13841 5.86665C2.09261 6.08025 2.08841 6.35652 2.08841 7.14506V12.3273C2.08841 13.7628 2.09 14.7639 2.19156 15.5192C2.29021 16.253 2.47066 16.6416 2.74742 16.9183C3.02418 17.1951 3.41276 17.3755 4.14653 17.4742C4.90188 17.5758 5.90299 17.5773 7.33841 17.5773H8.66198C10.0974 17.5773 11.0985 17.5758 11.8539 17.4742C12.5876 17.3755 12.9762 17.1951 13.253 16.9183C13.5297 16.6416 13.7102 16.253 13.8088 15.5192C13.9104 14.7639 13.912 13.7628 13.912 12.3273V7.14506C13.912 6.35652 13.9078 6.08025 13.862 5.86665C13.6763 5.00057 12.9997 4.32401 12.1337 4.13832C11.947 4.0983 11.7125 4.09005 11.13 4.0886C10.8144 5.05529 9.90542 5.75374 8.83333 5.75374H7.16789C6.09581 5.75374 5.18685 5.05529 4.87124 4.0886ZM11.1301 2.58833C11.6927 2.58886 12.0949 2.59592 12.4481 2.67166C13.8916 2.98114 15.0192 4.10872 15.3287 5.55219C15.4123 5.9423 15.4122 6.39202 15.412 7.05167L15.412 7.14506V12.3273V12.3822V12.3822C15.412 13.7498 15.412 14.8521 15.2955 15.7191C15.1744 16.6192 14.9156 17.3771 14.3136 17.979C13.7117 18.5809 12.9538 18.8398 12.0537 18.9608C11.1868 19.0774 10.0844 19.0774 8.71685 19.0773H8.66198H7.33841H7.28354C5.91595 19.0774 4.81363 19.0774 3.94666 18.9608C3.04655 18.8398 2.28868 18.5809 1.68676 17.979C1.08484 17.3771 0.82595 16.6192 0.704934 15.7191C0.588373 14.8521 0.58839 13.7498 0.588411 12.3822L0.588411 12.3273V7.14506L0.588395 7.05167C0.588219 6.39203 0.588099 5.9423 0.671738 5.55219C0.981218 4.10872 2.10881 2.98114 3.55228 2.67166C3.9057 2.59588 4.30806 2.58885 4.87113 2.58833C5.18664 1.62147 6.09568 0.922852 7.16789 0.922852H8.83333C9.90554 0.922852 10.8146 1.62147 11.1301 2.58833ZM6.25244 3.3383C6.25244 2.83271 6.6623 2.42285 7.16789 2.42285H8.83333C9.33892 2.42285 9.74878 2.83271 9.74878 3.3383C9.74878 3.84389 9.33892 4.25374 8.83333 4.25374H7.16789C6.6623 4.25374 6.25244 3.84388 6.25244 3.3383Z',
});
let ClipboardIconComponent = class ClipboardIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ClipboardIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: ClipboardIconComponent, selector: "kp-clipboard-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
ClipboardIconComponent = __decorate$34([
    Icon(kpClipboard, CustomIconStyle)
], ClipboardIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ClipboardIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-clipboard-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$34 = [ClipboardIconComponent];
class ClipboardIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ClipboardIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: ClipboardIconModule, declarations: [ClipboardIconComponent], exports: [ClipboardIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ClipboardIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ClipboardIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$34,
                    exports: DIRECTIVES$34,
                }]
        }] });

var __decorate$33 = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
const kpClock = defineCustomIcon('kp', {
    name: 'clock',
    height: 20,
    width: 20,
    svgPath: 'M10.0002 0.833332C4.93755 0.833332 0.833496 4.93739 0.833496 10C0.833496 15.0626 4.93755 19.1667 10.0002 19.1667C15.0628 19.1667 19.1668 15.0626 19.1668 10C19.1668 4.93739 15.0628 0.833332 10.0002 0.833332ZM10.7502 6.33333C10.7502 5.91912 10.4144 5.58333 10.0002 5.58333C9.58595 5.58333 9.25016 5.91912 9.25016 6.33333V10C9.25016 10.2508 9.37549 10.4849 9.58414 10.624L12.3341 12.4574C12.6788 12.6871 13.1444 12.594 13.3742 12.2494C13.604 11.9047 13.5108 11.4391 13.1662 11.2093L10.7502 9.59861V6.33333Z',
});
let ClockIconComponent = class ClockIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ClockIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: ClockIconComponent, selector: "kp-clock-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
ClockIconComponent = __decorate$33([
    Icon(kpClock, CustomIconStyle)
], ClockIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ClockIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-clock-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$33 = [ClockIconComponent];
class ClockIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ClockIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: ClockIconModule, declarations: [ClockIconComponent], exports: [ClockIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ClockIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ClockIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$33,
                    exports: DIRECTIVES$33,
                }]
        }] });

var __decorate$32 = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
const kpCloudCheck = defineCustomIcon('kp', {
    name: 'cloud-check',
    height: 17,
    width: 22,
    svgPath: 'M4.75 8C4.75 4.54822 7.54822 1.75 11 1.75C13.9382 1.75 16.4045 3.7782 17.0719 6.51188C17.1301 6.75035 17.3014 6.94523 17.5304 7.03365C19.1226 7.64848 20.25 9.19351 20.25 11C20.25 13.3472 18.3472 15.25 16 15.25H5C3.20507 15.25 1.75 13.7949 1.75 12C1.75 10.4863 2.78548 9.21263 4.18784 8.85211C4.52413 8.76566 4.7571 8.45969 4.75099 8.11252C4.75033 8.0751 4.75 8.0376 4.75 8ZM11 0.25C6.8612 0.25 3.48014 3.49432 3.26126 7.57854C1.49832 8.27243 0.25 9.98965 0.25 12C0.25 14.6234 2.37665 16.75 5 16.75H16C19.1756 16.75 21.75 14.1756 21.75 11C21.75 8.69141 20.3898 6.70217 18.4293 5.78706C17.4768 2.58539 14.512 0.25 11 0.25ZM10.5303 12.5303L14.5303 8.53033C14.8232 8.23744 14.8232 7.76256 14.5303 7.46967C14.2374 7.17678 13.7626 7.17678 13.4697 7.46967L10 10.9393L9.03033 9.96967C8.73744 9.67678 8.26256 9.67678 7.96967 9.96967C7.67678 10.2626 7.67678 10.7374 7.96967 11.0303L9.46967 12.5303C9.61032 12.671 9.80109 12.75 10 12.75C10.1989 12.75 10.3897 12.671 10.5303 12.5303Z',
});
let CloudCheckIconComponent = class CloudCheckIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: CloudCheckIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: CloudCheckIconComponent, selector: "kp-cloud-check-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
CloudCheckIconComponent = __decorate$32([
    Icon(kpCloudCheck, CustomIconStyle)
], CloudCheckIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: CloudCheckIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-cloud-check-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$32 = [CloudCheckIconComponent];
class CloudCheckIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: CloudCheckIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: CloudCheckIconModule, declarations: [CloudCheckIconComponent], exports: [CloudCheckIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: CloudCheckIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: CloudCheckIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$32,
                    exports: DIRECTIVES$32,
                }]
        }] });

var __decorate$31 = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
const kpCloudDownload = defineCustomIcon('kp', {
    name: 'cloud-download',
    height: 15,
    width: 16,
    svgPath: 'M3.8335 5.99967C3.8335 3.69849 5.69898 1.83301 8.00016 1.83301C9.95896 1.83301 11.6032 3.18514 12.0481 5.00759C12.0869 5.16657 12.2011 5.29649 12.3537 5.35544C13.4152 5.76533 14.1668 6.79535 14.1668 7.99967C14.1668 8.92632 13.7225 9.74904 13.0332 10.2668C12.8124 10.4326 12.7679 10.7461 12.9337 10.9669C13.0996 11.1877 13.413 11.2322 13.6338 11.0664C14.5637 10.3679 15.1668 9.25412 15.1668 7.99967C15.1668 6.46061 14.26 5.13446 12.953 4.52438C12.318 2.38993 10.3415 0.833008 8.00016 0.833008C5.24096 0.833008 2.98692 2.99589 2.84101 5.71871C1.66571 6.1813 0.833496 7.32611 0.833496 8.66634C0.833496 9.83908 1.47125 10.8623 2.41647 11.4091C2.6555 11.5473 2.96136 11.4656 3.09963 11.2266C3.2379 10.9876 3.15622 10.6817 2.91719 10.5435C2.26827 10.1681 1.8335 9.46768 1.8335 8.66634C1.8335 7.65723 2.52382 6.80809 3.45872 6.56775C3.68291 6.51012 3.83823 6.30613 3.83416 6.07469C3.83372 6.04974 3.8335 6.02474 3.8335 5.99967ZM8.50016 7.99967C8.50016 7.72353 8.2763 7.49967 8.00016 7.49967C7.72402 7.49967 7.50016 7.72353 7.50016 7.99967V12.7926L6.02038 11.3128C5.82512 11.1175 5.50854 11.1175 5.31328 11.3128C5.11801 11.508 5.11801 11.8246 5.31328 12.0199L7.64661 14.3532C7.74038 14.447 7.86755 14.4997 8.00016 14.4997C8.13277 14.4997 8.25995 14.447 8.35372 14.3532L10.687 12.0199C10.8823 11.8246 10.8823 11.508 10.687 11.3128C10.4918 11.1175 10.1752 11.1175 9.97994 11.3128L8.50016 12.7926V7.99967Z',
});
let CloudDownloadIconComponent = class CloudDownloadIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: CloudDownloadIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: CloudDownloadIconComponent, selector: "kp-cloud-download-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
CloudDownloadIconComponent = __decorate$31([
    Icon(kpCloudDownload, CustomIconStyle)
], CloudDownloadIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: CloudDownloadIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-cloud-download-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$31 = [CloudDownloadIconComponent];
class CloudDownloadIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: CloudDownloadIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: CloudDownloadIconModule, declarations: [CloudDownloadIconComponent], exports: [CloudDownloadIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: CloudDownloadIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: CloudDownloadIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$31,
                    exports: DIRECTIVES$31,
                }]
        }] });

var __decorate$30 = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
const kpCloudTimes = defineCustomIcon('kp', {
    name: 'cloud-times',
    height: 17,
    width: 22,
    svgPath: 'M5.75 11C5.75 7.54822 8.54822 4.75 12 4.75C14.9382 4.75 17.4045 6.7782 18.0719 9.51188C18.1301 9.75035 18.3014 9.94523 18.5304 10.0337C20.1226 10.6485 21.25 12.1935 21.25 14C21.25 16.3472 19.3472 18.25 17 18.25H6C4.20507 18.25 2.75 16.7949 2.75 15C2.75 13.4863 3.78548 12.2126 5.18784 11.8521C5.52413 11.7657 5.7571 11.4597 5.75099 11.1125C5.75033 11.0751 5.75 11.0376 5.75 11ZM12 3.25C7.8612 3.25 4.48014 6.49432 4.26126 10.5785C2.49832 11.2724 1.25 12.9897 1.25 15C1.25 17.6234 3.37665 19.75 6 19.75H17C20.1756 19.75 22.75 17.1756 22.75 14C22.75 11.6914 21.3898 9.70217 19.4293 8.78706C18.4768 5.58539 15.512 3.25 12 3.25ZM10.5303 10.4697C10.2374 10.1768 9.76256 10.1768 9.46967 10.4697C9.17678 10.7626 9.17678 11.2374 9.46967 11.5303L10.9393 13L9.46967 14.4697C9.17678 14.7626 9.17678 15.2374 9.46967 15.5303C9.76256 15.8232 10.2374 15.8232 10.5303 15.5303L12 14.0607L13.4697 15.5303C13.7626 15.8232 14.2374 15.8232 14.5303 15.5303C14.8232 15.2374 14.8232 14.7626 14.5303 14.4697L13.0607 13L14.5303 11.5303C14.8232 11.2374 14.8232 10.7626 14.5303 10.4697C14.2374 10.1768 13.7626 10.1768 13.4697 10.4697L12 11.9393L10.5303 10.4697Z',
});
let CloudTimesIconComponent = class CloudTimesIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: CloudTimesIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: CloudTimesIconComponent, selector: "kp-cloud-times-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
CloudTimesIconComponent = __decorate$30([
    Icon(kpCloudTimes, CustomIconStyle)
], CloudTimesIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: CloudTimesIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-cloud-times-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$30 = [CloudTimesIconComponent];
class CloudTimesIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: CloudTimesIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: CloudTimesIconModule, declarations: [CloudTimesIconComponent], exports: [CloudTimesIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: CloudTimesIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: CloudTimesIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$30,
                    exports: DIRECTIVES$30,
                }]
        }] });

var __decorate$2$ = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
const kpCode = defineCustomIcon('kp', {
    name: 'code',
    height: 12,
    width: 16,
    svgPath: 'M9.81857 0.788378C9.88554 0.520481 9.72266 0.249014 9.45476 0.182039C9.18687 0.115065 8.9154 0.277945 8.84842 0.545843L6.18176 11.2125C6.11478 11.4804 6.27766 11.7519 6.54556 11.8188C6.81346 11.8858 7.08493 11.7229 7.1519 11.455L9.81857 0.788378ZM4.35372 2.98024C4.54898 3.1755 4.54898 3.49208 4.35372 3.68734L2.0406 6.00046L4.35372 8.31357C4.54898 8.50883 4.54898 8.82542 4.35372 9.02068C4.15845 9.21594 3.84187 9.21594 3.64661 9.02068L0.979943 6.35401C0.784681 6.15875 0.784681 5.84217 0.979943 5.6469L3.64661 2.98024C3.84187 2.78498 4.15845 2.78498 4.35372 2.98024ZM11.6466 2.98024C11.8419 2.78498 12.1585 2.78498 12.3537 2.98024L15.0204 5.6469C15.2156 5.84217 15.2156 6.15875 15.0204 6.35401L12.3537 9.02068C12.1585 9.21594 11.8419 9.21594 11.6466 9.02068C11.4513 8.82542 11.4513 8.50883 11.6466 8.31357L13.9597 6.00046L11.6466 3.68734C11.4513 3.49208 11.4513 3.1755 11.6466 2.98024Z',
});
let CodeIconComponent = class CodeIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: CodeIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: CodeIconComponent, selector: "kp-code-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
CodeIconComponent = __decorate$2$([
    Icon(kpCode, CustomIconStyle)
], CodeIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: CodeIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-code-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$2$ = [CodeIconComponent];
class CodeIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: CodeIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: CodeIconModule, declarations: [CodeIconComponent], exports: [CodeIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: CodeIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: CodeIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$2$,
                    exports: DIRECTIVES$2$,
                }]
        }] });

var __decorate$2_ = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
const kpCopyAlt = defineCustomIcon('kp', {
    name: 'copy-alt',
    height: 15,
    width: 15,
    svgPath: 'M5.83317 0.666992H5.81265C5.05278 0.666989 4.45415 0.666987 3.97126 0.703554C3.47882 0.740845 3.06619 0.818255 2.68734 0.994915C1.94158 1.34267 1.34218 1.94206 0.994427 2.68783C0.817767 3.06668 0.740356 3.4793 0.703066 3.97174C0.666499 4.45464 0.666501 5.05327 0.666504 5.81314V5.83366V5.85418C0.666501 6.61405 0.666499 7.21268 0.703066 7.69557C0.740356 8.18801 0.817767 8.60064 0.994427 8.97949C1.34218 9.72525 1.94158 10.3246 2.68734 10.6724C3.06619 10.8491 3.47882 10.9265 3.97126 10.9638C4.18019 10.9796 4.41078 10.9886 4.66673 10.9937C4.66829 11.5729 4.67978 11.9654 4.76085 12.3075C5.06606 13.5953 6.07156 14.6008 7.35935 14.906C7.75817 15.0005 8.22563 15.0004 8.97758 15.0003H8.97761L9.05539 15.0003H9.83317H9.85369H9.85372C10.6136 15.0003 11.2122 15.0003 11.6951 14.9638C12.1875 14.9265 12.6002 14.8491 12.979 14.6724C13.7248 14.3246 14.3242 13.7253 14.6719 12.9795C14.8486 12.6006 14.926 12.188 14.9633 11.6956C14.9998 11.2127 14.9998 10.6141 14.9998 9.85421V9.85418V9.83366V9.05588L14.9998 8.97809V8.97807C15 8.22612 15 7.75866 14.9055 7.35984C14.6003 6.07204 13.5948 5.06655 12.307 4.76133C11.9649 4.68027 11.5724 4.66878 10.9932 4.66721C10.9881 4.41127 10.9791 4.18067 10.9633 3.97174C10.926 3.4793 10.8486 3.06668 10.6719 2.68783C10.3242 1.94206 9.72476 1.34267 8.979 0.994915C8.60015 0.818255 8.18752 0.740845 7.69508 0.703554C7.21219 0.666987 6.61356 0.666989 5.85369 0.666992H5.83317ZM10.9998 5.66755C10.9998 5.7154 10.9998 5.76392 10.9998 5.8131V5.81314V5.83366V5.85418V5.85421C10.9998 6.61407 10.9998 7.21269 10.9633 7.69557C10.926 8.18801 10.8486 8.60064 10.6719 8.97949C10.3242 9.72525 9.72476 10.3246 8.979 10.6724C8.60015 10.8491 8.18752 10.9265 7.69508 10.9638C7.2122 11.0003 6.61358 11.0003 5.85372 11.0003H5.85369H5.83317H5.81265H5.81262C5.76343 11.0003 5.71491 11.0003 5.66706 11.0003C5.66935 11.5786 5.68064 11.8522 5.73389 12.0769C5.9519 12.9967 6.67011 13.7149 7.58997 13.9329C7.85996 13.9969 8.20052 14.0003 9.05539 14.0003H9.83317C10.618 14.0003 11.1789 14 11.6196 13.9666C12.0551 13.9336 12.3325 13.8705 12.5564 13.7661C13.0891 13.5177 13.5172 13.0896 13.7656 12.5569C13.87 12.3329 13.9332 12.0556 13.9661 11.6201C13.9995 11.1794 13.9998 10.6185 13.9998 9.83366V9.05588C13.9998 8.20101 13.9964 7.86045 13.9325 7.59045C13.7144 6.6706 12.9962 5.95239 12.0764 5.73438C11.8517 5.68112 11.5781 5.66984 10.9998 5.66755ZM3.10996 1.90122C3.33388 1.79681 3.61126 1.73368 4.04677 1.7007C4.48748 1.66733 5.0483 1.66699 5.83317 1.66699C6.61804 1.66699 7.17886 1.66733 7.61957 1.7007C8.05508 1.73368 8.33246 1.79681 8.55638 1.90122C9.08907 2.14962 9.51721 2.57776 9.76561 3.11045C9.87002 3.33437 9.93315 3.61175 9.96613 4.04725C9.9995 4.48797 9.99984 5.04879 9.99984 5.83366C9.99984 6.61853 9.9995 7.17935 9.96613 7.62006C9.93315 8.05557 9.87002 8.33295 9.76561 8.55687C9.51721 9.08956 9.08907 9.5177 8.55638 9.7661C8.33246 9.87051 8.05508 9.93364 7.61957 9.96662C7.17886 9.99999 6.61804 10.0003 5.83317 10.0003C5.0483 10.0003 4.48748 9.99999 4.04677 9.96662C3.61126 9.93364 3.33388 9.87051 3.10996 9.7661C2.57727 9.5177 2.14913 9.08956 1.90073 8.55687C1.79632 8.33295 1.73319 8.05557 1.70021 7.62006C1.66684 7.17935 1.6665 6.61853 1.6665 5.83366C1.6665 5.04879 1.66684 4.48797 1.70021 4.04725C1.73319 3.61175 1.79632 3.33437 1.90073 3.11045C2.14913 2.57776 2.57727 2.14962 3.10996 1.90122Z',
});
let CopyAltIconComponent = class CopyAltIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: CopyAltIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: CopyAltIconComponent, selector: "kp-copy-alt-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
CopyAltIconComponent = __decorate$2_([
    Icon(kpCopyAlt, CustomIconStyle)
], CopyAltIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: CopyAltIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-copy-alt-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$2_ = [CopyAltIconComponent];
class CopyAltIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: CopyAltIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: CopyAltIconModule, declarations: [CopyAltIconComponent], exports: [CopyAltIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: CopyAltIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: CopyAltIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$2_,
                    exports: DIRECTIVES$2_,
                }]
        }] });

var __decorate$2Z = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
const kpCopy = defineCustomIcon('kp', {
    name: 'copy',
    height: 21,
    width: 21,
    svgPath: 'M0.878802 0.87868C0.00012207 1.75736 0.00012207 3.17157 0.00012207 6V8C0.00012207 10.8284 0.00012207 12.2426 0.878802 13.1213C1.75748 14 3.17169 14 6.00012 14H8.00012C10.8285 14 12.2428 14 13.1214 13.1213C14.0001 12.2426 14.0001 10.8284 14.0001 8V6C14.0001 3.17157 14.0001 1.75736 13.1214 0.87868C12.2428 0 10.8285 0 8.00012 0H6.00012C3.17169 0 1.75748 0 0.878802 0.87868ZM14.1668 6.75C15.52 6.75 16.4638 6.75142 17.1796 6.84252C17.8762 6.93117 18.2504 7.09386 18.5202 7.34113C18.5685 7.38535 18.6148 7.43166 18.659 7.47992C18.9063 7.74976 19.069 8.1239 19.1576 8.82052C19.2487 9.53635 19.2501 10.4801 19.2501 11.8333V14C19.2501 15.4354 19.2485 16.4365 19.147 17.1919C19.0483 17.9257 18.8679 18.3142 18.5911 18.591C18.3144 18.8678 17.9258 19.0482 17.192 19.1469C16.4367 19.2484 15.4355 19.25 14.0001 19.25H11.8335C10.4802 19.25 9.53647 19.2486 8.82064 19.1575C8.12402 19.0688 7.74988 18.9061 7.48004 18.6589C7.43178 18.6147 7.38547 18.5683 7.34125 18.5201C7.09399 18.2502 6.93129 17.8761 6.84264 17.1795C6.75155 16.4636 6.75012 15.5199 6.75012 14.1667H5.25012L5.25012 14.2183C5.2501 15.5078 5.25009 16.5472 5.35464 17.3688C5.46303 18.2206 5.69443 18.9432 6.23533 19.5335C6.30904 19.6139 6.38622 19.6911 6.46666 19.7648C7.05695 20.3057 7.77955 20.5371 8.63128 20.6455C9.45288 20.75 10.4924 20.75 11.7819 20.75H11.8335H14.0001H14.055C15.4226 20.75 16.5249 20.75 17.3919 20.6335C18.292 20.5125 19.0499 20.2536 19.6518 19.6516C20.2537 19.0497 20.5126 18.2919 20.6336 17.3918C20.7502 16.5248 20.7501 15.4225 20.7501 14.0549V14V11.8333V11.7817C20.7501 10.4922 20.7502 9.45275 20.6456 8.63116C20.5372 7.77943 20.3058 7.05683 19.7649 6.46654C19.6912 6.3861 19.614 6.30892 19.5336 6.23521C18.9433 5.6943 18.2207 5.46291 17.369 5.35452C16.5474 5.24997 15.5079 5.24998 14.2184 5.25L14.1668 5.25V6.75Z',
});
let CopyIconComponent = class CopyIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: CopyIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: CopyIconComponent, selector: "kp-copy-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
CopyIconComponent = __decorate$2Z([
    Icon(kpCopy, CustomIconStyle)
], CopyIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: CopyIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-copy-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$2Z = [CopyIconComponent];
class CopyIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: CopyIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: CopyIconModule, declarations: [CopyIconComponent], exports: [CopyIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: CopyIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: CopyIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$2Z,
                    exports: DIRECTIVES$2Z,
                }]
        }] });

var __decorate$2Y = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
const kpCreditCard = defineCustomIcon('kp', {
    name: 'credit-card',
    height: 18,
    width: 22,
    svgPath: 'M6.945 0.250001H6.99988H14.9999H15.0548C16.4223 0.24998 17.5247 0.249963 18.3916 0.366524C19.2917 0.48754 20.0496 0.746434 20.6515 1.34835C21.2534 1.95027 21.5123 2.70814 21.6334 3.60825C21.6818 3.9688 21.7101 4.37006 21.7267 4.81417C21.7418 4.87359 21.7499 4.93586 21.7499 5C21.7499 5.04818 21.7453 5.09529 21.7367 5.14095C21.7499 5.68232 21.7499 6.28254 21.7499 6.945V6.94513V7V11V11.0549C21.7499 12.4225 21.7499 13.5248 21.6334 14.3918C21.5123 15.2919 21.2534 16.0497 20.6515 16.6517C20.0496 17.2536 19.2917 17.5125 18.3916 17.6335C17.5247 17.75 16.4223 17.75 15.0547 17.75H14.9999H6.99988H6.94501C5.57742 17.75 4.4751 17.75 3.60813 17.6335C2.70802 17.5125 1.95014 17.2536 1.34823 16.6517C0.746312 16.0497 0.487418 15.2919 0.366402 14.3918C0.249841 13.5248 0.249858 12.4225 0.249878 11.0549V11V7V6.94513C0.249868 6.28262 0.249859 5.68236 0.263104 5.14096C0.254422 5.0953 0.249878 5.04818 0.249878 5C0.249878 4.93586 0.257931 4.87359 0.27308 4.81417C0.289614 4.37006 0.317927 3.9688 0.366402 3.60825C0.487418 2.70814 0.746312 1.95027 1.34823 1.34835C1.95014 0.746434 2.70802 0.48754 3.60813 0.366524C4.47509 0.249963 5.57741 0.24998 6.945 0.250001ZM20.1467 3.80812C20.1654 3.94675 20.1806 4.09365 20.1932 4.25H1.8066C1.81912 4.09365 1.83439 3.94675 1.85303 3.80812C1.95168 3.07435 2.13213 2.68577 2.40889 2.40901C2.68565 2.13225 3.07422 1.9518 3.808 1.85315C4.56335 1.75159 5.56446 1.75 6.99988 1.75H14.9999C16.4353 1.75 17.4364 1.75159 18.1918 1.85315C18.9255 1.9518 19.3141 2.13225 19.5909 2.40901C19.8676 2.68577 20.0481 3.07435 20.1467 3.80812ZM4.24988 9C4.24988 8.58579 4.58566 8.25 4.99988 8.25H8.99988C9.41409 8.25 9.74988 8.58579 9.74988 9C9.74988 9.41421 9.41409 9.75 8.99988 9.75H4.99988C4.58566 9.75 4.24988 9.41421 4.24988 9Z',
});
let CreditCardIconComponent = class CreditCardIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: CreditCardIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: CreditCardIconComponent, selector: "kp-credit-card-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
CreditCardIconComponent = __decorate$2Y([
    Icon(kpCreditCard, CustomIconStyle)
], CreditCardIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: CreditCardIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-credit-card-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$2Y = [CreditCardIconComponent];
class CreditCardIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: CreditCardIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: CreditCardIconModule, declarations: [CreditCardIconComponent], exports: [CreditCardIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: CreditCardIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: CreditCardIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$2Y,
                    exports: DIRECTIVES$2Y,
                }]
        }] });

var __decorate$2X = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
const kpDashboard = defineCustomIcon('kp', {
    name: 'dashboard',
    height: 20,
    width: 20,
    svgPath: 'M4.5 0.250001L4.46166 0.250001C3.79208 0.249984 3.23335 0.24997 2.78102 0.295985C2.30755 0.34415 1.86818 0.448864 1.47218 0.71346C1.17191 0.914096 0.914096 1.17191 0.71346 1.47218C0.448864 1.86818 0.34415 2.30755 0.295985 2.78102C0.24997 3.23335 0.249984 3.79208 0.250001 4.46166L0.250001 4.5V15.5L0.250001 15.5383C0.249984 16.2079 0.24997 16.7667 0.295985 17.219C0.34415 17.6925 0.448864 18.1318 0.71346 18.5278C0.914096 18.8281 1.17191 19.0859 1.47218 19.2865C1.86818 19.5511 2.30755 19.6559 2.78102 19.704C3.23336 19.75 3.79209 19.75 4.46168 19.75H4.5H4.53832C5.20791 19.75 5.76664 19.75 6.21898 19.704C6.69245 19.6559 7.13182 19.5511 7.52782 19.2865C7.82809 19.0859 8.08591 18.8281 8.28654 18.5278C8.55114 18.1318 8.65585 17.6925 8.70402 17.219C8.75003 16.7666 8.75002 16.2079 8.75 15.5383V15.5V4.5V4.46166C8.75002 3.79208 8.75003 3.23335 8.70402 2.78102C8.65585 2.30755 8.55114 1.86818 8.28654 1.47218C8.08591 1.17191 7.82809 0.914096 7.52782 0.71346C7.13182 0.448864 6.69245 0.34415 6.21898 0.295985C5.76665 0.24997 5.20793 0.249984 4.53835 0.250001L4.5 0.250001ZM15.4617 0.250001L15.5 0.250001L15.5383 0.250001C16.2079 0.249984 16.7667 0.24997 17.219 0.295985C17.6925 0.34415 18.1318 0.448864 18.5278 0.71346C18.8281 0.914096 19.0859 1.17191 19.2865 1.47218C19.5511 1.86818 19.6559 2.30755 19.704 2.78102C19.75 3.23335 19.75 3.79208 19.75 4.46166V4.5V4.53834C19.75 5.20792 19.75 5.76665 19.704 6.21898C19.6559 6.69245 19.5511 7.13182 19.2865 7.52782C19.0859 7.82809 18.8281 8.08591 18.5278 8.28654C18.1318 8.55114 17.6925 8.65585 17.219 8.70402C16.7666 8.75003 16.2079 8.75002 15.5383 8.75H15.5H15.4617C14.7921 8.75002 14.2334 8.75003 13.781 8.70402C13.3075 8.65585 12.8682 8.55114 12.4722 8.28654C12.1719 8.08591 11.9141 7.82809 11.7135 7.52782C11.4489 7.13182 11.3442 6.69245 11.296 6.21898C11.25 5.76665 11.25 5.20793 11.25 4.53835L11.25 4.5L11.25 4.46166C11.25 3.79208 11.25 3.23335 11.296 2.78102C11.3442 2.30755 11.4489 1.86818 11.7135 1.47218C11.9141 1.17191 12.1719 0.914096 12.4722 0.71346C12.8682 0.448864 13.3075 0.34415 13.781 0.295985C14.2334 0.24997 14.7921 0.249984 15.4617 0.250001ZM15.4617 11.25L15.5 11.25L15.5383 11.25C16.2079 11.25 16.7667 11.25 17.219 11.296C17.6925 11.3442 18.1318 11.4489 18.5278 11.7135C18.8281 11.9141 19.0859 12.1719 19.2865 12.4722C19.5511 12.8682 19.6559 13.3075 19.704 13.781C19.75 14.2334 19.75 14.7921 19.75 15.4617V15.5V15.5383C19.75 16.2079 19.75 16.7666 19.704 17.219C19.6559 17.6925 19.5511 18.1318 19.2865 18.5278C19.0859 18.8281 18.8281 19.0859 18.5278 19.2865C18.1318 19.5511 17.6925 19.6559 17.219 19.704C16.7667 19.75 16.208 19.75 15.5384 19.75H15.5384H15.5383L15.5 19.75L15.4617 19.75H15.4616H15.4616C14.7921 19.75 14.2333 19.75 13.781 19.704C13.3075 19.6559 12.8682 19.5511 12.4722 19.2865C12.1719 19.0859 11.9141 18.8281 11.7135 18.5278C11.4489 18.1318 11.3442 17.6925 11.296 17.219C11.25 16.7667 11.25 16.2079 11.25 15.5383L11.25 15.5L11.25 15.4617C11.25 14.7921 11.25 14.2334 11.296 13.781C11.3442 13.3075 11.4489 12.8682 11.7135 12.4722C11.9141 12.1719 12.1719 11.9141 12.4722 11.7135C12.8682 11.4489 13.3075 11.3442 13.781 11.296C14.2334 11.25 14.7921 11.25 15.4617 11.25Z',
});
let DashboardIconComponent = class DashboardIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: DashboardIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: DashboardIconComponent, selector: "kp-dashboard-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
DashboardIconComponent = __decorate$2X([
    Icon(kpDashboard, CustomIconStyle)
], DashboardIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: DashboardIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-dashboard-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$2X = [DashboardIconComponent];
class DashboardIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: DashboardIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: DashboardIconModule, declarations: [DashboardIconComponent], exports: [DashboardIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: DashboardIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: DashboardIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$2X,
                    exports: DIRECTIVES$2X,
                }]
        }] });

var __decorate$2W = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
const kpDollar = defineCustomIcon('kp', {
    name: 'dollar',
    height: 16,
    width: 24,
    svgPath: 'M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22ZM8.24981 9.74353C8.24981 8.36646 9.36615 7.25012 10.7432 7.25012H11.2499V6.50012C11.2499 6.08591 11.5857 5.75012 11.9999 5.75012C12.4141 5.75012 12.7499 6.08591 12.7499 6.50012V7.25012H12.9998C14.5186 7.25012 15.7498 8.48133 15.7498 10.0001C15.7498 10.4143 15.414 10.7501 14.9998 10.7501C14.5856 10.7501 14.2498 10.4143 14.2498 10.0001C14.2498 9.30976 13.6902 8.75012 12.9998 8.75012H12.7499V11.4596L14.0449 11.8912C15.063 12.2306 15.7498 13.1835 15.7498 14.2567C15.7498 15.6338 14.6335 16.7501 13.2564 16.7501H12.7499V17.5001C12.7499 17.9143 12.4141 18.2501 11.9999 18.2501C11.5857 18.2501 11.2499 17.9143 11.2499 17.5001V16.7501H10.9998C9.48103 16.7501 8.24981 15.5189 8.24981 14.0001C8.24981 13.5859 8.5856 13.2501 8.99981 13.2501C9.41402 13.2501 9.74981 13.5859 9.74981 14.0001C9.74981 14.6905 10.3095 15.2501 10.9998 15.2501H11.2499V12.5407L9.95474 12.109C8.93657 11.7696 8.24981 10.8168 8.24981 9.74353ZM11.2499 10.9596V8.75012H10.7432C10.1946 8.75012 9.74981 9.19488 9.74981 9.74353C9.74981 10.1711 10.0234 10.5508 10.4291 10.686L11.2499 10.9596ZM12.7499 13.0407V15.2501H13.2564C13.805 15.2501 14.2498 14.8053 14.2498 14.2567C14.2498 13.8291 13.9762 13.4495 13.5705 13.3143L12.7499 13.0407Z',
});
let DollarIconComponent = class DollarIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: DollarIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: DollarIconComponent, selector: "kp-dollar-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
DollarIconComponent = __decorate$2W([
    Icon(kpDollar, CustomIconStyle)
], DollarIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: DollarIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-dollar-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$2W = [DollarIconComponent];
class DollarIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: DollarIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: DollarIconModule, declarations: [DollarIconComponent], exports: [DollarIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: DollarIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: DollarIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$2W,
                    exports: DIRECTIVES$2W,
                }]
        }] });

var __decorate$2V = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
const kpEditAlt = defineCustomIcon('kp', {
    name: 'edit-alt',
    height: 18,
    width: 18,
    svgPath: 'M14.1593 1.9034C14.6361 1.47002 15.3642 1.47003 15.8409 1.90343C15.8596 1.92042 15.884 1.94453 15.9698 2.03027C16.0555 2.116 16.0796 2.14041 16.0966 2.1591C16.53 2.63587 16.53 3.36393 16.0966 3.84069C16.0796 3.85939 16.0555 3.88379 15.9697 3.96953L15.1627 4.77659C14.0865 4.80085 13.1991 3.9135 13.2233 2.83736L14.0305 2.03024C14.1162 1.9445 14.1406 1.9204 14.1593 1.9034ZM11.9524 4.10826L4.09722 11.9634C3.29045 12.7701 2.98987 13.0796 2.78372 13.4436C2.57758 13.8077 2.46691 14.2247 2.19019 15.3316L2.03079 15.9691L2.6684 15.8097C3.77527 15.533 4.1922 15.4224 4.55629 15.2162C4.92038 15.0101 5.22978 14.7095 6.03654 13.9027L13.8917 6.04753C13.0064 5.70189 12.2981 4.99358 11.9524 4.10826ZM16.8499 0.793495C15.801 -0.159987 14.1993 -0.160009 13.1504 0.793444C13.1016 0.837806 13.0497 0.889661 12.9824 0.956994L12.9698 0.969572L3.03657 10.9027L2.96302 10.9763C2.25466 11.6843 1.79718 12.1416 1.47843 12.7046C1.15967 13.2676 1.00291 13.8951 0.760186 14.8669L0.734976 14.9677L0.272409 16.818C0.208514 17.0736 0.283401 17.344 0.469686 17.5302C0.655971 17.7165 0.926336 17.7914 1.18192 17.7275L3.03221 17.265L3.1331 17.2397C4.1048 16.997 4.73236 16.8403 5.29534 16.5215C5.85831 16.2028 6.31561 15.7453 7.02368 15.0369L7.0972 14.9634L17.0304 5.03019L17.043 5.01757C17.1103 4.95027 17.1622 4.89844 17.2065 4.84965C18.16 3.80079 18.16 2.19905 17.2066 1.15017C17.1622 1.10139 17.1104 1.04957 17.0431 0.982293L17.043 0.982221L17.0305 0.969629L17.0179 0.957073L17.0179 0.957034C16.9506 0.889706 16.8987 0.837855 16.8499 0.793495Z',
});
let EditAltIconComponent = class EditAltIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: EditAltIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: EditAltIconComponent, selector: "kp-edit-alt-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
EditAltIconComponent = __decorate$2V([
    Icon(kpEditAlt, CustomIconStyle)
], EditAltIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: EditAltIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-edit-alt-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$2V = [EditAltIconComponent];
class EditAltIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: EditAltIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: EditAltIconModule, declarations: [EditAltIconComponent], exports: [EditAltIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: EditAltIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: EditAltIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$2V,
                    exports: DIRECTIVES$2V,
                }]
        }] });

var __decorate$2U = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
const kpEdit = defineCustomIcon('kp', {
    name: 'edit',
    height: 19,
    width: 19,
    svgPath: 'M17.626 1.59235C16.3918 0.358119 14.3907 0.358086 13.1565 1.59229L4.14155 10.6072C3.10713 11.6411 2.43907 12.3089 1.9736 13.1311C1.50812 13.9532 1.27921 14.8696 0.924758 16.2886L0.699228 17.1908C0.605922 17.564 0.715278 17.9588 0.987309 18.2309C1.25934 18.5029 1.65415 18.6122 2.02737 18.5189L2.92961 18.2934C4.34858 17.9389 5.265 17.71 6.08711 17.2446C6.90921 16.7791 7.577 16.1111 8.61098 15.0767L17.6259 6.0617C18.8601 4.82752 18.8601 2.82655 17.626 1.59235Z',
});
let EditIconComponent = class EditIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: EditIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: EditIconComponent, selector: "kp-edit-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
EditIconComponent = __decorate$2U([
    Icon(kpEdit, CustomIconStyle)
], EditIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: EditIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-edit-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$2U = [EditIconComponent];
class EditIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: EditIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: EditIconModule, declarations: [EditIconComponent], exports: [EditIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: EditIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: EditIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$2U,
                    exports: DIRECTIVES$2U,
                }]
        }] });

var __decorate$2T = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
const kpEllipsis = defineCustomIcon('kp', {
    name: 'ellipsis',
    height: 6,
    width: 22,
    svgPath: 'M16.8333 3.29167C16.8333 2.00301 17.878 0.958341 19.1667 0.958341C20.4553 0.958342 21.5 2.00301 21.5 3.29167C21.5 4.58034 20.4553 5.62501 19.1667 5.62501C17.878 5.62501 16.8333 4.58034 16.8333 3.29167ZM8.66667 3.29167C8.66667 2.00301 9.71134 0.958341 11 0.958341C12.2887 0.958341 13.3333 2.00301 13.3333 3.29167C13.3333 4.58034 12.2887 5.62501 11 5.62501C9.71134 5.62501 8.66667 4.58034 8.66667 3.29167ZM2.83333 0.95834C1.54467 0.95834 0.5 2.00301 0.5 3.29167C0.5 4.58034 1.54467 5.62501 2.83333 5.62501C4.122 5.62501 5.16667 4.58034 5.16667 3.29167C5.16667 2.00301 4.122 0.95834 2.83333 0.95834Z',
});
let EllipsisIconComponent = class EllipsisIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: EllipsisIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: EllipsisIconComponent, selector: "kp-ellipsis-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
EllipsisIconComponent = __decorate$2T([
    Icon(kpEllipsis, CustomIconStyle)
], EllipsisIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: EllipsisIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-ellipsis-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$2T = [EllipsisIconComponent];
class EllipsisIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: EllipsisIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: EllipsisIconModule, declarations: [EllipsisIconComponent], exports: [EllipsisIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: EllipsisIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: EllipsisIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$2T,
                    exports: DIRECTIVES$2T,
                }]
        }] });

var __decorate$2S = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
let ExclamationCircleAltIconComponent = class ExclamationCircleAltIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ExclamationCircleAltIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: ExclamationCircleAltIconComponent, selector: "kp-exclamation-circle-alt-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
ExclamationCircleAltIconComponent = __decorate$2S([
    Icon(faExclamationCircle)
], ExclamationCircleAltIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ExclamationCircleAltIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-exclamation-circle-alt-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$2S = [ExclamationCircleAltIconComponent];
class ExclamationCircleAltIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ExclamationCircleAltIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: ExclamationCircleAltIconModule, declarations: [ExclamationCircleAltIconComponent], exports: [ExclamationCircleAltIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ExclamationCircleAltIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ExclamationCircleAltIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$2S,
                    exports: DIRECTIVES$2S,
                }]
        }] });

var __decorate$2R = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
let ExclamationCircleIconComponent = class ExclamationCircleIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ExclamationCircleIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: ExclamationCircleIconComponent, selector: "kp-exclamation-circle-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
ExclamationCircleIconComponent = __decorate$2R([
    Icon(faExclamationCircle$1)
], ExclamationCircleIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ExclamationCircleIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-exclamation-circle-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$2R = [ExclamationCircleIconComponent];
class ExclamationCircleIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ExclamationCircleIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: ExclamationCircleIconModule, declarations: [ExclamationCircleIconComponent], exports: [ExclamationCircleIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ExclamationCircleIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ExclamationCircleIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$2R,
                    exports: DIRECTIVES$2R,
                }]
        }] });

var __decorate$2Q = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
let ExpandWideIconComponent = class ExpandWideIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ExpandWideIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: ExpandWideIconComponent, selector: "kp-expand-wide-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
ExpandWideIconComponent = __decorate$2Q([
    Icon(faExpandWide)
], ExpandWideIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ExpandWideIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-expand-wide-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$2Q = [ExpandWideIconComponent];
class ExpandWideIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ExpandWideIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: ExpandWideIconModule, declarations: [ExpandWideIconComponent], exports: [ExpandWideIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ExpandWideIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ExpandWideIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$2Q,
                    exports: DIRECTIVES$2Q,
                }]
        }] });

var __decorate$2P = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
const kpExternalLink = defineCustomIcon('kp', {
    name: 'external-link',
    height: 20,
    width: 20,
    svgPath: 'M12.25 1C12.25 1.41421 12.5858 1.75 13 1.75H17.1893L9.46967 9.46967C9.17678 9.76256 9.17678 10.2374 9.46967 10.5303C9.76256 10.8232 10.2374 10.8232 10.5303 10.5303L18.25 2.81066V7C18.25 7.41421 18.5858 7.75 19 7.75C19.4142 7.75 19.75 7.41421 19.75 7V1C19.75 0.585786 19.4142 0.25 19 0.25H13C12.5858 0.25 12.25 0.585786 12.25 1ZM7 3.25L6.94513 3.25C5.57754 3.24998 4.47522 3.24996 3.60825 3.36652C2.70814 3.48754 1.95027 3.74643 1.34835 4.34835C0.746433 4.95027 0.487539 5.70814 0.366523 6.60825C0.249962 7.47522 0.249979 8.57754 0.25 9.94513V10V13V13.0549C0.249979 14.4225 0.249962 15.5248 0.366523 16.3918C0.487539 17.2919 0.746433 18.0497 1.34835 18.6516L1.86635 18.1337L1.34835 18.6517C1.95027 19.2536 2.70814 19.5125 3.60825 19.6335C4.47522 19.75 5.57754 19.75 6.94513 19.75H7H10H10.0549C11.4225 19.75 12.5248 19.75 13.3918 19.6335C14.2919 19.5125 15.0497 19.2536 15.6517 18.6516C16.2536 18.0497 16.5125 17.2919 16.6335 16.3918C16.75 15.5248 16.75 14.4225 16.75 13.0549V13V12C16.75 11.5858 16.4142 11.25 16 11.25C15.5858 11.25 15.25 11.5858 15.25 12V13C15.25 14.4354 15.2484 15.4365 15.1469 16.1919C15.0482 16.9257 14.8678 17.3142 14.591 17.591C14.3142 17.8678 13.9257 18.0482 13.1919 18.1469C12.4365 18.2484 11.4354 18.25 10 18.25H7C5.56458 18.25 4.56347 18.2484 3.80812 18.1469C3.07434 18.0482 2.68577 17.8678 2.40901 17.591C2.13225 17.3142 1.9518 16.9257 1.85315 16.1919C1.75159 15.4365 1.75 14.4354 1.75 13V10C1.75 8.56458 1.75159 7.56347 1.85315 6.80812C1.9518 6.07434 2.13225 5.68577 2.40901 5.40901C2.68577 5.13225 3.07434 4.9518 3.80812 4.85315C4.56347 4.75159 5.56458 4.75 7 4.75H8C8.41421 4.75 8.75 4.41421 8.75 4C8.75 3.58579 8.41421 3.25 8 3.25H7ZZ',
});
let ExternalLinkIconComponent = class ExternalLinkIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ExternalLinkIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: ExternalLinkIconComponent, selector: "kp-external-link-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
ExternalLinkIconComponent = __decorate$2P([
    Icon(kpExternalLink, CustomIconStyle)
], ExternalLinkIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ExternalLinkIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-external-link-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$2P = [ExternalLinkIconComponent];
class ExternalLinkIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ExternalLinkIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: ExternalLinkIconModule, declarations: [ExternalLinkIconComponent], exports: [ExternalLinkIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ExternalLinkIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ExternalLinkIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$2P,
                    exports: DIRECTIVES$2P,
                }]
        }] });

var __decorate$2O = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
const kpEye = defineCustomIcon('kp', {
    name: 'eye',
    height: 16,
    width: 22,
    svgPath: 'M11 0.25C7.30768 0.25 4.62703 1.5736 2.86629 3.1989C1.99041 4.0074 1.34365 4.88909 0.912939 5.71605C0.489229 6.52957 0.25 7.33841 0.25 8C0.25 8.66159 0.489229 9.47043 0.912939 10.284C1.34365 11.1109 1.99041 11.9926 2.86629 12.8011C4.62703 14.4264 7.30768 15.75 11 15.75C14.6923 15.75 17.373 14.4264 19.1337 12.8011C20.0096 11.9926 20.6564 11.1109 21.0871 10.284C21.5108 9.47043 21.75 8.66159 21.75 8C21.75 7.33841 21.5108 6.52957 21.0871 5.71605C20.6564 4.88909 20.0096 4.0074 19.1337 3.1989C17.373 1.5736 14.6923 0.25 11 0.25ZM10.9999 5.75003C9.75724 5.75003 8.74988 6.75739 8.74988 8.00003C8.74988 9.24267 9.75724 10.25 10.9999 10.25C12.2425 10.25 13.2499 9.24267 13.2499 8.00003C13.2499 6.75739 12.2425 5.75003 10.9999 5.75003ZM7.24988 8.00003C7.24988 5.92896 8.92881 4.25003 10.9999 4.25003C13.0709 4.25003 14.7499 5.92896 14.7499 8.00003C14.7499 10.0711 13.0709 11.75 10.9999 11.75C8.92881 11.75 7.24988 10.0711 7.24988 8.00003Z',
});
let EyeIconComponent = class EyeIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: EyeIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: EyeIconComponent, selector: "kp-eye-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
EyeIconComponent = __decorate$2O([
    Icon(kpEye, CustomIconStyle)
], EyeIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: EyeIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-eye-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$2O = [EyeIconComponent];
class EyeIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: EyeIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: EyeIconModule, declarations: [EyeIconComponent], exports: [EyeIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: EyeIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: EyeIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$2O,
                    exports: DIRECTIVES$2O,
                }]
        }] });

var __decorate$2N = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
const kpEyeAlt = defineCustomIcon('kp', {
    name: 'eye-alt',
    height: 16,
    width: 22,
    svgPath: 'M1.75 8C1.75 7.66159 1.88577 7.09543 2.24331 6.40895C2.59385 5.73591 3.13459 4.9926 3.88371 4.3011C5.37297 2.9264 7.69232 1.75 11 1.75C14.3077 1.75 16.627 2.9264 18.1163 4.3011C18.8654 4.9926 19.4061 5.73591 19.7567 6.40895C20.1142 7.09543 20.25 7.66159 20.25 8C20.25 8.33841 20.1142 8.90457 19.7567 9.59105C19.4061 10.2641 18.8654 11.0074 18.1163 11.6989C16.627 13.0736 14.3077 14.25 11 14.25C7.69232 14.25 5.37297 13.0736 3.88371 11.6989C3.13459 11.0074 2.59385 10.2641 2.24331 9.59105C1.88577 8.90457 1.75 8.33841 1.75 8ZM11 0.25C7.30768 0.25 4.62703 1.5736 2.86629 3.1989C1.99041 4.0074 1.34365 4.88909 0.912939 5.71605C0.489229 6.52957 0.25 7.33841 0.25 8C0.25 8.66159 0.489229 9.47043 0.912939 10.284C1.34365 11.1109 1.99041 11.9926 2.86629 12.8011C4.62703 14.4264 7.30768 15.75 11 15.75C14.6923 15.75 17.373 14.4264 19.1337 12.8011C20.0096 11.9926 20.6564 11.1109 21.0871 10.284C21.5108 9.47043 21.75 8.66159 21.75 8C21.75 7.33841 21.5108 6.52957 21.0871 5.71605C20.6564 4.88909 20.0096 4.0074 19.1337 3.1989C17.373 1.5736 14.6923 0.25 11 0.25ZM8.7499 8.00002C8.7499 6.75738 9.75726 5.75002 10.9999 5.75002C12.2425 5.75002 13.2499 6.75738 13.2499 8.00002C13.2499 9.24266 12.2425 10.25 10.9999 10.25C9.75726 10.25 8.7499 9.24266 8.7499 8.00002ZM10.9999 4.25002C8.92883 4.25002 7.2499 5.92896 7.2499 8.00002C7.2499 10.0711 8.92883 11.75 10.9999 11.75C13.071 11.75 14.7499 10.0711 14.7499 8.00002C14.7499 5.92896 13.071 4.25002 10.9999 4.25002Z',
});
let EyeAltIconComponent = class EyeAltIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: EyeAltIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: EyeAltIconComponent, selector: "kp-eye-alt-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
EyeAltIconComponent = __decorate$2N([
    Icon(kpEyeAlt, CustomIconStyle)
], EyeAltIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: EyeAltIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-eye-alt-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$2N = [EyeAltIconComponent];
class EyeAltIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: EyeAltIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: EyeAltIconModule, declarations: [EyeAltIconComponent], exports: [EyeAltIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: EyeAltIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: EyeAltIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$2N,
                    exports: DIRECTIVES$2N,
                }]
        }] });

var __decorate$2M = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
const kpEyeSlash = defineCustomIcon('kp', {
    name: 'eye-slash',
    height: 20,
    width: 22,
    svgPath: 'M19.4698 0.46967C19.7627 0.176777 20.2375 0.176777 20.5304 0.46967C20.8233 0.762563 20.8233 1.23744 20.5304 1.53033L17.8628 4.19798C19.0889 5.01858 20.0083 6.00408 20.6429 6.96365C21.3559 8.04182 21.75 9.15127 21.75 9.99998C21.75 10.6616 21.5108 11.4704 21.0871 12.2839C20.6564 13.1109 20.0096 13.9926 19.1337 14.8011C17.373 16.4264 14.6923 17.75 11 17.75C8.82038 17.75 6.98908 17.2887 5.4893 16.5715L2.53043 19.5303C2.23753 19.8232 1.76266 19.8232 1.46977 19.5303C1.17687 19.2374 1.17687 18.7626 1.46977 18.4697L19.4698 0.46967ZM8.93214 13.1286L10.0299 12.0309C10.3235 12.1714 10.6523 12.25 11.0001 12.25C12.2428 12.25 13.2501 11.2426 13.2501 10C13.2501 9.65221 13.1716 9.32341 13.031 9.02978L14.1287 7.93202C14.5212 8.5248 14.7501 9.23623 14.7501 10C14.7501 12.0711 13.0712 13.75 11.0001 13.75C10.2364 13.75 9.52492 13.521 8.93214 13.1286ZM11 3.74998C12.0406 3.74998 12.9811 3.8664 13.8267 4.06725C14.2297 4.16296 14.634 3.91385 14.7297 3.51085C14.8254 3.10785 14.5763 2.70356 14.1733 2.60784C13.2103 2.37912 12.1533 2.24998 11 2.24998C7.30768 2.24998 4.62703 3.57357 2.86629 5.19887C1.99041 6.00738 1.34365 6.88906 0.912939 7.71602C0.489229 8.52955 0.25 9.33838 0.25 9.99998C0.25 11.2107 1.05435 13.0051 2.58484 14.5311C2.87816 14.8235 3.35304 14.8229 3.6455 14.5295C3.93797 14.2362 3.93727 13.7613 3.64395 13.4689C2.28494 12.1138 1.75 10.6791 1.75 9.99998C1.75 9.66157 1.88577 9.0954 2.24331 8.40893C2.59385 7.73589 3.13459 6.99257 3.88371 6.30108C5.37297 4.92638 7.69232 3.74998 11 3.74998Z',
});
let EyeSlashIconComponent = class EyeSlashIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: EyeSlashIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: EyeSlashIconComponent, selector: "kp-eye-slash-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
EyeSlashIconComponent = __decorate$2M([
    Icon(kpEyeSlash, CustomIconStyle)
], EyeSlashIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: EyeSlashIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-eye-slash-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$2M = [EyeSlashIconComponent];
class EyeSlashIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: EyeSlashIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: EyeSlashIconModule, declarations: [EyeSlashIconComponent], exports: [EyeSlashIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: EyeSlashIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: EyeSlashIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$2M,
                    exports: DIRECTIVES$2M,
                }]
        }] });

var __decorate$2L = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
const kpFileCheck = defineCustomIcon('kp', {
    name: 'file-check',
    height: 24,
    width: 18,
    svgPath: 'M10.1574 0.750037L10.3334 0.75V3.33351L10.3334 3.39934C10.3333 4.28583 10.3333 5.04964 10.4155 5.6613C10.5036 6.31682 10.7024 6.94517 11.2121 7.45483C11.7217 7.96449 12.3501 8.16324 13.0056 8.25137C13.6172 8.3336 14.3811 8.33356 15.2675 8.33351L15.3334 8.33351H17.9168L17.9168 8.50943L17.9167 8.65203V16.5001V16.555C17.9167 17.9225 17.9167 19.0249 17.8002 19.8918C17.6792 20.7919 17.4203 21.5498 16.8184 22.1517C16.2164 22.7536 15.4586 23.0125 14.5585 23.1336C13.6915 23.2501 12.5892 23.2501 11.2216 23.2501H11.1667H6.83337H6.7785C5.41091 23.2501 4.30859 23.2501 3.44162 23.1336C2.54152 23.0125 1.78364 22.7536 1.18172 22.1517C0.579808 21.5498 0.320915 20.7919 0.199898 19.8918C0.0833373 19.0249 0.0833539 17.9225 0.0833746 16.555L0.0833752 16.5001V7.50008L0.0833746 7.44521C0.0833539 6.07762 0.0833373 4.9753 0.199898 4.10833C0.320915 3.20822 0.579808 2.45035 1.18172 1.84843C1.78364 1.24651 2.54152 0.987621 3.44162 0.866605C4.30859 0.750044 5.41091 0.750061 6.7785 0.750081L6.83337 0.750082H10.0148L10.1574 0.750037ZM17.6313 5.97433C17.6799 6.09175 17.7203 6.21105 17.7538 6.33351H15.3334C14.3623 6.33351 13.7346 6.33139 13.2721 6.2692C12.8383 6.21089 12.7024 6.11674 12.6263 6.04062C12.5501 5.96449 12.456 5.82854 12.3977 5.39481C12.3355 4.9323 12.3334 4.30459 12.3334 3.33351V0.913049C12.4558 0.946545 12.5751 0.986905 12.6925 1.03553C13.3838 1.32191 13.9181 1.85669 14.6869 2.62622L14.6869 2.62623L14.6869 2.62624L14.7877 2.72711L15.9397 3.87906L16.0406 3.97986C16.8101 4.74867 17.3449 5.28295 17.6313 5.97433ZM13.2071 12.7073C13.5977 12.3168 13.5977 11.6836 13.2071 11.2931C12.8166 10.9025 12.1835 10.9025 11.7929 11.2931L7.83337 15.2526L6.79048 14.2097C6.39995 13.8192 5.76679 13.8192 5.37626 14.2097C4.98574 14.6003 4.98574 15.2334 5.37626 15.6239L7.12626 17.3739C7.3138 17.5615 7.56815 17.6668 7.83337 17.6668C8.09858 17.6668 8.35294 17.5615 8.54047 17.3739L13.2071 12.7073Z',
});
let FileCheckIconComponent = class FileCheckIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: FileCheckIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: FileCheckIconComponent, selector: "kp-file-check-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
FileCheckIconComponent = __decorate$2L([
    Icon(kpFileCheck, CustomIconStyle)
], FileCheckIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: FileCheckIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-file-check-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$2L = [FileCheckIconComponent];
class FileCheckIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: FileCheckIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: FileCheckIconModule, declarations: [FileCheckIconComponent], exports: [FileCheckIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: FileCheckIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: FileCheckIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$2L,
                    exports: DIRECTIVES$2L,
                }]
        }] });

var __decorate$2K = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
const kpFile = defineCustomIcon('kp', {
    name: 'file',
    height: 16,
    width: 18,
    svgPath: 'M16.4954 9.08325C16.4991 8.8554 16.4999 8.58669 16.4999 8.26464L16.4999 7.58325C16.4999 6.14784 16.4983 5.14672 16.3968 4.39137C16.2981 3.6576 16.1177 3.26903 15.8409 2.99226C15.5641 2.7155 15.1756 2.53505 14.4418 2.4364C13.6864 2.33485 12.6853 2.33325 11.2499 2.33325L6.74991 2.33325C5.3145 2.33325 4.31338 2.33485 3.55803 2.4364C2.82426 2.53505 2.43568 2.7155 2.15892 2.99226C1.88216 3.26903 1.70171 3.6576 1.60306 4.39137C1.5015 5.14672 1.49991 6.14783 1.49991 7.58325L1.49991 8.41659C1.49991 9.852 1.5015 10.8531 1.60306 11.6085C1.70171 12.3422 1.88216 12.7308 2.15892 13.0076C2.43568 13.2843 2.82426 13.4648 3.55803 13.5634C4.31338 13.665 5.31449 13.6666 6.74991 13.6666L11.098 13.6666C11.42 13.6666 11.6887 13.6658 11.9165 13.662C11.9166 12.8163 11.92 12.107 11.9964 11.5388C12.0808 10.911 12.2665 10.3442 12.722 9.88871C13.1774 9.43324 13.7442 9.24757 14.372 9.16316C14.9403 9.08676 15.6496 9.08338 16.4954 9.08325ZM13.4169 13.3867C13.7251 13.2017 14.0731 12.866 14.8103 12.1289L14.9622 11.9769C15.6993 11.2399 16.035 10.8919 16.2199 10.5836C15.4959 10.5855 14.9762 10.5954 14.5719 10.6498C14.1104 10.7118 13.9129 10.8191 13.7826 10.9494C13.6523 11.0797 13.5451 11.2772 13.483 11.7386C13.4287 12.143 13.4187 12.6626 13.4169 13.3867ZM17.7145 10.9423C18.0008 10.251 18.0005 9.49502 18 8.40724L17.9999 8.26464L17.9999 7.58325L17.9999 7.52838C17.9999 6.16079 17.9999 5.05847 17.8834 4.1915C17.7624 3.29139 17.5035 2.53352 16.9016 1.9316C16.2996 1.32969 15.5418 1.07079 14.6417 0.949776C13.7747 0.833215 12.6724 0.833232 11.3048 0.833252L11.2499 0.833253L6.74991 0.833253L6.69504 0.833252C5.32745 0.833231 4.22513 0.833215 3.35816 0.949775C2.45805 1.07079 1.70018 1.32969 1.09826 1.9316C0.496344 2.53352 0.23745 3.29139 0.116432 4.1915C-0.000127977 5.05847 -0.000110859 6.16079 -8.9938e-05 7.52838L-8.99404e-05 7.58325L-8.99769e-05 8.41659L-8.99793e-05 8.47146C-0.00011102 9.83905 -0.000128234 10.9414 0.116432 11.8083C0.237449 12.7084 0.496343 13.4663 1.09826 14.0682C1.70018 14.6702 2.45805 14.929 3.35816 15.0501C4.22512 15.1666 5.32744 15.1666 6.69503 15.1666L6.69504 15.1666L6.74991 15.1666L11.098 15.1666L11.2406 15.1666C12.3283 15.1672 13.0843 15.1675 13.7757 14.8811C14.467 14.5948 15.0013 14.06 15.7701 13.2904L15.7701 13.2904L15.8709 13.1896L16.0229 13.0376L16.1237 12.9368C16.8933 12.168 17.4281 11.6337 17.7145 10.9423Z',
});
let FileIconComponent = class FileIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: FileIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: FileIconComponent, selector: "kp-file-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
FileIconComponent = __decorate$2K([
    Icon(kpFile, CustomIconStyle)
], FileIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: FileIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-file-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$2K = [FileIconComponent];
class FileIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: FileIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: FileIconModule, declarations: [FileIconComponent], exports: [FileIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: FileIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: FileIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$2K,
                    exports: DIRECTIVES$2K,
                }]
        }] });

var __decorate$2J = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
const kpFileInvoice = defineCustomIcon('kp', {
    name: 'file-invoice',
    height: 24,
    width: 24,
    svgPath: 'M13.1539 1H12.9815L12.8851 1.00001H9.26154H9.22533C8.30828 1 7.5686 1 6.96963 1.04894C6.35292 1.09933 5.81121 1.20578 5.31005 1.46114C4.51397 1.86676 3.86674 2.51399 3.46113 3.31006C3.20577 3.81123 3.09932 4.35293 3.04892 4.96964C2.99999 5.56861 2.99999 6.30828 3 7.22532V7.22533V7.22534V7.26156V16.7385V16.7747V16.7748C2.99999 17.6918 2.99999 18.4314 3.04892 19.0304C3.09932 19.6471 3.20577 20.1888 3.46113 20.69C3.86674 21.4861 4.51397 22.1333 5.31005 22.5389C5.81121 22.7942 6.35292 22.9007 6.96963 22.9511C7.5686 23 8.30827 23 9.22531 23H9.26154H14.2257H14.2619C15.179 23 15.9186 23 16.5176 22.9511C17.1343 22.9007 17.6759 22.7942 18.1772 22.5389C18.9733 22.1333 19.6204 21.4861 20.0261 20.69C20.2814 20.1888 20.3879 19.6471 20.4382 19.0304C20.4872 18.4314 20.4872 17.6918 20.4872 16.7748V16.7385V8.60212V8.50571V8.5057V8.33345H17.6103H17.576C16.9733 8.33347 16.4703 8.33348 16.0593 8.2999C15.6307 8.26488 15.2284 8.18917 14.8479 7.9953C14.2641 7.69784 13.7895 7.22321 13.492 6.63943C13.2982 6.25892 13.2225 5.85654 13.1874 5.42795C13.1539 5.01702 13.1539 4.51401 13.1539 3.91138V3.87704V1ZM20.3364 6.64114C20.231 6.25735 20.0719 5.89011 19.8637 5.55037C19.6012 5.12204 19.2413 4.76235 18.7214 4.24271L18.6532 4.17454L17.3126 2.83398L17.2445 2.76579V2.76578C16.7249 2.24584 16.3652 1.88595 15.9368 1.62346C15.5971 1.41529 15.23 1.25627 14.8462 1.15093V3.87704C14.8462 4.52286 14.8468 4.95633 14.8741 5.29014C14.9005 5.61408 14.9478 5.76896 14.9998 5.87113C15.1351 6.13649 15.3508 6.35223 15.6162 6.48744C15.7184 6.53949 15.8733 6.58674 16.1972 6.61321C16.531 6.64049 16.9645 6.64114 17.6103 6.64114H20.3364ZM12.5898 10.0257V9.74369C12.5898 9.2764 12.2109 8.89756 11.7436 8.89756C11.2763 8.89756 10.8974 9.2764 10.8974 9.74369V10.0413C9.62821 10.1815 8.64102 11.2576 8.64102 12.5642C8.64102 13.9662 9.77759 15.1027 11.1795 15.1027H12.3077C12.775 15.1027 13.1539 15.4815 13.1539 15.9488C13.1539 16.4161 12.775 16.795 12.3077 16.795H11.7436H9.48717C9.01986 16.795 8.64102 17.1738 8.64102 17.6411C8.64102 18.1084 9.01986 18.4873 9.48717 18.4873H10.8974V18.7693C10.8974 19.2366 11.2763 19.6155 11.7436 19.6155C12.2109 19.6155 12.5898 19.2366 12.5898 18.7693V18.4718C13.859 18.3315 14.8462 17.2555 14.8462 15.9488C14.8462 14.5469 13.7096 13.4104 12.3077 13.4104H11.1795C10.7122 13.4104 10.3333 13.0315 10.3333 12.5642C10.3333 12.0969 10.7122 11.7181 11.1795 11.7181H11.7436H14C14.4673 11.7181 14.8462 11.3392 14.8462 10.8719C14.8462 10.4046 14.4673 10.0257 14 10.0257H12.5898Z',
});
let FileInvoiceIconComponent = class FileInvoiceIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: FileInvoiceIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: FileInvoiceIconComponent, selector: "kp-file-invoice-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
FileInvoiceIconComponent = __decorate$2J([
    Icon(kpFileInvoice, CustomIconStyle)
], FileInvoiceIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: FileInvoiceIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-file-invoice-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$2J = [FileInvoiceIconComponent];
class FileInvoiceIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: FileInvoiceIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: FileInvoiceIconModule, declarations: [FileInvoiceIconComponent], exports: [FileInvoiceIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: FileInvoiceIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: FileInvoiceIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$2J,
                    exports: DIRECTIVES$2J,
                }]
        }] });

var __decorate$2I = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
const kpFilePlus = defineCustomIcon('kp', {
    name: 'file-plus',
    height: 18,
    width: 14,
    svgPath: 'M7.1433 2.25007C7.45097 2.25007 7.70435 2.25083 7.91711 2.25437C7.91797 2.90941 7.92483 3.46554 7.97892 3.9232C8.04404 4.47409 8.18638 4.97903 8.54121 5.41139C8.65549 5.55063 8.78317 5.67831 8.92241 5.79259C9.35477 6.14741 9.85971 6.28976 10.4106 6.35487C10.8682 6.40897 11.4243 6.41583 12.0794 6.41669C12.0829 6.62943 12.0837 6.8828 12.0837 7.19043V10.6667C12.0837 12.02 12.0822 12.9637 11.9911 13.6796C11.9025 14.3762 11.7398 14.7503 11.4925 15.0201C11.4483 15.0684 11.402 15.1147 11.3537 15.1589C11.0839 15.4062 10.7098 15.5689 10.0131 15.6576C9.29731 15.7486 8.35358 15.7501 7.00033 15.7501C5.64707 15.7501 4.70334 15.7486 3.98751 15.6576C3.29089 15.5689 2.91675 15.4062 2.64692 15.1589C2.59865 15.1147 2.55234 15.0684 2.50812 15.0201C2.26086 14.7503 2.09817 14.3762 2.00951 13.6796C1.91842 12.9637 1.91699 12.02 1.91699 10.6667V7.39263C1.91699 6.01023 1.91848 5.04615 2.01323 4.31623C2.10539 3.60633 2.27432 3.22693 2.53189 2.95446C2.56087 2.92379 2.59071 2.89395 2.62138 2.86496C2.89385 2.6074 3.27325 2.43847 3.98315 2.34631C4.71307 2.25155 5.67715 2.25007 7.05955 2.25007H7.1433ZM10.6367 3.69707C11.2758 4.33616 11.579 4.64869 11.7549 4.91535C11.2625 4.912 10.8896 4.90105 10.5867 4.86525C10.1774 4.81687 9.99482 4.73222 9.874 4.63307C9.81071 4.58113 9.75267 4.52309 9.70073 4.4598C9.60158 4.33898 9.51693 4.1564 9.46855 3.74712C9.43275 3.44423 9.42179 3.07132 9.41845 2.57888C9.68509 2.75479 9.99763 3.05803 10.6367 3.69707ZM7.27114 0.750035C8.24613 0.749616 8.92288 0.749326 9.55118 0.980218C9.64631 1.01518 9.73998 1.05397 9.83196 1.09652C10.4395 1.37753 10.9178 1.85627 11.607 2.54598L11.6973 2.63641L11.7877 2.72678C12.4775 3.41591 12.9562 3.89423 13.2372 4.50177C13.2798 4.59376 13.3186 4.68742 13.3535 4.78255C13.5844 5.41085 13.5841 6.0876 13.5837 7.06259L13.5837 7.19043V10.6667V10.7183C13.5837 12.0078 13.5837 13.0473 13.4791 13.8689C13.3707 14.7206 13.1394 15.4432 12.5985 16.0335C12.5247 16.114 12.4476 16.1912 12.3671 16.2649C11.7768 16.8058 11.0542 17.0372 10.2025 17.1455C9.38091 17.2501 8.34141 17.2501 7.05193 17.2501H7.00033H6.94873C5.65924 17.2501 4.61975 17.2501 3.79815 17.1455C2.94642 17.0372 2.22383 16.8058 1.63353 16.2649C1.55309 16.1912 1.47591 16.114 1.4022 16.0335C0.861297 15.4432 0.629905 14.7206 0.521514 13.8689C0.41696 13.0473 0.416974 12.0078 0.416992 10.7184V10.7183V10.6667V7.39263V7.33986V7.33985C0.416973 6.02266 0.416958 4.96089 0.525716 4.12312C0.638522 3.25417 0.879519 2.5189 1.44181 1.92405C1.49012 1.87294 1.53986 1.8232 1.59097 1.77489C2.18582 1.2126 2.92109 0.971601 3.79004 0.858794C4.62781 0.750036 5.68959 0.750052 7.00678 0.750071L7.05955 0.750071H7.1433L7.27114 0.750035ZM7.75033 8.58348C7.75033 8.16927 7.41454 7.83348 7.00033 7.83348C6.58611 7.83348 6.25033 8.16927 6.25033 8.58348V9.91681H4.91699C4.50278 9.91681 4.16699 10.2526 4.16699 10.6668C4.16699 11.081 4.50278 11.4168 4.91699 11.4168H6.25033V12.7501C6.25033 13.1644 6.58611 13.5001 7.00033 13.5001C7.41454 13.5001 7.75033 13.1644 7.75033 12.7501V11.4168H9.08366C9.49787 11.4168 9.83366 11.081 9.83366 10.6668C9.83366 10.2526 9.49787 9.91681 9.08366 9.91681H7.75033V8.58348Z',
});
let FilePlusIconComponent = class FilePlusIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: FilePlusIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: FilePlusIconComponent, selector: "kp-file-plus-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
FilePlusIconComponent = __decorate$2I([
    Icon(kpFilePlus, CustomIconStyle)
], FilePlusIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: FilePlusIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-file-plus-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$2I = [FilePlusIconComponent];
class FilePlusIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: FilePlusIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: FilePlusIconModule, declarations: [FilePlusIconComponent], exports: [FilePlusIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: FilePlusIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: FilePlusIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$2I,
                    exports: DIRECTIVES$2I,
                }]
        }] });

var __decorate$2H = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
const kpFileText = defineCustomIcon('kp', {
    name: 'file-text',
    height: 24,
    width: 18,
    svgPath: 'M10.3334 0.75L10.1574 0.750037L10.0148 0.750082H6.83338L6.7785 0.750081C5.41091 0.750061 4.30859 0.750044 3.44162 0.866605C2.54152 0.987621 1.78364 1.24651 1.18172 1.84843C0.579808 2.45035 0.320915 3.20822 0.199898 4.10833C0.0833374 4.9753 0.0833538 6.07762 0.0833746 7.44521L0.0833753 7.50008V16.5001L0.0833746 16.555C0.0833538 17.9225 0.0833374 19.0249 0.199898 19.8918C0.320914 20.7919 0.579808 21.5498 1.18172 22.1517C1.78364 22.7536 2.54152 23.0125 3.44162 23.1336C4.30859 23.2501 5.41091 23.2501 6.7785 23.2501H6.83337H11.1667H11.2216C12.5892 23.2501 13.6915 23.2501 14.5585 23.1336C15.4586 23.0125 16.2164 22.7536 16.8184 22.1517C17.4203 21.5498 17.6792 20.7919 17.8002 19.8918C17.9167 19.0249 17.9167 17.9226 17.9167 16.555V16.555V16.555V16.555V16.555V16.5001V8.65203L17.9168 8.50943L17.9168 8.33351H15.3334L15.2675 8.33351C14.3811 8.33356 13.6172 8.3336 13.0056 8.25137C12.3501 8.16324 11.7217 7.96449 11.2121 7.45483C10.7024 6.94517 10.5036 6.31682 10.4155 5.6613C10.3333 5.04964 10.3333 4.28583 10.3334 3.39934L10.3334 3.33351V0.75ZM17.7538 6.33351C17.7203 6.21105 17.6799 6.09175 17.6313 5.97433C17.3449 5.28295 16.8101 4.74867 16.0405 3.97986L15.9397 3.87906L14.7877 2.72711L14.6869 2.62624L14.6869 2.62623C13.9181 1.8567 13.3838 1.32191 12.6925 1.03553C12.5751 0.986905 12.4558 0.946545 12.3334 0.913049V3.33351C12.3334 4.30459 12.3355 4.9323 12.3977 5.39481C12.456 5.82854 12.5501 5.96449 12.6263 6.04062C12.7024 6.11674 12.8383 6.21089 13.2721 6.2692C13.7346 6.33139 14.3623 6.33351 15.3334 6.33351H17.7538ZM4.50001 12.0002C4.50001 11.4479 4.94773 11.0002 5.50001 11.0002H12.5C13.0523 11.0002 13.5 11.4479 13.5 12.0002C13.5 12.5525 13.0523 13.0002 12.5 13.0002H5.50001C4.94773 13.0002 4.50001 12.5525 4.50001 12.0002ZM5.50001 15.6665C4.94773 15.6665 4.50001 16.1142 4.50001 16.6665C4.50001 17.2187 4.94773 17.6665 5.50001 17.6665H10.1667C10.719 17.6665 11.1667 17.2187 11.1667 16.6665C11.1667 16.1142 10.719 15.6665 10.1667 15.6665H5.50001Z',
});
let FileTextIconComponent = class FileTextIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: FileTextIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: FileTextIconComponent, selector: "kp-file-text-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
FileTextIconComponent = __decorate$2H([
    Icon(kpFileText, CustomIconStyle)
], FileTextIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: FileTextIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-file-text-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$2H = [FileTextIconComponent];
class FileTextIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: FileTextIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: FileTextIconModule, declarations: [FileTextIconComponent], exports: [FileTextIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: FileTextIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: FileTextIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$2H,
                    exports: DIRECTIVES$2H,
                }]
        }] });

var __decorate$2G = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
const kpFilmTape = defineCustomIcon('kp', {
    name: 'film-tape',
    height: 18,
    width: 18,
    svgPath: 'M6.75 1.14325e-06L6.69513 5.47199e-07C6.239 -6.33714e-06 5.81239 -1.27446e-05 5.41417 0.00430621C5.38761 0.00146059 5.36064 9.04827e-07 5.33333 9.04827e-07C5.29943 9.04827e-07 5.26605 0.00225041 5.23334 0.00660835C4.51452 0.0172076 3.89181 0.0447882 3.35825 0.116524C2.45814 0.23754 1.70027 0.496434 1.09835 1.09835C0.496434 1.70027 0.23754 2.45814 0.116524 3.35825C0.0828666 3.60859 0.0589294 3.87855 0.0419055 4.16886C0.0147595 4.24644 6.65709e-07 4.32983 6.65709e-07 4.41667C6.65709e-07 4.47907 0.00762064 4.53968 0.0219808 4.59764C-2.18052e-05 5.21326 -1.1285e-05 5.91054 5.465e-07 6.69513L1.14255e-06 6.75V8.99915L6.65709e-07 9L1.14255e-06 9.00086V11.25L5.465e-07 11.3049C-1.1285e-05 12.0895 -2.18052e-05 12.7867 0.0219808 13.4024C0.00762064 13.4603 6.65709e-07 13.5209 6.65709e-07 13.5833C6.65709e-07 13.6702 0.0147595 13.7536 0.0419055 13.8311C0.0589294 14.1214 0.0828666 14.3914 0.116524 14.6418C0.23754 15.5419 0.496434 16.2997 1.09835 16.9017C1.70027 17.5036 2.45814 17.7625 3.35825 17.8835C3.89181 17.9552 4.51452 17.9828 5.23335 17.9934C5.26606 17.9978 5.29943 18 5.33333 18C5.36064 18 5.3876 17.9985 5.41415 17.9957C5.81238 18 6.239 18 6.69513 18H6.75H11.25H11.3049C11.761 18 12.1876 18 12.5858 17.9957C12.6124 17.9985 12.6394 18 12.6667 18C12.7006 18 12.7339 17.9978 12.7667 17.9934C13.4855 17.9828 14.1082 17.9552 14.6418 17.8835C15.5419 17.7625 16.2997 17.5036 16.9017 16.9017C17.5036 16.2997 17.7625 15.5419 17.8835 14.6418C17.9171 14.3914 17.9411 14.1214 17.9581 13.8311C17.9852 13.7536 18 13.6702 18 13.5833C18 13.5209 17.9924 13.4603 17.978 13.4024C18 12.7868 18 12.0895 18 11.305V11.3049V11.25V9V6.75V6.69513V6.695C18 5.91046 18 5.21322 17.978 4.59763C17.9924 4.53967 18 4.47907 18 4.41667C18 4.32983 17.9852 4.24645 17.9581 4.16887C17.9411 3.87856 17.9171 3.60859 17.8835 3.35825C17.7625 2.45814 17.5036 1.70027 16.9017 1.09835C16.2997 0.496434 15.5419 0.23754 14.6418 0.116524C14.1082 0.044788 13.4855 0.0172075 12.7667 0.00660826C12.7339 0.00225029 12.7006 9.04827e-07 12.6667 9.04827e-07C12.6394 9.04827e-07 12.6124 0.00146058 12.5858 0.00430619C12.1876 -1.27744e-05 11.761 -6.33714e-06 11.3049 5.47199e-07L11.25 1.14325e-06H6.75ZM1.58943 14.3333C1.59379 14.3701 1.59836 14.4063 1.60315 14.4419C1.7018 15.1757 1.88225 15.5642 2.15901 15.841C2.43577 16.1178 2.82435 16.2982 3.55812 16.3969C3.85738 16.4371 4.19521 16.4616 4.58333 16.4766V14.3333H1.58943ZM4.58333 12.8333H1.50797C1.50028 12.372 1.5 11.8488 1.5 11.25V9.75H4.58333V12.8333ZM6.08333 13.5833V16.4995C6.29461 16.5 6.51656 16.5 6.75 16.5H11.25C11.4834 16.5 11.7054 16.5 11.9167 16.4995V13.5833V9.75H6.08333V13.5833ZM16.5 11.25C16.5 11.8488 16.4997 12.372 16.492 12.8333H13.4167V9.75H16.5V11.25ZM16.4106 14.3333H13.4167V16.4766C13.8048 16.4616 14.1426 16.4371 14.4419 16.3969C15.1757 16.2982 15.5642 16.1178 15.841 15.841C16.1178 15.5642 16.2982 15.1757 16.3969 14.4419C16.4016 14.4063 16.4062 14.3701 16.4106 14.3333ZM16.4106 3.66667C16.4062 3.62993 16.4016 3.59375 16.3969 3.55812C16.2982 2.82435 16.1178 2.43577 15.841 2.15901C15.5642 1.88225 15.1757 1.7018 14.4419 1.60315C14.1426 1.56291 13.8048 1.53837 13.4167 1.5234V3.66667H16.4106ZM11.9167 4.41667V1.50055C11.7054 1.50004 11.4834 1.5 11.25 1.5H6.75C6.51656 1.5 6.29461 1.50004 6.08333 1.50055V4.41667V8.25H11.9167V4.41667ZM13.4167 5.16667H16.492C16.4997 5.62799 16.5 6.15121 16.5 6.75V8.25H13.4167V5.16667ZM1.5 6.75C1.5 6.15121 1.50028 5.62799 1.50797 5.16667H4.58333V8.25H1.5V6.75ZM1.58943 3.66667H4.58333V1.5234C4.19521 1.53837 3.85738 1.56291 3.55812 1.60315C2.82435 1.7018 2.43577 1.88225 2.15901 2.15901C1.88225 2.43577 1.7018 2.82435 1.60315 3.55812C1.59836 3.59375 1.59379 3.62993 1.58943 3.66667Z',
});
let FilmTapeIconComponent = class FilmTapeIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: FilmTapeIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: FilmTapeIconComponent, selector: "kp-film-tape-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
FilmTapeIconComponent = __decorate$2G([
    Icon(kpFilmTape, CustomIconStyle)
], FilmTapeIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: FilmTapeIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-film-tape-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$2G = [FilmTapeIconComponent];
class FilmTapeIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: FilmTapeIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: FilmTapeIconModule, declarations: [FilmTapeIconComponent], exports: [FilmTapeIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: FilmTapeIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: FilmTapeIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$2G,
                    exports: DIRECTIVES$2G,
                }]
        }] });

var __decorate$2F = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
const kpFilter = defineCustomIcon('kp', {
    name: 'filter',
    height: 20,
    width: 20,
    svgPath: 'M4.45369 0.250004L4.39236 0.250002H4.39236C3.48668 0.249961 2.72399 0.249926 2.15811 0.331463C1.59737 0.41226 0.93169 0.612248 0.63043 1.28311C0.329171 1.95396 0.621951 2.58436 0.934098 3.05714C1.24911 3.53426 1.75584 4.10428 2.35757 4.78116L2.39832 4.827L5.79236 8.6453C6.44338 9.37769 6.6869 9.65849 6.85576 9.9693C6.99494 10.2255 7.09902 10.4992 7.16519 10.7832C7.24547 11.1276 7.25001 11.4993 7.25001 12.4792V17C7.25001 18.5188 8.48122 19.75 10 19.75C11.5188 19.75 12.75 18.5188 12.75 17V12.4792C12.75 11.4993 12.7545 11.1276 12.8348 10.7832C12.901 10.4992 13.0051 10.2255 13.1443 9.9693C13.3131 9.65849 13.5566 9.37769 14.2077 8.6453L17.6017 4.827L17.6425 4.78115C18.2442 4.10427 18.7509 3.53425 19.0659 3.05714C19.3781 2.58436 19.6708 1.95396 19.3696 1.28311C19.0683 0.612248 18.4026 0.41226 17.8419 0.331463C17.276 0.249926 16.5133 0.249961 15.6077 0.250002L15.5463 0.250004H4.45369Z',
});
let FilterIconComponent = class FilterIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: FilterIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: FilterIconComponent, selector: "kp-filter-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
FilterIconComponent = __decorate$2F([
    Icon(kpFilter, CustomIconStyle)
], FilterIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: FilterIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-filter-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$2F = [FilterIconComponent];
class FilterIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: FilterIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: FilterIconModule, declarations: [FilterIconComponent], exports: [FilterIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: FilterIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: FilterIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$2F,
                    exports: DIRECTIVES$2F,
                }]
        }] });

var __decorate$2E = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
const kpFolder = defineCustomIcon('kp', {
    name: 'folder',
    height: 19,
    width: 22,
    svgPath: 'M5.00005 0.250049L4.88425 0.250024C4.06629 0.249755 3.50814 0.249571 3.02948 0.377828C1.73538 0.724579 0.724579 1.73538 0.377828 3.02948C0.249571 3.50814 0.249755 4.06629 0.250024 4.88425L0.250049 5.00005V12L0.250049 12.0549C0.250028 13.4225 0.250012 14.5248 0.366572 15.3918C0.487589 16.2919 0.746482 17.0498 1.3484 17.6517C1.95032 18.2536 2.70819 18.5125 3.6083 18.6335C4.47527 18.7501 5.57759 18.7501 6.94518 18.75H7.00005H15H15.0549C16.4225 18.7501 17.5248 18.7501 18.3918 18.6335C19.2919 18.5125 20.0498 18.2536 20.6517 17.6517C21.2536 17.0498 21.5125 16.2919 21.6335 15.3918C21.7501 14.5248 21.7501 13.4225 21.75 12.0549V12V10V9.94518C21.7501 8.57759 21.7501 7.47527 21.6335 6.6083C21.5125 5.70819 21.2536 4.95032 20.6517 4.3484C20.0498 3.74648 19.2919 3.48759 18.3918 3.36657C17.5248 3.25001 16.4225 3.25003 15.0549 3.25005L15 3.25005H14.1214C13.2138 3.25005 12.9048 3.24337 12.6259 3.16386C12.4598 3.11651 12.2997 3.0502 12.1488 2.96623C11.8953 2.82521 11.6721 2.61149 11.0304 1.96972L10.9592 1.89847L10.9592 1.89847C10.4162 1.35512 10.04 0.978685 9.5806 0.723089C9.32905 0.583133 9.06222 0.472608 8.78538 0.393697C8.2798 0.249584 7.74763 0.249765 6.97947 0.250027L6.87873 0.250049H5.00005Z',
});
let FolderIconComponent = class FolderIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: FolderIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: FolderIconComponent, selector: "kp-folder-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
FolderIconComponent = __decorate$2E([
    Icon(kpFolder, CustomIconStyle)
], FolderIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: FolderIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-folder-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$2E = [FolderIconComponent];
class FolderIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: FolderIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: FolderIconModule, declarations: [FolderIconComponent], exports: [FolderIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: FolderIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: FolderIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$2E,
                    exports: DIRECTIVES$2E,
                }]
        }] });

var __decorate$2D = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
const kpFolderOpen = defineCustomIcon('kp', {
    name: 'folder-open',
    height: 19,
    width: 23,
    svgPath: 'M4.88401 0.24978L4.99981 0.249805H5.24245L5.30143 0.249797C5.75101 0.249707 6.06185 0.249645 6.36446 0.299306C6.93448 0.392853 7.47518 0.616819 7.94439 0.953736C8.19348 1.13259 8.41323 1.35243 8.73107 1.6704L8.73107 1.6704L8.77278 1.71212L9.03014 1.96947C9.6719 2.61124 9.89508 2.82497 10.1485 2.96598C10.2995 3.04995 10.4596 3.11627 10.6257 3.16362C10.9046 3.24312 11.2135 3.24981 12.1211 3.24981H14.9996L15.1154 3.24978C15.9335 3.24951 16.4918 3.24933 16.9706 3.37765C18.2645 3.72444 19.2752 4.7351 19.622 6.029C19.7194 6.39262 19.7428 6.80211 19.7482 7.33647C19.7797 7.34048 19.8108 7.34468 19.8414 7.34907C20.5667 7.45287 21.2165 7.68276 21.6868 8.25653C22.1572 8.8303 22.2552 9.5125 22.2148 10.2441C22.1761 10.9427 22.0014 11.8162 21.7897 12.8747L21.7897 12.8747L21.7897 12.8748L21.7783 12.9316L21.6999 13.3238L21.6904 13.3713C21.4676 14.4854 21.2869 15.3888 21.0533 16.0948C20.8099 16.8304 20.4836 17.4362 19.9178 17.9C19.352 18.3638 18.694 18.565 17.925 18.6594C17.7046 18.6865 17.4679 18.7054 17.2141 18.7188C17.1462 18.739 17.0743 18.7498 16.9998 18.7498H15.5635C15.4262 18.75 15.2855 18.75 15.1414 18.75H15.1293H15.0809H4.99976C4.93641 18.75 4.87401 18.742 4.81386 18.7266C4.36979 18.7101 3.96857 18.6818 3.60805 18.6333C2.70795 18.5123 1.95007 18.2534 1.34815 17.6515C0.746238 17.0495 0.487344 16.2917 0.366328 15.3916C0.249767 14.5246 0.249784 13.4223 0.249805 12.0547L0.249805 11.9998V4.99981L0.24978 4.88401C0.24951 4.06605 0.249327 3.50789 0.377583 3.02923C0.724335 1.73514 1.73514 0.724335 3.02923 0.377583C3.50789 0.249327 4.06605 0.24951 4.88401 0.24978ZM18.1731 6.41732C18.2215 6.59784 18.2396 6.81625 18.2462 7.25427C17.9138 7.24998 17.5578 7.24999 17.1784 7.25H17.1784L17.1205 7.25H11.9186L11.8702 7.25C10.734 7.24998 9.81268 7.24997 9.07455 7.34059C8.30547 7.435 7.64746 7.63616 7.08171 8.09997C6.51595 8.56378 6.18966 9.16955 5.94625 9.90518C5.71263 10.6112 5.53196 11.5146 5.30915 12.6287L5.30915 12.6288L5.29966 12.6762L4.39417 17.2037C4.183 17.1895 3.98844 17.1709 3.80793 17.1467C3.07415 17.048 2.68558 16.8676 2.40881 16.5908C2.13205 16.314 1.95161 15.9255 1.85295 15.1917C1.7514 14.4363 1.74981 13.4352 1.74981 11.9998V4.99981C1.74981 4.02155 1.75625 3.67955 1.82647 3.41746C2.03452 2.64101 2.64101 2.03452 3.41746 1.82647C3.67955 1.75625 4.02155 1.74981 4.99981 1.74981H5.24245C5.77305 1.74981 5.9546 1.75211 6.12154 1.77951C6.46355 1.83563 6.78797 1.97001 7.0695 2.17216C7.20692 2.27084 7.33692 2.39758 7.71212 2.77278L7.96947 3.03013L8.04069 3.10138C8.58367 3.64474 8.95985 4.02117 9.41925 4.27677C9.67081 4.41672 9.93764 4.52725 10.2145 4.60616C10.7201 4.75027 11.2522 4.75009 12.0204 4.74983L12.1211 4.74981H14.9996C15.9781 4.74981 16.3201 4.75625 16.5823 4.82651C17.3586 5.03458 17.965 5.64098 18.1731 6.41732Z',
});
let FolderOpenIconComponent = class FolderOpenIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: FolderOpenIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: FolderOpenIconComponent, selector: "kp-folder-open-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
FolderOpenIconComponent = __decorate$2D([
    Icon(kpFolderOpen, CustomIconStyle)
], FolderOpenIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: FolderOpenIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-folder-open-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$2D = [FolderOpenIconComponent];
class FolderOpenIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: FolderOpenIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: FolderOpenIconModule, declarations: [FolderOpenIconComponent], exports: [FolderOpenIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: FolderOpenIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: FolderOpenIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$2D,
                    exports: DIRECTIVES$2D,
                }]
        }] });

var __decorate$2C = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
let FolderPlusIconComponent = class FolderPlusIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: FolderPlusIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: FolderPlusIconComponent, selector: "kp-folder-plus-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
FolderPlusIconComponent = __decorate$2C([
    Icon(faFolderPlus)
], FolderPlusIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: FolderPlusIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-folder-plus-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$2C = [FolderPlusIconComponent];
class FolderPlusIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: FolderPlusIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: FolderPlusIconModule, declarations: [FolderPlusIconComponent], exports: [FolderPlusIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: FolderPlusIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: FolderPlusIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$2C,
                    exports: DIRECTIVES$2C,
                }]
        }] });

var __decorate$2B = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
let FontIconComponent = class FontIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: FontIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: FontIconComponent, selector: "kp-font-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
FontIconComponent = __decorate$2B([
    Icon(faFont)
], FontIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: FontIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-font-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$2B = [FontIconComponent];
class FontIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: FontIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: FontIconModule, declarations: [FontIconComponent], exports: [FontIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: FontIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: FontIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$2B,
                    exports: DIRECTIVES$2B,
                }]
        }] });

var __decorate$2A = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
const kpGrid = defineCustomIcon('kp', {
    name: 'grid',
    height: 18,
    width: 18,
    svgPath: 'M4.698 1.12109e-06L4.75 2.25357e-06H13.25L13.302 1.12109e-06C14.2005 -2.8264e-05 14.9497 -5.27615e-05 15.5445 0.0799135C16.1723 0.164318 16.7391 0.349989 17.1945 0.805459C17.65 1.26093 17.8357 1.82773 17.9201 2.45552C18.0001 3.05031 18 3.79953 18 4.69801V4.75V6.25V13.25V13.302C18 14.2005 18.0001 14.9497 17.9201 15.5445C17.8357 16.1723 17.65 16.7391 17.1945 17.1945C16.7391 17.65 16.1723 17.8357 15.5445 17.9201C14.9497 18.0001 14.2005 18 13.302 18H13.25H6.25H4.75H4.69801C3.79953 18 3.05031 18.0001 2.45552 17.9201C1.82773 17.8357 1.26093 17.65 0.805459 17.1945C0.349989 16.7391 0.164318 16.1723 0.0799135 15.5445C-5.27615e-05 14.9497 -2.8264e-05 14.2005 1.12109e-06 13.302L2.25357e-06 13.25V6.25V4.75L1.12109e-06 4.698C-2.8264e-05 3.79952 -5.27615e-05 3.0503 0.0799135 2.45552C0.164318 1.82773 0.349989 1.26093 0.805459 0.805459C1.26093 0.349989 1.82773 0.164318 2.45552 0.0799135C3.0503 -5.27615e-05 3.79952 -2.8264e-05 4.698 1.12109e-06ZM1.5 7V13.25C1.5 14.214 1.5016 14.8616 1.56654 15.3446C1.62858 15.8061 1.7358 16.0036 1.86612 16.1339C1.99644 16.2642 2.19393 16.3714 2.65539 16.4335C3.13843 16.4984 3.78599 16.5 4.75 16.5H5.5L5.5 7H1.5ZM6.24879 5.5H1.5V4.75C1.5 3.78599 1.5016 3.13843 1.56654 2.65539C1.62858 2.19393 1.7358 1.99644 1.86612 1.86612C1.99644 1.7358 2.19393 1.62858 2.65539 1.56654C3.13843 1.5016 3.78599 1.5 4.75 1.5H13.25C14.214 1.5 14.8616 1.5016 15.3446 1.56654C15.8061 1.62858 16.0036 1.7358 16.1339 1.86612C16.2642 1.99644 16.3714 2.19393 16.4335 2.65539C16.4984 3.13843 16.5 3.78599 16.5 4.75V5.5H6.25121C6.25081 5.5 6.2504 5.5 6.25 5.5C6.2496 5.5 6.2492 5.5 6.24879 5.5ZM7 7L7 16.5H13.25C14.214 16.5 14.8616 16.4984 15.3446 16.4335C15.8061 16.3714 16.0036 16.2642 16.1339 16.1339C16.2642 16.0036 16.3714 15.8061 16.4335 15.3446C16.4984 14.8616 16.5 14.214 16.5 13.25V7H7Z',
});
let GridIconComponent = class GridIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: GridIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: GridIconComponent, selector: "kp-grid-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
GridIconComponent = __decorate$2A([
    Icon(kpGrid, CustomIconStyle)
], GridIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: GridIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-grid-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$2A = [GridIconComponent];
class GridIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: GridIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: GridIconModule, declarations: [GridIconComponent], exports: [GridIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: GridIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: GridIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$2A,
                    exports: DIRECTIVES$2A,
                }]
        }] });

var __decorate$2z = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
const kpHeart = defineCustomIcon('kp', {
    name: 'heart',
    height: 18,
    width: 20,
    svgPath: 'M9.51055 1.92636C7.36526 -0.26769 3.83504 -0.26769 1.68975 1.92636C-0.388441 4.05178 -0.388445 7.44795 1.68974 9.57337L8.78891 16.8339C9.45341 17.5135 10.5469 17.5135 11.2114 16.8339L18.3106 9.57337C20.3887 7.44795 20.3887 4.05178 18.3106 1.92636C16.1653 -0.267691 12.635 -0.267688 10.4897 1.92636L10.0001 2.42709L9.51055 1.92636Z',
});
let HeartIconComponent = class HeartIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: HeartIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: HeartIconComponent, selector: "kp-heart-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
HeartIconComponent = __decorate$2z([
    Icon(kpHeart, CustomIconStyle)
], HeartIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: HeartIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-heart-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$2z = [HeartIconComponent];
class HeartIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: HeartIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: HeartIconModule, declarations: [HeartIconComponent], exports: [HeartIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: HeartIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: HeartIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$2z,
                    exports: DIRECTIVES$2z,
                }]
        }] });

var __decorate$2y = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
const kpLock = defineCustomIcon('kp', {
    name: 'lock',
    height: 20,
    width: 16,
    svgPath: 'M8.948 0.250001H9H9.052C9.95048 0.249971 10.6997 0.249946 11.2945 0.329912C11.9223 0.414317 12.4891 0.599988 12.9445 1.05546C13.4 1.51093 13.5857 2.07773 13.6701 2.70552C13.7501 3.3003 13.75 4.04951 13.75 4.94798V4.94801L13.75 5V6.38564C14.4689 6.49028 15.0875 6.68206 15.639 7.05052C16.1576 7.39707 16.6029 7.84239 16.9495 8.36104C17.3826 9.00926 17.5716 9.7503 17.6619 10.6379C17.75 11.5043 17.75 12.5892 17.75 13.9584V14V14.0416C17.75 15.4108 17.75 16.4957 17.6619 17.3621C17.5716 18.2497 17.3826 18.9907 16.9495 19.639C16.6029 20.1576 16.1576 20.6029 15.639 20.9495C14.9907 21.3826 14.2497 21.5716 13.3621 21.6619C12.4957 21.75 11.4108 21.75 10.0416 21.75H10H8H7.9584C6.5892 21.75 5.5043 21.75 4.63794 21.6619C3.7503 21.5716 3.00926 21.3826 2.36104 20.9495C1.84239 20.6029 1.39707 20.1576 1.05052 19.639C0.617393 18.9907 0.428414 18.2497 0.338116 17.3621C0.249984 16.4957 0.249991 15.4108 0.25 14.0416V14V13.9584C0.249991 12.5892 0.249984 11.5043 0.338116 10.6379C0.428414 9.7503 0.617393 9.00926 1.05052 8.36104C1.39707 7.84239 1.84239 7.39707 2.36104 7.05052C2.91248 6.68206 3.5311 6.49028 4.25 6.38564V5L4.25 4.948C4.24997 4.04952 4.24995 3.3003 4.32991 2.70552C4.41432 2.07773 4.59999 1.51093 5.05546 1.05546C5.51093 0.599988 6.07773 0.414317 6.70552 0.329912C7.3003 0.249946 8.04952 0.249971 8.948 0.250001ZM12.25 5V6.27016C11.6102 6.24999 10.8791 6.25 10.0416 6.25H10H8H7.95841C7.12092 6.25 6.3898 6.24999 5.75 6.27016V5C5.75 4.03599 5.75159 3.38843 5.81654 2.90539C5.87858 2.44393 5.9858 2.24643 6.11612 2.11612C6.24643 1.9858 6.44393 1.87858 6.90539 1.81654C7.38843 1.75159 8.03599 1.75 9 1.75C9.96401 1.75 10.6116 1.75159 11.0946 1.81654C11.5561 1.87858 11.7536 1.9858 11.8839 2.11612C12.0142 2.24643 12.1214 2.44393 12.1835 2.90539C12.2484 3.38843 12.25 4.03599 12.25 5ZM9 12.25C9.41421 12.25 9.75 12.5858 9.75 13V15C9.75 15.4142 9.41421 15.75 9 15.75C8.58579 15.75 8.25 15.4142 8.25 15V13C8.25 12.5858 8.58579 12.25 9 12.25Z',
});
let LockIconComponent = class LockIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: LockIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: LockIconComponent, selector: "kp-lock-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
LockIconComponent = __decorate$2y([
    Icon(kpLock, CustomIconStyle)
], LockIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: LockIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-lock-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$2y = [LockIconComponent];
class LockIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: LockIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: LockIconModule, declarations: [LockIconComponent], exports: [LockIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: LockIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: LockIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$2y,
                    exports: DIRECTIVES$2y,
                }]
        }] });

var __decorate$2x = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
const kpMagnifyingGlassChart = defineCustomIcon('kp', {
    name: 'magnifying-glass-chart',
    height: 28,
    width: 28,
    svgPath: 'M22.75 11.375C22.75 13.8852 21.9352 16.2039 20.5133 18.0852L27.4859 25.0141C28.1695 25.6977 28.1695 26.8023 27.4859 27.4859C26.8023 28.1695 25.6977 28.1695 25.0141 27.4859L18.0852 20.5133C16.2039 21.9352 13.8852 22.75 11.375 22.75C5.0925 22.75 0 17.6586 0 11.375C0 5.0925 5.0925 0 11.375 0C17.6586 0 22.75 5.0925 22.75 11.375ZM5.6875 15.3125C5.6875 16.0398 6.27266 16.625 7 16.625C7.72734 16.625 8.3125 16.0398 8.3125 15.3125V11.8125C8.3125 11.0852 7.72734 10.5 7 10.5C6.27266 10.5 5.6875 11.0852 5.6875 11.8125V15.3125ZM10.0625 15.3125C10.0625 16.0398 10.6477 16.625 11.375 16.625C12.1023 16.625 12.6875 16.0398 12.6875 15.3125V6.5625C12.6875 5.83516 12.1023 5.25 11.375 5.25C10.6477 5.25 10.0625 5.83516 10.0625 6.5625V15.3125ZM14.4375 15.3125C14.4375 16.0398 15.0227 16.625 15.75 16.625C16.4773 16.625 17.0625 16.0398 17.0625 15.3125V10.0625C17.0625 9.33516 16.4773 8.75 15.75 8.75C15.0227 8.75 14.4375 9.33516 14.4375 10.0625V15.3125Z',
});
let MagnifyingGlassChartIconComponent = class MagnifyingGlassChartIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: MagnifyingGlassChartIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: MagnifyingGlassChartIconComponent, selector: "kp-magnifying-glass-chart-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
MagnifyingGlassChartIconComponent = __decorate$2x([
    Icon(kpMagnifyingGlassChart, CustomIconStyle)
], MagnifyingGlassChartIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: MagnifyingGlassChartIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-magnifying-glass-chart-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$2x = [MagnifyingGlassChartIconComponent];
class MagnifyingGlassChartIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: MagnifyingGlassChartIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: MagnifyingGlassChartIconModule, declarations: [MagnifyingGlassChartIconComponent], exports: [MagnifyingGlassChartIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: MagnifyingGlassChartIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: MagnifyingGlassChartIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$2x,
                    exports: DIRECTIVES$2x,
                }]
        }] });

var __decorate$2w = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
const kpMegaphone = defineCustomIcon('kp', {
    name: 'megaphone',
    height: 20,
    width: 23,
    svgPath: 'M13.05 4.39999C14.4667 3.33746 15.4617 2.59372 16.238 2.17183C17.0192 1.7473 17.3493 1.77706 17.559 1.88195C17.7688 1.98685 17.9907 2.23307 18.1198 3.11273C18.2425 3.9494 18.2496 5.12315 18.25 6.77486L18.25 11.2189C18.2496 12.8739 18.2427 14.0495 18.1198 14.8872C17.9907 15.7669 17.7688 16.0131 17.559 16.118C17.3493 16.2229 17.0192 16.2527 16.238 15.8281C15.4617 15.4063 14.4667 14.6625 13.05 13.6L11.75 12.625L11.75 5.37499L13.05 4.39999ZM19.6039 2.89495C19.7002 3.55156 19.7331 4.36325 19.7443 5.32384C21.4588 5.66907 22.75 7.18374 22.75 8.99999C22.75 10.8162 21.4588 12.3309 19.7443 12.6761C19.7331 13.6367 19.7002 14.4484 19.6039 15.105C19.4566 16.1089 19.1256 17.0118 18.2299 17.4597C17.3341 17.9076 16.4132 17.6306 15.5217 17.1461C14.637 16.6653 13.5544 15.8533 12.2035 14.8401L12.15 14.8L10.75 13.75L8.75 13.75L8.75 17.5C8.75 18.7426 7.74264 19.75 6.5 19.75C5.25736 19.75 4.25 18.7426 4.25 17.5L4.25 13.6911C1.98301 13.3315 0.249998 11.3681 0.249998 8.99999C0.249998 6.37664 2.37665 4.24999 5 4.24999L10.75 4.24999L12.15 3.19999L12.2035 3.15987C13.5544 2.14665 14.637 1.33467 15.5217 0.853879C16.4132 0.369391 17.3341 0.0924221 18.2299 0.540313C19.1256 0.988204 19.4566 1.89105 19.6039 2.89495Z',
});
let MegaphoneIconComponent = class MegaphoneIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: MegaphoneIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: MegaphoneIconComponent, selector: "kp-megaphone-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
MegaphoneIconComponent = __decorate$2w([
    Icon(kpMegaphone, CustomIconStyle)
], MegaphoneIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: MegaphoneIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-megaphone-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$2w = [MegaphoneIconComponent];
class MegaphoneIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: MegaphoneIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: MegaphoneIconModule, declarations: [MegaphoneIconComponent], exports: [MegaphoneIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: MegaphoneIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: MegaphoneIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$2w,
                    exports: DIRECTIVES$2w,
                }]
        }] });

var __decorate$2v = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
const kpMenu = defineCustomIcon('kp', {
    name: 'menu',
    height: 16,
    width: 22,
    svgPath: 'M0.699951 2.00001C0.699951 1.28204 1.28198 0.700012 1.99995 0.700012H20C20.7179 0.700012 21.2999 1.28204 21.2999 2.00001C21.2999 2.71798 20.7179 3.30001 20 3.30001H1.99995C1.28198 3.30001 0.699951 2.71798 0.699951 2.00001ZM0.699951 8.00001C0.699951 7.28204 1.28198 6.70001 1.99995 6.70001H14C14.7179 6.70001 15.3 7.28204 15.3 8.00001C15.3 8.71798 14.7179 9.30001 14 9.30001H1.99995C1.28198 9.30001 0.699951 8.71798 0.699951 8.00001ZM1.99995 12.7C1.28198 12.7 0.699951 13.282 0.699951 14C0.699951 14.718 1.28198 15.3 1.99995 15.3H5.99995C6.71792 15.3 7.29995 14.718 7.29995 14C7.29995 13.282 6.71792 12.7 5.99995 12.7H1.99995Z',
});
let MenuIconComponent = class MenuIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: MenuIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: MenuIconComponent, selector: "kp-menu-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
MenuIconComponent = __decorate$2v([
    Icon(kpMenu, CustomIconStyle)
], MenuIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: MenuIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-menu-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$2v = [MenuIconComponent];
class MenuIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: MenuIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: MenuIconModule, declarations: [MenuIconComponent], exports: [MenuIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: MenuIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: MenuIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$2v,
                    exports: DIRECTIVES$2v,
                }]
        }] });

var __decorate$2u = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
const kpPhoneCall = defineCustomIcon('kp', {
    name: 'phone-call',
    height: 20,
    width: 20,
    svgPath: 'M3.52691 0.249802L3.46154 0.249778C3.07852 0.249573 2.7763 0.24941 2.47845 0.31594C1.39448 0.558066 0.484272 1.5614 0.348551 2.66376C0.311318 2.96617 0.336793 3.22603 0.36848 3.54927L0.373058 3.59605C0.630602 6.23571 1.29005 8.61139 2.32976 10.6807C3.87438 13.7548 6.24492 16.1253 9.31907 17.67C11.3883 18.7097 13.764 19.3691 16.4037 19.6267L16.4505 19.6312L16.4505 19.6312L16.4506 19.6313C16.7737 19.6629 17.0336 19.6884 17.336 19.6512C18.4383 19.5154 19.4417 18.6052 19.6838 17.5213C19.7503 17.2234 19.7501 16.9212 19.7499 16.5382L19.7499 16.4728L19.7499 16.4126C19.7501 16.0805 19.7502 15.8221 19.7081 15.5834C19.5494 14.684 18.9545 13.9221 18.1204 13.55C17.899 13.4512 17.6484 13.3887 17.3261 13.3083L17.3261 13.3083L17.2677 13.2937L15.6132 12.8801L15.5771 12.871L15.577 12.871L15.577 12.871C15.0167 12.7309 14.5412 12.612 14.1384 12.5548C13.7095 12.494 13.2998 12.4922 12.8792 12.6291C12.4587 12.766 12.1285 13.0087 11.8177 13.3104C11.5257 13.5938 11.2113 13.9698 10.8409 14.4129L10.817 14.4416L9.46912 16.0538C7.10973 14.7505 5.24925 12.89 3.94594 10.5306L5.55817 9.18273L5.58678 9.15881C6.02987 8.78839 6.40593 8.47402 6.68934 8.18203C6.99105 7.87118 7.23371 7.54101 7.37061 7.12049C7.50752 6.69997 7.50576 6.29022 7.44488 5.86132C7.3877 5.45846 7.26881 4.98295 7.12872 4.42268L7.12872 4.42267L7.11967 4.38648L6.70605 2.732L6.69147 2.67359L6.69146 2.67358C6.61105 2.35133 6.54851 2.10069 6.44975 1.87934C6.07763 1.04527 5.31569 0.450368 4.41628 0.291647C4.17758 0.249524 3.91925 0.249638 3.58711 0.249784L3.52691 0.249802ZM11.25 5.00002C11.25 4.5858 11.5857 4.25002 12 4.25002C13.0265 4.25002 13.934 4.88892 14.5475 5.5444C14.8911 5.91143 15.1821 6.32425 15.3909 6.7305C15.5921 7.12201 15.75 7.57167 15.75 8.00002C15.75 8.41423 15.4142 8.75002 15 8.75002C14.5857 8.75002 14.25 8.41423 14.25 8.00002C14.25 7.91006 14.2063 7.7071 14.0568 7.41611C13.9148 7.13986 13.7059 6.84028 13.4524 6.56944C12.9561 6.03926 12.4185 5.75002 12 5.75002C11.5857 5.75002 11.25 5.41423 11.25 5.00002ZM12 1.25002C11.5857 1.25002 11.25 1.5858 11.25 2.00002C11.25 2.41423 11.5857 2.75002 12 2.75002C14.8994 2.75002 17.25 5.10052 17.25 8.00002C17.25 8.41423 17.5857 8.75002 18 8.75002C18.4142 8.75002 18.75 8.41423 18.75 8.00002C18.75 4.27209 15.7279 1.25002 12 1.25002Z',
});
let PhoneCallIconComponent = class PhoneCallIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: PhoneCallIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: PhoneCallIconComponent, selector: "kp-phone-call-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
PhoneCallIconComponent = __decorate$2u([
    Icon(kpPhoneCall, CustomIconStyle)
], PhoneCallIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: PhoneCallIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-phone-call-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$2u = [PhoneCallIconComponent];
class PhoneCallIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: PhoneCallIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: PhoneCallIconModule, declarations: [PhoneCallIconComponent], exports: [PhoneCallIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: PhoneCallIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: PhoneCallIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$2u,
                    exports: DIRECTIVES$2u,
                }]
        }] });

var __decorate$2t = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
const kpPlay = defineCustomIcon('kp', {
    name: 'play',
    height: 19,
    width: 15,
    svgPath: 'M0.166748 12.7485V6.08476C0.166748 3.63196 0.166748 2.40556 0.664306 1.71435C1.09801 1.11184 1.76263 0.73177 2.48769 0.67163C3.31949 0.602635 4.32744 1.25263 6.34335 2.55261L11.5102 5.8845C13.3265 7.05577 14.2347 7.6414 14.5461 8.3912C14.8181 9.04604 14.8181 9.78728 14.5461 10.4421C14.2347 11.1919 13.3265 11.7775 11.5102 12.9488L6.34335 16.2807C4.32744 17.5807 3.31949 18.2307 2.48769 18.1617C1.76263 18.1015 1.09801 17.7215 0.664306 17.119C0.166748 16.4278 0.166748 15.2014 0.166748 12.7485Z',
});
let PlayIconComponent = class PlayIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: PlayIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: PlayIconComponent, selector: "kp-play-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
PlayIconComponent = __decorate$2t([
    Icon(kpPlay, CustomIconStyle)
], PlayIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: PlayIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-play-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$2t = [PlayIconComponent];
class PlayIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: PlayIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: PlayIconModule, declarations: [PlayIconComponent], exports: [PlayIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: PlayIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: PlayIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$2t,
                    exports: DIRECTIVES$2t,
                }]
        }] });

var __decorate$2s = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
const kpPlayAlt = defineCustomIcon('kp', {
    name: 'play-alt',
    height: 18,
    width: 15,
    svgPath: 'M4.53787 1.31803C4.55441 1.32867 4.57101 1.33934 4.58768 1.35005L12.8892 6.68672L12.9365 6.71713C13.4851 7.06978 13.9663 7.37906 14.3031 7.67358C14.6594 7.98517 15.0013 8.40518 15.0013 8.99996C15.0013 9.59475 14.6594 10.0148 14.3031 10.3263C13.9663 10.6209 13.4851 10.9302 12.9365 11.2828L12.8892 11.3132L4.58767 16.6499L4.53787 16.6819C3.90058 17.0916 3.35187 17.4444 2.89588 17.6504C2.42572 17.8629 1.8394 18.0195 1.26201 17.7043C0.684626 17.3891 0.49934 16.8112 0.42378 16.3008C0.350497 15.8058 0.350539 15.1535 0.350588 14.3958L0.350591 14.3366V3.66329C0.350591 3.64349 0.35059 3.62375 0.350588 3.60408C0.350539 2.84643 0.350497 2.1941 0.42378 1.69913C0.49934 1.18877 0.684626 0.610857 1.26201 0.295632C1.8394 -0.019592 2.42572 0.13706 2.89588 0.349482C3.35187 0.555498 3.90057 0.908284 4.53787 1.31803ZM1.95548 1.61982C1.95501 1.61919 1.95881 1.61789 1.96826 1.61781C1.96067 1.62041 1.95595 1.62045 1.95548 1.61982ZM1.98428 1.61859C2.02489 1.62239 2.11314 1.64183 2.27829 1.71644C2.62063 1.87111 3.07516 2.16094 3.77654 2.61182L12.078 7.94849C12.6882 8.34077 13.0729 8.59039 13.3157 8.80273C13.4306 8.90323 13.4778 8.96561 13.4959 8.99605C13.4967 8.99746 13.4974 8.99876 13.4981 8.99996C13.4974 9.00116 13.4967 9.00247 13.4959 9.00388C13.4778 9.03432 13.4306 9.0967 13.3157 9.1972C13.0729 9.40954 12.6882 9.65916 12.078 10.0514L3.77654 15.3881C3.07516 15.839 2.62063 16.1288 2.27829 16.2835C2.11314 16.3581 2.02489 16.3775 1.98428 16.3813C1.96552 16.3451 1.93415 16.2604 1.90761 16.0811C1.85259 15.7095 1.85059 15.1704 1.85059 14.3366V3.66329C1.85059 2.82949 1.85259 2.29042 1.90761 1.91882C1.93415 1.73955 1.96552 1.65481 1.98428 1.61859ZM1.95548 16.3801C1.95595 16.3795 1.96067 16.3795 1.96826 16.3821C1.95881 16.382 1.95501 16.3807 1.95548 16.3801ZM1.99229 16.3952C1.99858 16.4002 2.00117 16.4042 2.00089 16.4049C2.00061 16.4056 1.99747 16.4031 1.99229 16.3952ZM1.99229 1.60469C1.99747 1.59678 2.00061 1.59429 2.00089 1.59503C2.00117 1.59576 1.99858 1.59972 1.99229 1.60469Z',
});
let PlayAltIconComponent = class PlayAltIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: PlayAltIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: PlayAltIconComponent, selector: "kp-play-alt-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
PlayAltIconComponent = __decorate$2s([
    Icon(kpPlayAlt, CustomIconStyle)
], PlayAltIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: PlayAltIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-play-alt-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$2s = [PlayAltIconComponent];
class PlayAltIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: PlayAltIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: PlayAltIconModule, declarations: [PlayAltIconComponent], exports: [PlayAltIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: PlayAltIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: PlayAltIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$2s,
                    exports: DIRECTIVES$2s,
                }]
        }] });

var __decorate$2r = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
let PlusIconComponent = class PlusIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: PlusIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: PlusIconComponent, selector: "kp-plus-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
PlusIconComponent = __decorate$2r([
    Icon(faPlus)
], PlusIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: PlusIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-plus-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$2r = [PlusIconComponent];
class PlusIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: PlusIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: PlusIconModule, declarations: [PlusIconComponent], exports: [PlusIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: PlusIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: PlusIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$2r,
                    exports: DIRECTIVES$2r,
                }]
        }] });

var __decorate$2q = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
const kpPlusCircle = defineCustomIcon('kp', {
    name: 'plus-circle',
    height: 34,
    width: 34,
    svgPath: 'M17 33.7923C26.2048 33.7923 33.6667 26.3304 33.6667 17.1257C33.6667 7.92091 26.2048 0.458984 17 0.458984C7.79529 0.458984 0.333374 7.92091 0.333374 17.1257C0.333374 26.3304 7.79529 33.7923 17 33.7923ZM18 10.459C18 9.9067 17.5523 9.45899 17 9.45899C16.4477 9.45899 16 9.9067 16 10.459V16.1257H10.3333C9.78106 16.1257 9.33335 16.5734 9.33335 17.1257C9.33335 17.6779 9.78106 18.1257 10.3333 18.1257H16V23.7923C16 24.3446 16.4477 24.7923 17 24.7923C17.5523 24.7923 18 24.3446 18 23.7923V18.1257H23.6667C24.219 18.1257 24.6667 17.6779 24.6667 17.1257C24.6667 16.5734 24.219 16.1257 23.6667 16.1257H18V10.459Z',
});
let PlusCircleIconComponent = class PlusCircleIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: PlusCircleIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: PlusCircleIconComponent, selector: "kp-plus-circle-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
PlusCircleIconComponent = __decorate$2q([
    Icon(kpPlusCircle, CustomIconStyle)
], PlusCircleIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: PlusCircleIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-plus-circle-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$2q = [PlusCircleIconComponent];
class PlusCircleIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: PlusCircleIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: PlusCircleIconModule, declarations: [PlusCircleIconComponent], exports: [PlusCircleIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: PlusCircleIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: PlusCircleIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$2q,
                    exports: DIRECTIVES$2q,
                }]
        }] });

var __decorate$2p = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
const kpPresentation = defineCustomIcon('kp', {
    name: 'presentation',
    height: 20,
    width: 22,
    svgPath: 'M0.416626 1.25C0.416626 0.835786 0.752412 0.5 1.16663 0.5H22.8333C23.2475 0.5 23.5833 0.835786 23.5833 1.25C23.5833 1.66421 23.2475 2 22.8333 2H20V8C20 10.8284 20 12.2426 19.1213 13.1213C18.2426 14 16.8284 14 14 14H12.7073C12.7349 14.0782 12.75 14.1623 12.75 14.25V17.0754L17.8025 20.1069C18.1577 20.32 18.2729 20.7807 18.0597 21.1359C17.8466 21.4911 17.3859 21.6062 17.0308 21.3931L12.75 18.8246V20.75C12.75 21.1642 12.4142 21.5 12 21.5C11.5857 21.5 11.25 21.1642 11.25 20.75V18.8246L6.96917 21.3931C6.61398 21.6062 6.15329 21.4911 5.94017 21.1359C5.72706 20.7807 5.84224 20.32 6.19742 20.1069L11.25 17.0754V14.25C11.25 14.1623 11.265 14.0782 11.2926 14H8.99996C6.17154 14 4.75732 14 3.87864 13.1213C2.99996 12.2426 2.99996 10.8284 2.99996 8V2H1.16663C0.752412 2 0.416626 1.66421 0.416626 1.25ZM8.41663 7.75C8.41663 7.33579 8.08084 7 7.66663 7C7.25241 7 6.91663 7.33579 6.91663 7.75V9.91667C6.91663 10.3309 7.25241 10.6667 7.66663 10.6667C8.08084 10.6667 8.41663 10.3309 8.41663 9.91667V7.75ZM12 4.83203C12.4142 4.83203 12.75 5.16782 12.75 5.58203V9.91537C12.75 10.3296 12.4142 10.6654 12 10.6654C11.5857 10.6654 11.25 10.3296 11.25 9.91537V5.58203C11.25 5.16782 11.5857 4.83203 12 4.83203ZM17.0833 6.66797C17.0833 6.25376 16.7475 5.91797 16.3333 5.91797C15.9191 5.91797 15.5833 6.25376 15.5833 6.66797V9.91797C15.5833 10.3322 15.9191 10.668 16.3333 10.668C16.7475 10.668 17.0833 10.3322 17.0833 9.91797V6.66797Z',
});
let PresentationIconComponent = class PresentationIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: PresentationIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: PresentationIconComponent, selector: "kp-presentation-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
PresentationIconComponent = __decorate$2p([
    Icon(kpPresentation, CustomIconStyle)
], PresentationIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: PresentationIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-presentation-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$2p = [PresentationIconComponent];
class PresentationIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: PresentationIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: PresentationIconModule, declarations: [PresentationIconComponent], exports: [PresentationIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: PresentationIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: PresentationIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$2p,
                    exports: DIRECTIVES$2p,
                }]
        }] });

var __decorate$2o = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
let QuestionCircleIconComponent = class QuestionCircleIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: QuestionCircleIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: QuestionCircleIconComponent, selector: "kp-question-circle-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
QuestionCircleIconComponent = __decorate$2o([
    Icon(faQuestionCircle)
], QuestionCircleIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: QuestionCircleIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-question-circle-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$2o = [QuestionCircleIconComponent];
class QuestionCircleIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: QuestionCircleIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: QuestionCircleIconModule, declarations: [QuestionCircleIconComponent], exports: [QuestionCircleIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: QuestionCircleIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: QuestionCircleIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$2o,
                    exports: DIRECTIVES$2o,
                }]
        }] });

var __decorate$2n = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
let QuestionCircleAltIconComponent = class QuestionCircleAltIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: QuestionCircleAltIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: QuestionCircleAltIconComponent, selector: "kp-question-circle-alt-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
QuestionCircleAltIconComponent = __decorate$2n([
    Icon(faQuestionCircle$1)
], QuestionCircleAltIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: QuestionCircleAltIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-question-circle-alt-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$2n = [QuestionCircleAltIconComponent];
class QuestionCircleAltIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: QuestionCircleAltIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: QuestionCircleAltIconModule, declarations: [QuestionCircleAltIconComponent], exports: [QuestionCircleAltIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: QuestionCircleAltIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: QuestionCircleAltIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$2n,
                    exports: DIRECTIVES$2n,
                }]
        }] });

var __decorate$2m = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
const kpRocket = defineCustomIcon('kp', {
    name: 'rocket',
    height: 25,
    width: 25,
    svgPath: 'M15.26 0.728365C14.7087 0.956726 14.2752 1.39027 13.4081 2.25736L9.18858 6.47684C8.50208 5.98983 7.91777 5.59004 7.39572 5.30523C6.71525 4.93399 6.06346 4.71327 5.33525 4.75976C5.2886 4.76274 5.24201 4.76659 5.19551 4.77131C4.46954 4.84499 3.86283 5.16972 3.25253 5.64763C2.66617 6.1068 2.01307 6.7599 1.20696 7.56601L1.20695 7.56602L1.20694 7.56602L1.17294 7.60003L0.636671 8.13628C0.480778 8.29217 0.401368 8.50875 0.419548 8.72847C0.437728 8.94818 0.551657 9.14876 0.731053 9.27691L4.07703 11.667C3.35327 12.5094 2.99139 13.1691 2.99139 13.9167C2.99139 15.1372 3.95612 16.1236 5.88557 18.0535L8.80259 15.1365C9.09548 14.8436 9.57035 14.8436 9.86325 15.1365C10.1561 15.4294 10.1561 15.9042 9.86325 16.1971L6.94623 19.1141C8.87544 21.043 9.86176 22.0074 11.0821 22.0074C11.8298 22.0074 12.4896 21.6453 13.3323 20.9212L15.7238 24.2693C15.8519 24.4487 16.0525 24.5626 16.2722 24.5808C16.4919 24.599 16.7085 24.5196 16.8644 24.3637L17.3987 23.8294L17.4327 23.7953L17.4327 23.7953L17.4328 23.7953C18.2397 22.9883 18.8936 22.3346 19.3531 21.7476C19.8315 21.1366 20.1563 20.5292 20.2296 19.8023C20.2341 19.7575 20.2378 19.7125 20.2407 19.6676C20.2877 18.9386 20.0669 18.2861 19.6953 17.6049C19.4102 17.0822 19.0099 16.4972 18.5222 15.8099L22.7414 11.5907C23.6085 10.7236 24.042 10.2901 24.2704 9.73875C24.4988 9.18743 24.4988 8.57431 24.4988 7.34806L24.4988 4.5C24.4988 2.61439 24.4988 1.67158 23.913 1.08579C23.3272 0.500004 22.3844 0.500004 20.4988 0.500004L17.6507 0.500003C16.4244 0.500003 15.8113 0.500003 15.26 0.728365ZM14.4382 19.8888C14.7116 19.6205 15.0054 19.3267 15.3247 19.0074L15.3247 19.0074L17.4459 16.8862C17.8541 17.466 18.1602 17.9231 18.3785 18.3232C18.6771 18.8706 18.7657 19.2327 18.7438 19.571C18.7421 19.598 18.7399 19.625 18.7372 19.6519C18.7032 19.9893 18.5564 20.3319 18.172 20.8229C17.7898 21.3111 17.2298 21.8767 16.4298 22.677L14.4382 19.8888ZM8.11227 7.55315L5.99139 9.67403C5.67188 9.99354 5.37789 10.2875 5.10943 10.5611L2.32336 8.57094C3.12377 7.77081 3.68923 7.21084 4.17734 6.82862C4.66779 6.44455 5.01004 6.29784 5.34698 6.26364C5.37488 6.26081 5.40283 6.2585 5.43082 6.25671C5.7688 6.23513 6.13049 6.32367 6.67734 6.62201C7.07693 6.84001 7.53337 7.14568 8.11227 7.55315ZM17.5026 9.83285C17.5026 11.1215 16.458 12.1662 15.1693 12.1662C13.8806 12.1662 12.836 11.1215 12.836 9.83285C12.836 8.54418 13.8806 7.49951 15.1693 7.49951C16.458 7.49951 17.5026 8.54418 17.5026 9.83285Z',
});
let RocketIconComponent = class RocketIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: RocketIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: RocketIconComponent, selector: "kp-rocket-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
RocketIconComponent = __decorate$2m([
    Icon(kpRocket, CustomIconStyle)
], RocketIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: RocketIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-rocket-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$2m = [RocketIconComponent];
class RocketIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: RocketIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: RocketIconModule, declarations: [RocketIconComponent], exports: [RocketIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: RocketIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: RocketIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$2m,
                    exports: DIRECTIVES$2m,
                }]
        }] });

var __decorate$2l = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
const kpSearch = defineCustomIcon('kp', {
    name: 'search',
    height: 20,
    width: 20,
    svgPath: 'M19.6935 17.9925L15.1596 13.4586C16.2511 12.0054 16.8404 10.2367 16.8384 8.4192C16.8384 3.77688 13.0615 0 8.4192 0C3.77688 0 0 3.77688 0 8.4192C0 13.0615 3.77688 16.8384 8.4192 16.8384C10.2367 16.8404 12.0054 16.2511 13.4586 15.1596L17.9925 19.6935C18.222 19.8986 18.5213 20.0081 18.829 19.9995C19.1367 19.9909 19.4295 19.8648 19.6472 19.6472C19.8648 19.4295 19.9909 19.1367 19.9995 18.829C20.0081 18.5213 19.8986 18.222 19.6935 17.9925ZM2.40549 8.4192C2.40549 7.2298 2.75818 6.06711 3.41898 5.07816C4.07977 4.08921 5.01899 3.31842 6.11785 2.86325C7.21671 2.40809 8.42587 2.289 9.59242 2.52104C10.759 2.75308 11.8305 3.32583 12.6715 4.16686C13.5126 5.00789 14.0853 6.07944 14.3174 7.24598C14.5494 8.41253 14.4303 9.62168 13.9751 10.7205C13.52 11.8194 12.7492 12.7586 11.7602 13.4194C10.7713 14.0802 9.6086 14.4329 8.4192 14.4329C6.82485 14.431 5.29635 13.7968 4.16897 12.6694C3.0416 11.542 2.4074 10.0135 2.40549 8.4192Z',
});
let SearchIconComponent = class SearchIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: SearchIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: SearchIconComponent, selector: "kp-search-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
SearchIconComponent = __decorate$2l([
    Icon(kpSearch, CustomIconStyle)
], SearchIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: SearchIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-search-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$2l = [SearchIconComponent];
class SearchIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: SearchIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: SearchIconModule, declarations: [SearchIconComponent], exports: [SearchIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: SearchIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: SearchIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$2l,
                    exports: DIRECTIVES$2l,
                }]
        }] });

var __decorate$2k = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
let ShareIconComponent = class ShareIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ShareIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: ShareIconComponent, selector: "kp-share-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
ShareIconComponent = __decorate$2k([
    Icon(faDiagramProject)
], ShareIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ShareIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-share-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$2k = [ShareIconComponent];
class ShareIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ShareIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: ShareIconModule, declarations: [ShareIconComponent], exports: [ShareIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ShareIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ShareIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$2k,
                    exports: DIRECTIVES$2k,
                }]
        }] });

var __decorate$2j = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
const kpSignOut = defineCustomIcon('kp', {
    name: 'sign-out',
    height: 20,
    width: 18,
    svgPath: 'M0.401802 2C-0.00012207 2.69615 -0.00012207 3.63077 -0.00012207 5.5L-0.00012207 15.5C-0.00012207 17.3692 -0.00012207 18.3038 0.401802 19C0.665107 19.4561 1.04382 19.8348 1.49988 20.0981C2.19603 20.5 3.13065 20.5 4.99988 20.5C6.86911 20.5 7.80373 20.5 8.49988 20.0981C8.95594 19.8348 9.33465 19.4561 9.59795 19C9.99988 18.3038 9.99988 17.3692 9.99988 15.5V11.2502L16.1894 11.2502L13.4697 13.9698C13.1768 14.2627 13.1768 14.7376 13.4697 15.0305C13.7626 15.3234 14.2375 15.3234 14.5304 15.0305L18.5304 11.0305C18.8233 10.7376 18.8233 10.2627 18.5304 9.96982L14.5304 5.96982C14.2375 5.67693 13.7626 5.67693 13.4697 5.96982C13.1768 6.26272 13.1768 6.73759 13.4697 7.03048L16.1894 9.75015L9.99988 9.75015L9.99988 5.5C9.99988 3.63077 9.99988 2.69615 9.59795 2C9.33465 1.54394 8.95594 1.16523 8.49988 0.901924C7.80373 0.5 6.86911 0.5 4.99988 0.5C3.13065 0.5 2.19603 0.5 1.49988 0.901924C1.04382 1.16523 0.665107 1.54394 0.401802 2ZM9.99988 9.75015L5.00006 9.75015C4.58585 9.75015 4.25006 10.0859 4.25006 10.5002C4.25006 10.9144 4.58585 11.2502 5.00006 11.2502L9.99988 11.2502V9.75015Z',
});
let SignOutIconComponent = class SignOutIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: SignOutIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: SignOutIconComponent, selector: "kp-sign-out-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
SignOutIconComponent = __decorate$2j([
    Icon(kpSignOut, CustomIconStyle)
], SignOutIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: SignOutIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-sign-out-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$2j = [SignOutIconComponent];
class SignOutIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: SignOutIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: SignOutIconModule, declarations: [SignOutIconComponent], exports: [SignOutIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: SignOutIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: SignOutIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$2j,
                    exports: DIRECTIVES$2j,
                }]
        }] });

var __decorate$2i = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
const kpStar = defineCustomIcon('kp', {
    name: 'star',
    height: 28,
    width: 28,
    svgPath: 'M14.9624 0.599929C14.7823 0.232731 14.409 0 14.0001 0C13.591 0 13.2177 0.232731 13.0376 0.599929L8.99808 8.83612L0.980328 10.0164C0.579084 10.0755 0.245531 10.356 0.118454 10.7411C-0.00861006 11.1263 0.0925548 11.5501 0.37987 11.8364L6.39159 17.8258L4.37894 26.6907C4.28668 27.097 4.4383 27.5201 4.76772 27.7754C5.09715 28.0307 5.54464 28.0718 5.91513 27.881L14.0001 23.7171L22.085 27.881C22.4555 28.0718 22.903 28.0307 23.2323 27.7754C23.5617 27.5201 23.7134 27.097 23.621 26.6907L21.6085 17.8258L27.6202 11.8364C27.9075 11.5501 28.0087 11.1263 27.8816 10.7411C27.7545 10.356 27.4209 10.0755 27.0197 10.0164L19.002 8.83612L14.9624 0.599929Z',
});
let StarIconComponent = class StarIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: StarIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: StarIconComponent, selector: "kp-star-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
StarIconComponent = __decorate$2i([
    Icon(kpStar, CustomIconStyle)
], StarIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: StarIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-star-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$2i = [StarIconComponent];
class StarIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: StarIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: StarIconModule, declarations: [StarIconComponent], exports: [StarIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: StarIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: StarIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$2i,
                    exports: DIRECTIVES$2i,
                }]
        }] });

var __decorate$2h = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
const kpSwatchbook = defineCustomIcon('kp', {
    name: 'swatchbook',
    height: 20,
    width: 20,
    svgPath: 'M5 0.250001H4.948C4.04952 0.249972 3.3003 0.249947 2.70552 0.329913C2.07773 0.414318 1.51093 0.599988 1.05546 1.05546C0.599988 1.51093 0.414318 2.07773 0.329913 2.70552C0.249947 3.3003 0.249972 4.04952 0.250001 4.948V5V15V15.052C0.249972 15.9505 0.249947 16.6997 0.329913 17.2945C0.414318 17.9223 0.599988 18.4891 1.05546 18.9445C1.51093 19.4 2.07773 19.5857 2.70552 19.6701C3.30031 19.7501 4.04953 19.75 4.94801 19.75H4.99829H5H5.052H15L15.052 19.75H15.0521H15.0522H15.0522C15.9506 19.75 16.6997 19.7501 17.2945 19.6701C17.9223 19.5857 18.4891 19.4 18.9445 18.9445C19.4 18.4891 19.5857 17.9223 19.6701 17.2945C19.7501 16.6997 19.75 15.9506 19.75 15.0522V15.0522V15.0521V15.052L19.75 15L19.75 14.948V14.9479V14.9478V14.9478C19.75 14.0494 19.7501 13.3003 19.6701 12.7055C19.5857 12.0777 19.4 11.5109 18.9445 11.0555C18.4891 10.6 17.9223 10.4143 17.2945 10.3299C17.134 10.3083 16.9624 10.2926 16.7796 10.2811L17.2801 9.7806L17.3146 9.74602L17.3147 9.74601C17.9139 9.14682 18.4137 8.64708 18.7616 8.19769C19.128 7.72435 19.3926 7.22535 19.4189 6.61957C19.4224 6.5399 19.4224 6.46011 19.4189 6.38044C19.3925 5.77466 19.1279 5.27567 18.7615 4.80236C18.4135 4.35298 17.9137 3.85328 17.3145 3.25413L17.2799 3.21953L17.2453 3.18493C16.6461 2.58579 16.1464 2.08609 15.697 1.73822C15.2237 1.37183 14.7247 1.10732 14.119 1.08098C14.0393 1.07752 13.9595 1.07752 13.8799 1.08099C13.2741 1.10738 12.7751 1.37193 12.3019 1.73836C11.8525 2.08626 11.3528 2.58599 10.7537 3.18518L10.7537 3.18519L10.7191 3.21978L9.74797 4.19096C9.74307 3.62035 9.72643 3.1246 9.67009 2.70552C9.58569 2.07773 9.40002 1.51093 8.94455 1.05546C8.48908 0.599988 7.92228 0.414318 7.29448 0.329913C6.6997 0.249947 5.95048 0.249972 5.052 0.250001H5ZM9.75 6.3103V15L9.75 15.052V15.052C9.75001 15.0982 9.75001 15.144 9.75 15.1893L16.2194 8.71994C16.8622 8.07715 17.2929 7.64442 17.5755 7.27944C17.8462 6.92978 17.9129 6.72583 17.9203 6.55439C17.9219 6.51818 17.9219 6.48191 17.9203 6.4457C17.9129 6.27426 17.8461 6.07031 17.5754 5.72067C17.2928 5.35571 16.8621 4.923 16.2193 4.28024C15.5765 3.63754 15.1438 3.20688 14.7788 2.92437C14.4292 2.65371 14.2252 2.58702 14.0538 2.57957C14.0176 2.57799 13.9813 2.57799 13.9451 2.57957C13.7737 2.58704 13.5698 2.65374 13.2201 2.92443C12.8552 3.20697 12.4225 3.63766 11.7798 4.28042L9.75 6.3103ZM9.65375 17.4069C9.60713 17.7045 9.53486 17.9874 9.41833 18.25H15C15.964 18.25 16.6116 18.2484 17.0946 18.1835C17.5561 18.1214 17.7536 18.0142 17.8839 17.8839C18.0142 17.7536 18.1214 17.5561 18.1835 17.0946C18.2484 16.6116 18.25 15.964 18.25 15C18.25 14.036 18.2484 13.3884 18.1835 12.9054C18.1214 12.4439 18.0142 12.2464 17.8839 12.1161C17.7536 11.9858 17.5561 11.8786 17.0946 11.8165C16.6654 11.7588 16.1062 11.7511 15.3105 11.7501L9.65375 17.4069ZM5 16C5.55229 16 6 15.5523 6 15C6 14.4477 5.55229 14 5 14C4.44772 14 4 14.4477 4 15C4 15.5523 4.44772 16 5 16Z',
});
let SwatchbookIconComponent = class SwatchbookIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: SwatchbookIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: SwatchbookIconComponent, selector: "kp-swatchbook-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
SwatchbookIconComponent = __decorate$2h([
    Icon(kpSwatchbook, CustomIconStyle)
], SwatchbookIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: SwatchbookIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-swatchbook-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$2h = [SwatchbookIconComponent];
class SwatchbookIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: SwatchbookIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: SwatchbookIconModule, declarations: [SwatchbookIconComponent], exports: [SwatchbookIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: SwatchbookIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: SwatchbookIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$2h,
                    exports: DIRECTIVES$2h,
                }]
        }] });

var __decorate$2g = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
const kpTag = defineCustomIcon('kp', {
    name: 'tag',
    height: 19,
    width: 19,
    svgPath: 'M12.4139 15.0713L15.9361 11.5492C16.8852 10.6001 17.3598 10.1255 17.6505 9.54258C17.7336 9.3758 17.805 9.20341 17.8642 9.02667C18.0708 8.40897 18.0708 7.73782 18.0708 6.39552C18.0708 4.08222 18.0708 2.92557 17.4669 2.12397C17.2985 1.90043 17.0996 1.70155 16.876 1.53313C16.0744 0.929199 14.9178 0.929199 12.6045 0.929199H12.6045C11.2622 0.929199 10.591 0.929199 9.97333 1.13585C9.79659 1.19497 9.6242 1.26638 9.45742 1.34954C8.87452 1.6402 8.39994 2.11478 7.45079 3.06393L3.92867 6.58605C1.92867 8.58605 0.928665 9.58605 0.928665 10.8287C0.928665 12.0713 1.92867 13.0713 3.92867 15.0713C5.92867 17.0713 6.92867 18.0713 8.17131 18.0713C9.41395 18.0713 10.4139 17.0713 12.4139 15.0713ZM11.707 9.29348C10.6024 9.29348 9.707 8.39805 9.707 7.29348C9.707 6.18891 10.6024 5.29348 11.707 5.29348C12.8116 5.29348 13.707 6.18891 13.707 7.29348C13.707 8.39805 12.8116 9.29348 11.707 9.29348Z',
});
let TagIconComponent = class TagIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TagIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: TagIconComponent, selector: "kp-tag-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
TagIconComponent = __decorate$2g([
    Icon(kpTag, CustomIconStyle)
], TagIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TagIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-tag-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$2g = [TagIconComponent];
class TagIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TagIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: TagIconModule, declarations: [TagIconComponent], exports: [TagIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TagIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TagIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$2g,
                    exports: DIRECTIVES$2g,
                }]
        }] });

var __decorate$2f = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
let TeamIconComponent = class TeamIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TeamIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: TeamIconComponent, selector: "kp-team-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
TeamIconComponent = __decorate$2f([
    Icon(faUsers)
], TeamIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TeamIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-team-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$2f = [TeamIconComponent];
class TeamIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TeamIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: TeamIconModule, declarations: [TeamIconComponent], exports: [TeamIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TeamIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TeamIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$2f,
                    exports: DIRECTIVES$2f,
                }]
        }] });

var __decorate$2e = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
let TimesIconComponent = class TimesIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TimesIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: TimesIconComponent, selector: "kp-times-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
TimesIconComponent = __decorate$2e([
    Icon(faTimes)
], TimesIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TimesIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-times-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$2e = [TimesIconComponent];
class TimesIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TimesIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: TimesIconModule, declarations: [TimesIconComponent], exports: [TimesIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TimesIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TimesIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$2e,
                    exports: DIRECTIVES$2e,
                }]
        }] });

var __decorate$2d = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
let TrademarkIconComponent = class TrademarkIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TrademarkIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: TrademarkIconComponent, selector: "kp-trademark-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
TrademarkIconComponent = __decorate$2d([
    Icon(faTrademark)
], TrademarkIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TrademarkIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-trademark-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$2d = [TrademarkIconComponent];
class TrademarkIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TrademarkIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: TrademarkIconModule, declarations: [TrademarkIconComponent], exports: [TrademarkIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TrademarkIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TrademarkIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$2d,
                    exports: DIRECTIVES$2d,
                }]
        }] });

var __decorate$2c = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
const kpTrash = defineCustomIcon('kp', {
    name: 'trash',
    height: 20,
    width: 20,
    svgPath: 'M8.44152 0.250006L8.40621 0.250003C8.08881 0.249964 7.79077 0.249927 7.54102 0.278441C7.26287 0.310197 6.97045 0.383615 6.69752 0.580332C6.42459 0.777049 6.26246 1.03125 6.14438 1.28509C6.03835 1.513 5.94414 1.79575 5.84381 2.09687L5.8438 2.09688L5.83264 2.13038L5.45943 3.25001H3H1C0.585786 3.25001 0.25 3.58579 0.25 4.00001C0.25 4.41422 0.585786 4.75001 1 4.75001H2.29834L2.87827 13.449L2.88178 13.5016C2.96727 14.7844 3.03633 15.8205 3.19831 16.6336C3.3667 17.4789 3.65312 18.185 4.24472 18.7384C4.83631 19.2919 5.55986 19.5307 6.41451 19.6425C7.23655 19.75 8.27499 19.75 9.56058 19.75H9.61332H10.3867H10.4394C11.725 19.75 12.7634 19.75 13.5855 19.6425C14.4401 19.5307 15.1637 19.2919 15.7553 18.7384C16.3469 18.185 16.6333 17.4789 16.8017 16.6336C16.9637 15.8205 17.0327 14.7844 17.1182 13.5016L17.1217 13.449L17.7017 4.75001H19C19.4142 4.75001 19.75 4.41422 19.75 4.00001C19.75 3.58579 19.4142 3.25001 19 3.25001H17H14.5406L14.1674 2.13038L14.1562 2.09691L14.1562 2.09688C14.0559 1.79576 13.9617 1.513 13.8556 1.28509C13.7375 1.03125 13.5754 0.777049 13.3025 0.580332C13.0296 0.383615 12.7371 0.310197 12.459 0.278441C12.2092 0.249927 11.9112 0.249964 11.5938 0.250003L11.5585 0.250006H8.44152ZM12.9594 3.25001H7.04057L7.25566 2.60472C7.37113 2.25832 7.43889 2.05863 7.50441 1.91778C7.53397 1.85424 7.55428 1.82282 7.56516 1.80824C7.57019 1.8015 7.57302 1.79862 7.57363 1.79802L7.57458 1.7972L7.57566 1.79655C7.57643 1.79616 7.58005 1.79439 7.58804 1.79175C7.60531 1.78604 7.64154 1.77671 7.71117 1.76876C7.86551 1.75114 8.07638 1.75001 8.44152 1.75001H11.5585C11.9236 1.75001 12.1345 1.75114 12.2888 1.76876C12.3585 1.77671 12.3947 1.78604 12.412 1.79175C12.42 1.79439 12.4236 1.79616 12.4243 1.79655L12.4254 1.7972L12.4264 1.79802C12.427 1.79862 12.4298 1.8015 12.4348 1.80824C12.4457 1.82282 12.466 1.85424 12.4956 1.91778C12.5611 2.05863 12.6289 2.25832 12.7443 2.60472L12.9594 3.25001Z',
});
let TrashIconComponent = class TrashIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TrashIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: TrashIconComponent, selector: "kp-trash-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
TrashIconComponent = __decorate$2c([
    Icon(kpTrash, CustomIconStyle)
], TrashIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TrashIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-trash-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$2c = [TrashIconComponent];
class TrashIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TrashIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: TrashIconModule, declarations: [TrashIconComponent], exports: [TrashIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TrashIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TrashIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$2c,
                    exports: DIRECTIVES$2c,
                }]
        }] });

var __decorate$2b = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
const kpUser = defineCustomIcon('kp', {
    name: 'user',
    height: 20,
    width: 16,
    svgPath: 'M8 0.25C5.37665 0.25 3.25 2.37665 3.25 5C3.25 7.62335 5.37665 9.75 8 9.75C10.6234 9.75 12.75 7.62335 12.75 5C12.75 2.37665 10.6234 0.25 8 0.25ZM5 12.25C2.37665 12.25 0.25 14.3766 0.25 17C0.25 18.5188 1.48122 19.75 3 19.75H13C14.5188 19.75 15.75 18.5188 15.75 17C15.75 14.3766 13.6234 12.25 11 12.25H5Z',
});
let UserIconComponent = class UserIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: UserIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: UserIconComponent, selector: "kp-user-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
UserIconComponent = __decorate$2b([
    Icon(kpUser, CustomIconStyle)
], UserIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: UserIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-user-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$2b = [UserIconComponent];
class UserIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: UserIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: UserIconModule, declarations: [UserIconComponent], exports: [UserIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: UserIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: UserIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$2b,
                    exports: DIRECTIVES$2b,
                }]
        }] });

var __decorate$2a = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
const kpUsers = defineCustomIcon('kp', {
    name: 'team',
    height: 20,
    width: 22,
    svgPath: 'M7 0.25C4.37665 0.25 2.25 2.37665 2.25 5C2.25 7.62335 4.37665 9.75 7 9.75C9.62335 9.75 11.75 7.62335 11.75 5C11.75 2.37665 9.62335 0.25 7 0.25ZM12.25 1C12.25 0.585786 12.5858 0.25 13 0.25C15.6234 0.25 17.75 2.37665 17.75 5C17.75 7.62335 15.6234 9.75 13 9.75C12.5858 9.75 12.25 9.41421 12.25 9C12.25 8.58579 12.5858 8.25 13 8.25C14.7949 8.25 16.25 6.79493 16.25 5C16.25 3.20507 14.7949 1.75 13 1.75C12.5858 1.75 12.25 1.41421 12.25 1ZM5 12.25C2.37665 12.25 0.25 14.3766 0.25 17C0.25 18.5188 1.48122 19.75 3 19.75H11C12.5188 19.75 13.75 18.5188 13.75 17C13.75 14.3766 11.6234 12.25 9 12.25H5ZM15 12.25C14.5858 12.25 14.25 12.5858 14.25 13C14.25 13.4142 14.5858 13.75 15 13.75H17C18.7949 13.75 20.25 15.2051 20.25 17C20.25 17.6904 19.6904 18.25 19 18.25H15C14.5858 18.25 14.25 18.5858 14.25 19C14.25 19.4142 14.5858 19.75 15 19.75H19C20.5188 19.75 21.75 18.5188 21.75 17C21.75 14.3766 19.6234 12.25 17 12.25H15Z',
});
let UsersIconComponent = class UsersIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: UsersIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: UsersIconComponent, selector: "kp-users-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
UsersIconComponent = __decorate$2a([
    Icon(kpUsers, CustomIconStyle)
], UsersIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: UsersIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-users-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$2a = [UsersIconComponent];
class UsersIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: UsersIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: UsersIconModule, declarations: [UsersIconComponent], exports: [UsersIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: UsersIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: UsersIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$2a,
                    exports: DIRECTIVES$2a,
                }]
        }] });

var __decorate$29 = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
const kpWallet = defineCustomIcon('kp', {
    name: 'wallet',
    height: 20,
    width: 22,
    svgPath: 'M13 1.75C13.964 1.75 14.6116 1.7516 15.0946 1.81654C15.5561 1.87858 15.7536 1.9858 15.8839 2.11612C16.0142 2.24644 16.1214 2.44393 16.1835 2.90539C16.2299 3.25089 16.244 3.68057 16.2482 4.25H15.1619L15.0549 4.25L15 4.25H11H3C2.30965 4.25 1.75 3.69036 1.75 3C1.75 2.30965 2.30965 1.75 3 1.75H13ZM0.250001 3C0.250001 1.48122 1.48122 0.250001 3 0.250001H13H13.052H13.052C13.9505 0.249972 14.6997 0.249948 15.2945 0.329914C15.9223 0.414319 16.4891 0.599989 16.9445 1.05546C17.4 1.51093 17.5857 2.07773 17.6701 2.70552C17.7301 3.15172 17.7451 3.68484 17.7488 4.30268C17.9761 4.31864 18.1903 4.33944 18.3918 4.36653C19.2919 4.48754 20.0497 4.74644 20.6517 5.34835C21.2536 5.95027 21.5125 6.70814 21.6335 7.60825C21.6973 8.08322 21.7262 8.62883 21.7393 9.25H20C18.4812 9.25 17.25 10.4812 17.25 12C17.25 13.5188 18.4812 14.75 20 14.75H21.7393C21.7262 15.3712 21.6973 15.9168 21.6335 16.3918C21.5125 17.2919 21.2536 18.0497 20.6517 18.6517C20.0497 19.2536 19.2919 19.5125 18.3918 19.6335C17.5248 19.75 16.4225 19.75 15.0549 19.75H15H7H6.94513C5.57754 19.75 4.47522 19.75 3.60825 19.6335C2.70814 19.5125 1.95027 19.2536 1.34835 18.6517C0.746434 18.0497 0.48754 17.2919 0.366524 16.3918C0.249963 15.5248 0.24998 14.4225 0.250001 13.0549V13V3ZM21.75 10.75C21.75 10.8144 21.75 10.8795 21.75 10.9451V11V13V13.0549C21.75 13.1205 21.75 13.1856 21.75 13.25H20C19.3096 13.25 18.75 12.6904 18.75 12C18.75 11.3096 19.3096 10.75 20 10.75H21.75Z',
});
let WalletIconComponent = class WalletIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: WalletIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: WalletIconComponent, selector: "kp-wallet-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
WalletIconComponent = __decorate$29([
    Icon(kpWallet, CustomIconStyle)
], WalletIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: WalletIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-wallet-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$29 = [WalletIconComponent];
class WalletIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: WalletIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: WalletIconModule, declarations: [WalletIconComponent], exports: [WalletIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: WalletIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: WalletIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$29,
                    exports: DIRECTIVES$29,
                }]
        }] });

var __decorate$28 = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
const kpWalletPlus = defineCustomIcon('kp', {
    name: 'wallet-plus',
    height: 24,
    width: 26,
    svgPath: 'M18.061 2.5643C17.5985 2.50212 16.9708 2.5 15.9997 2.5H3.66634C2.92996 2.5 2.33301 3.09695 2.33301 3.83333C2.33301 4.56971 2.92996 5.16666 3.66634 5.16666H18.9995C18.9983 4.38143 18.9901 3.84601 18.9354 3.4387C18.8771 3.00496 18.7829 2.86901 18.7068 2.79289C18.6307 2.71677 18.4947 2.62262 18.061 2.5643ZM18.3275 0.582138C18.983 0.67027 19.6113 0.869015 20.121 1.37868C20.6307 1.88834 20.8294 2.51669 20.9175 3.17221C20.9998 3.78387 20.9997 4.54769 20.9997 5.43419V5.44587C21.3856 5.46299 21.7379 5.49014 22.0581 5.53319C22.9582 5.6542 23.7161 5.9131 24.318 6.51501C24.9199 7.11693 25.1788 7.8748 25.2998 8.77491C25.383 9.39326 25.4068 10.1313 25.4136 11H23.4997C21.6587 11 20.1663 12.4924 20.1663 14.3333C20.1663 16.1743 21.6587 17.6667 23.4997 17.6667H25.4136C25.4068 18.5353 25.383 19.2734 25.2998 19.8918C25.1788 20.7919 24.9199 21.5497 24.318 22.1516C23.7161 22.7536 22.9582 23.0125 22.0581 23.1335C21.1911 23.25 20.0888 23.25 18.7212 23.25H18.6663H7.33301H7.27814C5.91055 23.25 4.80823 23.25 3.94126 23.1335C3.04115 23.0125 2.28327 22.7536 1.68136 22.1516C1.07944 21.5497 0.820547 20.7919 0.699531 19.8918C0.58297 19.0248 0.582987 17.9225 0.583007 16.5549V16.5549V16.5V5.10219C0.421882 4.71107 0.333008 4.28258 0.333008 3.83333C0.333008 1.99238 1.82539 0.499997 3.66634 0.499997H15.9997L16.0655 0.499995C16.952 0.499945 17.7158 0.499902 18.3275 0.582138ZM23.4997 13H25.4163V15.6667H23.4997C22.7633 15.6667 22.1663 15.0697 22.1663 14.3333C22.1663 13.597 22.7633 13 23.4997 13ZM11.6663 11.4167C11.6663 10.8644 11.2186 10.4167 10.6663 10.4167C10.1141 10.4167 9.66634 10.8644 9.66634 11.4167V13.3333H7.74967C7.19739 13.3333 6.74967 13.781 6.74967 14.3333C6.74967 14.8856 7.19739 15.3333 7.74967 15.3333H9.66634V17.25C9.66634 17.8023 10.1141 18.25 10.6663 18.25C11.2186 18.25 11.6663 17.8023 11.6663 17.25V15.3333H13.583C14.1353 15.3333 14.583 14.8856 14.583 14.3333C14.583 13.781 14.1353 13.3333 13.583 13.3333H11.6663V11.4167Z',
});
let WalletPlusIconComponent = class WalletPlusIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: WalletPlusIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: WalletPlusIconComponent, selector: "kp-wallet-plus-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
WalletPlusIconComponent = __decorate$28([
    Icon(kpWalletPlus, CustomIconStyle)
], WalletPlusIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: WalletPlusIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-wallet-plus-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$28 = [WalletPlusIconComponent];
class WalletPlusIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: WalletPlusIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: WalletPlusIconModule, declarations: [WalletPlusIconComponent], exports: [WalletPlusIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: WalletPlusIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: WalletPlusIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$28,
                    exports: DIRECTIVES$28,
                }]
        }] });

var __decorate$27 = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
const kpPlusSquare = defineCustomIcon('kp', {
    name: 'plus-square',
    height: 18,
    width: 18,
    svgPath: 'M8.75 6.79243e-07L8.69358 3.21616e-07C6.85582 -1.55332e-05 5.40019 -2.81694e-05 4.26098 0.153135C3.08856 0.310763 2.13961 0.64288 1.39124 1.39124C0.64288 2.13961 0.310763 3.08856 0.153135 4.26098C-2.81694e-05 5.40019 -1.55332e-05 6.85582 3.21616e-07 8.69358L6.79243e-07 8.75V9.25L3.21616e-07 9.30642C-1.55332e-05 11.1442 -2.81694e-05 12.5998 0.153135 13.739C0.310763 14.9114 0.64288 15.8604 1.39124 16.6088C2.13961 17.3571 3.08856 17.6892 4.26098 17.8469C5.40018 18 6.8558 18 8.69354 18H8.69359H8.75H9.25H9.30641H9.30646C11.1442 18 12.5998 18 13.739 17.8469C14.9114 17.6892 15.8604 17.3571 16.6088 16.6088C17.3571 15.8604 17.6892 14.9114 17.8469 13.739C18 12.5998 18 11.1442 18 9.30646V9.30641V9.25V8.75V8.69359V8.69354C18 6.8558 18 5.40018 17.8469 4.26098C17.6892 3.08856 17.3571 2.13961 16.6088 1.39124C15.8604 0.64288 14.9114 0.310763 13.739 0.153135C12.5998 -2.81694e-05 11.1442 -1.55332e-05 9.30642 3.21616e-07L9.25 6.79243e-07H8.75ZM2.4519 2.4519C2.87511 2.02869 3.45476 1.77502 4.46085 1.63976C5.48851 1.50159 6.84318 1.5 8.75 1.5H9.25C11.1568 1.5 12.5115 1.50159 13.5392 1.63976C14.5452 1.77502 15.1249 2.02869 15.5481 2.4519C15.9713 2.87511 16.225 3.45476 16.3602 4.46085C16.4984 5.48851 16.5 6.84318 16.5 8.75V9.25C16.5 11.1568 16.4984 12.5115 16.3602 13.5392C16.225 14.5452 15.9713 15.1249 15.5481 15.5481C15.1249 15.9713 14.5452 16.225 13.5392 16.3602C12.5115 16.4984 11.1568 16.5 9.25 16.5H8.75C6.84318 16.5 5.48851 16.4984 4.46085 16.3602C3.45476 16.225 2.87511 15.9713 2.4519 15.5481C2.02869 15.1249 1.77502 14.5452 1.63976 13.5392C1.50159 12.5115 1.5 11.1568 1.5 9.25V8.75C1.5 6.84318 1.50159 5.48851 1.63976 4.46085C1.77502 3.45476 2.02869 2.87511 2.4519 2.4519ZM9.74998 5.33333C9.74998 4.91912 9.41419 4.58333 8.99998 4.58333C8.58576 4.58333 8.24998 4.91912 8.24998 5.33333V8.25H5.33331C4.9191 8.25 4.58331 8.58579 4.58331 9C4.58331 9.41421 4.9191 9.75 5.33331 9.75H8.24998V12.6667C8.24998 13.0809 8.58576 13.4167 8.99998 13.4167C9.41419 13.4167 9.74998 13.0809 9.74998 12.6667V9.75H12.6666C13.0809 9.75 13.4166 9.41421 13.4166 9C13.4166 8.58579 13.0809 8.25 12.6666 8.25H9.74998V5.33333Z',
});
let PlusSquareIconComponent = class PlusSquareIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: PlusSquareIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: PlusSquareIconComponent, selector: "kp-plus-square-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
PlusSquareIconComponent = __decorate$27([
    Icon(kpPlusSquare, CustomIconStyle)
], PlusSquareIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: PlusSquareIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-plus-square-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$27 = [PlusSquareIconComponent];
class PlusSquareIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: PlusSquareIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: PlusSquareIconModule, declarations: [PlusSquareIconComponent], exports: [PlusSquareIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: PlusSquareIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: PlusSquareIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$27,
                    exports: DIRECTIVES$27,
                }]
        }] });

var __decorate$26 = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
const kpUndo = defineCustomIcon('kp', {
    name: 'undo',
    height: 17,
    width: 18,
    svgPath: 'M4.947 0.21967C5.23989 0.512563 5.23989 0.987437 4.947 1.28033L2.56066 3.66667H11.75C15.2018 3.66667 18 6.46489 18 9.91667C18 13.3684 15.2018 16.1667 11.75 16.1667H8.08333C7.66912 16.1667 7.33333 15.8309 7.33333 15.4167C7.33333 15.0025 7.66912 14.6667 8.08333 14.6667H11.75C14.3734 14.6667 16.5 12.54 16.5 9.91667C16.5 7.29331 14.3734 5.16667 11.75 5.16667H2.56066L4.947 7.553C5.23989 7.8459 5.23989 8.32077 4.947 8.61366C4.6541 8.90656 4.17923 8.90656 3.88634 8.61366L0.21967 4.947C0.0790176 4.80634 0 4.61558 0 4.41667C0 4.21775 0.0790176 4.02699 0.21967 3.88634L3.88634 0.21967C4.17923 -0.0732233 4.6541 -0.0732233 4.947 0.21967Z',
});
let UndoIconComponent = class UndoIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: UndoIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: UndoIconComponent, selector: "kp-undo-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
UndoIconComponent = __decorate$26([
    Icon(kpUndo, CustomIconStyle)
], UndoIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: UndoIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-undo-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$26 = [UndoIconComponent];
class UndoIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: UndoIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: UndoIconModule, declarations: [UndoIconComponent], exports: [UndoIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: UndoIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: UndoIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$26,
                    exports: DIRECTIVES$26,
                }]
        }] });

var __decorate$25 = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
const kpRedo = defineCustomIcon('kp', {
    name: 'redo',
    height: 17,
    width: 18,
    svgPath: 'M13.053 0.21967C13.3459 -0.0732233 13.8208 -0.0732233 14.1137 0.21967L17.7803 3.88634C17.921 4.02699 18 4.21775 18 4.41667C18 4.61558 17.921 4.80634 17.7803 4.947L14.1137 8.61366C13.8208 8.90656 13.3459 8.90656 13.053 8.61366C12.7601 8.32077 12.7601 7.8459 13.053 7.553L15.4393 5.16667H6.25C3.62665 5.16667 1.5 7.29331 1.5 9.91667C1.5 12.54 3.62665 14.6667 6.25 14.6667H9.91667C10.3309 14.6667 10.6667 15.0025 10.6667 15.4167C10.6667 15.8309 10.3309 16.1667 9.91667 16.1667H6.25C2.79822 16.1667 0 13.3684 0 9.91667C0 6.46489 2.79822 3.66667 6.25 3.66667H15.4393L13.053 1.28033C12.7601 0.987437 12.7601 0.512563 13.053 0.21967Z',
});
let RedoIconComponent = class RedoIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: RedoIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: RedoIconComponent, selector: "kp-redo-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
RedoIconComponent = __decorate$25([
    Icon(kpRedo, CustomIconStyle)
], RedoIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: RedoIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-redo-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$25 = [RedoIconComponent];
class RedoIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: RedoIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: RedoIconModule, declarations: [RedoIconComponent], exports: [RedoIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: RedoIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: RedoIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$25,
                    exports: DIRECTIVES$25,
                }]
        }] });

var __decorate$24 = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
const kpImage = defineCustomIcon('kp', {
    name: 'image',
    height: 18,
    width: 18,
    svgPath: 'M8.95109 0.75L9 0.75V2.25C7.33789 2.25 6.15724 2.25123 5.25133 2.35831C4.36197 2.46344 3.83563 2.66158 3.43822 2.98772C3.27366 3.12277 3.12277 3.27366 2.98772 3.43822C2.66158 3.83563 2.46344 4.36197 2.35831 5.25133C2.25123 6.15724 2.25 7.33789 2.25 9C2.25 10.6621 2.25123 11.8428 2.35831 12.7487C2.36424 12.7988 2.37046 12.8478 2.37699 12.8957L2.63634 12.6363L3.55791 11.7148L3.59468 11.678C4.22998 11.0427 4.75974 10.5129 5.23686 10.1488C5.74046 9.76459 6.27253 9.49509 6.91667 9.49509C7.5608 9.49509 8.09288 9.76459 8.59648 10.1488C9.0736 10.5129 9.60335 11.0427 10.2387 11.678L10.2754 11.7148L10.6683 12.1077C10.9407 11.8412 11.1864 11.6113 11.4147 11.426C11.793 11.1191 12.188 10.8792 12.6679 10.7907C12.9976 10.7299 13.3357 10.7299 13.6655 10.7907C14.1454 10.8792 14.5403 11.1191 14.9186 11.426C15.1556 11.6183 15.4114 11.8587 15.6962 12.1383C15.7492 11.3245 15.75 10.3123 15.75 9H17.25V9.04895C17.25 10.6512 17.25 11.9205 17.1313 12.9248C17.0095 13.9557 16.7536 14.8044 16.1718 15.5134C15.9744 15.7539 15.7539 15.9744 15.5134 16.1718C14.8044 16.7536 13.9557 17.0095 12.9248 17.1313C11.9205 17.25 10.6512 17.25 9.04891 17.25H9H8.95109C7.34882 17.25 6.07947 17.25 5.07525 17.1313C4.04428 17.0095 3.19558 16.7536 2.48663 16.1718C2.24612 15.9744 2.02558 15.7539 1.8282 15.5134C1.70378 15.3618 1.59427 15.2038 1.49778 15.039C1.45847 15.0124 1.42112 14.9818 1.38634 14.947C1.21735 14.778 1.14586 14.5484 1.17187 14.3282C1.02234 13.9017 0.929064 13.4356 0.86868 12.9248C0.749978 11.9205 0.749988 10.6512 0.75 9.04891L0.75 9L0.75 8.95109C0.749988 7.34882 0.749978 6.07947 0.86868 5.07525C0.990545 4.04428 1.24638 3.19558 1.8282 2.48663C2.02558 2.24612 2.24612 2.02558 2.48663 1.8282C3.19558 1.24638 4.04428 0.990545 5.07525 0.86868C6.07947 0.749978 7.34883 0.749988 8.95109 0.75ZM2.98772 14.5618C2.96437 14.5333 2.94168 14.5042 2.91963 14.4744L3.697 13.697L4.61857 12.7754C5.30023 12.0938 5.75925 11.637 6.14673 11.3414C6.5169 11.0589 6.73237 10.9951 6.91667 10.9951C7.10096 10.9951 7.31643 11.0589 7.6866 11.3414C8.07408 11.637 8.53311 12.0938 9.21476 12.7754L10.1363 13.697L12.1357 15.6964C11.3224 15.7492 10.3109 15.75 9 15.75C7.33789 15.75 6.15724 15.7488 5.25133 15.6417C4.36197 15.5366 3.83563 15.3384 3.43822 15.0123C3.27366 14.8772 3.12277 14.7263 2.98772 14.5618ZM14.5618 15.0123C14.3852 15.1572 14.1831 15.2769 13.935 15.3743L11.7291 13.1685C11.9782 12.9248 12.1797 12.737 12.3599 12.5908C12.6431 12.361 12.8082 12.2901 12.9399 12.2658C13.0898 12.2382 13.2435 12.2382 13.3934 12.2658C13.5252 12.2901 13.6903 12.361 13.9734 12.5908C14.2668 12.8288 14.6165 13.1772 15.1363 13.697L15.3743 13.935C15.2769 14.1831 15.1572 14.3852 15.0123 14.5618C14.8772 14.7263 14.7263 14.8772 14.5618 15.0123ZM14.5303 2.86367C14.6977 2.69632 14.969 2.69632 15.1363 2.86367C15.3037 3.03101 15.3037 3.30233 15.1363 3.46967L13.634 4.97199C13.061 5.54498 12.8415 5.76002 12.5965 5.92545C12.4124 6.04976 12.216 6.15486 12.0104 6.23911C11.9014 6.28378 11.7881 6.32071 11.641 6.35903C11.6793 6.2119 11.7162 6.09859 11.7609 5.98959C11.8451 5.78402 11.9502 5.58764 12.0745 5.40352C12.24 5.15847 12.455 4.93898 13.028 4.36598L14.5303 2.86367ZM16.197 1.803C15.4439 1.04987 14.2228 1.04988 13.4697 1.80301L11.9674 3.30532L11.915 3.35768C11.4117 3.86078 11.0872 4.18523 10.8313 4.5642C10.6497 4.8333 10.4961 5.12032 10.3729 5.42076C10.1995 5.84385 10.1096 6.29386 9.97014 6.99165L9.95563 7.06427L9.93123 7.18625C9.88205 7.43214 9.95902 7.68635 10.1363 7.86367C10.3137 8.04098 10.5679 8.11795 10.8138 8.06877L10.9357 8.04438L11.0084 8.02986C11.7061 7.8904 12.1562 7.80047 12.5792 7.62707C12.8797 7.50394 13.1667 7.35033 13.4358 7.16865C13.8148 6.9128 14.1392 6.58825 14.6423 6.08501L14.6947 6.03265L16.197 4.53033C16.9501 3.7772 16.9501 2.55614 16.197 1.803Z',
});
let ImageIconComponent = class ImageIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ImageIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: ImageIconComponent, selector: "kp-image-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
ImageIconComponent = __decorate$24([
    Icon(kpImage, CustomIconStyle)
], ImageIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ImageIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-image-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$24 = [ImageIconComponent];
class ImageIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ImageIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: ImageIconModule, declarations: [ImageIconComponent], exports: [ImageIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ImageIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ImageIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$24,
                    exports: DIRECTIVES$24,
                }]
        }] });

var __decorate$23 = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
const kpSliderVertical = defineCustomIcon('kp', {
    name: 'film-tape',
    height: 20,
    width: 18,
    svgPath: 'M1.83366 0.833252C1.83366 0.419038 2.16945 0.083252 2.58366 0.083252H15.417C15.8312 0.083252 16.167 0.419038 16.167 0.833252C16.167 1.24747 15.8312 1.58325 15.417 1.58325H2.58366C2.16945 1.58325 1.83366 1.24747 1.83366 0.833252ZM5.615 3.74992L5.667 3.74992H12.3337L12.3857 3.74992C13.2841 3.74989 14.0334 3.74986 14.6281 3.82983C15.2559 3.91423 15.8227 4.09991 16.2782 4.55537C16.7337 5.01084 16.9193 5.57764 17.0037 6.20544C17.0837 6.80021 17.0837 7.54942 17.0837 8.44789V8.44792L17.0837 8.49992V11.4999L17.0837 11.5519V11.5519C17.0837 12.4504 17.0837 13.1996 17.0037 13.7944C16.9193 14.4222 16.7337 14.989 16.2782 15.4445C15.8227 15.8999 15.2559 16.0856 14.6281 16.17C14.0334 16.25 13.2842 16.2499 12.3858 16.2499H12.3858H12.3857L12.3337 16.2499H5.667L5.615 16.2499H5.61485H5.61483C4.71643 16.2499 3.96726 16.25 3.37252 16.17C2.74472 16.0856 2.17792 15.8999 1.72245 15.4445C1.26698 14.989 1.08131 14.4222 0.996906 13.7944C0.916939 13.1996 0.916964 12.4504 0.916993 11.5519L0.916994 11.4999V8.49992L0.916993 8.44792C0.916964 7.54944 0.916939 6.80022 0.996906 6.20544C1.08131 5.57764 1.26698 5.01084 1.72245 4.55537C2.17792 4.09991 2.74472 3.91423 3.37252 3.82983C3.9673 3.74986 4.71652 3.74989 5.615 3.74992ZM3.57239 5.31645C3.11092 5.3785 2.91343 5.48572 2.78311 5.61603C2.65279 5.74635 2.54557 5.94385 2.48353 6.40531C2.41859 6.88835 2.41699 7.53591 2.41699 8.49992V11.4999C2.41699 12.4639 2.41859 13.1115 2.48353 13.5945C2.54557 14.056 2.65279 14.2535 2.78311 14.3838C2.91343 14.5141 3.11092 14.6213 3.57239 14.6834C4.05542 14.7483 4.70298 14.7499 5.667 14.7499H12.3337C13.2977 14.7499 13.9452 14.7483 14.4283 14.6834C14.8897 14.6213 15.0872 14.5141 15.2175 14.3838C15.3479 14.2535 15.4551 14.056 15.5171 13.5945C15.5821 13.1115 15.5837 12.4639 15.5837 11.4999V8.49992C15.5837 7.53591 15.5821 6.88835 15.5171 6.40531C15.4551 5.94385 15.3479 5.74635 15.2175 5.61603C15.0872 5.48572 14.8897 5.3785 14.4283 5.31645C13.9452 5.25151 13.2977 5.24992 12.3337 5.24992H5.667C4.70298 5.24992 4.05542 5.25151 3.57239 5.31645ZM2.58366 18.4166C2.16945 18.4166 1.83366 18.7524 1.83366 19.1666C1.83366 19.5808 2.16945 19.9166 2.58366 19.9166H15.417C15.8312 19.9166 16.167 19.5808 16.167 19.1666C16.167 18.7524 15.8312 18.4166 15.417 18.4166H2.58366Z',
});
let SliderVerticalIconComponent = class SliderVerticalIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: SliderVerticalIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: SliderVerticalIconComponent, selector: "kp-slider-vertical-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
SliderVerticalIconComponent = __decorate$23([
    Icon(kpSliderVertical, CustomIconStyle)
], SliderVerticalIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: SliderVerticalIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-slider-vertical-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$23 = [SliderVerticalIconComponent];
class SliderVerticalIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: SliderVerticalIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: SliderVerticalIconModule, declarations: [SliderVerticalIconComponent], exports: [SliderVerticalIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: SliderVerticalIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: SliderVerticalIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$23,
                    exports: DIRECTIVES$23,
                }]
        }] });

var __decorate$22 = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
const kpTrashAlt = defineCustomIcon('kp', {
    name: 'trash-alt',
    height: 20,
    width: 20,
    svgPath: 'M8.40621 0.249997L8.44152 0.25H11.5585L11.5938 0.249997C11.9112 0.249958 12.2092 0.249922 12.459 0.278435C12.7371 0.310192 13.0296 0.383609 13.3025 0.580326C13.5754 0.777043 13.7375 1.03124 13.8556 1.28508C13.9616 1.51299 14.0559 1.79574 14.1562 2.09685L14.1562 2.09687L14.1562 2.0969L14.1674 2.13037L14.5406 3.25H17H19C19.4142 3.25 19.75 3.58579 19.75 4C19.75 4.41421 19.4142 4.75 19 4.75H17.7017L17.1217 13.449L17.1182 13.5016C17.0327 14.7844 16.9637 15.8205 16.8017 16.6336C16.6333 17.4789 16.3469 18.185 15.7553 18.7384C15.1637 19.2919 14.4401 19.5307 13.5855 19.6425C12.7634 19.75 11.725 19.75 10.4394 19.75H10.3867H9.61332H9.56058C8.27499 19.75 7.23655 19.75 6.41451 19.6425C5.55986 19.5307 4.83631 19.2919 4.24472 18.7384C3.65312 18.185 3.3667 17.4789 3.19831 16.6336C3.03633 15.8205 2.96727 14.7844 2.88178 13.5016L2.87827 13.449L2.29834 4.75H1C0.585786 4.75 0.25 4.41421 0.25 4C0.25 3.58579 0.585786 3.25 1 3.25H3H5.45943L5.83264 2.13037L5.8438 2.09688L5.84381 2.09686C5.94414 1.79575 6.03835 1.51299 6.14438 1.28508C6.26246 1.03124 6.42459 0.777043 6.69752 0.580326C6.97045 0.383609 7.26287 0.310192 7.54102 0.278435C7.79077 0.249922 8.08881 0.249958 8.40621 0.249997ZM7.04057 3.25H12.9594L12.7443 2.60472C12.6289 2.25832 12.5611 2.05863 12.4956 1.91778C12.466 1.85423 12.4457 1.82281 12.4348 1.80824C12.4298 1.80149 12.427 1.79862 12.4264 1.79801L12.4254 1.79719L12.4243 1.79654C12.4236 1.79616 12.42 1.79439 12.412 1.79174C12.3947 1.78604 12.3585 1.7767 12.2888 1.76875C12.1345 1.75113 11.9236 1.75 11.5585 1.75H8.44152C8.07638 1.75 7.86551 1.75113 7.71117 1.76875C7.64154 1.7767 7.60531 1.78604 7.58804 1.79174C7.58005 1.79439 7.57643 1.79616 7.57566 1.79654L7.57458 1.79719L7.57363 1.79801C7.57302 1.79862 7.57019 1.80149 7.56516 1.80824C7.55428 1.82281 7.53397 1.85423 7.50441 1.91778C7.43889 2.05863 7.37113 2.25832 7.25566 2.60472L7.04057 3.25ZM3.80166 4.75L4.37495 13.3492C4.4648 14.6971 4.52883 15.6349 4.6694 16.3405C4.80575 17.025 4.99608 17.3873 5.2695 17.6431C5.54291 17.8988 5.91707 18.0647 6.60907 18.1552C7.32247 18.2485 8.26248 18.25 9.61332 18.25H10.3867C11.7375 18.25 12.6775 18.2485 13.3909 18.1552C14.0829 18.0647 14.4571 17.8988 14.7305 17.6431C15.0039 17.3873 15.1943 17.025 15.3306 16.3405C15.4712 15.6349 15.5352 14.6971 15.6251 13.3492L16.1983 4.75H14H6H3.80166ZM8 7.25C8.41421 7.25 8.75 7.58579 8.75 8V15C8.75 15.4142 8.41421 15.75 8 15.75C7.58579 15.75 7.25 15.4142 7.25 15V8C7.25 7.58579 7.58579 7.25 8 7.25ZM12.75 8C12.75 7.58579 12.4142 7.25 12 7.25C11.5858 7.25 11.25 7.58579 11.25 8V12C11.25 12.4142 11.5858 12.75 12 12.75C12.4142 12.75 12.75 12.4142 12.75 12V8Z',
});
let TrashAltIconComponent = class TrashAltIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TrashAltIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: TrashAltIconComponent, selector: "kp-trash-alt-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
TrashAltIconComponent = __decorate$22([
    Icon(kpTrashAlt, CustomIconStyle)
], TrashAltIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TrashAltIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-trash-alt-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$22 = [TrashAltIconComponent];
class TrashAltIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TrashAltIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: TrashAltIconModule, declarations: [TrashAltIconComponent], exports: [TrashAltIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TrashAltIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TrashAltIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$22,
                    exports: DIRECTIVES$22,
                }]
        }] });

var __decorate$21 = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
let SpinnerThirdIconComponent = class SpinnerThirdIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: SpinnerThirdIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: SpinnerThirdIconComponent, selector: "kp-spinner-third-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
SpinnerThirdIconComponent = __decorate$21([
    Icon(faSpinnerThird)
], SpinnerThirdIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: SpinnerThirdIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-spinner-third-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$21 = [SpinnerThirdIconComponent];
class SpinnerThirdIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: SpinnerThirdIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: SpinnerThirdIconModule, declarations: [SpinnerThirdIconComponent], exports: [SpinnerThirdIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: SpinnerThirdIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: SpinnerThirdIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$21,
                    exports: DIRECTIVES$21,
                }]
        }] });

var __decorate$20 = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
const kpSave = defineCustomIcon('kp', {
    name: 'save',
    height: 18,
    width: 18,
    svgPath: 'M13.2017 1.67136C12.8203 1.51336 12.3855 1.50009 11.0981 1.50009H6.75C5.31459 1.50009 4.31347 1.50168 3.55812 1.60324C2.82435 1.70189 2.43577 1.88234 2.15901 2.1591C1.88225 2.43586 1.7018 2.82443 1.60315 3.55821C1.50159 4.31356 1.5 5.31467 1.5 6.75009V11.2501C1.5 12.6855 1.50159 13.6866 1.60315 14.442C1.7018 15.1757 1.88225 15.5643 2.15901 15.8411C2.43577 16.1178 2.82435 16.2983 3.55812 16.3969C3.85738 16.4372 4.19521 16.4617 4.58333 16.4767V13.5834L4.58333 13.5406C4.58331 12.7952 4.5833 12.1734 4.63972 11.6729C4.69892 11.1478 4.82809 10.6643 5.15161 10.2427C5.29791 10.052 5.46858 9.88134 5.65924 9.73503C6.08087 9.41151 6.56433 9.28234 7.08947 9.22314C7.58994 9.16672 8.21179 9.16673 8.95716 9.16675H8.95718L9 9.16676L9.04283 9.16675H9.04285C9.78821 9.16673 10.4101 9.16672 10.9105 9.22314C11.4357 9.28234 11.9191 9.41151 12.3408 9.73503C12.5314 9.88134 12.7021 10.052 12.8484 10.2427C13.1719 10.6643 13.3011 11.1478 13.3603 11.6729C13.4167 12.1734 13.4167 12.7952 13.4167 13.5406V13.5834V16.4767C13.8048 16.4617 14.1426 16.4372 14.4419 16.3969C15.1757 16.2983 15.5642 16.1178 15.841 15.8411C16.1178 15.5643 16.2982 15.1757 16.3969 14.442C16.4984 13.6866 16.5 12.6855 16.5 11.2501V6.90204C16.5 5.61459 16.4867 5.17979 16.3287 4.79836C16.1707 4.41692 15.8727 4.10009 14.9623 3.18973L14.8104 3.03778C13.9 2.12742 13.5832 1.82936 13.2017 1.67136ZM12.7667 17.9935C13.4855 17.9829 14.1082 17.9553 14.6418 17.8836C15.5419 17.7625 16.2997 17.5037 16.9017 16.9017C17.5036 16.2998 17.7625 15.5419 17.8835 14.6418C18 13.7749 18 12.6726 18 11.305V11.2501V6.90204L18 6.75943C18.0006 5.67166 18.0009 4.91571 17.7146 4.22433C17.4282 3.53296 16.8934 2.99868 16.1238 2.22987L16.023 2.12907L15.871 1.97712L15.7702 1.87625C15.0014 1.10671 14.4671 0.571917 13.7758 0.28554C13.0844 -0.000836823 12.3284 -0.000475917 11.2407 4.33585e-05L11.0981 8.84793e-05H6.75L6.69513 8.78832e-05C5.32754 6.726e-05 4.22522 5.06303e-05 3.35825 0.116611C2.45814 0.237628 1.70027 0.496521 1.09835 1.09844C0.496435 1.70035 0.237541 2.45823 0.116525 3.35834C-3.5752e-05 4.2253 -1.95615e-05 5.32762 5.25343e-07 6.6952V6.69521V6.75009V11.2501V11.305V11.305C-1.95615e-05 12.6726 -3.57515e-05 13.7749 0.116525 14.6418C0.237541 15.5419 0.496435 16.2998 1.09835 16.9017C1.70027 17.5037 2.45814 17.7625 3.35825 17.8836C3.89182 17.9553 4.51452 17.9829 5.23335 17.9935C5.26606 17.9978 5.29943 18.0001 5.33333 18.0001C5.36065 18.0001 5.3876 17.9986 5.41415 17.9958C5.81238 18.0001 6.239 18.0001 6.69513 18.0001H6.75H11.25H11.3049C11.761 18.0001 12.1876 18.0001 12.5858 17.9958C12.6124 17.9986 12.6394 18.0001 12.6667 18.0001C12.7006 18.0001 12.7339 17.9978 12.7667 17.9935ZM11.9167 16.4995V13.5834C11.9167 12.7841 11.9156 12.2474 11.8697 11.8409C11.8256 11.4496 11.7481 11.2728 11.6584 11.1558C11.5919 11.0691 11.5143 10.9916 11.4276 10.9251C11.3106 10.8353 11.1339 10.7578 10.7425 10.7137C10.336 10.6679 9.7993 10.6668 9 10.6668C8.2007 10.6668 7.664 10.6679 7.25751 10.7137C6.86614 10.7578 6.68937 10.8353 6.57238 10.9251C6.48572 10.9916 6.40814 11.0691 6.34164 11.1558C6.25188 11.2728 6.1744 11.4496 6.13028 11.8409C6.08445 12.2474 6.08333 12.7841 6.08333 13.5834V16.4995C6.29461 16.5 6.51656 16.5001 6.75 16.5001H11.25C11.4834 16.5001 11.7054 16.5 11.9167 16.4995ZM4.58333 5.33342C4.58333 4.91921 4.91912 4.58342 5.33333 4.58342L12.6667 4.58342C13.0809 4.58342 13.4167 4.91921 13.4167 5.33342C13.4167 5.74764 13.0809 6.08342 12.6667 6.08342L5.33333 6.08342C4.91912 6.08342 4.58333 5.74764 4.58333 5.33342Z',
});
let SaveIconComponent = class SaveIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: SaveIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: SaveIconComponent, selector: "kp-save-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
SaveIconComponent = __decorate$20([
    Icon(kpSave, CustomIconStyle)
], SaveIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: SaveIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-save-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$20 = [SaveIconComponent];
class SaveIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: SaveIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: SaveIconModule, declarations: [SaveIconComponent], exports: [SaveIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: SaveIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: SaveIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$20,
                    exports: DIRECTIVES$20,
                }]
        }] });

var __decorate$1$ = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
let LaptopIconComponent = class LaptopIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: LaptopIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: LaptopIconComponent, selector: "kp-laptop-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
LaptopIconComponent = __decorate$1$([
    Icon(faLaptop)
], LaptopIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: LaptopIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-laptop-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$1$ = [LaptopIconComponent];
class LaptopIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: LaptopIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: LaptopIconModule, declarations: [LaptopIconComponent], exports: [LaptopIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: LaptopIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: LaptopIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$1$,
                    exports: DIRECTIVES$1$,
                }]
        }] });

var __decorate$1_ = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
let PptIconComponent = class PptIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: PptIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: PptIconComponent, selector: "kp-ppt-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
PptIconComponent = __decorate$1_([
    Icon(faFilePowerpoint)
], PptIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: PptIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-ppt-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$1_ = [PptIconComponent];
class PptIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: PptIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: PptIconModule, declarations: [PptIconComponent], exports: [PptIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: PptIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: PptIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$1_,
                    exports: DIRECTIVES$1_,
                }]
        }] });

var __decorate$1Z = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
let PdfIconComponent = class PdfIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: PdfIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: PdfIconComponent, selector: "kp-pdf-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
PdfIconComponent = __decorate$1Z([
    Icon(faFilePdf)
], PdfIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: PdfIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-pdf-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$1Z = [PdfIconComponent];
class PdfIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: PdfIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: PdfIconModule, declarations: [PdfIconComponent], exports: [PdfIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: PdfIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: PdfIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$1Z,
                    exports: DIRECTIVES$1Z,
                }]
        }] });

var __decorate$1Y = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
let TextIconComponent = class TextIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TextIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: TextIconComponent, selector: "kp-text-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
TextIconComponent = __decorate$1Y([
    Icon(faText)
], TextIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TextIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-text-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$1Y = [TextIconComponent];
class TextIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TextIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: TextIconModule, declarations: [TextIconComponent], exports: [TextIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TextIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TextIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$1Y,
                    exports: DIRECTIVES$1Y,
                }]
        }] });

var __decorate$1X = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
let HeadingIconComponent = class HeadingIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: HeadingIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: HeadingIconComponent, selector: "kp-heading-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
HeadingIconComponent = __decorate$1X([
    Icon(faHeading)
], HeadingIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: HeadingIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-heading-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$1X = [HeadingIconComponent];
class HeadingIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: HeadingIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: HeadingIconModule, declarations: [HeadingIconComponent], exports: [HeadingIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: HeadingIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: HeadingIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$1X,
                    exports: DIRECTIVES$1X,
                }]
        }] });

var __decorate$1W = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
let ParagraphIconComponent = class ParagraphIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ParagraphIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: ParagraphIconComponent, selector: "kp-paragraph-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
ParagraphIconComponent = __decorate$1W([
    Icon(faParagraph)
], ParagraphIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ParagraphIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-paragraph-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$1W = [ParagraphIconComponent];
class ParagraphIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ParagraphIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: ParagraphIconModule, declarations: [ParagraphIconComponent], exports: [ParagraphIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ParagraphIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ParagraphIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$1W,
                    exports: DIRECTIVES$1W,
                }]
        }] });

var __decorate$1V = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
let QuoteIconComponent = class QuoteIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: QuoteIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: QuoteIconComponent, selector: "kp-quote-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
QuoteIconComponent = __decorate$1V([
    Icon(faQuoteLeft)
], QuoteIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: QuoteIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-quote-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$1V = [QuoteIconComponent];
class QuoteIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: QuoteIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: QuoteIconModule, declarations: [QuoteIconComponent], exports: [QuoteIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: QuoteIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: QuoteIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$1V,
                    exports: DIRECTIVES$1V,
                }]
        }] });

var __decorate$1U = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
let LongArrowRightIconComponent = class LongArrowRightIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: LongArrowRightIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: LongArrowRightIconComponent, selector: "kp-long-arrow-right-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
LongArrowRightIconComponent = __decorate$1U([
    Icon(faLongArrowRight)
], LongArrowRightIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: LongArrowRightIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-long-arrow-right-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$1U = [LongArrowRightIconComponent];
class LongArrowRightIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: LongArrowRightIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: LongArrowRightIconModule, declarations: [LongArrowRightIconComponent], exports: [LongArrowRightIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: LongArrowRightIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: LongArrowRightIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$1U,
                    exports: DIRECTIVES$1U,
                }]
        }] });

var __decorate$1T = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
let MinusIconComponent = class MinusIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: MinusIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: MinusIconComponent, selector: "kp-minus-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
MinusIconComponent = __decorate$1T([
    Icon(faMinus)
], MinusIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: MinusIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-minus-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$1T = [MinusIconComponent];
class MinusIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: MinusIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: MinusIconModule, declarations: [MinusIconComponent], exports: [MinusIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: MinusIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: MinusIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$1T,
                    exports: DIRECTIVES$1T,
                }]
        }] });

var __decorate$1S = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
let PhotoVideoIconComponent = class PhotoVideoIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: PhotoVideoIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: PhotoVideoIconComponent, selector: "kp-photo-video-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
PhotoVideoIconComponent = __decorate$1S([
    Icon(faPhotoVideo)
], PhotoVideoIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: PhotoVideoIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-photo-video-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$1S = [PhotoVideoIconComponent];
class PhotoVideoIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: PhotoVideoIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: PhotoVideoIconModule, declarations: [PhotoVideoIconComponent], exports: [PhotoVideoIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: PhotoVideoIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: PhotoVideoIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$1S,
                    exports: DIRECTIVES$1S,
                }]
        }] });

var __decorate$1R = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
let TaxiIconComponent = class TaxiIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TaxiIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: TaxiIconComponent, selector: "kp-taxi-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
TaxiIconComponent = __decorate$1R([
    Icon(faTaxi)
], TaxiIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TaxiIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-taxi-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$1R = [TaxiIconComponent];
class TaxiIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TaxiIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: TaxiIconModule, declarations: [TaxiIconComponent], exports: [TaxiIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TaxiIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TaxiIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$1R,
                    exports: DIRECTIVES$1R,
                }]
        }] });

var __decorate$1Q = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
let WaveformIconComponent = class WaveformIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: WaveformIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: WaveformIconComponent, selector: "kp-wave-form-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
WaveformIconComponent = __decorate$1Q([
    Icon(faWaveform)
], WaveformIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: WaveformIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-wave-form-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$1Q = [WaveformIconComponent];
class WaveformIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: WaveformIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: WaveformIconModule, declarations: [WaveformIconComponent], exports: [WaveformIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: WaveformIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: WaveformIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$1Q,
                    exports: DIRECTIVES$1Q,
                }]
        }] });

var __decorate$1P = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
let ChartBarIconComponent = class ChartBarIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ChartBarIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: ChartBarIconComponent, selector: "kp-chart-bar-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
ChartBarIconComponent = __decorate$1P([
    Icon(faChartBar)
], ChartBarIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ChartBarIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-chart-bar-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$1P = [ChartBarIconComponent];
class ChartBarIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ChartBarIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: ChartBarIconModule, declarations: [ChartBarIconComponent], exports: [ChartBarIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ChartBarIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ChartBarIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$1P,
                    exports: DIRECTIVES$1P,
                }]
        }] });

var __decorate$1O = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
let ChartAreaIconComponent = class ChartAreaIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ChartAreaIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: ChartAreaIconComponent, selector: "kp-chart-area-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
ChartAreaIconComponent = __decorate$1O([
    Icon(faChartArea)
], ChartAreaIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ChartAreaIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-chart-area-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$1O = [ChartAreaIconComponent];
class ChartAreaIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ChartAreaIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: ChartAreaIconModule, declarations: [ChartAreaIconComponent], exports: [ChartAreaIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ChartAreaIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ChartAreaIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$1O,
                    exports: DIRECTIVES$1O,
                }]
        }] });

var __decorate$1N = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
let TriangleIconComponent = class TriangleIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TriangleIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: TriangleIconComponent, selector: "kp-triangle-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
TriangleIconComponent = __decorate$1N([
    Icon(faTriangle)
], TriangleIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TriangleIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-triangle-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$1N = [TriangleIconComponent];
class TriangleIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TriangleIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: TriangleIconModule, declarations: [TriangleIconComponent], exports: [TriangleIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TriangleIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TriangleIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$1N,
                    exports: DIRECTIVES$1N,
                }]
        }] });

var __decorate$1M = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
let ShapesIconComponent = class ShapesIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ShapesIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: ShapesIconComponent, selector: "kp-shapes-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
ShapesIconComponent = __decorate$1M([
    Icon(faShapes)
], ShapesIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ShapesIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-shapes-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$1M = [ShapesIconComponent];
class ShapesIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ShapesIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: ShapesIconModule, declarations: [ShapesIconComponent], exports: [ShapesIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ShapesIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ShapesIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$1M,
                    exports: DIRECTIVES$1M,
                }]
        }] });

var __decorate$1L = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
let SquareIconComponent = class SquareIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: SquareIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: SquareIconComponent, selector: "kp-square-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
SquareIconComponent = __decorate$1L([
    Icon(faSquare)
], SquareIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: SquareIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-square-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$1L = [SquareIconComponent];
class SquareIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: SquareIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: SquareIconModule, declarations: [SquareIconComponent], exports: [SquareIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: SquareIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: SquareIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$1L,
                    exports: DIRECTIVES$1L,
                }]
        }] });

var __decorate$1K = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
let TableIconComponent = class TableIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TableIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: TableIconComponent, selector: "kp-table-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
TableIconComponent = __decorate$1K([
    Icon(faTable)
], TableIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TableIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-table-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$1K = [TableIconComponent];
class TableIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TableIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: TableIconModule, declarations: [TableIconComponent], exports: [TableIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TableIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TableIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$1K,
                    exports: DIRECTIVES$1K,
                }]
        }] });

var __decorate$1J = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
let ProjectorIconComponent = class ProjectorIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ProjectorIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: ProjectorIconComponent, selector: "kp-projector-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
ProjectorIconComponent = __decorate$1J([
    Icon(faProjector)
], ProjectorIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ProjectorIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-projector-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$1J = [ProjectorIconComponent];
class ProjectorIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ProjectorIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: ProjectorIconModule, declarations: [ProjectorIconComponent], exports: [ProjectorIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ProjectorIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ProjectorIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$1J,
                    exports: DIRECTIVES$1J,
                }]
        }] });

var __decorate$1I = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
let LevelUpIconComponent = class LevelUpIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: LevelUpIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: LevelUpIconComponent, selector: "kp-level-up-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
LevelUpIconComponent = __decorate$1I([
    Icon(faLevelUp)
], LevelUpIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: LevelUpIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-level-up-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$1I = [LevelUpIconComponent];
class LevelUpIconIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: LevelUpIconIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: LevelUpIconIconModule, declarations: [LevelUpIconComponent], exports: [LevelUpIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: LevelUpIconIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: LevelUpIconIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$1I,
                    exports: DIRECTIVES$1I,
                }]
        }] });

var __decorate$1H = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
let LevelDownIconComponent = class LevelDownIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: LevelDownIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: LevelDownIconComponent, selector: "kp-level-down-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
LevelDownIconComponent = __decorate$1H([
    Icon(faLevelDown)
], LevelDownIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: LevelDownIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-level-down-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$1H = [LevelDownIconComponent];
class LevelDownIconIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: LevelDownIconIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: LevelDownIconIconModule, declarations: [LevelDownIconComponent], exports: [LevelDownIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: LevelDownIconIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: LevelDownIconIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$1H,
                    exports: DIRECTIVES$1H,
                }]
        }] });

var __decorate$1G = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
const kpClone = defineCustomIcon('kp', {
    name: 'clone',
    height: 21,
    width: 21,
    svgPath: 'M0.87868 0.87868C0 1.75736 0 3.17157 0 6V8C0 10.8284 0 12.2426 0.87868 13.1213C1.75736 14 3.17157 14 6 14H7V15C7 17.8284 7 19.2426 7.87868 20.1213C8.75736 21 10.1716 21 13 21H15C17.8284 21 19.2426 21 20.1213 20.1213C21 19.2426 21 17.8284 21 15V13C21 10.1716 21 8.75736 20.1213 7.87868C19.2426 7 17.8284 7 15 7H14V6C14 3.17157 14 1.75736 13.1213 0.87868C12.2426 0 10.8284 0 8 0H6C3.17157 0 1.75736 0 0.87868 0.87868Z M14 7V8C14 10.8284 14 12.2426 13.1213 13.1213C12.2426 14 10.8284 14 8 14H7V13C7 10.1716 7 8.75736 7.87868 7.87868C8.75736 7 10.1716 7 13 7H14Z',
});
let CloneIconComponent = class CloneIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: CloneIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: CloneIconComponent, selector: "kp-clone-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
CloneIconComponent = __decorate$1G([
    Icon(kpClone, CustomIconStyle)
], CloneIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: CloneIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-clone-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$1G = [CloneIconComponent];
class CloneIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: CloneIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: CloneIconModule, declarations: [CloneIconComponent], exports: [CloneIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: CloneIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: CloneIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$1G,
                    exports: DIRECTIVES$1G,
                }]
        }] });

var __decorate$1F = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
let PaintBrushPencilIconComponent = class PaintBrushPencilIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: PaintBrushPencilIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: PaintBrushPencilIconComponent, selector: "kp-paintbrush-pencil-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
PaintBrushPencilIconComponent = __decorate$1F([
    Icon(faPencilPaintbrush)
], PaintBrushPencilIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: PaintBrushPencilIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-paintbrush-pencil-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$1F = [PaintBrushPencilIconComponent];
class PaintBrushPencilIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: PaintBrushPencilIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: PaintBrushPencilIconModule, declarations: [PaintBrushPencilIconComponent], exports: [PaintBrushPencilIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: PaintBrushPencilIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: PaintBrushPencilIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$1F,
                    exports: DIRECTIVES$1F,
                }]
        }] });

var __decorate$1E = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
const faFileEdit = defineCustomIcon('kp', {
    name: 'file-edit',
    height: 576,
    width: 512,
    svgPath: 'M64 464H262.5L257.4 484.2C255.1 493.6 255.7 503.2 258.8 512H64C28.65 512 0 483.3 0 448V64C0 28.65 28.65 0 64 0H229.5C246.5 0 262.7 6.743 274.7 18.75L365.3 109.3C377.3 121.3 384 137.5 384 154.5V299.6L336 347.6V160H256C238.3 160 224 145.7 224 128V48H64C55.16 48 48 55.16 48 64V448C48 456.8 55.16 464 64 464H64zM564.1 250.1C579.8 265.7 579.8 291 564.1 306.7L534.7 336.1L463.8 265.1L493.2 235.7C508.8 220.1 534.1 220.1 549.8 235.7L564.1 250.1zM311.9 416.1L441.1 287.8L512.1 358.7L382.9 487.9C378.8 492 373.6 494.9 368 496.3L307.9 511.4C302.4 512.7 296.7 511.1 292.7 507.2C288.7 503.2 287.1 497.4 288.5 491.1L303.5 431.8C304.9 426.2 307.8 421.1 311.9 416.1V416.1z',
});
let FileEditIconComponent = class FileEditIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: FileEditIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: FileEditIconComponent, selector: "kp-file-edit-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
FileEditIconComponent = __decorate$1E([
    Icon(faFileEdit, CustomIconStyle)
], FileEditIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: FileEditIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-file-edit-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$1E = [FileEditIconComponent];
class FileEditIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: FileEditIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: FileEditIconModule, declarations: [FileEditIconComponent], exports: [FileEditIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: FileEditIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: FileEditIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$1E,
                    exports: DIRECTIVES$1E,
                }]
        }] });

var __decorate$1D = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
const kpCloudUpload = defineCustomIcon('kp', {
    name: 'cloud-upload',
    height: 20,
    width: 24,
    svgPath: 'M5.75 9C5.75 5.54822 8.54822 2.75 12 2.75C14.9382 2.75 17.4045 4.7782 18.0719 7.51188C18.1301 7.75035 18.3014 7.94523 18.5304 8.03365C20.1226 8.64848 21.25 10.1935 21.25 12C21.25 13.39 20.5835 14.624 19.5496 15.4007C19.2184 15.6495 19.1516 16.1196 19.4003 16.4508C19.6491 16.782 20.1192 16.8488 20.4504 16.6C21.8454 15.5523 22.75 13.8817 22.75 12C22.75 9.69141 21.3898 7.70217 19.4293 6.78706C18.4768 3.58539 15.512 1.25 12 1.25C7.8612 1.25 4.48014 4.49432 4.26126 8.57854C2.49832 9.27243 1.25 10.9897 1.25 13C1.25 14.7591 2.20663 16.2939 3.62446 17.1141C3.983 17.3215 4.4418 17.199 4.6492 16.8404C4.85661 16.4819 4.73409 16.0231 4.37554 15.8157C3.40216 15.2526 2.75 14.202 2.75 13C2.75 11.4863 3.78548 10.2126 5.18784 9.85211C5.52413 9.76566 5.7571 9.45969 5.75099 9.11252C5.75033 9.0751 5.75 9.0376 5.75 9ZM16.0303 14.9697L12.5303 11.4697C12.2374 11.1768 11.7626 11.1768 11.4697 11.4697L7.96967 14.9697C7.67678 15.2626 7.67678 15.7374 7.96967 16.0303C8.26256 16.3232 8.73744 16.3232 9.03033 16.0303L11.25 13.8107V21C11.25 21.4142 11.5858 21.75 12 21.75C12.4142 21.75 12.75 21.4142 12.75 21V13.8107L14.9697 16.0303C15.2626 16.3232 15.7374 16.3232 16.0303 16.0303C16.3232 15.7374 16.3232 15.2626 16.0303 14.9697Z',
});
let CloudUploadIconComponent = class CloudUploadIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: CloudUploadIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: CloudUploadIconComponent, selector: "kp-cloud-upload-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
CloudUploadIconComponent = __decorate$1D([
    Icon(kpCloudUpload, CustomIconStyle)
], CloudUploadIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: CloudUploadIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-cloud-upload-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$1D = [CloudUploadIconComponent];
class CloudUploadIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: CloudUploadIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: CloudUploadIconModule, declarations: [CloudUploadIconComponent], exports: [CloudUploadIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: CloudUploadIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: CloudUploadIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$1D,
                    exports: DIRECTIVES$1D,
                }]
        }] });

var __decorate$1C = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
const kpHeartAlt = defineCustomIcon('kp', {
    name: 'heart-alt',
    height: 18,
    width: 20,
    svgPath: 'M2.7625 2.97504C4.3194 1.38275 6.88138 1.38275 8.43829 2.97504L9.46414 4.0242C9.60524 4.16852 9.79856 4.24987 10.0004 4.24987C10.2022 4.24987 10.3955 4.16852 10.5366 4.0242L11.5625 2.97504C13.1194 1.38275 15.6814 1.38275 17.2383 2.97504C18.7465 4.51752 18.7465 6.98222 17.2383 8.5247L10.1391 15.7852C10.063 15.863 9.93777 15.863 9.86167 15.7852L2.7625 8.52469C1.2543 6.98221 1.2543 4.51752 2.7625 2.97504ZM9.5108 1.92636C7.3655 -0.26769 3.83529 -0.26769 1.68999 1.92636C-0.388197 4.05178 -0.388201 7.44795 1.68999 9.57337L8.78916 16.8339C9.45366 17.5135 10.5471 17.5135 11.2116 16.8339L18.3108 9.57337C20.389 7.44795 20.389 4.05178 18.3108 1.92636C16.1655 -0.267691 12.6353 -0.267688 10.49 1.92636L10.0004 2.42709L9.5108 1.92636Z',
});
let HeartAltIconComponent = class HeartAltIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: HeartAltIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: HeartAltIconComponent, selector: "kp-heart-alt-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
HeartAltIconComponent = __decorate$1C([
    Icon(kpHeartAlt, CustomIconStyle)
], HeartAltIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: HeartAltIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-heart-alt-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$1C = [HeartAltIconComponent];
class HeartAltIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: HeartAltIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: HeartAltIconModule, declarations: [HeartAltIconComponent], exports: [HeartAltIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: HeartAltIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: HeartAltIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$1C,
                    exports: DIRECTIVES$1C,
                }]
        }] });

var __decorate$1B = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
let LinkIconComponent = class LinkIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: LinkIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: LinkIconComponent, selector: "kp-link-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
LinkIconComponent = __decorate$1B([
    Icon(faLink)
], LinkIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: LinkIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-link-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$1B = [LinkIconComponent];
class LinkIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: LinkIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: LinkIconModule, declarations: [LinkIconComponent], exports: [LinkIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: LinkIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: LinkIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$1B,
                    exports: DIRECTIVES$1B,
                }]
        }] });

var __decorate$1A = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
let TeamAltIconComponent = class TeamAltIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TeamAltIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: TeamAltIconComponent, selector: "kp-team-alt-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
TeamAltIconComponent = __decorate$1A([
    Icon(faUsers$1)
], TeamAltIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TeamAltIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-team-alt-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$1A = [TeamAltIconComponent];
class TeamAltIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TeamAltIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: TeamAltIconModule, declarations: [TeamAltIconComponent], exports: [TeamAltIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TeamAltIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TeamAltIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$1A,
                    exports: DIRECTIVES$1A,
                }]
        }] });

var __decorate$1z = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
const kpMarkdown = defineCustomIcon('kp', {
    name: 'markdown',
    height: 14,
    width: 18,
    svgPath: 'M11.4714 1.13566L7.72144 13.0601C7.59644 13.3864 7.26831 13.5663 6.94019 13.4661C6.60946 13.3428 6.42456 13.0189 6.52873 12.6951L10.2787 0.770209C10.3803 0.444748 10.7319 0.262845 11.06 0.363819C11.3907 0.464793 11.5756 0.8103 11.4714 1.13566ZM13.8022 3.38155L17.1355 6.46552C17.2631 6.58117 17.3334 6.74565 17.3334 6.89213C17.3334 7.08488 17.2631 7.22623 17.1355 7.36501L13.8022 10.449C13.5496 10.6828 13.1563 10.67 12.9194 10.4207C12.6824 10.1714 12.6954 9.78335 12.948 9.54948L15.7944 6.89213L12.948 4.28104C12.6954 4.04718 12.6824 3.65911 12.9194 3.40982C13.1563 3.16054 13.5496 3.14769 13.8022 3.38155ZM5.05216 4.28104L2.20555 6.89213L5.05216 9.54948C5.30477 9.78335 5.31779 10.1714 5.08081 10.4207C4.84383 10.67 4.4506 10.6828 4.198 10.449L0.864274 7.36501C0.738258 7.22623 0.666748 7.08488 0.666748 6.89213C0.666748 6.74565 0.738258 6.58117 0.864274 6.46552L4.198 3.38155C4.4506 3.14769 4.84383 3.16054 5.08081 3.40982C5.31779 3.65911 5.30477 4.04718 5.05216 4.28104Z',
});
let MarkdownIconComponent = class MarkdownIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: MarkdownIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: MarkdownIconComponent, selector: "kp-markdown-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
MarkdownIconComponent = __decorate$1z([
    Icon(kpMarkdown, CustomIconStyle)
], MarkdownIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: MarkdownIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-markdown-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$1z = [MarkdownIconComponent];
class MarkdownIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: MarkdownIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: MarkdownIconModule, declarations: [MarkdownIconComponent], exports: [MarkdownIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: MarkdownIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: MarkdownIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$1z,
                    exports: DIRECTIVES$1z,
                }]
        }] });

var __decorate$1y = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
const kpFlag = defineCustomIcon('kp', {
    name: 'flag',
    height: 16,
    width: 22,
    svgPath: 'M1 0.25C1.41421 0.25 1.75 0.585786 1.75 1V2.08515L3.49013 1.73712C5.19344 1.39646 6.96257 1.7029 8.45207 2.5966C10.0594 3.56103 12.0481 3.6374 13.7248 2.79909L14.6646 2.32918C14.8971 2.21293 15.1732 2.22536 15.3943 2.36201C15.6154 2.49867 15.75 2.74007 15.75 3V13C15.75 13.2841 15.5895 13.5438 15.3354 13.6708L14.3956 14.1407C12.2603 15.2084 9.72747 15.1111 7.68032 13.8828C6.5108 13.1811 5.12171 12.9405 3.7843 13.208L1.75 13.6149V21C1.75 21.4142 1.41421 21.75 1 21.75C0.585786 21.75 0.25 21.4142 0.25 21V13V3V1C0.25 0.585786 0.585786 0.25 1 0.25ZM1.75 12.0851L3.49013 11.7371C5.19344 11.3965 6.96257 11.7029 8.45207 12.5966C10.0594 13.561 12.0481 13.6374 13.7248 12.7991L14.25 12.5365V4.21144C12.1459 5.20341 9.68114 5.08333 7.68032 3.88284C6.5108 3.18112 5.12171 2.94051 3.7843 3.20799L1.75 3.61485V12.0851Z',
});
let FlagIconComponent = class FlagIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: FlagIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: FlagIconComponent, selector: "kp-flag-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
FlagIconComponent = __decorate$1y([
    Icon(kpFlag, CustomIconStyle)
], FlagIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: FlagIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-flag-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$1y = [FlagIconComponent];
class FlagIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: FlagIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: FlagIconModule, declarations: [FlagIconComponent], exports: [FlagIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: FlagIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: FlagIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$1y,
                    exports: DIRECTIVES$1y,
                }]
        }] });

var __decorate$1x = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
const kpEnvelope = defineCustomIcon('kp', {
    name: 'envelope',
    height: 16,
    width: 20,
    svgPath: 'M1.17157 1.17157C0 2.34315 0 4.22876 0 8C0 11.7712 0 13.6569 1.17157 14.8284C2.34315 16 4.22876 16 8 16H12C15.7712 16 17.6569 16 18.8284 14.8284C20 13.6569 20 11.7712 20 8C20 4.22876 20 2.34315 18.8284 1.17157C17.6569 0 15.7712 0 12 0H8C4.22876 0 2.34315 0 1.17157 1.17157ZM4.41603 3.37596C4.07138 3.1462 3.60573 3.23933 3.37596 3.58397C3.1462 3.92862 3.23933 4.39427 3.58397 4.62404L9.58397 8.62404C9.8359 8.79199 10.1641 8.79199 10.416 8.62404L16.416 4.62404C16.7607 4.39427 16.8538 3.92862 16.624 3.58397C16.3943 3.23933 15.9286 3.1462 15.584 3.37596L10 7.09861L4.41603 3.37596Z',
});
let EnvelopeIconComponent = class EnvelopeIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: EnvelopeIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: EnvelopeIconComponent, selector: "kp-envelope-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
EnvelopeIconComponent = __decorate$1x([
    Icon(kpEnvelope, CustomIconStyle)
], EnvelopeIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: EnvelopeIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-envelope-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$1x = [EnvelopeIconComponent];
class EnvelopeIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: EnvelopeIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: EnvelopeIconModule, declarations: [EnvelopeIconComponent], exports: [EnvelopeIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: EnvelopeIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: EnvelopeIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$1x,
                    exports: DIRECTIVES$1x,
                }]
        }] });

var __decorate$1w = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
const kpBellAlt = defineCustomIcon('kp', {
    name: 'bell-alt',
    height: 20,
    width: 20,
    svgPath: 'M9.99993 1.73438C6.76391 1.73438 4.03183 4.13874 3.62058 7.34852L2.81294 13.6521C2.63689 15.0261 3.70735 16.2425 5.09263 16.2425H6.18398C6.87157 17.7289 8.3605 18.6815 9.99973 18.6815C11.6389 18.6815 13.1279 17.7289 13.8155 16.2425H14.9072C16.2925 16.2425 17.363 15.0261 17.1869 13.6521L16.3793 7.34852C15.968 4.13874 13.2359 1.73438 9.99993 1.73438ZM13.35 14.7425C13.339 14.7423 13.328 14.7423 13.317 14.7425H6.68247C6.67147 14.7423 6.66045 14.7423 6.64941 14.7425H5.09263C4.61145 14.7425 4.23962 14.32 4.30077 13.8427L5.10842 7.53915C5.42376 5.07797 7.51864 3.23438 9.99993 3.23438C12.4812 3.23438 14.5761 5.07798 14.8914 7.53915L15.6991 13.8427C15.7602 14.32 15.3884 14.7425 14.9072 14.7425H13.35ZM7.95092 16.2425H12.0485C11.5428 16.8294 10.799 17.1815 9.99973 17.1815C9.20042 17.1815 8.45668 16.8294 7.95092 16.2425Z',
});
let BellAltIconComponent = class BellAltIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: BellAltIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: BellAltIconComponent, selector: "kp-bell-alt-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
BellAltIconComponent = __decorate$1w([
    Icon(kpBellAlt, CustomIconStyle)
], BellAltIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: BellAltIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-bell-alt-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$1w = [BellAltIconComponent];
class BellAltIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: BellAltIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: BellAltIconModule, declarations: [BellAltIconComponent], exports: [BellAltIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: BellAltIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: BellAltIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$1w,
                    exports: DIRECTIVES$1w,
                }]
        }] });

var __decorate$1v = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
const kpLockAlt = defineCustomIcon('kp', {
    name: 'lock-alt',
    height: 22,
    width: 18,
    svgPath: 'M8.948 0.25H9H9.052C9.95048 0.249969 10.6997 0.249945 11.2945 0.329911C11.9223 0.414316 12.4891 0.599986 12.9445 1.05546C13.4 1.51093 13.5857 2.07773 13.6701 2.70552C13.7501 3.3003 13.75 4.04951 13.75 4.94797V4.94801L13.75 5V6.38564C14.4689 6.49028 15.0875 6.68206 15.639 7.05052C16.1576 7.39707 16.6029 7.84239 16.9495 8.36104C17.3826 9.00926 17.5716 9.7503 17.6619 10.6379C17.75 11.5043 17.75 12.5892 17.75 13.9584V14V14.0416C17.75 15.4108 17.75 16.4957 17.6619 17.3621C17.5716 18.2497 17.3826 18.9907 16.9495 19.639C16.6029 20.1576 16.1576 20.6029 15.639 20.9495C14.9907 21.3826 14.2497 21.5716 13.3621 21.6619C12.4957 21.75 11.4108 21.75 10.0416 21.75H10H8H7.9584C6.5892 21.75 5.5043 21.75 4.63794 21.6619C3.7503 21.5716 3.00926 21.3826 2.36104 20.9495C1.84239 20.6029 1.39707 20.1576 1.05052 19.639C0.617393 18.9907 0.428414 18.2497 0.338116 17.3621C0.249984 16.4957 0.249991 15.4108 0.25 14.0416V14V13.9584C0.249991 12.5892 0.249984 11.5043 0.338116 10.6379C0.428414 9.7503 0.617393 9.00926 1.05052 8.36104C1.39707 7.84239 1.84239 7.39707 2.36104 7.05052C2.91248 6.68206 3.5311 6.49028 4.25 6.38564V5L4.25 4.948C4.24997 4.04952 4.24994 3.3003 4.32991 2.70552C4.41432 2.07773 4.59999 1.51093 5.05546 1.05546C5.51093 0.599986 6.07773 0.414316 6.70552 0.329911C7.3003 0.249945 8.04952 0.249969 8.948 0.25ZM12.25 5V6.27015C11.6102 6.24999 10.8791 6.24999 10.0416 6.25H10H8H7.95841C7.12092 6.24999 6.3898 6.24999 5.75 6.27015V5C5.75 4.03599 5.75159 3.38843 5.81654 2.90539C5.87858 2.44393 5.9858 2.24643 6.11612 2.11612C6.24643 1.9858 6.44393 1.87858 6.90539 1.81654C7.38843 1.75159 8.03599 1.75 9 1.75C9.96401 1.75 10.6116 1.75159 11.0946 1.81654C11.5561 1.87858 11.7536 1.9858 11.8839 2.11612C12.0142 2.24643 12.1214 2.44393 12.1835 2.90539C12.2484 3.38843 12.25 4.03599 12.25 5ZM4.78975 7.83041C4.02071 7.90865 3.55507 8.05673 3.1944 8.29772C2.83953 8.53484 2.53484 8.83953 2.29772 9.1944C2.05673 9.55507 1.90865 10.0207 1.83041 10.7898C1.75091 11.5713 1.75 12.5799 1.75 14C1.75 15.4201 1.75091 16.4287 1.83041 17.2102C1.90865 17.9793 2.05673 18.4449 2.29772 18.8056C2.53484 19.1605 2.83953 19.4652 3.1944 19.7023C3.55507 19.9433 4.02071 20.0914 4.78975 20.1696C5.57133 20.2491 6.57993 20.25 8 20.25H10C11.4201 20.25 12.4287 20.2491 13.2102 20.1696C13.9793 20.0914 14.4449 19.9433 14.8056 19.7023C15.1605 19.4652 15.4652 19.1605 15.7023 18.8056C15.9433 18.4449 16.0914 17.9793 16.1696 17.2102C16.2491 16.4287 16.25 15.4201 16.25 14C16.25 12.5799 16.2491 11.5713 16.1696 10.7898C16.0914 10.0207 15.9433 9.55507 15.7023 9.1944C15.4652 8.83953 15.1605 8.53484 14.8056 8.29772C14.4449 8.05673 13.9793 7.90865 13.2102 7.83041C12.4287 7.75091 11.4201 7.75 10 7.75H8C6.57993 7.75 5.57133 7.75091 4.78975 7.83041ZM9.75 13C9.75 12.5858 9.41421 12.25 9 12.25C8.58579 12.25 8.25 12.5858 8.25 13V15C8.25 15.4142 8.58579 15.75 9 15.75C9.41421 15.75 9.75 15.4142 9.75 15V13Z',
});
let LockAltIconComponent = class LockAltIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: LockAltIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: LockAltIconComponent, selector: "kp-lock-alt-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
LockAltIconComponent = __decorate$1v([
    Icon(kpLockAlt, CustomIconStyle)
], LockAltIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: LockAltIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-lock-alt-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$1v = [LockAltIconComponent];
class LockAltIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: LockAltIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: LockAltIconModule, declarations: [LockAltIconComponent], exports: [LockAltIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: LockAltIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: LockAltIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$1v,
                    exports: DIRECTIVES$1v,
                }]
        }] });

var __decorate$1u = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
let FolderAltIconComponent = class FolderAltIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: FolderAltIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: FolderAltIconComponent, selector: "kp-folder-alt-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
FolderAltIconComponent = __decorate$1u([
    Icon(faFolder)
], FolderAltIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: FolderAltIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-folder-alt-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$1u = [FolderAltIconComponent];
class FolderAltIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: FolderAltIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: FolderAltIconModule, declarations: [FolderAltIconComponent], exports: [FolderAltIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: FolderAltIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: FolderAltIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$1u,
                    exports: DIRECTIVES$1u,
                }]
        }] });

var __decorate$1t = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
let FolderTimesIconComponent = class FolderTimesIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: FolderTimesIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: FolderTimesIconComponent, selector: "kp-folder-times-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
FolderTimesIconComponent = __decorate$1t([
    Icon(faFolderTimes)
], FolderTimesIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: FolderTimesIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-folder-times-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$1t = [FolderTimesIconComponent];
class FolderTimesIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: FolderTimesIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: FolderTimesIconModule, declarations: [FolderTimesIconComponent], exports: [FolderTimesIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: FolderTimesIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: FolderTimesIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$1t,
                    exports: DIRECTIVES$1t,
                }]
        }] });

var __decorate$1s = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
let CheckCircleAltIconComponent = class CheckCircleAltIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: CheckCircleAltIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: CheckCircleAltIconComponent, selector: "kp-check-circle-alt-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
CheckCircleAltIconComponent = __decorate$1s([
    Icon(faCheckCircle$1)
], CheckCircleAltIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: CheckCircleAltIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-check-circle-alt-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$1s = [CheckCircleAltIconComponent];
class CheckCircleAltIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: CheckCircleAltIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: CheckCircleAltIconModule, declarations: [CheckCircleAltIconComponent], exports: [CheckCircleAltIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: CheckCircleAltIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: CheckCircleAltIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$1s,
                    exports: DIRECTIVES$1s,
                }]
        }] });

var __decorate$1r = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
let UserPlusIconComponent = class UserPlusIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: UserPlusIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: UserPlusIconComponent, selector: "kp-user-plus-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
UserPlusIconComponent = __decorate$1r([
    Icon(faUserPlus)
], UserPlusIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: UserPlusIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-user-plus-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$1r = [UserPlusIconComponent];
class UserPlusIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: UserPlusIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: UserPlusIconModule, declarations: [UserPlusIconComponent], exports: [UserPlusIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: UserPlusIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: UserPlusIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$1r,
                    exports: DIRECTIVES$1r,
                }]
        }] });

var __decorate$1q = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
let TimelineIconComponent = class TimelineIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TimelineIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: TimelineIconComponent, selector: "kp-timeline-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
TimelineIconComponent = __decorate$1q([
    Icon(faTimeline)
], TimelineIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TimelineIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-timeline-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$1q = [TimelineIconComponent];
class TimelineIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TimelineIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: TimelineIconModule, declarations: [TimelineIconComponent], exports: [TimelineIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TimelineIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TimelineIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$1q,
                    exports: DIRECTIVES$1q,
                }]
        }] });

var __decorate$1p = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
let DiagramVennIconComponent = class DiagramVennIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: DiagramVennIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: DiagramVennIconComponent, selector: "kp-diagram-venn-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
DiagramVennIconComponent = __decorate$1p([
    Icon(faDiagramVenn)
], DiagramVennIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: DiagramVennIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-diagram-venn-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$1p = [DiagramVennIconComponent];
class DiagramVennIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: DiagramVennIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: DiagramVennIconModule, declarations: [DiagramVennIconComponent], exports: [DiagramVennIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: DiagramVennIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: DiagramVennIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$1p,
                    exports: DIRECTIVES$1p,
                }]
        }] });

var __decorate$1o = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
let TallyIconComponent = class TallyIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TallyIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: TallyIconComponent, selector: "kp-tally-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
TallyIconComponent = __decorate$1o([
    Icon(faTally)
], TallyIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TallyIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-tally-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$1o = [TallyIconComponent];
class TallyIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TallyIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: TallyIconModule, declarations: [TallyIconComponent], exports: [TallyIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TallyIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TallyIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$1o,
                    exports: DIRECTIVES$1o,
                }]
        }] });

var __decorate$1n = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
let DotCircleIconComponent = class DotCircleIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: DotCircleIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: DotCircleIconComponent, selector: "kp-dot-circle-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
DotCircleIconComponent = __decorate$1n([
    Icon(faDotCircle)
], DotCircleIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: DotCircleIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-dot-circle-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$1n = [DotCircleIconComponent];
class DotCircleIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: DotCircleIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: DotCircleIconModule, declarations: [DotCircleIconComponent], exports: [DotCircleIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: DotCircleIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: DotCircleIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$1n,
                    exports: DIRECTIVES$1n,
                }]
        }] });

var __decorate$1m = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
let FilterAltIconComponent = class FilterAltIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: FilterAltIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: FilterAltIconComponent, selector: "kp-filter-alt-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
FilterAltIconComponent = __decorate$1m([
    Icon(faFilter)
], FilterAltIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: FilterAltIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-filter-alt-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$1m = [FilterAltIconComponent];
class FilterAltIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: FilterAltIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: FilterAltIconModule, declarations: [FilterAltIconComponent], exports: [FilterAltIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: FilterAltIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: FilterAltIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$1m,
                    exports: DIRECTIVES$1m,
                }]
        }] });

var __decorate$1l = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
const kpGoogleDrive = defineCustomIcon('kp', {
    name: 'google-drive',
    height: 512,
    width: 512,
    svgPath: 'M339 314.9L175.4 32h161.2l163.6 282.9H339zm-137.5 23.6L120.9 480h310.5L512 338.5H201.5zM154.1 67.4L0 338.5 80.6 480 237 208.8 154.1 67.4z',
});
let GoogleDriveIconComponent = class GoogleDriveIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: GoogleDriveIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: GoogleDriveIconComponent, selector: "kp-google-drive-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
GoogleDriveIconComponent = __decorate$1l([
    Icon(kpGoogleDrive, CustomIconStyle)
], GoogleDriveIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: GoogleDriveIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-google-drive-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$1l = [GoogleDriveIconComponent];
class GoogleDriveIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: GoogleDriveIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: GoogleDriveIconModule, declarations: [GoogleDriveIconComponent], exports: [GoogleDriveIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: GoogleDriveIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: GoogleDriveIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$1l,
                    exports: DIRECTIVES$1l,
                }]
        }] });

var __decorate$1k = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
let CircleDashedIconComponent = class CircleDashedIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: CircleDashedIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: CircleDashedIconComponent, selector: "kp-circle-dashed-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
CircleDashedIconComponent = __decorate$1k([
    Icon(faCircleDashed)
], CircleDashedIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: CircleDashedIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-circle-dashed-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$1k = [CircleDashedIconComponent];
class CircleDashedIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: CircleDashedIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: CircleDashedIconModule, declarations: [CircleDashedIconComponent], exports: [CircleDashedIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: CircleDashedIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: CircleDashedIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$1k,
                    exports: DIRECTIVES$1k,
                }]
        }] });

var __decorate$1j = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
let PauseIconComponent = class PauseIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: PauseIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: PauseIconComponent, selector: "kp-pause-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
PauseIconComponent = __decorate$1j([
    Icon(faPause)
], PauseIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: PauseIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-pause-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$1j = [PauseIconComponent];
class PauseIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: PauseIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: PauseIconModule, declarations: [PauseIconComponent], exports: [PauseIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: PauseIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: PauseIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$1j,
                    exports: DIRECTIVES$1j,
                }]
        }] });

var __decorate$1i = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
let PersonChalkboardIconComponent = class PersonChalkboardIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: PersonChalkboardIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: PersonChalkboardIconComponent, selector: "kp-person-chalkboard-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
PersonChalkboardIconComponent = __decorate$1i([
    Icon(faPersonChalkboard)
], PersonChalkboardIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: PersonChalkboardIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-person-chalkboard-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$1i = [PersonChalkboardIconComponent];
class PersonChalkboardIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: PersonChalkboardIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: PersonChalkboardIconModule, declarations: [PersonChalkboardIconComponent], exports: [PersonChalkboardIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: PersonChalkboardIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: PersonChalkboardIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$1i,
                    exports: DIRECTIVES$1i,
                }]
        }] });

var __decorate$1h = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
let ExchangeAltIconComponent = class ExchangeAltIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ExchangeAltIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: ExchangeAltIconComponent, selector: "kp-exchange-alt-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
ExchangeAltIconComponent = __decorate$1h([
    Icon(faExchangeAlt)
], ExchangeAltIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ExchangeAltIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-exchange-alt-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$1h = [ExchangeAltIconComponent];
class ExchangeAltIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ExchangeAltIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: ExchangeAltIconModule, declarations: [ExchangeAltIconComponent], exports: [ExchangeAltIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ExchangeAltIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ExchangeAltIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$1h,
                    exports: DIRECTIVES$1h,
                }]
        }] });

var __decorate$1g = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
let MagicSparklesIconComponent = class MagicSparklesIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: MagicSparklesIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: MagicSparklesIconComponent, selector: "kp-magic-sparkles-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
MagicSparklesIconComponent = __decorate$1g([
    Icon(faMagicWandSparkles)
], MagicSparklesIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: MagicSparklesIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-magic-sparkles-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$1g = [MagicSparklesIconComponent];
class MagicSparklesIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: MagicSparklesIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: MagicSparklesIconModule, declarations: [MagicSparklesIconComponent], exports: [MagicSparklesIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: MagicSparklesIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: MagicSparklesIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$1g,
                    exports: DIRECTIVES$1g,
                }]
        }] });

var __decorate$1f = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
let PaletteIconComponent = class PaletteIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: PaletteIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: PaletteIconComponent, selector: "kp-palette-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
PaletteIconComponent = __decorate$1f([
    Icon(faPalette)
], PaletteIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: PaletteIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-palette-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$1f = [PaletteIconComponent];
class PaletteIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: PaletteIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: PaletteIconModule, declarations: [PaletteIconComponent], exports: [PaletteIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: PaletteIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: PaletteIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$1f,
                    exports: DIRECTIVES$1f,
                }]
        }] });

var __decorate$1e = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
let TextHeightIconComponent = class TextHeightIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TextHeightIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: TextHeightIconComponent, selector: "kp-text-height-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
TextHeightIconComponent = __decorate$1e([
    Icon(faTextHeight)
], TextHeightIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TextHeightIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-text-height-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$1e = [TextHeightIconComponent];
class TextHeightIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TextHeightIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: TextHeightIconModule, declarations: [TextHeightIconComponent], exports: [TextHeightIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TextHeightIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TextHeightIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$1e,
                    exports: DIRECTIVES$1e,
                }]
        }] });

var __decorate$1d = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
let AlignLeftIconComponent = class AlignLeftIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: AlignLeftIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: AlignLeftIconComponent, selector: "kp-align-left-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
AlignLeftIconComponent = __decorate$1d([
    Icon(faAlignLeft)
], AlignLeftIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: AlignLeftIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-align-left-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$1d = [AlignLeftIconComponent];
class AlignLeftIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: AlignLeftIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: AlignLeftIconModule, declarations: [AlignLeftIconComponent], exports: [AlignLeftIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: AlignLeftIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: AlignLeftIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$1d,
                    exports: DIRECTIVES$1d,
                }]
        }] });

var __decorate$1c = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
let AlignCenterIconComponent = class AlignCenterIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: AlignCenterIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: AlignCenterIconComponent, selector: "kp-align-center-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
AlignCenterIconComponent = __decorate$1c([
    Icon(faAlignCenter)
], AlignCenterIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: AlignCenterIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-align-center-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$1c = [AlignCenterIconComponent];
class AlignCenterIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: AlignCenterIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: AlignCenterIconModule, declarations: [AlignCenterIconComponent], exports: [AlignCenterIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: AlignCenterIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: AlignCenterIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$1c,
                    exports: DIRECTIVES$1c,
                }]
        }] });

var __decorate$1b = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
let AlignRightIconComponent = class AlignRightIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: AlignRightIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: AlignRightIconComponent, selector: "kp-align-right-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
AlignRightIconComponent = __decorate$1b([
    Icon(faAlignRight)
], AlignRightIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: AlignRightIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-align-right-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$1b = [AlignRightIconComponent];
class AlignRightIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: AlignRightIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: AlignRightIconModule, declarations: [AlignRightIconComponent], exports: [AlignRightIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: AlignRightIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: AlignRightIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$1b,
                    exports: DIRECTIVES$1b,
                }]
        }] });

var __decorate$1a = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
let FillDripIconComponent = class FillDripIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: FillDripIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: FillDripIconComponent, selector: "kp-fill-drip-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
FillDripIconComponent = __decorate$1a([
    Icon(faFillDrip)
], FillDripIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: FillDripIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-fill-drip-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$1a = [FillDripIconComponent];
class FillDripIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: FillDripIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: FillDripIconModule, declarations: [FillDripIconComponent], exports: [FillDripIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: FillDripIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: FillDripIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$1a,
                    exports: DIRECTIVES$1a,
                }]
        }] });

var __decorate$19 = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
let EmptySetIconComponent = class EmptySetIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: EmptySetIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: EmptySetIconComponent, selector: "kp-empty-set-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
EmptySetIconComponent = __decorate$19([
    Icon(faEmptySet)
], EmptySetIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: EmptySetIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-empty-set-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$19 = [EmptySetIconComponent];
class EmptySetIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: EmptySetIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: EmptySetIconModule, declarations: [EmptySetIconComponent], exports: [EmptySetIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: EmptySetIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: EmptySetIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$19,
                    exports: DIRECTIVES$19,
                }]
        }] });

var __decorate$18 = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
let BoldIconComponent = class BoldIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: BoldIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: BoldIconComponent, selector: "kp-bold-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
BoldIconComponent = __decorate$18([
    Icon(faBold)
], BoldIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: BoldIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-bold-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$18 = [BoldIconComponent];
class BoldIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: BoldIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: BoldIconModule, declarations: [BoldIconComponent], exports: [BoldIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: BoldIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: BoldIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$18,
                    exports: DIRECTIVES$18,
                }]
        }] });

var __decorate$17 = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
let ItalicIconComponent = class ItalicIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ItalicIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: ItalicIconComponent, selector: "kp-italic-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
ItalicIconComponent = __decorate$17([
    Icon(faItalic)
], ItalicIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ItalicIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-italic-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$17 = [ItalicIconComponent];
class ItalicIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ItalicIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: ItalicIconModule, declarations: [ItalicIconComponent], exports: [ItalicIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ItalicIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ItalicIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$17,
                    exports: DIRECTIVES$17,
                }]
        }] });

var __decorate$16 = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
let UnderlineIconComponent = class UnderlineIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: UnderlineIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: UnderlineIconComponent, selector: "kp-underline-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
UnderlineIconComponent = __decorate$16([
    Icon(faUnderline)
], UnderlineIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: UnderlineIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-underline-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$16 = [UnderlineIconComponent];
class UnderlineIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: UnderlineIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: UnderlineIconModule, declarations: [UnderlineIconComponent], exports: [UnderlineIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: UnderlineIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: UnderlineIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$16,
                    exports: DIRECTIVES$16,
                }]
        }] });

var __decorate$15 = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
let StrikethroughIconComponent = class StrikethroughIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: StrikethroughIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: StrikethroughIconComponent, selector: "kp-strikethrough-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
StrikethroughIconComponent = __decorate$15([
    Icon(faStrikethrough)
], StrikethroughIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: StrikethroughIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-strikethrough-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$15 = [StrikethroughIconComponent];
class StrikethroughIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: StrikethroughIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: StrikethroughIconModule, declarations: [StrikethroughIconComponent], exports: [StrikethroughIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: StrikethroughIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: StrikethroughIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$15,
                    exports: DIRECTIVES$15,
                }]
        }] });

var __decorate$14 = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
let SuperscriptIconComponent = class SuperscriptIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: SuperscriptIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: SuperscriptIconComponent, selector: "kp-superscript-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
SuperscriptIconComponent = __decorate$14([
    Icon(faSuperscript)
], SuperscriptIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: SuperscriptIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-superscript-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$14 = [SuperscriptIconComponent];
class SuperscriptIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: SuperscriptIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: SuperscriptIconModule, declarations: [SuperscriptIconComponent], exports: [SuperscriptIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: SuperscriptIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: SuperscriptIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$14,
                    exports: DIRECTIVES$14,
                }]
        }] });

var __decorate$13 = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
let SubscriptIconComponent = class SubscriptIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: SubscriptIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: SubscriptIconComponent, selector: "kp-subscript-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
SubscriptIconComponent = __decorate$13([
    Icon(faSubscript)
], SubscriptIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: SubscriptIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-subscript-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$13 = [SubscriptIconComponent];
class SubscriptIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: SubscriptIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: SubscriptIconModule, declarations: [SubscriptIconComponent], exports: [SubscriptIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: SubscriptIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: SubscriptIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$13,
                    exports: DIRECTIVES$13,
                }]
        }] });

var __decorate$12 = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
let ListUlIconComponent = class ListUlIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ListUlIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: ListUlIconComponent, selector: "kp-list-ul-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
ListUlIconComponent = __decorate$12([
    Icon(faListUl)
], ListUlIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ListUlIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-list-ul-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$12 = [ListUlIconComponent];
class ListUlIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ListUlIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: ListUlIconModule, declarations: [ListUlIconComponent], exports: [ListUlIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ListUlIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ListUlIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$12,
                    exports: DIRECTIVES$12,
                }]
        }] });

var __decorate$11 = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
let ListOlIconComponent = class ListOlIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ListOlIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: ListOlIconComponent, selector: "kp-list-ol-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
ListOlIconComponent = __decorate$11([
    Icon(faListOl)
], ListOlIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ListOlIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-list-ol-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$11 = [ListOlIconComponent];
class ListOlIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ListOlIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: ListOlIconModule, declarations: [ListOlIconComponent], exports: [ListOlIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ListOlIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ListOlIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$11,
                    exports: DIRECTIVES$11,
                }]
        }] });

var __decorate$10 = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
let UnlinkIconComponent = class UnlinkIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: UnlinkIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: UnlinkIconComponent, selector: "kp-unlink-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
UnlinkIconComponent = __decorate$10([
    Icon(faUnlink)
], UnlinkIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: UnlinkIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-unlink-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$10 = [UnlinkIconComponent];
class UnlinkIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: UnlinkIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: UnlinkIconModule, declarations: [UnlinkIconComponent], exports: [UnlinkIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: UnlinkIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: UnlinkIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$10,
                    exports: DIRECTIVES$10,
                }]
        }] });

var __decorate$$ = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
let TextSlashIconComponent = class TextSlashIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TextSlashIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: TextSlashIconComponent, selector: "kp-text-slash-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
TextSlashIconComponent = __decorate$$([
    Icon(faTextSlash)
], TextSlashIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TextSlashIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-text-slash-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$$ = [TextSlashIconComponent];
class TextSlashIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TextSlashIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: TextSlashIconModule, declarations: [TextSlashIconComponent], exports: [TextSlashIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TextSlashIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TextSlashIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$$,
                    exports: DIRECTIVES$$,
                }]
        }] });

var __decorate$_ = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
let FaceLaughWinkIconComponent = class FaceLaughWinkIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: FaceLaughWinkIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: FaceLaughWinkIconComponent, selector: "kp-face-laugh-wink-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
FaceLaughWinkIconComponent = __decorate$_([
    Icon(faFaceLaughWink)
], FaceLaughWinkIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: FaceLaughWinkIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-face-laugh-wink-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$_ = [FaceLaughWinkIconComponent];
class FaceLaughWinkIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: FaceLaughWinkIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: FaceLaughWinkIconModule, declarations: [FaceLaughWinkIconComponent], exports: [FaceLaughWinkIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: FaceLaughWinkIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: FaceLaughWinkIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$_,
                    exports: DIRECTIVES$_,
                }]
        }] });

var __decorate$Z = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
let BringFrontIconComponent = class BringFrontIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: BringFrontIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: BringFrontIconComponent, selector: "kp-bring-front-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
BringFrontIconComponent = __decorate$Z([
    Icon(faBringFront)
], BringFrontIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: BringFrontIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-bring-front-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$Z = [BringFrontIconComponent];
class BringFrontIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: BringFrontIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: BringFrontIconModule, declarations: [BringFrontIconComponent], exports: [BringFrontIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: BringFrontIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: BringFrontIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$Z,
                    exports: DIRECTIVES$Z,
                }]
        }] });

var __decorate$Y = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
let SendBackIconComponent = class SendBackIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: SendBackIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: SendBackIconComponent, selector: "kp-send-back-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
SendBackIconComponent = __decorate$Y([
    Icon(faSendBack)
], SendBackIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: SendBackIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-send-back-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$Y = [SendBackIconComponent];
class SendBackIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: SendBackIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: SendBackIconModule, declarations: [SendBackIconComponent], exports: [SendBackIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: SendBackIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: SendBackIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$Y,
                    exports: DIRECTIVES$Y,
                }]
        }] });

var __decorate$X = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
const kpCurvedLine = defineCustomIcon('kp', {
    name: 'curved-line',
    height: 24,
    width: 24,
    svgPath: 'M23.3412 8.36533L17.1708 14.1244C16.0302 15.1889 14.2572 15.179 13.1286 14.1017L10.1035 11.2141C9.38378 10.5271 8.26157 10.4942 7.50289 11.1379L1.32349 16.3811L0.676514 15.6186L6.85592 10.3754C8.00476 9.40064 9.70412 9.45039 10.794 10.4907L13.8191 13.3783C14.5644 14.0898 15.7352 14.0963 16.4884 13.3933L22.6588 7.63428L23.3412 8.36533Z',
});
let CurvedLineIconComponent = class CurvedLineIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: CurvedLineIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: CurvedLineIconComponent, selector: "kp-curved-line-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
CurvedLineIconComponent = __decorate$X([
    Icon(kpCurvedLine, CustomIconStyle)
], CurvedLineIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: CurvedLineIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-curved-line-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$X = [CurvedLineIconComponent];
class CurvedLineIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: CurvedLineIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: CurvedLineIconModule, declarations: [CurvedLineIconComponent], exports: [CurvedLineIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: CurvedLineIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: CurvedLineIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$X,
                    exports: DIRECTIVES$X,
                }]
        }] });

var __decorate$W = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
const kpStraightLine = defineCustomIcon('kp', {
    name: 'straight-line',
    height: 24,
    width: 24,
    svgPath: 'M23.3235 8.38116L15.1429 15.3223L8.85715 9.98897L1.32349 16.3812L0.676514 15.6187L8.85715 8.67751L15.1429 14.0108L22.6765 7.61865L23.3235 8.38116Z',
});
let StraightLineIconComponent = class StraightLineIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: StraightLineIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: StraightLineIconComponent, selector: "kp-straight-line-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
StraightLineIconComponent = __decorate$W([
    Icon(kpStraightLine, CustomIconStyle)
], StraightLineIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: StraightLineIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-straight-line-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$W = [StraightLineIconComponent];
class StraightLineIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: StraightLineIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: StraightLineIconModule, declarations: [StraightLineIconComponent], exports: [StraightLineIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: StraightLineIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: StraightLineIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$W,
                    exports: DIRECTIVES$W,
                }]
        }] });

var __decorate$V = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
const kpDiagonalSolidRegularLine = defineCustomIcon('kp', {
    name: 'diagonal-solid-regular-line',
    height: 24,
    width: 24,
    svgPath: 'M21.3847 2.99994L2.99994 21.3847L1.23218 19.617L19.617 1.23218L21.3847 2.99994Z',
});
let DiagonalSolidRegularLineIconComponent = class DiagonalSolidRegularLineIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: DiagonalSolidRegularLineIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: DiagonalSolidRegularLineIconComponent, selector: "kp-diagonal-solid-regular-line-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
DiagonalSolidRegularLineIconComponent = __decorate$V([
    Icon(kpDiagonalSolidRegularLine, CustomIconStyle)
], DiagonalSolidRegularLineIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: DiagonalSolidRegularLineIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-diagonal-solid-regular-line-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$V = [DiagonalSolidRegularLineIconComponent];
class DiagonalSolidRegularLineIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: DiagonalSolidRegularLineIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: DiagonalSolidRegularLineIconModule, declarations: [DiagonalSolidRegularLineIconComponent], exports: [DiagonalSolidRegularLineIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: DiagonalSolidRegularLineIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: DiagonalSolidRegularLineIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$V,
                    exports: DIRECTIVES$V,
                }]
        }] });

var __decorate$U = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
const kpDiagonalDashedRegularLine = defineCustomIcon('kp', {
    name: 'diagonal-dashed-regular-line',
    height: 24,
    width: 24,
    svgPath: 'M4.87842 17.8596L5.58553 17.1524L6.29263 17.8596L5.58553 18.5667L4.87842 17.8596Z M1.99976 20.7382L2.70686 20.0311L3.41397 20.7382L2.70686 21.4453L1.99976 20.7382Z M7.70685 15.0311L8.41395 14.324L9.12106 15.0311L8.41395 15.7382L7.70685 15.0311Z M10.5353 12.2027L11.2424 11.4956L11.9495 12.2027L11.2424 12.9098L10.5353 12.2027Z M13.3637 9.37427L14.0708 8.66716L14.7779 9.37427L14.0708 10.0814L13.3637 9.37427Z M16.1921 6.54584L16.8992 5.83874L17.6063 6.54584L16.8992 7.25295L16.1921 6.54584Z M18.9703 3.76765L19.6774 3.06055L20.3845 3.76765L19.6774 4.47476L18.9703 3.76765Z',
});
let DiagonalDashedRegularLineIconComponent = class DiagonalDashedRegularLineIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: DiagonalDashedRegularLineIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: DiagonalDashedRegularLineIconComponent, selector: "kp-diagonal-dashed-regular-line-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
DiagonalDashedRegularLineIconComponent = __decorate$U([
    Icon(kpDiagonalDashedRegularLine, CustomIconStyle)
], DiagonalDashedRegularLineIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: DiagonalDashedRegularLineIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-diagonal-dashed-regular-line-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$U = [DiagonalDashedRegularLineIconComponent];
class DiagonalDashedRegularLineIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: DiagonalDashedRegularLineIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: DiagonalDashedRegularLineIconModule, declarations: [DiagonalDashedRegularLineIconComponent], exports: [DiagonalDashedRegularLineIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: DiagonalDashedRegularLineIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: DiagonalDashedRegularLineIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$U,
                    exports: DIRECTIVES$U,
                }]
        }] });

var __decorate$T = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
const kpDiagonalSolidThinLine = defineCustomIcon('kp', {
    name: 'diagonal-solid-thin-line',
    height: 24,
    width: 24,
    svgPath: 'M21.3849 3.00008L3.00008 21.3849L2.29297 20.6777L20.6777 2.29297L21.3849 3.00008Z',
});
let DiagonalSolidThinLineIconComponent = class DiagonalSolidThinLineIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: DiagonalSolidThinLineIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: DiagonalSolidThinLineIconComponent, selector: "kp-diagonal-solid-thin-line-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
DiagonalSolidThinLineIconComponent = __decorate$T([
    Icon(kpDiagonalSolidThinLine, CustomIconStyle)
], DiagonalSolidThinLineIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: DiagonalSolidThinLineIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-diagonal-solid-thin-line-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$T = [DiagonalSolidThinLineIconComponent];
class DiagonalSolidThinLineIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: DiagonalSolidThinLineIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: DiagonalSolidThinLineIconModule, declarations: [DiagonalSolidThinLineIconComponent], exports: [DiagonalSolidThinLineIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: DiagonalSolidThinLineIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: DiagonalSolidThinLineIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$T,
                    exports: DIRECTIVES$T,
                }]
        }] });

var __decorate$S = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
const kpDiagonalSolidThickLine = defineCustomIcon('kp', {
    name: 'diagonal-solid-thick-line',
    height: 24,
    width: 24,
    svgPath: 'M21.3848 3.00006L3.00006 21.3848L0.171631 18.5564L18.5564 0.171631L21.3848 3.00006Z',
});
let DiagonalSolidThickLineIconComponent = class DiagonalSolidThickLineIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: DiagonalSolidThickLineIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: DiagonalSolidThickLineIconComponent, selector: "kp-diagonal-solid-thick-line-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
DiagonalSolidThickLineIconComponent = __decorate$S([
    Icon(kpDiagonalSolidThickLine, CustomIconStyle)
], DiagonalSolidThickLineIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: DiagonalSolidThickLineIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-diagonal-solid-thick-line-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$S = [DiagonalSolidThickLineIconComponent];
class DiagonalSolidThickLineIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: DiagonalSolidThickLineIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: DiagonalSolidThickLineIconModule, declarations: [DiagonalSolidThickLineIconComponent], exports: [DiagonalSolidThickLineIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: DiagonalSolidThickLineIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: DiagonalSolidThickLineIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$S,
                    exports: DIRECTIVES$S,
                }]
        }] });

var __decorate$R = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
const kpTriangleArrowRight = defineCustomIcon('kp', {
    name: 'triangle-arrow-right',
    height: 24,
    width: 24,
    svgPath: 'M12 17.7018L23 11.351L12 5.00012L12 10.5362L0.999999 10.5362L0.999999 12.1658L12 12.1658L12 17.7018Z',
});
let TriangleArrowRightIconComponent = class TriangleArrowRightIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TriangleArrowRightIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: TriangleArrowRightIconComponent, selector: "kp-triangle-arrow-right-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
TriangleArrowRightIconComponent = __decorate$R([
    Icon(kpTriangleArrowRight, CustomIconStyle)
], TriangleArrowRightIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TriangleArrowRightIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-triangle-arrow-right-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$R = [TriangleArrowRightIconComponent];
class TriangleArrowRightIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TriangleArrowRightIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: TriangleArrowRightIconModule, declarations: [TriangleArrowRightIconComponent], exports: [TriangleArrowRightIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TriangleArrowRightIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TriangleArrowRightIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$R,
                    exports: DIRECTIVES$R,
                }]
        }] });

var __decorate$Q = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
const kpTriangleArrowLeft = defineCustomIcon('kp', {
    name: 'triangle-arrow-left',
    height: 24,
    width: 24,
    svgPath: 'M12 6.29817L1 12.649L12 18.9999L12 13.4638L23 13.4638L23 11.8342L12 11.8342L12 6.29817Z',
});
let TriangleArrowLeftIconComponent = class TriangleArrowLeftIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TriangleArrowLeftIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: TriangleArrowLeftIconComponent, selector: "kp-triangle-arrow-left-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
TriangleArrowLeftIconComponent = __decorate$Q([
    Icon(kpTriangleArrowLeft, CustomIconStyle)
], TriangleArrowLeftIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TriangleArrowLeftIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-triangle-arrow-left-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$Q = [TriangleArrowLeftIconComponent];
class TriangleArrowLeftIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TriangleArrowLeftIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: TriangleArrowLeftIconModule, declarations: [TriangleArrowLeftIconComponent], exports: [TriangleArrowLeftIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TriangleArrowLeftIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TriangleArrowLeftIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$Q,
                    exports: DIRECTIVES$Q,
                }]
        }] });

var __decorate$P = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
const kpLineCirclePointerRight = defineCustomIcon('kp', {
    name: 'line-circle-pointer-right',
    height: 24,
    width: 24,
    svgPath: 'M11.2138 12.7692L1 12.7692V11.0769L11.2138 11.0769C11.6244 8.20652 14.093 6 17.0769 6C20.3482 6 23 8.65185 23 11.9231C23 15.1943 20.3481 17.8462 17.0769 17.8462C14.093 17.8462 11.6244 15.6396 11.2138 12.7692Z',
});
let LineCirclePointerRightIconComponent = class LineCirclePointerRightIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: LineCirclePointerRightIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: LineCirclePointerRightIconComponent, selector: "kp-line-circle-pointer-right-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
LineCirclePointerRightIconComponent = __decorate$P([
    Icon(kpLineCirclePointerRight, CustomIconStyle)
], LineCirclePointerRightIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: LineCirclePointerRightIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-line-circle-pointer-right-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$P = [LineCirclePointerRightIconComponent];
class LineCirclePointerRightIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: LineCirclePointerRightIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: LineCirclePointerRightIconModule, declarations: [LineCirclePointerRightIconComponent], exports: [LineCirclePointerRightIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: LineCirclePointerRightIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: LineCirclePointerRightIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$P,
                    exports: DIRECTIVES$P,
                }]
        }] });

var __decorate$O = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
const kpLineCirclePointerLeft = defineCustomIcon('kp', {
    name: 'line-circle-pointer-left',
    height: 24,
    width: 24,
    svgPath: 'M12.7862 11.2308L23 11.2308L23 12.9231L12.7862 12.9231C12.3756 15.7935 9.90701 18 6.92307 18C3.65185 18 1 15.3481 1 12.0769C1 8.8057 3.65185 6.15384 6.92308 6.15385C9.90701 6.15385 12.3756 8.36037 12.7862 11.2308Z',
});
let LineCirclePointerLeftIconComponent = class LineCirclePointerLeftIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: LineCirclePointerLeftIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: LineCirclePointerLeftIconComponent, selector: "kp-line-circle-pointer-left-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
LineCirclePointerLeftIconComponent = __decorate$O([
    Icon(kpLineCirclePointerLeft, CustomIconStyle)
], LineCirclePointerLeftIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: LineCirclePointerLeftIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-line-circle-pointer-left-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$O = [LineCirclePointerLeftIconComponent];
class LineCirclePointerLeftIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: LineCirclePointerLeftIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: LineCirclePointerLeftIconModule, declarations: [LineCirclePointerLeftIconComponent], exports: [LineCirclePointerLeftIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: LineCirclePointerLeftIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: LineCirclePointerLeftIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$O,
                    exports: DIRECTIVES$O,
                }]
        }] });

var __decorate$N = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
const kpHorizontalLine = defineCustomIcon('kp', {
    name: 'horizontal-line',
    height: 24,
    width: 24,
    svgPath: 'M23 13L1 13L1 11L23 11V13Z',
});
let HorizontalLineIconComponent = class HorizontalLineIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: HorizontalLineIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: HorizontalLineIconComponent, selector: "kp-horizontal-line-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
HorizontalLineIconComponent = __decorate$N([
    Icon(kpHorizontalLine, CustomIconStyle)
], HorizontalLineIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: HorizontalLineIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-horizontal-line-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$N = [HorizontalLineIconComponent];
class HorizontalLineIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: HorizontalLineIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: HorizontalLineIconModule, declarations: [HorizontalLineIconComponent], exports: [HorizontalLineIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: HorizontalLineIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: HorizontalLineIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$N,
                    exports: DIRECTIVES$N,
                }]
        }] });

var __decorate$M = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
let ArrowUpToLineIconComponent = class ArrowUpToLineIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ArrowUpToLineIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: ArrowUpToLineIconComponent, selector: "kp-arrow-up-to-line-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
ArrowUpToLineIconComponent = __decorate$M([
    Icon(faArrowUpToLine)
], ArrowUpToLineIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ArrowUpToLineIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-arrow-up-to-line-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$M = [ArrowUpToLineIconComponent];
class ArrowUpToLineIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ArrowUpToLineIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: ArrowUpToLineIconModule, declarations: [ArrowUpToLineIconComponent], exports: [ArrowUpToLineIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ArrowUpToLineIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ArrowUpToLineIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$M,
                    exports: DIRECTIVES$M,
                }]
        }] });

var __decorate$L = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
let ArrowDownToLineIconComponent = class ArrowDownToLineIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ArrowDownToLineIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: ArrowDownToLineIconComponent, selector: "kp-arrow-down-to-line-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
ArrowDownToLineIconComponent = __decorate$L([
    Icon(faArrowDownToLine)
], ArrowDownToLineIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ArrowDownToLineIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-arrow-down-to-line-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$L = [ArrowDownToLineIconComponent];
class ArrowDownToLineIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ArrowDownToLineIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: ArrowDownToLineIconModule, declarations: [ArrowDownToLineIconComponent], exports: [ArrowDownToLineIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ArrowDownToLineIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ArrowDownToLineIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$L,
                    exports: DIRECTIVES$L,
                }]
        }] });

var __decorate$K = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
let ArrowLeftToLineIconComponent = class ArrowLeftToLineIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ArrowLeftToLineIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: ArrowLeftToLineIconComponent, selector: "kp-arrow-left-to-line-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
ArrowLeftToLineIconComponent = __decorate$K([
    Icon(faArrowLeftToLine)
], ArrowLeftToLineIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ArrowLeftToLineIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-arrow-left-to-line-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$K = [ArrowLeftToLineIconComponent];
class ArrowLeftToLineIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ArrowLeftToLineIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: ArrowLeftToLineIconModule, declarations: [ArrowLeftToLineIconComponent], exports: [ArrowLeftToLineIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ArrowLeftToLineIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ArrowLeftToLineIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$K,
                    exports: DIRECTIVES$K,
                }]
        }] });

var __decorate$J = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
let ArrowRightToLineIconComponent = class ArrowRightToLineIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ArrowRightToLineIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: ArrowRightToLineIconComponent, selector: "kp-arrow-right-to-line-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
ArrowRightToLineIconComponent = __decorate$J([
    Icon(faArrowRightToLine)
], ArrowRightToLineIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ArrowRightToLineIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-arrow-right-to-line-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$J = [ArrowRightToLineIconComponent];
class ArrowRightToLineIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ArrowRightToLineIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: ArrowRightToLineIconModule, declarations: [ArrowRightToLineIconComponent], exports: [ArrowRightToLineIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ArrowRightToLineIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ArrowRightToLineIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$J,
                    exports: DIRECTIVES$J,
                }]
        }] });

var __decorate$I = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
let SortIconComponent = class SortIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: SortIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: SortIconComponent, selector: "kp-sort-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
SortIconComponent = __decorate$I([
    Icon(faSort)
], SortIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: SortIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-sort-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$I = [SortIconComponent];
class SortIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: SortIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: SortIconModule, declarations: [SortIconComponent], exports: [SortIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: SortIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: SortIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$I,
                    exports: DIRECTIVES$I,
                }]
        }] });

var __decorate$H = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
const kpTimelineTopDistribution = defineCustomIcon('kp', {
    name: 'timeline-top-distribution',
    height: 24,
    width: 24,
    svgPath: 'M9 1L5 1L5 4L6.25 4L6.25 10.1454C5.74849 10.3484 5.34806 10.7489 5.14518 11.2505L2.62268e-07 11.2505L0 12.7505L5.14558 12.7505C5.44239 13.4832 6.16084 14 7 14C7.83916 14 8.55761 13.4832 8.85442 12.7505L15.1454 12.7505C15.4421 13.4834 16.1607 14.0005 17 14.0005C17.8393 14.0005 18.5579 13.4834 18.8546 12.7505H24V11.2505H18.8546C18.6517 10.7491 18.2513 10.3488 17.75 10.1459L17.75 4.00049L19 4.00049V1.00049L15 1.00049V4.00049L16.25 4.00049L16.25 10.1459C15.7487 10.3488 15.3483 10.7491 15.1454 11.2505L8.85482 11.2505C8.65194 10.7489 8.25151 10.3484 7.75 10.1454L7.75 4L9 4V1Z',
});
let TimelineTopDistributionIconComponent = class TimelineTopDistributionIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TimelineTopDistributionIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: TimelineTopDistributionIconComponent, selector: "kp-timeline-top-distribution-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
TimelineTopDistributionIconComponent = __decorate$H([
    Icon(kpTimelineTopDistribution, CustomIconStyle)
], TimelineTopDistributionIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TimelineTopDistributionIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-timeline-top-distribution-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$H = [TimelineTopDistributionIconComponent];
class TimelineTopDistributionIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TimelineTopDistributionIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: TimelineTopDistributionIconModule, declarations: [TimelineTopDistributionIconComponent], exports: [TimelineTopDistributionIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TimelineTopDistributionIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TimelineTopDistributionIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$H,
                    exports: DIRECTIVES$H,
                }]
        }] });

var __decorate$G = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
const kpTimelineAlternateDistribution = defineCustomIcon('kp', {
    name: 'timeline-alternate-distribution',
    height: 24,
    width: 24,
    svgPath: 'M19 1H15V4H16.25V10.1454C15.7487 10.3483 15.3483 10.7487 15.1454 11.25H8.85462C8.55793 10.517 7.83934 10 7 10C6.16066 10 5.44207 10.517 5.14538 11.25H0V12.75H5.14538C5.34832 13.2513 5.74865 13.6517 6.25 13.8546V20H5V23H9V20H7.75V13.8546C8.25135 13.6517 8.65168 13.2513 8.85462 12.75H15.1454C15.4421 13.483 16.1607 14 17 14C17.8393 14 18.5579 13.483 18.8546 12.75H24V11.25H18.8546C18.6517 10.7487 18.2514 10.3483 17.75 10.1454V4H19V1Z',
});
let TimelineAlternateDistributionIconComponent = class TimelineAlternateDistributionIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TimelineAlternateDistributionIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: TimelineAlternateDistributionIconComponent, selector: "kp-timeline-alternate-distribution-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
TimelineAlternateDistributionIconComponent = __decorate$G([
    Icon(kpTimelineAlternateDistribution, CustomIconStyle)
], TimelineAlternateDistributionIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TimelineAlternateDistributionIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-timeline-alternate-distribution-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$G = [TimelineAlternateDistributionIconComponent];
class TimelineAlternateDistributionIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TimelineAlternateDistributionIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: TimelineAlternateDistributionIconModule, declarations: [TimelineAlternateDistributionIconComponent], exports: [TimelineAlternateDistributionIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TimelineAlternateDistributionIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TimelineAlternateDistributionIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$G,
                    exports: DIRECTIVES$G,
                }]
        }] });

var __decorate$F = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
const kpTimelineBottomDistribution = defineCustomIcon('kp', {
    name: 'timeline-bottom-distribution',
    height: 24,
    width: 24,
    svgPath: 'M15.1456 11.25H8.85462C8.55793 10.517 7.83934 10 7 10C6.16066 10 5.44207 10.517 5.14538 11.25H0V12.75H5.14538C5.34832 13.2513 5.74865 13.6517 6.25 13.8546V20H5V23H9V20H7.75V13.8546C8.25135 13.6517 8.65168 13.2513 8.85462 12.75H15.1452C15.3481 13.2516 15.7485 13.6521 16.25 13.8551L16.25 20.0005H15V23.0005H19V20.0005H17.75L17.75 13.8551C18.2515 13.6521 18.6519 13.2516 18.8548 12.75H24V11.25H18.8544C18.5576 10.5173 17.8392 10.0005 17 10.0005C16.1608 10.0005 15.4424 10.5173 15.1456 11.25Z',
});
let TimelineBottomDistributionIconComponent = class TimelineBottomDistributionIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TimelineBottomDistributionIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: TimelineBottomDistributionIconComponent, selector: "kp-timeline-bottom-distribution-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
TimelineBottomDistributionIconComponent = __decorate$F([
    Icon(kpTimelineBottomDistribution, CustomIconStyle)
], TimelineBottomDistributionIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TimelineBottomDistributionIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-timeline-bottom-distribution-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$F = [TimelineBottomDistributionIconComponent];
class TimelineBottomDistributionIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TimelineBottomDistributionIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: TimelineBottomDistributionIconModule, declarations: [TimelineBottomDistributionIconComponent], exports: [TimelineBottomDistributionIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TimelineBottomDistributionIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TimelineBottomDistributionIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$F,
                    exports: DIRECTIVES$F,
                }]
        }] });

var __decorate$E = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
let PersonChalkboardAltIconComponent = class PersonChalkboardAltIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: PersonChalkboardAltIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: PersonChalkboardAltIconComponent, selector: "kp-person-chalkboard-alt-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
PersonChalkboardAltIconComponent = __decorate$E([
    Icon(faPersonChalkboard$1)
], PersonChalkboardAltIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: PersonChalkboardAltIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-person-chalkboard-alt-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$E = [PersonChalkboardAltIconComponent];
class PersonChalkboardAltIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: PersonChalkboardAltIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: PersonChalkboardAltIconModule, declarations: [PersonChalkboardAltIconComponent], exports: [PersonChalkboardAltIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: PersonChalkboardAltIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: PersonChalkboardAltIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$E,
                    exports: DIRECTIVES$E,
                }]
        }] });

var __decorate$D = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
let ObjectGroupIconComponent = class ObjectGroupIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ObjectGroupIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: ObjectGroupIconComponent, selector: "kp-object-group-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
ObjectGroupIconComponent = __decorate$D([
    Icon(faObjectGroup)
], ObjectGroupIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ObjectGroupIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-object-group-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$D = [ObjectGroupIconComponent];
class ObjectGroupIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ObjectGroupIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: ObjectGroupIconModule, declarations: [ObjectGroupIconComponent], exports: [ObjectGroupIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ObjectGroupIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ObjectGroupIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$D,
                    exports: DIRECTIVES$D,
                }]
        }] });

var __decorate$C = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
let TriangleExclamationIconComponent = class TriangleExclamationIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TriangleExclamationIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: TriangleExclamationIconComponent, selector: "kp-triangle-exclamation-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
TriangleExclamationIconComponent = __decorate$C([
    Icon(faTriangleExclamation)
], TriangleExclamationIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TriangleExclamationIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-triangle-exclamation-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$C = [TriangleExclamationIconComponent];
class TriangleExclamationIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TriangleExclamationIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: TriangleExclamationIconModule, declarations: [TriangleExclamationIconComponent], exports: [TriangleExclamationIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TriangleExclamationIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TriangleExclamationIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$C,
                    exports: DIRECTIVES$C,
                }]
        }] });

var __decorate$B = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
let InfoCircleIconComponent = class InfoCircleIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: InfoCircleIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: InfoCircleIconComponent, selector: "kp-info-circle-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
InfoCircleIconComponent = __decorate$B([
    Icon(faInfoCircle)
], InfoCircleIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: InfoCircleIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-info-circle-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$B = [InfoCircleIconComponent];
class InfoCircleIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: InfoCircleIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: InfoCircleIconModule, declarations: [InfoCircleIconComponent], exports: [InfoCircleIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: InfoCircleIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: InfoCircleIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$B,
                    exports: DIRECTIVES$B,
                }]
        }] });

var __decorate$A = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
let TimesCircleIconComponent = class TimesCircleIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TimesCircleIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: TimesCircleIconComponent, selector: "kp-times-circle-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
TimesCircleIconComponent = __decorate$A([
    Icon(faTimesCircle)
], TimesCircleIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TimesCircleIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-times-circle-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$A = [TimesCircleIconComponent];
class TimesCircleIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TimesCircleIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: TimesCircleIconModule, declarations: [TimesCircleIconComponent], exports: [TimesCircleIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TimesCircleIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TimesCircleIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$A,
                    exports: DIRECTIVES$A,
                }]
        }] });

var __decorate$z = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
let ScissorsIconComponent = class ScissorsIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ScissorsIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: ScissorsIconComponent, selector: "kp-scissors-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
ScissorsIconComponent = __decorate$z([
    Icon(faScissors)
], ScissorsIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ScissorsIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-scissors-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$z = [ScissorsIconComponent];
class ScissorsIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ScissorsIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: ScissorsIconModule, declarations: [ScissorsIconComponent], exports: [ScissorsIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ScissorsIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ScissorsIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$z,
                    exports: DIRECTIVES$z,
                }]
        }] });

var __decorate$y = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
let PasteIconComponent = class PasteIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: PasteIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: PasteIconComponent, selector: "kp-paste-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
PasteIconComponent = __decorate$y([
    Icon(faPaste)
], PasteIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: PasteIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-paste-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$y = [PasteIconComponent];
class PasteIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: PasteIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: PasteIconModule, declarations: [PasteIconComponent], exports: [PasteIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: PasteIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: PasteIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$y,
                    exports: DIRECTIVES$y,
                }]
        }] });

var __decorate$x = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
let HandPointUpIconComponent = class HandPointUpIconComponent extends IconPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: HandPointUpIconComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: HandPointUpIconComponent, selector: "kp-hand-point-up-icon", providers: ICON_PROVIDERS, usesInheritance: true, ngImport: i0, template: '<ng-container #icon></ng-container>', isInline: true }); }
};
HandPointUpIconComponent = __decorate$x([
    Icon(faHandPointUp)
], HandPointUpIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: HandPointUpIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-hand-point-up-icon',
                    template: '<ng-container #icon></ng-container>',
                    providers: ICON_PROVIDERS,
                }]
        }] });
const DIRECTIVES$x = [HandPointUpIconComponent];
class HandPointUpIconModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: HandPointUpIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: HandPointUpIconModule, declarations: [HandPointUpIconComponent], exports: [HandPointUpIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: HandPointUpIconModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: HandPointUpIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$x,
                    exports: DIRECTIVES$x,
                }]
        }] });

class IconsModule {
    static forRoot() {
        return {
            ngModule: IconsModule,
            providers: [FA_CONFIG_INITIALIZER],
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: IconsModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: IconsModule, exports: [IconModule,
            ArrowLeftIconModule,
            ArrowRightIconModule,
            AwardIconModule,
            BlogsNewsIconModule,
            CalendarIconModule,
            ChartColumnIconModule,
            ChartLineIconModule,
            ChartMixedUpCircleDollarIconModule,
            ChartMixedUpCircleDollarAltIconModule,
            ChartPieIconModule,
            CheckIconModule,
            CheckCircleIconModule,
            ChevronDownIconModule,
            ChevronLeftIconModule,
            ChevronRightIconModule,
            ChevronUpIconModule,
            CircleNotchIconModule,
            ClipboardIconModule,
            ClipboardCheckIconModule,
            ClockIconModule,
            CopyIconModule,
            CreditCardIconModule,
            DashboardIconModule,
            EditIconModule,
            EditAltIconModule,
            EllipsisIconModule,
            ExclamationCircleIconModule,
            ExclamationCircleAltIconModule,
            ExternalLinkIconModule,
            EyeIconModule,
            EyeSlashIconModule,
            ExpandWideIconModule,
            FileCheckIconModule,
            FileInvoiceIconModule,
            FileTextIconModule,
            FilterIconModule,
            FolderIconModule,
            FolderOpenIconModule,
            HeartIconModule,
            IconModule,
            LockIconModule,
            MagnifyingGlassChartIconModule,
            MegaphoneIconModule,
            MenuIconModule,
            PhoneCallIconModule,
            PlayIconModule,
            PlayAltIconModule,
            PlusIconModule,
            PlusCircleIconModule,
            PresentationIconModule,
            QuestionCircleIconModule,
            QuestionCircleAltIconModule,
            RocketIconModule,
            SearchIconModule,
            ShareIconModule,
            SignOutIconModule,
            StarIconModule,
            SwatchbookIconModule,
            TagIconModule,
            TeamIconModule,
            TimesIconModule,
            TrademarkIconModule,
            TrashIconModule,
            UserIconModule,
            UsersIconModule,
            WalletIconModule,
            WalletPlusIconModule,
            CloudCheckIconModule,
            CloudTimesIconModule,
            FileIconModule,
            PlusSquareIconModule,
            UndoIconModule,
            RedoIconModule,
            FilePlusIconModule,
            FilmTapeIconModule,
            ImageIconModule,
            SliderVerticalIconModule,
            GridIconModule,
            FontIconModule,
            BrushAltIconModule,
            TrashAltIconModule,
            SpinnerThirdIconModule,
            DollarIconModule,
            BackwardIconModule,
            ArrowCircleRightIconModule,
            FolderPlusIconModule,
            CloudDownloadIconModule,
            CodeIconModule,
            CircleAltIconModule,
            CopyAltIconModule,
            SaveIconModule,
            LaptopIconModule,
            PptIconModule,
            PdfIconModule,
            TextIconModule,
            HeadingIconModule,
            ParagraphIconModule,
            QuoteIconModule,
            LongArrowRightIconModule,
            MinusIconModule,
            PhotoVideoIconModule,
            TaxiIconModule,
            WaveformIconModule,
            ChartBarIconModule,
            ChartAreaIconModule,
            TriangleIconModule,
            ShapesIconModule,
            SquareIconModule,
            TableIconModule,
            ProjectorIconModule,
            LevelUpIconIconModule,
            LevelDownIconIconModule,
            CloneIconModule,
            PaintBrushPencilIconModule,
            FileEditIconModule,
            CloudUploadIconModule,
            HeartAltIconModule,
            EyeAltIconModule,
            LinkIconModule,
            TeamAltIconModule,
            MarkdownIconModule,
            FlagIconModule,
            EnvelopeIconModule,
            BellAltIconModule,
            LockAltIconModule,
            FolderAltIconModule,
            FolderTimesIconModule,
            CheckCircleAltIconModule,
            UserPlusIconModule,
            TimelineIconModule,
            DiagramVennIconModule,
            TallyIconModule,
            DotCircleIconModule,
            FilterAltIconModule,
            GoogleDriveIconModule,
            CircleDashedIconModule,
            PauseIconModule,
            PersonChalkboardIconModule,
            PersonChalkboardAltIconModule,
            ExchangeAltIconModule,
            MagicSparklesIconModule,
            PaletteIconModule,
            TextHeightIconModule,
            AlignLeftIconModule,
            AlignCenterIconModule,
            AlignRightIconModule,
            FillDripIconModule,
            EmptySetIconModule,
            BoldIconModule,
            ItalicIconModule,
            UnderlineIconModule,
            StrikethroughIconModule,
            SubscriptIconModule,
            SuperscriptIconModule,
            ListUlIconModule,
            ListOlIconModule,
            UnlinkIconModule,
            TextSlashIconModule,
            FaceLaughWinkIconModule,
            BringFrontIconModule,
            SendBackIconModule,
            CurvedLineIconModule,
            StraightLineIconModule,
            DiagonalSolidRegularLineIconModule,
            DiagonalSolidThinLineIconModule,
            DiagonalSolidThickLineIconModule,
            DiagonalDashedRegularLineIconModule,
            TriangleArrowRightIconModule,
            TriangleArrowLeftIconModule,
            LineCirclePointerRightIconModule,
            LineCirclePointerLeftIconModule,
            HorizontalLineIconModule,
            ArrowUpToLineIconModule,
            ArrowDownToLineIconModule,
            ArrowLeftToLineIconModule,
            ArrowRightToLineIconModule,
            SortIconModule,
            TimelineTopDistributionIconModule,
            TimelineAlternateDistributionIconModule,
            TimelineBottomDistributionIconModule,
            ObjectGroupIconModule,
            TriangleExclamationIconModule,
            InfoCircleIconModule,
            TimesCircleIconModule,
            ScissorsIconModule,
            PasteIconModule,
            HandPointUpIconModule] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: IconsModule, imports: [IconModule,
            ArrowLeftIconModule,
            ArrowRightIconModule,
            AwardIconModule,
            BlogsNewsIconModule,
            CalendarIconModule,
            ChartColumnIconModule,
            ChartLineIconModule,
            ChartMixedUpCircleDollarIconModule,
            ChartMixedUpCircleDollarAltIconModule,
            ChartPieIconModule,
            CheckIconModule,
            CheckCircleIconModule,
            ChevronDownIconModule,
            ChevronLeftIconModule,
            ChevronRightIconModule,
            ChevronUpIconModule,
            CircleNotchIconModule,
            ClipboardIconModule,
            ClipboardCheckIconModule,
            ClockIconModule,
            CopyIconModule,
            CreditCardIconModule,
            DashboardIconModule,
            EditIconModule,
            EditAltIconModule,
            EllipsisIconModule,
            ExclamationCircleIconModule,
            ExclamationCircleAltIconModule,
            ExternalLinkIconModule,
            EyeIconModule,
            EyeSlashIconModule,
            ExpandWideIconModule,
            FileCheckIconModule,
            FileInvoiceIconModule,
            FileTextIconModule,
            FilterIconModule,
            FolderIconModule,
            FolderOpenIconModule,
            HeartIconModule,
            IconModule,
            LockIconModule,
            MagnifyingGlassChartIconModule,
            MegaphoneIconModule,
            MenuIconModule,
            PhoneCallIconModule,
            PlayIconModule,
            PlayAltIconModule,
            PlusIconModule,
            PlusCircleIconModule,
            PresentationIconModule,
            QuestionCircleIconModule,
            QuestionCircleAltIconModule,
            RocketIconModule,
            SearchIconModule,
            ShareIconModule,
            SignOutIconModule,
            StarIconModule,
            SwatchbookIconModule,
            TagIconModule,
            TeamIconModule,
            TimesIconModule,
            TrademarkIconModule,
            TrashIconModule,
            UserIconModule,
            UsersIconModule,
            WalletIconModule,
            WalletPlusIconModule,
            CloudCheckIconModule,
            CloudTimesIconModule,
            FileIconModule,
            PlusSquareIconModule,
            UndoIconModule,
            RedoIconModule,
            FilePlusIconModule,
            FilmTapeIconModule,
            ImageIconModule,
            SliderVerticalIconModule,
            GridIconModule,
            FontIconModule,
            BrushAltIconModule,
            TrashAltIconModule,
            SpinnerThirdIconModule,
            DollarIconModule,
            BackwardIconModule,
            ArrowCircleRightIconModule,
            FolderPlusIconModule,
            CloudDownloadIconModule,
            CodeIconModule,
            CircleAltIconModule,
            CopyAltIconModule,
            SaveIconModule,
            LaptopIconModule,
            PptIconModule,
            PdfIconModule,
            TextIconModule,
            HeadingIconModule,
            ParagraphIconModule,
            QuoteIconModule,
            LongArrowRightIconModule,
            MinusIconModule,
            PhotoVideoIconModule,
            TaxiIconModule,
            WaveformIconModule,
            ChartBarIconModule,
            ChartAreaIconModule,
            TriangleIconModule,
            ShapesIconModule,
            SquareIconModule,
            TableIconModule,
            ProjectorIconModule,
            LevelUpIconIconModule,
            LevelDownIconIconModule,
            CloneIconModule,
            PaintBrushPencilIconModule,
            FileEditIconModule,
            CloudUploadIconModule,
            HeartAltIconModule,
            EyeAltIconModule,
            LinkIconModule,
            TeamAltIconModule,
            MarkdownIconModule,
            FlagIconModule,
            EnvelopeIconModule,
            BellAltIconModule,
            LockAltIconModule,
            FolderAltIconModule,
            FolderTimesIconModule,
            CheckCircleAltIconModule,
            UserPlusIconModule,
            TimelineIconModule,
            DiagramVennIconModule,
            TallyIconModule,
            DotCircleIconModule,
            FilterAltIconModule,
            GoogleDriveIconModule,
            CircleDashedIconModule,
            PauseIconModule,
            PersonChalkboardIconModule,
            PersonChalkboardAltIconModule,
            ExchangeAltIconModule,
            MagicSparklesIconModule,
            PaletteIconModule,
            TextHeightIconModule,
            AlignLeftIconModule,
            AlignCenterIconModule,
            AlignRightIconModule,
            FillDripIconModule,
            EmptySetIconModule,
            BoldIconModule,
            ItalicIconModule,
            UnderlineIconModule,
            StrikethroughIconModule,
            SubscriptIconModule,
            SuperscriptIconModule,
            ListUlIconModule,
            ListOlIconModule,
            UnlinkIconModule,
            TextSlashIconModule,
            FaceLaughWinkIconModule,
            BringFrontIconModule,
            SendBackIconModule,
            CurvedLineIconModule,
            StraightLineIconModule,
            DiagonalSolidRegularLineIconModule,
            DiagonalSolidThinLineIconModule,
            DiagonalSolidThickLineIconModule,
            DiagonalDashedRegularLineIconModule,
            TriangleArrowRightIconModule,
            TriangleArrowLeftIconModule,
            LineCirclePointerRightIconModule,
            LineCirclePointerLeftIconModule,
            HorizontalLineIconModule,
            ArrowUpToLineIconModule,
            ArrowDownToLineIconModule,
            ArrowLeftToLineIconModule,
            ArrowRightToLineIconModule,
            SortIconModule,
            TimelineTopDistributionIconModule,
            TimelineAlternateDistributionIconModule,
            TimelineBottomDistributionIconModule,
            ObjectGroupIconModule,
            TriangleExclamationIconModule,
            InfoCircleIconModule,
            TimesCircleIconModule,
            ScissorsIconModule,
            PasteIconModule,
            HandPointUpIconModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: IconsModule, decorators: [{
            type: NgModule,
            args: [{
                    exports: [
                        IconModule,
                        ArrowLeftIconModule,
                        ArrowRightIconModule,
                        AwardIconModule,
                        BlogsNewsIconModule,
                        CalendarIconModule,
                        ChartColumnIconModule,
                        ChartLineIconModule,
                        ChartMixedUpCircleDollarIconModule,
                        ChartMixedUpCircleDollarAltIconModule,
                        ChartPieIconModule,
                        CheckIconModule,
                        CheckCircleIconModule,
                        ChevronDownIconModule,
                        ChevronLeftIconModule,
                        ChevronRightIconModule,
                        ChevronUpIconModule,
                        CircleNotchIconModule,
                        ClipboardIconModule,
                        ClipboardCheckIconModule,
                        ClockIconModule,
                        CopyIconModule,
                        CreditCardIconModule,
                        DashboardIconModule,
                        EditIconModule,
                        EditAltIconModule,
                        EllipsisIconModule,
                        ExclamationCircleIconModule,
                        ExclamationCircleAltIconModule,
                        ExternalLinkIconModule,
                        EyeIconModule,
                        EyeSlashIconModule,
                        ExpandWideIconModule,
                        FileCheckIconModule,
                        FileInvoiceIconModule,
                        FileTextIconModule,
                        FilterIconModule,
                        FolderIconModule,
                        FolderOpenIconModule,
                        HeartIconModule,
                        IconModule,
                        LockIconModule,
                        MagnifyingGlassChartIconModule,
                        MegaphoneIconModule,
                        MenuIconModule,
                        PhoneCallIconModule,
                        PlayIconModule,
                        PlayAltIconModule,
                        PlusIconModule,
                        PlusCircleIconModule,
                        PresentationIconModule,
                        QuestionCircleIconModule,
                        QuestionCircleAltIconModule,
                        RocketIconModule,
                        SearchIconModule,
                        ShareIconModule,
                        SignOutIconModule,
                        StarIconModule,
                        SwatchbookIconModule,
                        TagIconModule,
                        TeamIconModule,
                        TimesIconModule,
                        TrademarkIconModule,
                        TrashIconModule,
                        UserIconModule,
                        UsersIconModule,
                        WalletIconModule,
                        WalletPlusIconModule,
                        CloudCheckIconModule,
                        CloudTimesIconModule,
                        FileIconModule,
                        PlusSquareIconModule,
                        UndoIconModule,
                        RedoIconModule,
                        FilePlusIconModule,
                        FilmTapeIconModule,
                        ImageIconModule,
                        SliderVerticalIconModule,
                        GridIconModule,
                        FontIconModule,
                        BrushAltIconModule,
                        TrashAltIconModule,
                        SpinnerThirdIconModule,
                        DollarIconModule,
                        BackwardIconModule,
                        ArrowCircleRightIconModule,
                        FolderPlusIconModule,
                        CloudDownloadIconModule,
                        CodeIconModule,
                        CircleAltIconModule,
                        CopyAltIconModule,
                        SaveIconModule,
                        LaptopIconModule,
                        PptIconModule,
                        PdfIconModule,
                        TextIconModule,
                        HeadingIconModule,
                        ParagraphIconModule,
                        QuoteIconModule,
                        LongArrowRightIconModule,
                        MinusIconModule,
                        PhotoVideoIconModule,
                        TaxiIconModule,
                        WaveformIconModule,
                        ChartBarIconModule,
                        ChartAreaIconModule,
                        TriangleIconModule,
                        ShapesIconModule,
                        SquareIconModule,
                        TableIconModule,
                        ProjectorIconModule,
                        LevelUpIconIconModule,
                        LevelDownIconIconModule,
                        CloneIconModule,
                        PaintBrushPencilIconModule,
                        FileEditIconModule,
                        CloudUploadIconModule,
                        HeartAltIconModule,
                        EyeAltIconModule,
                        LinkIconModule,
                        TeamAltIconModule,
                        MarkdownIconModule,
                        FlagIconModule,
                        EnvelopeIconModule,
                        BellAltIconModule,
                        LockAltIconModule,
                        FolderAltIconModule,
                        FolderTimesIconModule,
                        CheckCircleAltIconModule,
                        UserPlusIconModule,
                        TimelineIconModule,
                        DiagramVennIconModule,
                        TallyIconModule,
                        DotCircleIconModule,
                        FilterAltIconModule,
                        GoogleDriveIconModule,
                        CircleDashedIconModule,
                        PauseIconModule,
                        PersonChalkboardIconModule,
                        PersonChalkboardAltIconModule,
                        ExchangeAltIconModule,
                        MagicSparklesIconModule,
                        PaletteIconModule,
                        TextHeightIconModule,
                        AlignLeftIconModule,
                        AlignCenterIconModule,
                        AlignRightIconModule,
                        FillDripIconModule,
                        EmptySetIconModule,
                        BoldIconModule,
                        ItalicIconModule,
                        UnderlineIconModule,
                        StrikethroughIconModule,
                        SubscriptIconModule,
                        SuperscriptIconModule,
                        ListUlIconModule,
                        ListOlIconModule,
                        UnlinkIconModule,
                        TextSlashIconModule,
                        FaceLaughWinkIconModule,
                        BringFrontIconModule,
                        SendBackIconModule,
                        CurvedLineIconModule,
                        StraightLineIconModule,
                        DiagonalSolidRegularLineIconModule,
                        DiagonalSolidThinLineIconModule,
                        DiagonalSolidThickLineIconModule,
                        DiagonalDashedRegularLineIconModule,
                        TriangleArrowRightIconModule,
                        TriangleArrowLeftIconModule,
                        LineCirclePointerRightIconModule,
                        LineCirclePointerLeftIconModule,
                        HorizontalLineIconModule,
                        ArrowUpToLineIconModule,
                        ArrowDownToLineIconModule,
                        ArrowLeftToLineIconModule,
                        ArrowRightToLineIconModule,
                        SortIconModule,
                        TimelineTopDistributionIconModule,
                        TimelineAlternateDistributionIconModule,
                        TimelineBottomDistributionIconModule,
                        ObjectGroupIconModule,
                        TriangleExclamationIconModule,
                        InfoCircleIconModule,
                        TimesCircleIconModule,
                        ScissorsIconModule,
                        PasteIconModule,
                        HandPointUpIconModule,
                    ],
                }]
        }] });

var __decorate$w = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
function compound$4(tone, variant) {
    if (variant === 'subtle') {
        return {
            tone,
            variant,
            css: {
                color: `$${tone}-foreground`,
                backgroundColor: '$none',
                borderColor: `$${tone}-solid`,
                borderStyle: 'solid',
                '&:hover': {
                    color: `$${tone}-foreground`,
                    backgroundColor: `$${tone}-subtle`,
                    borderColor: `$${tone}-solid-hover`,
                },
                '&:active': {
                    color: `$${tone}-foreground`,
                    backgroundColor: `$${tone}-subtle-hover`,
                    borderColor: `$${tone}-solid-active`,
                },
            },
        };
    }
    if (variant === 'ghost') {
        return {
            tone,
            variant,
            css: {
                color: `$${tone}-foreground`,
                backgroundColor: '$none',
                '&:hover': {
                    color: `$${tone}-foreground`,
                    backgroundColor: `$${tone}-subtle`,
                },
                '&:active': {
                    color: `$${tone}-foreground`,
                    backgroundColor: `$${tone}-subtle-hover`,
                },
            },
        };
    }
    return {
        tone,
        variant,
        css: {
            color: `$${tone}-foreground-contrast`,
            backgroundColor: `$${tone}-solid`,
            '&:hover': {
                color: `$${tone}-foreground-contrast`,
                backgroundColor: `$${tone}-solid-hover`,
            },
            '&:active': {
                color: `$${tone}-foreground-contrast`,
                backgroundColor: `$${tone}-solid-active`,
            },
        },
    };
}
// TODO: add/remove tones once they are determined
const $Button = css({
    display: 'inline-flex',
    justifyContent: 'center',
    alignItems: 'center',
    textAlign: 'center',
    cursor: 'pointer',
    appearance: 'none',
    userSelect: 'none',
    lineHeight: '$none',
    fontFamily: '$heading',
    borderWidth: '$thick',
    borderColor: '$none',
    borderRadius: '$pill',
    minW: '3rem',
    transitionDuration: '200ms',
    transitionTimingFunction: 'ease',
    transitionProperty: 'border-color, color, background-color',
    '&:focus': { outline: 'none' },
    '&:disabled, &[disabled]': {
        pointerEvents: 'none',
        cursor: 'not-allowed',
        opacity: 0.4,
    },
    variants: {
        tone: {
            primary: {},
            accent: {},
            neutral: {},
            success: {},
            info: {},
            warning: {},
            danger: {},
            promote: {},
        },
        variant: {
            solid: {},
            subtle: {},
            ghost: {},
        },
        size: {
            small: {
                px: '1rem',
                h: '2rem',
                fontSize: '$small',
            },
            medium: {
                px: '1.25rem',
                h: '2.75rem',
                fontSize: '$medium',
            },
            large: {
                px: '1.75rem',
                h: '3.5rem',
                fontSize: '$large',
            },
        },
        subtle: {
            true: {
                backgroundColor: '$none',
                borderStyle: 'solid',
            },
        },
        grow: {
            true: { w: '$full' },
        },
        wrap: {
            true: { whiteSpace: 'wrap' },
            false: { whiteSpace: 'nowrap' },
        },
    },
    defaultVariants: {
        tone: 'primary',
        size: 'medium',
        variant: 'solid',
        wrap: false,
    },
    compoundVariants: compoundVariants(compound$4, [
        'primary',
        'accent',
        'neutral',
        'success',
        'info',
        'warning',
        'danger',
        'promote',
    ], ['solid', 'subtle', 'ghost']),
});
let ButtonDirective = class ButtonDirective {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ButtonDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.1.4", type: ButtonDirective, selector: "button[kp-button], a[kp-button]", inputs: { tone: "tone", size: "size", variant: "variant", wrap: "wrap", grow: "grow" }, host: { properties: { "class": "this.class" } }, ngImport: i0 }); }
};
__decorate$w([
    CoerceBooleanVariant()
], ButtonDirective.prototype, "wrap", void 0);
__decorate$w([
    CoerceBooleanVariant()
], ButtonDirective.prototype, "grow", void 0);
ButtonDirective = __decorate$w([
    Styled($Button)
], ButtonDirective);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ButtonDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: 'button[kp-button], a[kp-button]',
                }]
        }], propDecorators: { class: [{
                type: HostBinding,
                args: ['class']
            }], tone: [{
                type: Input
            }], size: [{
                type: Input
            }], variant: [{
                type: Input
            }], wrap: [{
                type: Input
            }], grow: [{
                type: Input
            }] } });
const DIRECTIVES$w = [ButtonDirective];
class ButtonModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ButtonModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: ButtonModule, declarations: [ButtonDirective], exports: [ButtonDirective] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ButtonModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ButtonModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$w,
                    exports: DIRECTIVES$w,
                }]
        }] });

var __decorate$v = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
function compound$3(tone, variant) {
    if (variant === 'subtle') {
        return {
            tone,
            variant,
            css: {
                color: `$${tone}-foreground`,
                backgroundColor: '$none',
                borderColor: `$${tone}-subtle-hover`,
                '&:hover': {
                    color: `$${tone}-foreground`,
                    borderColor: `$${tone}-subtle-hover`,
                    backgroundColor: `$${tone}-subtle`,
                },
                '&:active': { backgroundColor: `$${tone}-subtle-hover` },
            },
        };
    }
    if (variant === 'ghost') {
        return {
            tone,
            variant,
            css: {
                color: `$${tone}-foreground`,
                backgroundColor: '$none',
                '&:hover': {
                    color: `$${tone}-foreground`,
                    backgroundColor: `$${tone}-subtle`,
                },
                '&:active': { backgroundColor: `$${tone}-subtle-hover` },
            },
        };
    }
    if (variant === 'solid') {
        return {
            tone,
            variant,
            css: {
                color: '$white',
                backgroundColor: `${tone}-solid`,
                '&:hover': {
                    backgroundColor: `$${tone}-solid-hover`,
                },
                '&:active': { backgroundColor: `$${tone}-solid-hover` },
            },
        };
    }
    return {
        tone,
        variant,
        css: {
            color: `$${tone}-foreground`,
            backgroundColor: `$${tone}-subtle`,
            borderColor: `$${tone}-subtle-hover`,
            '&:hover': {
                color: `$${tone}-foreground`,
                boxShadow: 'inset 0px 5px 10px rgba(49, 107, 232, 0.1), inset 0px 1px 3px rgba(49, 107, 232, 0.08), inset 0px 0px 1px rgba(49, 107, 232, 0.04)',
                backgroundColor: `$${tone}-subtle`,
            },
            '&:active': { backgroundColor: `$${tone}-subtle-hover` },
        },
    };
}
const $IconButton = css(ButtonDirective, {
    borderWidth: '$thick',
    borderStyle: 'solid',
    borderRadius: '$round',
    transitionProperty: 'border-color, color, background-color, box-shadow',
    variants: {
        tone: {
            primary: {},
            accent: {},
            neutral: {},
            success: {},
            info: {},
            warning: {},
            danger: {},
            promote: {},
        },
        variant: {
            solid: {},
            soft: {},
            subtle: {},
            ghost: {},
        },
        size: {
            xxxsmall: {
                size: '1.5rem',
                minW: '1.5rem',
                fontSize: '0.55rem',
            },
            xxsmall: {
                size: '2rem',
                minW: '2rem',
                fontSize: '0.7rem',
            },
            xsmall: {
                size: '2.5rem',
                minW: '2.5rem',
                fontSize: '0.85rem',
            },
            small: {
                size: '3rem',
                fontSize: '1rem',
                px: '$none',
            },
            medium: {
                size: '3.5rem',
                fontSize: '1.25rem',
                px: '$none',
            },
            large: {
                size: '4.5rem',
                fontSize: '1.5rem',
                px: '$none',
            },
        },
    },
    defaultVariants: {
        tone: 'primary',
        size: 'medium',
        variant: 'soft',
    },
    compoundVariants: compoundVariants(compound$3, [
        'primary',
        'accent',
        'neutral',
        'success',
        'info',
        'warning',
        'danger',
        'promote',
    ], ['solid', 'soft', 'subtle', 'ghost']),
});
let IconButtonComponent = class IconButtonComponent {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: IconButtonComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: IconButtonComponent, selector: "button[kp-icon-button], a[kp-icon-button]", inputs: { icon: "icon", spin: "spin", tone: "tone", size: "size", variant: "variant" }, host: { properties: { "class": "this.class" } }, ngImport: i0, template: `<kp-icon *ngIf="!!icon" [icon]="icon" [spin]="spin" />`, isInline: true, dependencies: [{ kind: "directive", type: i1$1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: IconComponent, selector: "kp-icon", inputs: ["icon"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
};
__decorate$v([
    CoerceIcon()
], IconButtonComponent.prototype, "icon", void 0);
__decorate$v([
    CoerceBoolean()
], IconButtonComponent.prototype, "spin", void 0);
IconButtonComponent = __decorate$v([
    Styled($IconButton)
], IconButtonComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: IconButtonComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'button[kp-icon-button], a[kp-icon-button]',
                    template: `<kp-icon *ngIf="!!icon" [icon]="icon" [spin]="spin" />`,
                    changeDetection: ChangeDetectionStrategy.OnPush,
                }]
        }], propDecorators: { class: [{
                type: HostBinding,
                args: ['class']
            }], icon: [{
                type: Input
            }], spin: [{
                type: Input
            }], tone: [{
                type: Input
            }], size: [{
                type: Input
            }], variant: [{
                type: Input
            }] } });
const DIRECTIVES$v = [IconButtonComponent];
class IconButtonModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: IconButtonModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: IconButtonModule, declarations: [IconButtonComponent], imports: [CommonModule, IconsModule], exports: [IconButtonComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: IconButtonModule, imports: [CommonModule, IconsModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: IconButtonModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, IconsModule],
                    declarations: DIRECTIVES$v,
                    exports: DIRECTIVES$v,
                }]
        }] });

var __decorate$u = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
const $Divider = css({
    display: 'block',
    border: 0,
    borderStyle: 'solid',
    variants: {
        orientation: {
            vertical: {
                borderLeftWidth: '$regular',
                height: 'auto',
            },
            horizontal: {
                borderTopWidth: '$regular',
                width: '$full',
            },
        },
        weight: {
            regular: { borderColor: '$border-subtle' },
            strong: { borderColor: '$neutral-foreground' },
        },
    },
    defaultVariants: {
        weight: 'regular',
        orientation: 'horizontal',
    },
});
let DividerComponent = class DividerComponent {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: DividerComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: DividerComponent, selector: "kp-divider", inputs: { weight: "weight", orientation: "orientation" }, host: { properties: { "attr.role": "'separator'", "attr.aria-orientation": "orientation", "class": "this.class" } }, ngImport: i0, template: '', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
};
DividerComponent = __decorate$u([
    Styled($Divider)
], DividerComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: DividerComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-divider',
                    host: {
                        '[attr.role]': "'separator'",
                        '[attr.aria-orientation]': 'orientation',
                    },
                    template: '',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                }]
        }], propDecorators: { class: [{
                type: HostBinding,
                args: ['class']
            }], weight: [{
                type: Input
            }], orientation: [{
                type: Input
            }] } });
const DIRECTIVES$u = [DividerComponent];
class DividerModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: DividerModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: DividerModule, declarations: [DividerComponent], exports: [DividerComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: DividerModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: DividerModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$u,
                    exports: DIRECTIVES$u,
                }]
        }] });

var __decorate$t = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
function compound$2(collapseBelow, space, reverse, wrap) {
    const selector = wrap
        ? '& > *'
        : reverse
            ? '& > *:not(:last-child)'
            : '& > * + *';
    if (collapseBelow === 'none') {
        return {
            collapseBelow,
            space,
            reverse,
            wrap,
            css: {
                flexWrap: wrap ? 'wrap' : 'nowrap',
                flexDirection: reverse ? 'row-reverse' : 'row',
                ml: wrap ? `-$${space}` : '$none',
                mt: wrap ? `-$${space}` : '$none',
                [selector]: { ml: `$${space}`, mt: wrap ? `$${space}` : '$none' },
            },
        };
    }
    const style = {
        flexDirection: reverse ? 'column-reverse' : 'column',
        ml: `$none`,
        mt: wrap ? `-$${space}` : '$none',
        [selector]: { ml: '$none', mt: `$${space}` },
    };
    style[`@${collapseBelow}`] = {
        flexWrap: wrap ? 'wrap' : 'nowrap',
        flexDirection: reverse ? 'row-reverse' : 'row',
        ml: wrap ? `-$${space}` : '$none',
        mt: wrap ? `-$${space}` : '$none',
        [selector]: { ml: `$${space}`, mt: wrap ? `$${space}` : '$none' },
    };
    return {
        collapseBelow,
        space,
        reverse,
        wrap,
        css: style,
    };
}
function flexAlignment(alignment) {
    if (alignment === 'left' || alignment === 'top')
        return 'flex-start';
    if (alignment === 'center')
        return 'center';
    if (alignment === 'right' || alignment === 'bottom')
        return 'flex-end';
    return void 0;
}
function compoundAlignment(alignmentX, alignmentY, collapseBelow) {
    if (alignmentX == null && alignmentX == null)
        return void 0;
    const style = {
        justifyContent: alignmentY == null
            ? flexAlignment(alignmentX)
            : flexAlignment(alignmentY),
        alignItems: alignmentX == null
            ? flexAlignment(alignmentY)
            : flexAlignment(alignmentX),
        [`@${collapseBelow}`]: {
            justifyContent: alignmentX == null
                ? flexAlignment(alignmentY)
                : flexAlignment(alignmentX),
            alignItems: alignmentY == null
                ? flexAlignment(alignmentX)
                : flexAlignment(alignmentY),
        },
    };
    return {
        alignmentX,
        alignmentY,
        collapseBelow,
        css: style,
    };
}
const $SetContent = css({
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'nowrap',
    '& > *[truncate]': {
        minW: 0,
    },
    [`& > ${SpacerComponent}`]: {
        m: '$none !important',
        p: '$none !important',
    },
    variants: {
        space: {
            none: {},
            xsmall: {},
            small: {},
            medium: {},
            large: {},
            xlarge: {},
        },
        alignmentX: {
            left: { justifyContent: 'flex-start' },
            center: { justifyContent: 'center' },
            right: { justifyContent: 'flex-end' },
        },
        alignmentY: {
            top: { alignItems: 'flex-start' },
            center: { alignItems: 'center' },
            bottom: { alignItems: 'flex-end' },
        },
        collapseBelow: {
            none: {},
            tablet: {},
            desktop: {},
        },
        reverse: {
            true: {},
            false: {},
        },
        wrap: {
            true: {},
            false: {},
        },
    },
    defaultVariants: {
        space: 'none',
        collapseBelow: 'none',
        alignmentY: 'center',
        reverse: false,
        wrap: false,
    },
    compoundVariants: [
        ...compoundVariants(compound$2, ['none', 'tablet', 'desktop'], ['none', 'xsmall', 'small', 'medium', 'large', 'xlarge'], [true, false], [true, false]),
        ...compoundVariants(compoundAlignment, ['left', 'center', 'right', void 0], ['top', 'center', 'bottom', void 0], ['tablet', 'desktop']).filter(Boolean),
    ],
});
let SetContentComponent = class SetContentComponent {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: SetContentComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: SetContentComponent, selector: "kp-set-content", inputs: { space: "space", alignmentX: "alignmentX", alignmentY: "alignmentY", collapseBelow: "collapseBelow", wrap: "wrap", reverse: "reverse" }, host: { properties: { "class": "this.class" } }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
};
__decorate$t([
    CoerceBooleanVariant()
], SetContentComponent.prototype, "wrap", void 0);
__decorate$t([
    CoerceBooleanVariant()
], SetContentComponent.prototype, "reverse", void 0);
SetContentComponent = __decorate$t([
    Styled($SetContent)
], SetContentComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: SetContentComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-set-content',
                    template: '<ng-content></ng-content>',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                }]
        }], propDecorators: { class: [{
                type: HostBinding,
                args: ['class']
            }], space: [{
                type: Input
            }], alignmentX: [{
                type: Input
            }], alignmentY: [{
                type: Input
            }], collapseBelow: [{
                type: Input
            }], wrap: [{
                type: Input
            }], reverse: [{
                type: Input
            }] } });
const $Set = css({
    display: 'block',
    [`${SetContentComponent}`]: {
        height: '$full',
    },
    variants: {
        wrap: {
            true: {
                overflow: 'hidden',
            },
            false: {},
        },
        grow: {
            true: {
                width: '$full',
            },
        },
    },
    defaultVariants: {
        wrap: false,
    },
});
let SetComponent = class SetComponent {
    constructor() {
        this.space = 'none';
        this.alignmentY = 'center';
        this.collapseBelow = 'none';
        this.wrap = false;
        this.reverse = false;
        this.grow = false;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: SetComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: SetComponent, selector: "kp-set", inputs: { space: "space", alignmentX: "alignmentX", alignmentY: "alignmentY", collapseBelow: "collapseBelow", wrap: "wrap", reverse: "reverse", grow: "grow" }, host: { properties: { "class": "this.class" } }, ngImport: i0, template: `
    <kp-set-content
      [space]="space"
      [alignmentX]="alignmentX"
      [alignmentY]="alignmentY"
      [collapseBelow]="collapseBelow"
      [wrap]="wrap"
      [reverse]="reverse"
    >
      <ng-content></ng-content>
    </kp-set-content>
  `, isInline: true, dependencies: [{ kind: "component", type: SetContentComponent, selector: "kp-set-content", inputs: ["space", "alignmentX", "alignmentY", "collapseBelow", "wrap", "reverse"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
};
__decorate$t([
    CoerceBooleanVariant()
], SetComponent.prototype, "wrap", void 0);
__decorate$t([
    CoerceBooleanVariant()
], SetComponent.prototype, "reverse", void 0);
__decorate$t([
    CoerceBooleanVariant()
], SetComponent.prototype, "grow", void 0);
SetComponent = __decorate$t([
    Styled($Set)
], SetComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: SetComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-set',
                    template: `
    <kp-set-content
      [space]="space"
      [alignmentX]="alignmentX"
      [alignmentY]="alignmentY"
      [collapseBelow]="collapseBelow"
      [wrap]="wrap"
      [reverse]="reverse"
    >
      <ng-content></ng-content>
    </kp-set-content>
  `,
                    changeDetection: ChangeDetectionStrategy.OnPush,
                }]
        }], propDecorators: { class: [{
                type: HostBinding,
                args: ['class']
            }], space: [{
                type: Input
            }], alignmentX: [{
                type: Input
            }], alignmentY: [{
                type: Input
            }], collapseBelow: [{
                type: Input
            }], wrap: [{
                type: Input
            }], reverse: [{
                type: Input
            }], grow: [{
                type: Input
            }] } });
const DIRECTIVES$t = [SetComponent];
class SetModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: SetModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: SetModule, declarations: [SetComponent, SetContentComponent], imports: [CommonModule], exports: [SetComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: SetModule, imports: [CommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: SetModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                    declarations: [...DIRECTIVES$t, SetContentComponent],
                    exports: DIRECTIVES$t,
                }]
        }] });

var __decorate$s = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
function hideStyles(breakpoint, direction, display = 'block') {
    return {
        display: direction === 'below' ? 'none !important' : `${display} !important`,
        [`@${breakpoint}`]: {
            display: direction === 'below' ? `${display} !important` : 'none !important',
        },
    };
}
const $Hide = css({
    display: 'none !important',
    variants: {
        below: {
            tablet: hideStyles('tablet', 'below'),
            desktop: hideStyles('desktop', 'below'),
        },
        above: {
            tablet: hideStyles('tablet', 'above'),
            desktop: hideStyles('desktop', 'above'),
        },
    },
});
let HideComponent = class HideComponent {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: HideComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: HideComponent, selector: "kp-hide", inputs: { above: "above", below: "below" }, host: { properties: { "class": "this.class" } }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
};
HideComponent = __decorate$s([
    Styled($Hide)
], HideComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: HideComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-hide',
                    template: '<ng-content></ng-content>',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                }]
        }], propDecorators: { class: [{
                type: HostBinding,
                args: ['class']
            }], above: [{
                type: Input
            }], below: [{
                type: Input
            }] } });
const $HideDisplay = css($Hide, {
    variants: {
        display: {
            flex: {},
        },
    },
    compoundVariants: [
        {
            below: 'tablet',
            display: 'flex',
            css: hideStyles('tablet', 'below', 'flex'),
        },
        {
            above: 'tablet',
            display: 'flex',
            css: hideStyles('tablet', 'above', 'flex'),
        },
        {
            below: 'desktop',
            display: 'flex',
            css: hideStyles('desktop', 'below', 'flex'),
        },
        {
            above: 'desktop',
            display: 'flex',
            css: hideStyles('desktop', 'above', 'flex'),
        },
    ],
});
let HideDirective = class HideDirective {
    constructor() {
        this._class = '';
        this._hide = true;
    }
    get class() {
        return this.hide === false ? '' : this._class;
    }
    set class(value) {
        this._class = value;
    }
    get hide() {
        return this._hide;
    }
    set hide(value) {
        this._hide = String(value) !== 'false';
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: HideDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.1.4", type: HideDirective, selector: "[kp-hide]", inputs: { hide: ["kp-hide", "hide"], above: "above", below: "below", display: "display" }, host: { properties: { "class": "this.class" } }, ngImport: i0 }); }
};
HideDirective = __decorate$s([
    Styled($HideDisplay)
], HideDirective);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: HideDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[kp-hide]',
                }]
        }], propDecorators: { class: [{
                type: HostBinding,
                args: ['class']
            }], hide: [{
                type: Input,
                args: ['kp-hide']
            }], above: [{
                type: Input
            }], below: [{
                type: Input
            }], display: [{
                type: Input
            }] } });
const DIRECTIVES$s = [HideComponent, HideDirective];
class HideModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: HideModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: HideModule, declarations: [HideComponent, HideDirective], exports: [HideComponent, HideDirective] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: HideModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: HideModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$s,
                    exports: DIRECTIVES$s,
                }]
        }] });

var __decorate$r = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
const $Stack = css({
    display: 'flex',
    flexDirection: 'column',
    [`& > ${SpacerComponent}`]: {
        m: '$none !important',
        p: '$none !important',
    },
    variants: {
        alignment: {
            none: { alignItems: 'stretch' },
            left: { alignItems: 'flex-start' },
            center: { alignItems: 'center' },
            right: { alignItems: 'flex-end' },
        },
        space: {
            none: { '& > * + *': { mt: '$none' } },
            xsmall: { '& > * + *': { mt: '$xsmall' } },
            small: { '& > * + *': { mt: '$small' } },
            medium: { '& > * + *': { mt: '$medium' } },
            large: { '& > * + *': { mt: '$large' } },
            xlarge: { '& > * + *': { mt: '$xlarge' } },
        },
        grow: {
            true: { height: '$full' },
        },
    },
    defaultVariants: {
        space: 'none',
        alignment: 'none',
    },
});
let StackComponent = class StackComponent {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: StackComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: StackComponent, selector: "kp-stack", inputs: { space: "space", alignment: "alignment", grow: "grow" }, host: { properties: { "class": "this.class" } }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
};
__decorate$r([
    CoerceBooleanVariant()
], StackComponent.prototype, "grow", void 0);
StackComponent = __decorate$r([
    Styled($Stack)
], StackComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: StackComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-stack',
                    template: '<ng-content></ng-content>',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                }]
        }], propDecorators: { class: [{
                type: HostBinding,
                args: ['class']
            }], space: [{
                type: Input
            }], alignment: [{
                type: Input
            }], grow: [{
                type: Input
            }] } });
const DIRECTIVES$r = [StackComponent];
class StackModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: StackModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: StackModule, declarations: [StackComponent], exports: [StackComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: StackModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: StackModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$r,
                    exports: DIRECTIVES$r,
                }]
        }] });

var __decorate$q = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
const $TooltipArrow = css({
    display: 'block',
    color: '$panel',
    lineHeight: '$none',
    '&[data-popper-placement^="top"]': {
        bottom: '-8px',
    },
    '&[data-popper-placement^="bottom"]': {
        top: '-8px',
        '& > *': {
            transform: 'rotate(180deg)',
        },
    },
    '&[data-popper-placement^="left"]': {
        right: '-5px',
        '& > *': {
            transform: 'rotate(270deg)',
        },
    },
    '&[data-popper-placement^="right"]': {
        left: '-5px',
        '& > *': {
            transform: 'rotate(90deg)',
        },
    },
});
let TooltipArrowComponent = class TooltipArrowComponent {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TooltipArrowComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: TooltipArrowComponent, selector: "kp-tooltip-arrow", host: { properties: { "class": "this.class" } }, ngImport: i0, template: `<hy-arrow></hy-arrow>`, isInline: true, dependencies: [{ kind: "component", type: i1$2.ArrowComponent, selector: "span[hy-arrow], div[hy-arrow], hy-arrow", inputs: ["width", "height", "decorative"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
};
TooltipArrowComponent = __decorate$q([
    Styled($TooltipArrow)
], TooltipArrowComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TooltipArrowComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-tooltip-arrow',
                    template: `<hy-arrow></hy-arrow>`,
                    changeDetection: ChangeDetectionStrategy.OnPush,
                }]
        }], propDecorators: { class: [{
                type: HostBinding,
                args: ['class']
            }] } });
const $TooltipContent = css({
    display: 'block',
});
let TooltipContentComponent = class TooltipContentComponent {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TooltipContentComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: TooltipContentComponent, selector: "kp-tooltip-content", host: { properties: { "class": "this.class" } }, ngImport: i0, template: `<ng-content></ng-content>`, isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
};
TooltipContentComponent = __decorate$q([
    Styled($TooltipContent)
], TooltipContentComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TooltipContentComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-tooltip-content',
                    template: `<ng-content></ng-content>`,
                    changeDetection: ChangeDetectionStrategy.OnPush,
                }]
        }], propDecorators: { class: [{
                type: HostBinding,
                args: ['class']
            }] } });
const $Tooltip = css({
    display: 'block',
    padding: '$small',
    borderRadius: '$small',
    backgroundColor: '$panel',
    color: '$neutral-foreground',
    boxShadow: '$small',
    maxWidth: '$64',
});
class TooltipBackgroundBehavior extends BackgroundPrimitive {
    // We need the background context registered in the TooltipComponet's injector,
    // we override the constructor to avoid the `@Host` flag in the super constructor.
    constructor(context) {
        super(context);
    }
    getVariantToUse() {
        return [false, void 0];
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TooltipBackgroundBehavior, deps: [{ token: BackgroundContext }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.1.4", type: TooltipBackgroundBehavior, selector: "kp-tooltip", usesInheritance: true, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TooltipBackgroundBehavior, decorators: [{
            type: Directive,
            args: [{
                    selector: 'kp-tooltip',
                }]
        }], ctorParameters: () => [{ type: BackgroundContext }] });
let TooltipComponent = class TooltipComponent {
    constructor(behavior) {
        this.behavior = behavior;
        this.behavior.asLight();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TooltipComponent, deps: [{ token: TooltipBackgroundBehavior, host: true }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: TooltipComponent, selector: "kp-tooltip", host: { properties: { "class": "this.class" } }, providers: [TooltipBackgroundBehavior], ngImport: i0, template: `
    <kp-tooltip-content>
      <ng-content></ng-content>
    </kp-tooltip-content>
    <kp-tooltip-arrow></kp-tooltip-arrow>
  `, isInline: true, dependencies: [{ kind: "component", type: TooltipArrowComponent, selector: "kp-tooltip-arrow" }, { kind: "component", type: TooltipContentComponent, selector: "kp-tooltip-content" }], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
};
TooltipComponent = __decorate$q([
    Styled($Tooltip)
], TooltipComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TooltipComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-tooltip',
                    template: `
    <kp-tooltip-content>
      <ng-content></ng-content>
    </kp-tooltip-content>
    <kp-tooltip-arrow></kp-tooltip-arrow>
  `,
                    providers: [TooltipBackgroundBehavior],
                    changeDetection: ChangeDetectionStrategy.OnPush,
                }]
        }], ctorParameters: () => [{ type: TooltipBackgroundBehavior, decorators: [{
                    type: Host
                }] }], propDecorators: { class: [{
                type: HostBinding,
                args: ['class']
            }] } });
class TooltipBehavior extends TooltipPrimitive {
    createTooltipComponent(value) {
        const background = {
            provide: BackgroundContext,
            useValue: new BackgroundContext(),
        };
        return this.componentFactory.create(TooltipComponent, {
            content: { value, providers: [background] },
            providers: [background],
        });
    }
    getOptions() {
        const options = super.getOptions();
        const modifiers = [{ name: 'offset', options: { offset: [0, 8] } }];
        options.modifiers = !!options.modifiers
            ? options.modifiers.concat(modifiers)
            : modifiers;
        return options;
    }
    getArrowSelector() {
        return 'kp-tooltip-arrow';
    }
    getShowDelay() {
        return 150;
    }
    getShowAnimation() {
        return {
            keyframes: [
                { opacity: 0, transform: 'translate3d(0, -8px, 0) scale(0.9)' },
                { opacity: 1, transform: 'translate3d(0, 0, 0) scale(1)' },
            ],
            options: { duration: 200, easing: 'ease-out' },
        };
    }
    getHideAnimation() {
        return {
            keyframes: [
                { opacity: 1, transform: 'translate3d(0, 0, 0) scale(1)' },
                { opacity: 0, transform: 'translate3d(0, -8px, 0) scale(0.9)' },
            ],
            options: { duration: 200, easing: 'ease-in' },
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TooltipBehavior, deps: null, target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.1.4", type: TooltipBehavior, selector: "[kp-tooltip]", inputs: { content: ["kp-tooltip", "content"], trigger: ["kp-tooltip-trigger", "trigger"], placement: ["kp-tooltip-placement", "placement"] }, providers: [TOOLTIP_PROVIDERS, TEXT_CONTENT_PROVIDERS], exportAs: ["tooltip"], usesInheritance: true, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TooltipBehavior, decorators: [{
            type: Directive,
            args: [{
                    selector: '[kp-tooltip]',
                    exportAs: 'tooltip',
                    providers: [TOOLTIP_PROVIDERS, TEXT_CONTENT_PROVIDERS],
                }]
        }], propDecorators: { content: [{
                type: Input,
                args: ['kp-tooltip']
            }], trigger: [{
                type: Input,
                args: ['kp-tooltip-trigger']
            }], placement: [{
                type: Input,
                args: ['kp-tooltip-placement']
            }] } });
class TooltipDirective {
    constructor(behavior) {
        this.behavior = behavior;
    }
    show() {
        return this.behavior.show();
    }
    hide() {
        return this.behavior.hide();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TooltipDirective, deps: [{ token: TooltipBehavior, host: true }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.1.4", type: TooltipDirective, selector: "[kp-tooltip]", exportAs: ["tooltip"], ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TooltipDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[kp-tooltip]',
                    exportAs: 'tooltip',
                }]
        }], ctorParameters: () => [{ type: TooltipBehavior, decorators: [{
                    type: Host
                }] }] });
class TooltipModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TooltipModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: TooltipModule, declarations: [TooltipDirective, TooltipBehavior, TooltipArrowComponent, TooltipContentComponent, TooltipComponent, TooltipBackgroundBehavior], imports: [TextRendererModule, ArrowModule], exports: [TooltipDirective, TooltipBehavior] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TooltipModule, imports: [TextRendererModule, ArrowModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TooltipModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [TextRendererModule, ArrowModule],
                    declarations: [
                        TooltipDirective,
                        TooltipBehavior,
                        TooltipArrowComponent,
                        TooltipContentComponent,
                        TooltipComponent,
                        TooltipBackgroundBehavior,
                    ],
                    exports: [TooltipDirective, TooltipBehavior],
                }]
        }] });

var __decorate$p = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
const $Card = css({
    display: 'block',
    bg: '$panel',
    py: '$large',
    px: '$large',
    variants: {
        shadow: {
            none: { boxShadow: '$none' },
            small: { boxShadow: '$small' },
            medium: { boxShadow: '$medium' },
            large: { boxShadow: '$large' },
        },
        radii: {
            none: { borderRadius: '$none' },
            small: { borderRadius: '$small' },
            medium: { borderRadius: '$medium' },
            large: { borderRadius: '$large' },
        },
    },
    defaultVariants: {
        shadow: 'none',
        radii: 'large',
    },
});
let CardComponent = class CardComponent {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: CardComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: CardComponent, selector: "kp-card", inputs: { shadow: "shadow", radii: "radii" }, host: { properties: { "class": "this.class" } }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
};
CardComponent = __decorate$p([
    Styled($Card)
], CardComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: CardComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-card',
                    template: '<ng-content></ng-content>',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                }]
        }], propDecorators: { class: [{
                type: HostBinding,
                args: ['class']
            }], shadow: [{
                type: Input
            }], radii: [{
                type: Input
            }] } });
const DIRECTIVES$q = [CardComponent];
class CardModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: CardModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: CardModule, declarations: [CardComponent], exports: [CardComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: CardModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: CardModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$q,
                    exports: DIRECTIVES$q,
                }]
        }] });

var __decorate$o = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
function compound$1(tone) {
    const token = tone === 'none' ? 'primary' : tone;
    const color = `$${token}-foreground`;
    const backgroundColor = `$${token}-subtle`;
    return {
        active: true,
        tone,
        css: {
            color,
            backgroundColor,
            '&:hover': { backgroundColor },
            '&:active': { backgroundColor },
        },
    };
}
const $MenuActive = css({
    variants: {
        active: {
            true: {
                cursor: 'default !important',
            },
        },
        tone: {
            none: {},
            primary: {},
            accent: {},
            neutral: {},
            danger: {},
            warning: {},
            success: {},
            info: {},
            promote: {},
        },
    },
    defaultVariants: {
        tone: 'none',
    },
    compoundVariants: [
        compound$1('none'),
        compound$1('primary'),
        compound$1('accent'),
        compound$1('neutral'),
        compound$1('danger'),
        compound$1('warning'),
        compound$1('success'),
        compound$1('info'),
        compound$1('promote'),
    ],
});
let MenuActiveDirective = class MenuActiveDirective {
    constructor() {
        this.active = false;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: MenuActiveDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.1.4", type: MenuActiveDirective, selector: "kp-menu-item[kp-menu-active], [kp-menu-item][kp-menu-active]", inputs: { active: ["kp-menu-active", "active"], tone: "tone" }, host: { properties: { "class": "this.class" } }, ngImport: i0 }); }
};
__decorate$o([
    CoerceBoolean()
], MenuActiveDirective.prototype, "active", void 0);
MenuActiveDirective = __decorate$o([
    Styled($MenuActive)
], MenuActiveDirective);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: MenuActiveDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: 'kp-menu-item[kp-menu-active], [kp-menu-item][kp-menu-active]',
                }]
        }], propDecorators: { class: [{
                type: HostBinding,
                args: ['class']
            }], active: [{
                type: Input,
                args: ['kp-menu-active']
            }], tone: [{
                type: Input
            }] } });
const DIRECTIVES$p = [MenuActiveDirective];
class MenuActiveModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: MenuActiveModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: MenuActiveModule, declarations: [MenuActiveDirective], exports: [MenuActiveDirective] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: MenuActiveModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: MenuActiveModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$p,
                    exports: DIRECTIVES$p,
                }]
        }] });

var __decorate$n = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
class MenuItemContentSlot {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: MenuItemContentSlot, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.1.4", type: MenuItemContentSlot, selector: "[kp-menu-item-content]", ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: MenuItemContentSlot, decorators: [{
            type: Directive,
            args: [{ selector: '[kp-menu-item-content]' }]
        }] });
class MenuItemPrefixSlot {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: MenuItemPrefixSlot, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.1.4", type: MenuItemPrefixSlot, selector: "[kp-menu-item-prefix]", ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: MenuItemPrefixSlot, decorators: [{
            type: Directive,
            args: [{ selector: '[kp-menu-item-prefix]' }]
        }] });
class MenuItemSuffixSlot {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: MenuItemSuffixSlot, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.1.4", type: MenuItemSuffixSlot, selector: "[kp-menu-item-suffix]", ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: MenuItemSuffixSlot, decorators: [{
            type: Directive,
            args: [{ selector: '[kp-menu-item-suffix]' }]
        }] });
function tone$4(color) {
    const token = color === 'none' ? 'primary' : color;
    return {
        ...(color === 'none' ? {} : { color: `$${color}-foreground` }),
        '&:hover': { backgroundColor: `$${token}-subtle` },
        '&:active': { backgroundColor: `$${token}-subtle-hover` },
    };
}
const $MenuItem = css({
    display: 'block',
    px: '$medium',
    py: '$small',
    cursor: 'pointer',
    appearance: 'none',
    userSelect: 'none',
    '&:focus': { outline: 'none' },
    variants: {
        tone: {
            none: tone$4('none'),
            primary: tone$4('primary'),
            accent: tone$4('accent'),
            neutral: tone$4('neutral'),
            danger: tone$4('danger'),
            warning: tone$4('warning'),
            success: tone$4('success'),
            info: tone$4('info'),
            promote: tone$4('promote'),
        },
        disabled: {
            true: {
                cursor: 'not-allowed',
                pointerEvents: 'none',
                color: '$neutral-foreground',
                backgroundColor: '$none',
                opacity: 0.4,
            },
        },
    },
    defaultVariants: {
        tone: 'none',
    },
});
let MenuItemComponent = class MenuItemComponent {
    constructor(detector) {
        this.detector = detector;
        this.space = 'small';
    }
    ngAfterContentInit() {
        this.detector.detectChanges();
    }
    get hasContentSlot() {
        return !!this.contentSlot;
    }
    get hasPrefixSlot() {
        return !!this.prefixSlot;
    }
    get hasSuffixSlot() {
        return !!this.suffixSlot;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: MenuItemComponent, deps: [{ token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: MenuItemComponent, selector: "kp-menu-item, [kp-menu-item]", inputs: { disabled: "disabled", tone: "tone", space: "space" }, host: { properties: { "class": "this.class" } }, queries: [{ propertyName: "contentSlot", first: true, predicate: MenuItemContentSlot, descendants: true }, { propertyName: "prefixSlot", first: true, predicate: MenuItemPrefixSlot, descendants: true }, { propertyName: "suffixSlot", first: true, predicate: MenuItemSuffixSlot, descendants: true }], ngImport: i0, template: `
    <ng-container *ngIf="hasContentSlot">
      <ng-content select="[kp-menu-item-content]"></ng-content>
    </ng-container>
    <ng-container *ngIf="!hasContentSlot">
      <kp-columns [space]="space">
        <kp-column *ngIf="hasPrefixSlot" width="content">
          <ng-content select="[kp-menu-item-prefix]"></ng-content>
        </kp-column>
        <kp-column>
          <ng-content></ng-content>
        </kp-column>
        <kp-column *ngIf="hasSuffixSlot" width="content">
          <ng-content select="[kp-menu-item-suffix]"></ng-content>
        </kp-column>
      </kp-columns>
    </ng-container>
  `, isInline: true, dependencies: [{ kind: "directive", type: i1$1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: ColumnComponent, selector: "kp-column", inputs: ["width"] }, { kind: "component", type: ColumnsComponent, selector: "kp-columns", inputs: ["space", "alignmentX", "alignmentY", "collapseBelow", "wrap", "reverse"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
};
__decorate$n([
    CoerceBoolean()
], MenuItemComponent.prototype, "disabled", void 0);
MenuItemComponent = __decorate$n([
    Styled($MenuItem)
], MenuItemComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: MenuItemComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-menu-item, [kp-menu-item]',
                    template: `
    <ng-container *ngIf="hasContentSlot">
      <ng-content select="[kp-menu-item-content]"></ng-content>
    </ng-container>
    <ng-container *ngIf="!hasContentSlot">
      <kp-columns [space]="space">
        <kp-column *ngIf="hasPrefixSlot" width="content">
          <ng-content select="[kp-menu-item-prefix]"></ng-content>
        </kp-column>
        <kp-column>
          <ng-content></ng-content>
        </kp-column>
        <kp-column *ngIf="hasSuffixSlot" width="content">
          <ng-content select="[kp-menu-item-suffix]"></ng-content>
        </kp-column>
      </kp-columns>
    </ng-container>
  `,
                    changeDetection: ChangeDetectionStrategy.OnPush,
                }]
        }], ctorParameters: () => [{ type: i0.ChangeDetectorRef }], propDecorators: { class: [{
                type: HostBinding,
                args: ['class']
            }], disabled: [{
                type: Input
            }], tone: [{
                type: Input
            }], space: [{
                type: Input
            }], contentSlot: [{
                type: ContentChild,
                args: [MenuItemContentSlot]
            }], prefixSlot: [{
                type: ContentChild,
                args: [MenuItemPrefixSlot]
            }], suffixSlot: [{
                type: ContentChild,
                args: [MenuItemSuffixSlot]
            }] } });
const DIRECTIVES$o = [
    MenuItemContentSlot,
    MenuItemPrefixSlot,
    MenuItemSuffixSlot,
    MenuItemComponent,
];
class MenuItemModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: MenuItemModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: MenuItemModule, declarations: [MenuItemContentSlot, MenuItemPrefixSlot, MenuItemSuffixSlot, MenuItemComponent], imports: [CommonModule, ColumnsModule, TextModule], exports: [MenuItemContentSlot, MenuItemPrefixSlot, MenuItemSuffixSlot, MenuItemComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: MenuItemModule, imports: [CommonModule, ColumnsModule, TextModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: MenuItemModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, ColumnsModule, TextModule],
                    declarations: DIRECTIVES$o,
                    exports: DIRECTIVES$o,
                }]
        }] });

var __decorate$m = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
const $MenuGroupHeader = css({
    display: 'block',
    px: '$medium',
    py: '$small',
    fontSize: '$small',
    fontWeight: '$bold',
    color: '$neutral-foreground',
    pointerEvents: 'none',
});
let MenuGroupHeaderComponent = class MenuGroupHeaderComponent {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: MenuGroupHeaderComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: MenuGroupHeaderComponent, selector: "kp-menu-group-header", host: { properties: { "class": "this.class" } }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
};
MenuGroupHeaderComponent = __decorate$m([
    Styled($MenuGroupHeader)
], MenuGroupHeaderComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: MenuGroupHeaderComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-menu-group-header',
                    template: '<ng-content></ng-content>',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                }]
        }], propDecorators: { class: [{
                type: HostBinding,
                args: ['class']
            }] } });
class MenuGroupHeaderSlot {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: MenuGroupHeaderSlot, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.1.4", type: MenuGroupHeaderSlot, selector: "[kp-menu-group-header]", ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: MenuGroupHeaderSlot, decorators: [{
            type: Directive,
            args: [{ selector: '[kp-menu-group-header]' }]
        }] });
const $MenuGroup = css({
    display: 'block',
    variants: {
        indent: {
            true: {
                [`& > ${StackComponent} > * + ${MenuItemComponent}`]: {
                    pl: '$xlarge',
                },
            },
        },
    },
});
let MenuGroupComponent = class MenuGroupComponent {
    constructor(detector) {
        this.detector = detector;
        this.space = 'xsmall';
    }
    ngAfterContentInit() {
        this.detector.detectChanges();
    }
    get hasHeaderSlot() {
        return !!this.headerSlot;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: MenuGroupComponent, deps: [{ token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: MenuGroupComponent, selector: "kp-menu-group", inputs: { header: "header", space: "space", indent: "indent" }, host: { properties: { "class": "this.class" } }, queries: [{ propertyName: "headerSlot", first: true, predicate: MenuGroupHeaderSlot, descendants: true }], ngImport: i0, template: `
    <kp-stack [space]="space">
      <ng-container *ngIf="hasHeaderSlot">
        <ng-content select="[kp-menu-group-header]"></ng-content>
      </ng-container>
      <kp-menu-group-header *ngIf="!!header && !hasHeaderSlot">
        <kp-text>{{ header }}</kp-text>
      </kp-menu-group-header>
      <ng-content
        select="kp-menu-item, [kp-menu-item], kp-menu-divider"
      ></ng-content>
    </kp-stack>
  `, isInline: true, dependencies: [{ kind: "directive", type: i1$1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: TextComponent, selector: "kp-text, span[kp-text], a[kp-text]", inputs: ["tone", "weight", "size", "contrast"] }, { kind: "directive", type: TextBackgroundBehavior, selector: "kp-text, span[kp-text], a[kp-text], kp-text-block, div[kp-text-block], p[kp-text-block], a[kp-text-block]" }, { kind: "component", type: StackComponent, selector: "kp-stack", inputs: ["space", "alignment", "grow"] }, { kind: "component", type: MenuGroupHeaderComponent, selector: "kp-menu-group-header" }], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
};
__decorate$m([
    CoerceBooleanVariant()
], MenuGroupComponent.prototype, "indent", void 0);
MenuGroupComponent = __decorate$m([
    Styled($MenuGroup)
], MenuGroupComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: MenuGroupComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-menu-group',
                    template: `
    <kp-stack [space]="space">
      <ng-container *ngIf="hasHeaderSlot">
        <ng-content select="[kp-menu-group-header]"></ng-content>
      </ng-container>
      <kp-menu-group-header *ngIf="!!header && !hasHeaderSlot">
        <kp-text>{{ header }}</kp-text>
      </kp-menu-group-header>
      <ng-content
        select="kp-menu-item, [kp-menu-item], kp-menu-divider"
      ></ng-content>
    </kp-stack>
  `,
                    changeDetection: ChangeDetectionStrategy.OnPush,
                }]
        }], ctorParameters: () => [{ type: i0.ChangeDetectorRef }], propDecorators: { class: [{
                type: HostBinding,
                args: ['class']
            }], header: [{
                type: Input
            }], space: [{
                type: Input
            }], indent: [{
                type: Input
            }], headerSlot: [{
                type: ContentChild,
                args: [MenuGroupHeaderSlot]
            }] } });
const DIRECTIVES$n = [
    MenuGroupHeaderSlot,
    MenuGroupHeaderComponent,
    MenuGroupComponent,
];
class MenuGroupModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: MenuGroupModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: MenuGroupModule, declarations: [MenuGroupHeaderSlot, MenuGroupHeaderComponent, MenuGroupComponent], imports: [CommonModule, TextModule, StackModule, SetModule], exports: [MenuGroupHeaderSlot, MenuGroupHeaderComponent, MenuGroupComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: MenuGroupModule, imports: [CommonModule, TextModule, StackModule, SetModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: MenuGroupModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, TextModule, StackModule, SetModule],
                    declarations: DIRECTIVES$n,
                    exports: DIRECTIVES$n,
                }]
        }] });

var __decorate$l = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
const $MenuDivider = css({
    display: 'block',
    py: '$xsmall',
    pointerEvents: 'none',
});
let MenuDividerComponent = class MenuDividerComponent {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: MenuDividerComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: MenuDividerComponent, selector: "kp-menu-divider", host: { properties: { "class": "this.class" } }, ngImport: i0, template: '<kp-divider></kp-divider>', isInline: true, dependencies: [{ kind: "component", type: DividerComponent, selector: "kp-divider", inputs: ["weight", "orientation"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
};
MenuDividerComponent = __decorate$l([
    Styled($MenuDivider)
], MenuDividerComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: MenuDividerComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-menu-divider',
                    template: '<kp-divider></kp-divider>',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                }]
        }], propDecorators: { class: [{
                type: HostBinding,
                args: ['class']
            }] } });
const DIRECTIVES$m = [MenuDividerComponent];
class MenuDividerModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: MenuDividerModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: MenuDividerModule, declarations: [MenuDividerComponent], imports: [DividerModule], exports: [MenuDividerComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: MenuDividerModule, imports: [DividerModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: MenuDividerModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [DividerModule],
                    declarations: DIRECTIVES$m,
                    exports: DIRECTIVES$m,
                }]
        }] });

var __decorate$k = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
const $Menu = css({
    display: 'block',
});
let MenuComponent = class MenuComponent {
    constructor() {
        this.space = 'xsmall';
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: MenuComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: MenuComponent, selector: "kp-menu, [kp-menu]", inputs: { space: "space" }, host: { properties: { "class": "this.class" } }, ngImport: i0, template: `
    <kp-stack [space]="space">
      <ng-content
        select="kp-menu-item, [kp-menu-item], kp-menu-group, [kp-menu-group], kp-menu-divider"
      ></ng-content>
    </kp-stack>
  `, isInline: true, dependencies: [{ kind: "component", type: StackComponent, selector: "kp-stack", inputs: ["space", "alignment", "grow"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
};
MenuComponent = __decorate$k([
    Styled($Menu)
], MenuComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: MenuComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-menu, [kp-menu]',
                    template: `
    <kp-stack [space]="space">
      <ng-content
        select="kp-menu-item, [kp-menu-item], kp-menu-group, [kp-menu-group], kp-menu-divider"
      ></ng-content>
    </kp-stack>
  `,
                    changeDetection: ChangeDetectionStrategy.OnPush,
                }]
        }], propDecorators: { class: [{
                type: HostBinding,
                args: ['class']
            }], space: [{
                type: Input
            }] } });
const DIRECTIVES$l = [MenuComponent];
class MenuModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: MenuModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: MenuModule, declarations: [MenuComponent], imports: [CommonModule,
            TextModule,
            StackModule,
            SetModule,
            MenuActiveModule,
            MenuItemModule,
            MenuGroupModule,
            MenuDividerModule], exports: [MenuComponent, MenuActiveModule,
            MenuItemModule,
            MenuGroupModule,
            MenuDividerModule] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: MenuModule, imports: [CommonModule,
            TextModule,
            StackModule,
            SetModule,
            MenuActiveModule,
            MenuItemModule,
            MenuGroupModule,
            MenuDividerModule, MenuActiveModule,
            MenuItemModule,
            MenuGroupModule,
            MenuDividerModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: MenuModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        TextModule,
                        StackModule,
                        SetModule,
                        MenuActiveModule,
                        MenuItemModule,
                        MenuGroupModule,
                        MenuDividerModule,
                    ],
                    declarations: DIRECTIVES$l,
                    exports: [
                        ...DIRECTIVES$l,
                        MenuActiveModule,
                        MenuItemModule,
                        MenuGroupModule,
                        MenuDividerModule,
                    ],
                }]
        }] });

var __decorate$j = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
const $Link = css({
    textDecoration: 'none',
    color: 'unset',
    variants: {
        text: {
            true: {
                cursor: 'pointer',
                '&:hover': { textDecoration: 'underline' },
                '&[disabled]': {
                    pointerEvents: 'none',
                    cursor: 'not-allowed',
                    textDecoration: 'none',
                },
            },
        },
    },
});
let LinkComponent = class LinkComponent {
    constructor(textHost, textBlockHost, headingHost) {
        this.textHost = textHost;
        this.textBlockHost = textBlockHost;
        this.headingHost = headingHost;
        this.external = false;
    }
    get boundTarget() {
        return this.external ? this.target ?? '_blank' : this.target;
    }
    get boundRel() {
        return this.external ? this.rel ?? 'noopener noreferrer' : this.rel;
    }
    get text() {
        return !!this.textHost || !!this.textBlockHost || !!this.headingHost;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: LinkComponent, deps: [{ token: TextComponent, host: true, optional: true }, { token: TextBlockComponent, host: true, optional: true }, { token: HeadingComponent, host: true, optional: true }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.1.4", type: LinkComponent, selector: "a[kp-button], a[kp-icon-button], a[kp-text], a[kp-text-block], a[kp-heading]", inputs: { external: "external", target: "target", rel: "rel" }, host: { properties: { "class": "this.class", "attr.target": "this.boundTarget", "attr.rel": "this.boundRel" } }, ngImport: i0 }); }
};
__decorate$j([
    CoerceBoolean()
], LinkComponent.prototype, "external", void 0);
LinkComponent = __decorate$j([
    Styled($Link)
], LinkComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: LinkComponent, decorators: [{
            type: Directive,
            args: [{
                    selector: 'a[kp-button], a[kp-icon-button], a[kp-text], a[kp-text-block], a[kp-heading]',
                }]
        }], ctorParameters: () => [{ type: TextComponent, decorators: [{
                    type: Optional
                }, {
                    type: Host
                }] }, { type: TextBlockComponent, decorators: [{
                    type: Optional
                }, {
                    type: Host
                }] }, { type: HeadingComponent, decorators: [{
                    type: Optional
                }, {
                    type: Host
                }] }], propDecorators: { class: [{
                type: HostBinding,
                args: ['class']
            }], external: [{
                type: Input
            }], target: [{
                type: Input
            }], rel: [{
                type: Input
            }], boundTarget: [{
                type: HostBinding,
                args: ['attr.target']
            }], boundRel: [{
                type: HostBinding,
                args: ['attr.rel']
            }] } });
const DIRECTIVES$k = [LinkComponent];
class LinkModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: LinkModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: LinkModule, declarations: [LinkComponent], exports: [LinkComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: LinkModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: LinkModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$k,
                    exports: DIRECTIVES$k,
                }]
        }] });

var __decorate$i = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
const $Label = css({
    display: 'block',
    lineHeight: '$none',
    fontWeight: '$regular',
    fontFamily: '$body',
    label: {
        all: 'unset',
        display: 'inline-block',
        fontSize: '$small',
        fontWeight: '$bold',
    },
});
class LabelActionSlot {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: LabelActionSlot, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.1.4", type: LabelActionSlot, selector: "[kp-label-action]", ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: LabelActionSlot, decorators: [{
            type: Directive,
            args: [{ selector: '[kp-label-action]' }]
        }] });
let LabelComponent = class LabelComponent {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: LabelComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: LabelComponent, selector: "kp-label", inputs: { id: ["html-id", "id"], for: "for", text: "text", secondary: "secondary", description: "description" }, host: { properties: { "class": "this.class" } }, ngImport: i0, template: `
    <kp-stack space="xsmall">
      <kp-set space="small">
        <label [attr.id]="id || null" [attr.for]="for || null">
          {{ text }}
        </label>
        <kp-text *ngIf="secondary" size="small" tone="neutral">{{
          secondary
        }}</kp-text>
        <kp-spacer></kp-spacer>
        <ng-content select="[kp-label-action]"></ng-content>
      </kp-set>
      <kp-text-block *ngIf="description" size="small" tone="subtle">
        {{ description }}
      </kp-text-block>
    </kp-stack>
  `, isInline: true, dependencies: [{ kind: "directive", type: i1$1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: SetComponent, selector: "kp-set", inputs: ["space", "alignmentX", "alignmentY", "collapseBelow", "wrap", "reverse", "grow"] }, { kind: "component", type: StackComponent, selector: "kp-stack", inputs: ["space", "alignment", "grow"] }, { kind: "component", type: SpacerComponent, selector: "kp-spacer" }, { kind: "component", type: TextComponent, selector: "kp-text, span[kp-text], a[kp-text]", inputs: ["tone", "weight", "size", "contrast"] }, { kind: "component", type: TextBlockComponent, selector: "kp-text-block, div[kp-text-block], p[kp-text-block], a[kp-text-block]", inputs: ["tone", "weight", "size", "alignment", "contrast", "truncate", "clamp"] }, { kind: "directive", type: TextBackgroundBehavior, selector: "kp-text, span[kp-text], a[kp-text], kp-text-block, div[kp-text-block], p[kp-text-block], a[kp-text-block]" }], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
};
LabelComponent = __decorate$i([
    Styled($Label)
], LabelComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: LabelComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-label',
                    template: `
    <kp-stack space="xsmall">
      <kp-set space="small">
        <label [attr.id]="id || null" [attr.for]="for || null">
          {{ text }}
        </label>
        <kp-text *ngIf="secondary" size="small" tone="neutral">{{
          secondary
        }}</kp-text>
        <kp-spacer></kp-spacer>
        <ng-content select="[kp-label-action]"></ng-content>
      </kp-set>
      <kp-text-block *ngIf="description" size="small" tone="subtle">
        {{ description }}
      </kp-text-block>
    </kp-stack>
  `,
                    changeDetection: ChangeDetectionStrategy.OnPush,
                }]
        }], propDecorators: { class: [{
                type: HostBinding,
                args: ['class']
            }], id: [{
                type: Input,
                args: ['html-id']
            }], for: [{
                type: Input
            }], text: [{
                type: Input
            }], secondary: [{
                type: Input
            }], description: [{
                type: Input
            }] } });
const DIRECTIVES$j = [LabelComponent, LabelActionSlot];
class LabelModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: LabelModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: LabelModule, declarations: [LabelComponent, LabelActionSlot], imports: [CommonModule, SetModule, StackModule, SpacerModule, TextModule], exports: [LabelComponent, LabelActionSlot] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: LabelModule, imports: [CommonModule, SetModule, StackModule, SpacerModule, TextModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: LabelModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, SetModule, StackModule, SpacerModule, TextModule],
                    declarations: DIRECTIVES$j,
                    exports: DIRECTIVES$j,
                }]
        }] });

var __decorate$h = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
class ContentCardHeaderActionSlot {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ContentCardHeaderActionSlot, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.1.4", type: ContentCardHeaderActionSlot, selector: "[kp-content-card-header-action]", ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ContentCardHeaderActionSlot, decorators: [{
            type: Directive,
            args: [{ selector: '[kp-content-card-header-action]' }]
        }] });
class ContentCardHeaderContentSlot {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ContentCardHeaderContentSlot, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.1.4", type: ContentCardHeaderContentSlot, selector: "[kp-content-card-header-content]", ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ContentCardHeaderContentSlot, decorators: [{
            type: Directive,
            args: [{ selector: '[kp-content-card-header-content]' }]
        }] });
const $ContentCard = css({
    display: 'block',
    [`& > ${CardComponent}`]: {
        h: '$full',
    },
});
let ContentCardComponent = class ContentCardComponent {
    constructor() {
        this.collapseHeaderBelow = 'none';
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ContentCardComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: ContentCardComponent, selector: "kp-content-card", inputs: { header: "header", radii: "radii", shadow: "shadow", collapseHeaderBelow: "collapseHeaderBelow", headerLevel: "headerLevel" }, host: { properties: { "class": "this.class" } }, queries: [{ propertyName: "action", first: true, predicate: ContentCardHeaderActionSlot, descendants: true }, { propertyName: "content", first: true, predicate: ContentCardHeaderContentSlot, descendants: true }], ngImport: i0, template: `
    <kp-card [radii]="radii" [shadow]="shadow">
      <kp-stack space="large">
        <kp-columns
          *ngIf="!!content || !!action || !!header"
          alignmentY="center"
          space="medium"
          [collapseBelow]="collapseHeaderBelow"
        >
          <kp-column>
            <ng-container *ngIf="!!content && !header">
              <ng-content
                select="[kp-content-card-header-content]"
              ></ng-content>
            </ng-container>

            <kp-heading
              *ngIf="!!header && !content"
              [level]="headerLevel"
              weight="bold"
            >
              {{ header }}
            </kp-heading>
          </kp-column>
          <kp-column *ngIf="!!action" width="content">
            <ng-content select="[kp-content-card-header-action]"></ng-content>
          </kp-column>
        </kp-columns>
        <ng-content></ng-content>
      </kp-stack>
    </kp-card>
  `, isInline: true, dependencies: [{ kind: "directive", type: i1$1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: HeadingComponent, selector: "kp-heading, span[kp-heading], a[kp-heading]", inputs: ["tone", "weight", "level", "alignment", "contrast", "truncate", "clamp"] }, { kind: "directive", type: HeadingBackgroundBehavior, selector: "kp-heading, span[kp-heading], a[kp-heading]" }, { kind: "component", type: ColumnComponent, selector: "kp-column", inputs: ["width"] }, { kind: "component", type: ColumnsComponent, selector: "kp-columns", inputs: ["space", "alignmentX", "alignmentY", "collapseBelow", "wrap", "reverse"] }, { kind: "component", type: StackComponent, selector: "kp-stack", inputs: ["space", "alignment", "grow"] }, { kind: "component", type: CardComponent, selector: "kp-card", inputs: ["shadow", "radii"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
};
ContentCardComponent = __decorate$h([
    Styled($ContentCard)
], ContentCardComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ContentCardComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-content-card',
                    template: `
    <kp-card [radii]="radii" [shadow]="shadow">
      <kp-stack space="large">
        <kp-columns
          *ngIf="!!content || !!action || !!header"
          alignmentY="center"
          space="medium"
          [collapseBelow]="collapseHeaderBelow"
        >
          <kp-column>
            <ng-container *ngIf="!!content && !header">
              <ng-content
                select="[kp-content-card-header-content]"
              ></ng-content>
            </ng-container>

            <kp-heading
              *ngIf="!!header && !content"
              [level]="headerLevel"
              weight="bold"
            >
              {{ header }}
            </kp-heading>
          </kp-column>
          <kp-column *ngIf="!!action" width="content">
            <ng-content select="[kp-content-card-header-action]"></ng-content>
          </kp-column>
        </kp-columns>
        <ng-content></ng-content>
      </kp-stack>
    </kp-card>
  `,
                    changeDetection: ChangeDetectionStrategy.OnPush,
                }]
        }], propDecorators: { class: [{
                type: HostBinding,
                args: ['class']
            }], header: [{
                type: Input
            }], radii: [{
                type: Input
            }], shadow: [{
                type: Input
            }], collapseHeaderBelow: [{
                type: Input
            }], headerLevel: [{
                type: Input
            }], action: [{
                type: ContentChild,
                args: [ContentCardHeaderActionSlot]
            }], content: [{
                type: ContentChild,
                args: [ContentCardHeaderContentSlot]
            }] } });
const DIRECTIVES$i = [
    ContentCardHeaderActionSlot,
    ContentCardComponent,
    ContentCardHeaderContentSlot,
];
class ContentCardModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ContentCardModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: ContentCardModule, declarations: [ContentCardHeaderActionSlot, ContentCardComponent, ContentCardHeaderContentSlot], imports: [CommonModule,
            HeadingModule,
            ColumnsModule,
            StackModule,
            CardModule], exports: [ContentCardHeaderActionSlot, ContentCardComponent, ContentCardHeaderContentSlot] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ContentCardModule, imports: [CommonModule,
            HeadingModule,
            ColumnsModule,
            StackModule,
            CardModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ContentCardModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        HeadingModule,
                        ColumnsModule,
                        StackModule,
                        CardModule,
                    ],
                    declarations: DIRECTIVES$i,
                    exports: DIRECTIVES$i,
                }]
        }] });

class RangePipe {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    transform(_ = [], size = 0, start = 0, step = 1) {
        return Array.from({ length: size }, (_, i) => i * step + start);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: RangePipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: RangePipe, name: "range" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: RangePipe, decorators: [{
            type: Pipe,
            args: [{ name: 'range' }]
        }] });
const DIRECTIVES$h = [RangePipe];
class RangeModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: RangeModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: RangeModule, declarations: [RangePipe], exports: [RangePipe] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: RangeModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: RangeModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$h,
                    exports: DIRECTIVES$h,
                }]
        }] });

var __decorate$g = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
// TODO: create a variable for these colors in stitches config
const skeletonStart = '$neutral-2';
const skeletonEnd = '$neutral-5';
const $animation = keyframes({
    '0%': { background: skeletonStart, borderColor: skeletonStart },
    '100%': { background: skeletonEnd, boderColor: skeletonEnd },
});
const $fade = keyframes({
    from: { opacity: 0 },
    to: { opacity: 1 },
});
const $SkeletonBase = css({
    display: 'block',
    variants: {
        status: {
            loading: {
                animation: `1s ease-in-out infinite alternate ${$animation}`,
                boxShadow: '$none',
                backgroundClip: 'padding-box',
                cursor: 'default',
                color: 'transparent',
                pointerEvents: 'none',
                userSelect: 'none',
                borderRadius: '$small',
                opacity: '0.7',
                '&::before, &::after, *': {
                    visibility: 'hidden',
                },
            },
            loaded: {
                animation: `${$fade} 400ms ease-in-out`,
            },
        },
    },
});
const $Skeleton = css($SkeletonBase, {
    variants: {
        inline: {
            true: { display: 'inline-block' },
        },
        r: {
            none: { borderRadius: '$none' },
            small: { borderRadius: '$small' },
            medium: { borderRadius: '$medium' },
            large: { borderRadius: '$large' },
            round: { borderRadius: '$round' },
            pill: { borderRadius: '$pill' },
        },
    },
});
let SkeletonComponent = class SkeletonComponent {
    constructor() {
        this.loading = false;
    }
    get css() {
        return propsToCss({
            width: this.width,
            minWidth: this.minWidth,
            maxWidth: this.maxWidth,
            height: this.height,
            minHeight: this.minHeight,
            maxHeight: this.maxHeight,
            // There is an issue with the utils types, casting to any to avoid it.
            size: this.size,
        });
    }
    get status() {
        return this.loading ? 'loading' : 'loaded';
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: SkeletonComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: SkeletonComponent, selector: "kp-skeleton", inputs: { loading: "loading", inline: "inline", r: "r", width: "width", minWidth: "minWidth", maxWidth: "maxWidth", height: "height", minHeight: "minHeight", maxHeight: "maxHeight", size: "size" }, host: { properties: { "class": "this.class" } }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
};
__decorate$g([
    CoerceBoolean()
], SkeletonComponent.prototype, "loading", void 0);
__decorate$g([
    CoerceBoolean()
], SkeletonComponent.prototype, "inline", void 0);
SkeletonComponent = __decorate$g([
    Styled$1($Skeleton)
], SkeletonComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: SkeletonComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-skeleton',
                    template: '<ng-content></ng-content>',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                }]
        }], propDecorators: { class: [{
                type: HostBinding,
                args: ['class']
            }], loading: [{
                type: Input
            }], inline: [{
                type: Input
            }], r: [{
                type: Input
            }], width: [{
                type: Input
            }], minWidth: [{
                type: Input
            }], maxWidth: [{
                type: Input
            }], height: [{
                type: Input
            }], minHeight: [{
                type: Input
            }], maxHeight: [{
                type: Input
            }], size: [{
                type: Input
            }] } });
const $SkeletonParagraph = css($SkeletonBase);
let SkeletonParagraphComponent = class SkeletonParagraphComponent {
    constructor() {
        this.loading = false;
        this.lines = 1;
        this.space = 'small';
    }
    get css() {
        return propsToCss({
            width: this.width,
            minWidth: this.minWidth,
            maxWidth: this.maxWidth,
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: SkeletonParagraphComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: SkeletonParagraphComponent, selector: "kp-skeleton-paragraph", inputs: { loading: "loading", lines: "lines", r: "r", space: "space", textSize: "textSize", width: "width", minWidth: "minWidth", maxWidth: "maxWidth" }, host: { properties: { "class": "this.class" } }, ngImport: i0, template: `
    <ng-container *ngIf="loading">
      <kp-stack [space]="space">
        <ng-container *ngFor="let i of [] | range: lines; let last = last">
          <kp-skeleton
            [loading]="loading"
            [width]="last && lines > 1 ? '85%' : '$full'"
            [r]="r"
          >
            <kp-text-block [size]="textSize">&nbsp;</kp-text-block>
          </kp-skeleton>
        </ng-container>
      </kp-stack>
    </ng-container>
    <ng-container *ngIf="!loading">
      <ng-content></ng-content>
    </ng-container>
  `, isInline: true, dependencies: [{ kind: "directive", type: i1$1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1$1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: TextBlockComponent, selector: "kp-text-block, div[kp-text-block], p[kp-text-block], a[kp-text-block]", inputs: ["tone", "weight", "size", "alignment", "contrast", "truncate", "clamp"] }, { kind: "directive", type: TextBackgroundBehavior, selector: "kp-text, span[kp-text], a[kp-text], kp-text-block, div[kp-text-block], p[kp-text-block], a[kp-text-block]" }, { kind: "component", type: StackComponent, selector: "kp-stack", inputs: ["space", "alignment", "grow"] }, { kind: "component", type: SkeletonComponent, selector: "kp-skeleton", inputs: ["loading", "inline", "r", "width", "minWidth", "maxWidth", "height", "minHeight", "maxHeight", "size"] }, { kind: "pipe", type: RangePipe, name: "range" }], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
};
__decorate$g([
    CoerceBoolean()
], SkeletonParagraphComponent.prototype, "loading", void 0);
SkeletonParagraphComponent = __decorate$g([
    Styled$1($SkeletonParagraph)
], SkeletonParagraphComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: SkeletonParagraphComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-skeleton-paragraph',
                    template: `
    <ng-container *ngIf="loading">
      <kp-stack [space]="space">
        <ng-container *ngFor="let i of [] | range: lines; let last = last">
          <kp-skeleton
            [loading]="loading"
            [width]="last && lines > 1 ? '85%' : '$full'"
            [r]="r"
          >
            <kp-text-block [size]="textSize">&nbsp;</kp-text-block>
          </kp-skeleton>
        </ng-container>
      </kp-stack>
    </ng-container>
    <ng-container *ngIf="!loading">
      <ng-content></ng-content>
    </ng-container>
  `,
                    changeDetection: ChangeDetectionStrategy.OnPush,
                }]
        }], propDecorators: { class: [{
                type: HostBinding,
                args: ['class']
            }], loading: [{
                type: Input
            }], lines: [{
                type: Input
            }], r: [{
                type: Input
            }], space: [{
                type: Input
            }], textSize: [{
                type: Input
            }], width: [{
                type: Input
            }], minWidth: [{
                type: Input
            }], maxWidth: [{
                type: Input
            }] } });
const DIRECTIVES$g = [SkeletonComponent, SkeletonParagraphComponent];
class SkeletonModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: SkeletonModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: SkeletonModule, declarations: [SkeletonComponent, SkeletonParagraphComponent], imports: [CommonModule, TextModule, StackModule, RangeModule], exports: [SkeletonComponent, SkeletonParagraphComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: SkeletonModule, imports: [CommonModule, TextModule, StackModule, RangeModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: SkeletonModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, TextModule, StackModule, RangeModule],
                    declarations: [DIRECTIVES$g],
                    exports: DIRECTIVES$g,
                }]
        }] });

var __decorate$f = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
class Backdrop extends BackdropPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: Backdrop, deps: null, target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.1.4", type: Backdrop, usesInheritance: true, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: Backdrop, decorators: [{
            type: Directive
        }] });
const $Backdrop = css({
    display: 'block',
    position: 'fixed',
    inset: 0,
    zIndex: '$backdrop',
    backgroundColor: '$backdrop',
});
let BackdropComponent = class BackdropComponent extends Backdrop {
    constructor(animator, element) {
        super(animator, element);
    }
    getShowAnimation() {
        return {
            keyframes: [{ opacity: 0 }, { opacity: 1 }],
            options: { duration: 400, easing: 'ease-in-out' },
        };
    }
    getHideAnimation() {
        return {
            keyframes: [{ opacity: 1 }, { opacity: 0 }],
            options: { duration: 400, easing: 'ease-in-out' },
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: BackdropComponent, deps: [{ token: i1$3.Animator }, { token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: BackdropComponent, selector: "kp-backdrop", host: { properties: { "class": "this.class" } }, usesInheritance: true, ngImport: i0, template: '', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
};
BackdropComponent = __decorate$f([
    Styled($Backdrop)
], BackdropComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: BackdropComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-backdrop',
                    template: '',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                }]
        }], ctorParameters: () => [{ type: i1$3.Animator }, { type: i0.ElementRef }], propDecorators: { class: [{
                type: HostBinding,
                args: ['class']
            }] } });
const DIRECTIVES$f = [BackdropComponent];
class BackdropModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: BackdropModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: BackdropModule, declarations: [BackdropComponent], exports: [BackdropComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: BackdropModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: BackdropModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: DIRECTIVES$f,
                    exports: DIRECTIVES$f,
                }]
        }] });

var __decorate$e = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
const $Header = css({
    display: 'block',
    p: '$medium',
    borderTopLeftRadius: '$small',
    borderTopRightRadius: '$small',
    variants: {
        tone: {
            primary: {
                background: '$primary-subtle',
                color: '$primary-foreground',
            },
            accent: {
                background: '$accent-subtle',
                color: '$accent-foreground',
            },
            neutral: {
                background: '$neutral-subtle',
                color: '$neutral-foreground',
            },
            success: {
                background: '$success-subtle',
                color: '$success-foreground',
            },
            warning: {
                background: '$warning-subtle',
                color: '$warning-foreground',
            },
            danger: {
                background: '$danger-subtle',
                color: '$danger-foreground',
            },
        },
    },
    defaultVariants: {
        tone: 'primary',
    },
});
let DialogLayoutHeaderComponent = class DialogLayoutHeaderComponent {
    constructor() {
        this.header = '';
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: DialogLayoutHeaderComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: DialogLayoutHeaderComponent, selector: "kp-dialog-layout-header", inputs: { header: "header", icon: "icon", tone: "tone" }, host: { properties: { "class": "this.class" } }, ngImport: i0, template: `
    <kp-set space="medium">
      <kp-icon *ngIf="!!icon" [icon]="icon" />

      <kp-text-block weight="bold">
        {{ header }}
      </kp-text-block>
    </kp-set>
  `, isInline: true, dependencies: [{ kind: "directive", type: i1$1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: SetComponent, selector: "kp-set", inputs: ["space", "alignmentX", "alignmentY", "collapseBelow", "wrap", "reverse", "grow"] }, { kind: "component", type: IconComponent, selector: "kp-icon", inputs: ["icon"] }, { kind: "component", type: TextBlockComponent, selector: "kp-text-block, div[kp-text-block], p[kp-text-block], a[kp-text-block]", inputs: ["tone", "weight", "size", "alignment", "contrast", "truncate", "clamp"] }, { kind: "directive", type: TextBackgroundBehavior, selector: "kp-text, span[kp-text], a[kp-text], kp-text-block, div[kp-text-block], p[kp-text-block], a[kp-text-block]" }], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
};
DialogLayoutHeaderComponent = __decorate$e([
    Styled($Header)
], DialogLayoutHeaderComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: DialogLayoutHeaderComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-dialog-layout-header',
                    template: `
    <kp-set space="medium">
      <kp-icon *ngIf="!!icon" [icon]="icon" />

      <kp-text-block weight="bold">
        {{ header }}
      </kp-text-block>
    </kp-set>
  `,
                    changeDetection: ChangeDetectionStrategy.OnPush,
                }]
        }], propDecorators: { class: [{
                type: HostBinding,
                args: ['class']
            }], header: [{
                type: Input
            }], icon: [{
                type: Input
            }], tone: [{
                type: Input
            }] } });
const $Content = css({
    display: 'block',
    p: '$large',
});
let DialogLayoutContentComponent = class DialogLayoutContentComponent {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: DialogLayoutContentComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: DialogLayoutContentComponent, selector: "kp-dialog-layout-content", host: { properties: { "class": "this.class" } }, ngImport: i0, template: `<ng-content></ng-content>`, isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
};
DialogLayoutContentComponent = __decorate$e([
    Styled($Content)
], DialogLayoutContentComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: DialogLayoutContentComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-dialog-layout-content',
                    template: `<ng-content></ng-content>`,
                    changeDetection: ChangeDetectionStrategy.OnPush,
                }]
        }], propDecorators: { class: [{
                type: HostBinding,
                args: ['class']
            }] } });
const $Layout = css({
    display: 'block',
    maxW: '80vw',
    variants: {
        width: {
            small: {
                '@tablet': {
                    maxW: '30rem',
                },
            },
            medium: {
                '@tablet': {
                    maxW: '50rem',
                },
            },
            large: {
                '@tablet': {
                    maxW: '60rem',
                },
            },
        },
    },
    defaultVariants: {
        width: 'medium',
    },
});
let DialogLayoutComponent = class DialogLayoutComponent {
    constructor() {
        this.space = 'none';
        this.header = '';
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: DialogLayoutComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: DialogLayoutComponent, selector: "kp-dialog-layout", inputs: { space: "space", tone: "tone", header: "header", icon: "icon", width: "width" }, host: { properties: { "class": "this.class" } }, ngImport: i0, template: `<kp-stack [space]="space">
    <kp-dialog-layout-header [header]="header" [icon]="icon" [tone]="tone" />

    <kp-dialog-layout-content>
      <ng-content></ng-content>
    </kp-dialog-layout-content>
  </kp-stack>`, isInline: true, dependencies: [{ kind: "component", type: StackComponent, selector: "kp-stack", inputs: ["space", "alignment", "grow"] }, { kind: "component", type: DialogLayoutHeaderComponent, selector: "kp-dialog-layout-header", inputs: ["header", "icon", "tone"] }, { kind: "component", type: DialogLayoutContentComponent, selector: "kp-dialog-layout-content" }], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
};
DialogLayoutComponent = __decorate$e([
    Styled($Layout)
], DialogLayoutComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: DialogLayoutComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-dialog-layout',
                    template: `<kp-stack [space]="space">
    <kp-dialog-layout-header [header]="header" [icon]="icon" [tone]="tone" />

    <kp-dialog-layout-content>
      <ng-content></ng-content>
    </kp-dialog-layout-content>
  </kp-stack>`,
                    changeDetection: ChangeDetectionStrategy.OnPush,
                }]
        }], propDecorators: { class: [{
                type: HostBinding,
                args: ['class']
            }], space: [{
                type: Input
            }], tone: [{
                type: Input
            }], header: [{
                type: Input
            }], icon: [{
                type: Input
            }], width: [{
                type: Input
            }] } });
globalCss({
    'body[hy-dialog]': {
        overflow: 'hidden',
    },
})();
const $Dialog = css({
    '& > *:last-child': {
        position: 'fixed',
        inset: 0,
        zIndex: '$4',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'center',
        alignItems: 'center',
        [`& > ${CardComponent}`]: {
            position: 'relative',
            p: '$none',
        },
    },
    variants: {
        overflow: {
            true: {
                '& > *:last-child': {
                    overflow: 'auto',
                    alignItems: 'flex-start',
                    [`& > ${CardComponent}`]: {
                        m: 'auto',
                    },
                },
            },
        },
    },
});
let DialogComponent = class DialogComponent extends DialogElement {
    get overflow() {
        return this.context.hasOverflowEnabled;
    }
    constructor(context, element, zone, animator, document) {
        super();
        this.context = context;
        this.element = element;
        this.zone = zone;
        this.animator = animator;
        this.document = document;
        this.destroy$ = new Subject();
    }
    async ngAfterViewInit() {
        const body = this.element.nativeElement;
        const dialog = this.dialog.nativeElement;
        this.zone.runOutsideAngular(() => {
            fromEvent(body, 'click')
                .pipe(filter(e => {
                const target = e.target;
                return (!this.context.isLocked &&
                    target !== dialog &&
                    !dialog.contains(target) &&
                    (target.isConnected != null
                        ? target.isConnected
                        : this.document.body.contains(target)));
            }), distinctUntilChanged(), takeUntil(this.destroy$))
                .subscribe(() => this.zone.run(() => this.context.dismiss()));
            fromEvent(document, 'keydown')
                .pipe(filter((e) => e.key === 'Escape' &&
                this.context.isDismissOnEscapeEnabled &&
                !this.context.isLocked), takeUntil(this.destroy$))
                .subscribe(e => this.zone.run(() => {
                e.stopImmediatePropagation();
                this.context.dismiss();
            }));
        });
        await this.show();
    }
    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }
    async show() {
        if (!this.dialog)
            return;
        const dialog = this.dialog.nativeElement;
        await this.animator.stop(dialog);
        const animation = {
            keyframes: [
                { opacity: 0, transform: 'translate3d(0, -8rem, 0)' },
                { opacity: 1, transform: 'translate3d(0, 0, 0)' },
            ],
            options: { duration: 400, easing: 'cubic-bezier(0.7,0,0.3,1)' },
        };
        if (!animation)
            return;
        await this.animator.animate(dialog, animation);
    }
    async hide() {
        if (!this.dialog)
            return;
        const dialog = this.dialog.nativeElement;
        await this.animator.stop(dialog);
        const animation = {
            keyframes: [
                { opacity: 1, transform: 'translate3d(0, 0, 0)' },
                { opacity: 0, transform: 'translate3d(0, 2rem, 0)' },
            ],
            options: { duration: 400, easing: 'cubic-bezier(0.7,0,0.3,1)' },
        };
        if (!animation)
            return;
        await Promise.all([
            this.animator.animate(dialog, animation),
            this.backdrop.hide(),
        ]);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: DialogComponent, deps: [{ token: i6.DialogContext }, { token: i0.ElementRef }, { token: i0.NgZone }, { token: i1$3.Animator }, { token: DOCUMENT }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: DialogComponent, selector: "kp-dialog", host: { properties: { "class": "this.class" } }, viewQueries: [{ propertyName: "dialog", first: true, predicate: ["dialog"], descendants: true, read: ElementRef, static: true }, { propertyName: "backdrop", first: true, predicate: ["backdrop"], descendants: true, static: true }], usesInheritance: true, ngImport: i0, template: `
    <kp-backdrop #backdrop></kp-backdrop>
    <div>
      <kp-card
        #dialog
        role="dialog"
        [attr.tabindex]="0"
        shadow="medium"
        radii="small"
      >
        <ng-content></ng-content>
      </kp-card>
    </div>
  `, isInline: true, dependencies: [{ kind: "component", type: CardComponent, selector: "kp-card", inputs: ["shadow", "radii"] }, { kind: "component", type: BackdropComponent, selector: "kp-backdrop" }], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
};
DialogComponent = __decorate$e([
    Styled($Dialog)
], DialogComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: DialogComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-dialog',
                    template: `
    <kp-backdrop #backdrop></kp-backdrop>
    <div>
      <kp-card
        #dialog
        role="dialog"
        [attr.tabindex]="0"
        shadow="medium"
        radii="small"
      >
        <ng-content></ng-content>
      </kp-card>
    </div>
  `,
                    changeDetection: ChangeDetectionStrategy.OnPush,
                }]
        }], ctorParameters: () => [{ type: i6.DialogContext }, { type: i0.ElementRef }, { type: i0.NgZone }, { type: i1$3.Animator }, { type: Document, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }], propDecorators: { class: [{
                type: HostBinding,
                args: ['class']
            }], dialog: [{
                type: ViewChild,
                args: ['dialog', { static: true, read: ElementRef }]
            }], backdrop: [{
                type: ViewChild,
                args: ['backdrop', { static: true }]
            }] } });
class DialogContent extends DialogPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: DialogContent, deps: null, target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.1.4", type: DialogContent, usesInheritance: true, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: DialogContent, decorators: [{
            type: Directive
        }] });
class Dialog extends DialogService {
    createDialogComponent(context, content) {
        return this.componentFactory.create(DialogComponent, {
            content: content
                ? {
                    value: content,
                    providers: [{ provide: DialogContext, useValue: context }],
                }
                : void 0,
            providers: [{ provide: DialogContext, useValue: context }],
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: Dialog, deps: null, target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: Dialog, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: Dialog, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }] });
const DIRECTIVES$e = [DialogComponent, DialogLayoutComponent];
class DialogModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: DialogModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: DialogModule, declarations: [DialogComponent, DialogLayoutComponent, DialogLayoutHeaderComponent, DialogLayoutContentComponent], imports: [CommonModule,
            CardModule,
            BackdropModule,
            StackModule,
            SetModule,
            IconsModule,
            TextModule], exports: [DialogComponent, DialogLayoutComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: DialogModule, imports: [CommonModule,
            CardModule,
            BackdropModule,
            StackModule,
            SetModule,
            IconsModule,
            TextModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: DialogModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        CardModule,
                        BackdropModule,
                        StackModule,
                        SetModule,
                        IconsModule,
                        TextModule,
                    ],
                    declarations: [
                        ...DIRECTIVES$e,
                        DialogLayoutHeaderComponent,
                        DialogLayoutContentComponent,
                    ],
                    exports: DIRECTIVES$e,
                }]
        }] });

var __decorate$d = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
const ARROW_SVG$1 = '%3Csvg%20width%3D%2210%22%20height%3D%226%22%20viewBox%3D%220%200%2010%206%22%20fill%3D%22none%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%0A%3Cpath%20d%3D%22M9%201L5%205L1%201%22%20stroke%3D%22%23677594%22%20stroke-width%3D%222%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%2F%3E%0A%3C%2Fsvg%3E%0A';
const $SuffixDropdownIcon = css({
    display: 'inline-block',
    w: '$3',
    h: '$3',
    color: '$foreground',
    backgroundPosition: 'bottom 50% left 0',
    backgroundRepeat: 'no-repeat',
    backgroundImage: `url("data:image/svg+xml,${ARROW_SVG$1}")`,
    variants: {
        active: { true: { color: '$foreground' } },
    },
});
let SuffixDropdownIconComponent = class SuffixDropdownIconComponent {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: SuffixDropdownIconComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: SuffixDropdownIconComponent, selector: "kp-suffix-dropdown-icon", inputs: { active: "active" }, host: { properties: { "class": "this.class" } }, ngImport: i0, template: '', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
};
SuffixDropdownIconComponent = __decorate$d([
    Styled($SuffixDropdownIcon)
], SuffixDropdownIconComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: SuffixDropdownIconComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-suffix-dropdown-icon',
                    template: '',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                }]
        }], propDecorators: { class: [{
                type: HostBinding,
                args: ['class']
            }], active: [{
                type: Input
            }] } });
function tone$3(color) {
    const border = color === 'none' ? 'border' : `${color}-border`;
    return {
        borderColor: `$${border}`,
        '&:hover': {
            borderColor: `$${border}-active`,
        },
        '&[disabled="true"], &[aria-disabled="true"]': {
            pointerEvents: 'none',
            cursor: 'not-allowed',
            bg: '$neutral-subtle',
            color: '$neutral-foreground',
        },
    };
}
function compound(tone) {
    const border = tone === 'none' ? 'border' : `${tone}-border`;
    return {
        active: true,
        tone,
        css: { borderColor: `$${border}-active` },
    };
}
const PADDING$3 = {
    small: '1.25rem',
    medium: '1.5rem',
    large: '1.75rem',
};
const $DropdownTrigger = css({
    display: 'flex',
    alignItems: 'center',
    fontSize: '$small',
    lineHeight: '$none',
    bg: '$panel',
    color: '$foreground',
    px: '$medium',
    h: '$full',
    minW: '$10',
    borderRadius: '$pill',
    borderWidth: '$regular',
    borderStyle: 'solid',
    cursor: 'pointer',
    transition: 'border-color, transform 300ms ease',
    '& > kp-columns': {
        overflow: 'hidden',
    },
    '&[disabled="true"], &[aria-disabled="true"]': {
        pointerEvents: 'none',
        cursor: 'not-allowed',
        bg: '$neutral-subtle',
        color: '$neutral-foreground',
        '&:active': { transform: 'none' },
    },
    variants: {
        active: {
            true: {
                borderColor: 'blue',
            },
        },
        size: {
            small: {
                h: '3rem',
                fontSize: '$small',
                '& > input': {
                    px: PADDING$3.small,
                },
            },
            medium: {
                h: '3.5rem',
                fontSize: '$medium',
                '& > input': {
                    px: PADDING$3.medium,
                },
            },
            large: {
                h: '4.5rem',
                fontSize: '$large',
                '& > input': {
                    px: PADDING$3.large,
                },
            },
        },
        tone: {
            none: tone$3('none'),
            success: tone$3('success'),
            warning: tone$3('warning'),
            danger: tone$3('danger'),
        },
    },
    defaultVariants: {
        size: 'small',
        tone: 'none',
    },
    compoundVariants: [
        compound('none'),
        compound('success'),
        compound('warning'),
        compound('danger'),
    ],
});
class DropdownTriggerSlot {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: DropdownTriggerSlot, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.1.4", type: DropdownTriggerSlot, selector: "[kp-dropdown-trigger]", ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: DropdownTriggerSlot, decorators: [{
            type: Directive,
            args: [{ selector: '[kp-dropdown-trigger]' }]
        }] });
class DropdownTriggerContentSlot {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: DropdownTriggerContentSlot, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.1.4", type: DropdownTriggerContentSlot, selector: "[kp-dropdown-trigger-content]", ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: DropdownTriggerContentSlot, decorators: [{
            type: Directive,
            args: [{ selector: '[kp-dropdown-trigger-content]' }]
        }] });
class DropdownTriggerPrefixIconSlot {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: DropdownTriggerPrefixIconSlot, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.1.4", type: DropdownTriggerPrefixIconSlot, selector: "[kp-dropdown-trigger-prefix-icon]", ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: DropdownTriggerPrefixIconSlot, decorators: [{
            type: Directive,
            args: [{ selector: '[kp-dropdown-trigger-prefix-icon]' }]
        }] });
class DropdownTriggerSuffixIconSlot {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: DropdownTriggerSuffixIconSlot, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.1.4", type: DropdownTriggerSuffixIconSlot, selector: "[kp-dropdown-trigger-suffix-icon]", ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: DropdownTriggerSuffixIconSlot, decorators: [{
            type: Directive,
            args: [{ selector: '[kp-dropdown-trigger-suffix-icon]' }]
        }] });
let DropdownTriggerComponent = class DropdownTriggerComponent {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: DropdownTriggerComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: DropdownTriggerComponent, selector: "kp-dropdown-trigger", inputs: { text: "text", active: "active", tone: "tone", size: "size" }, host: { properties: { "class": "this.class" } }, queries: [{ propertyName: "content", first: true, predicate: DropdownTriggerContentSlot, descendants: true }, { propertyName: "suffixIcon", first: true, predicate: DropdownTriggerSuffixIconSlot, descendants: true }, { propertyName: "prefixIcon", first: true, predicate: DropdownTriggerPrefixIconSlot, descendants: true }], ngImport: i0, template: `
    <kp-columns alignmentY="center" space="medium">
      <kp-column width="content">
        <ng-container *ngIf="!!prefixIcon" class="dropdown-icon">
          <ng-content select="[kp-dropdown-trigger-prefix-icon]"></ng-content>
        </ng-container>
      </kp-column>
      <kp-column>
        <kp-text-block *ngIf="!content">
          {{ text }}
        </kp-text-block>
        <ng-container *ngIf="!!content">
          <ng-content select="[kp-dropdown-trigger-content]"></ng-content>
        </ng-container>
      </kp-column>
      <kp-column width="content">
        <kp-suffix-dropdown-icon
          *ngIf="!suffixIcon"
          [active]="active"
        ></kp-suffix-dropdown-icon>
        <ng-container *ngIf="!!suffixIcon" class="dropdown-icon">
          <ng-content select="[kp-dropdown-trigger-suffix-icon]"></ng-content>
        </ng-container>
      </kp-column>
    </kp-columns>
  `, isInline: true, dependencies: [{ kind: "directive", type: i1$1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: TextBlockComponent, selector: "kp-text-block, div[kp-text-block], p[kp-text-block], a[kp-text-block]", inputs: ["tone", "weight", "size", "alignment", "contrast", "truncate", "clamp"] }, { kind: "directive", type: TextBackgroundBehavior, selector: "kp-text, span[kp-text], a[kp-text], kp-text-block, div[kp-text-block], p[kp-text-block], a[kp-text-block]" }, { kind: "component", type: ColumnComponent, selector: "kp-column", inputs: ["width"] }, { kind: "component", type: ColumnsComponent, selector: "kp-columns", inputs: ["space", "alignmentX", "alignmentY", "collapseBelow", "wrap", "reverse"] }, { kind: "component", type: SuffixDropdownIconComponent, selector: "kp-suffix-dropdown-icon", inputs: ["active"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
};
DropdownTriggerComponent = __decorate$d([
    Styled($DropdownTrigger)
], DropdownTriggerComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: DropdownTriggerComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-dropdown-trigger',
                    template: `
    <kp-columns alignmentY="center" space="medium">
      <kp-column width="content">
        <ng-container *ngIf="!!prefixIcon" class="dropdown-icon">
          <ng-content select="[kp-dropdown-trigger-prefix-icon]"></ng-content>
        </ng-container>
      </kp-column>
      <kp-column>
        <kp-text-block *ngIf="!content">
          {{ text }}
        </kp-text-block>
        <ng-container *ngIf="!!content">
          <ng-content select="[kp-dropdown-trigger-content]"></ng-content>
        </ng-container>
      </kp-column>
      <kp-column width="content">
        <kp-suffix-dropdown-icon
          *ngIf="!suffixIcon"
          [active]="active"
        ></kp-suffix-dropdown-icon>
        <ng-container *ngIf="!!suffixIcon" class="dropdown-icon">
          <ng-content select="[kp-dropdown-trigger-suffix-icon]"></ng-content>
        </ng-container>
      </kp-column>
    </kp-columns>
  `,
                    changeDetection: ChangeDetectionStrategy.OnPush,
                }]
        }], propDecorators: { class: [{
                type: HostBinding,
                args: ['class']
            }], text: [{
                type: Input
            }], active: [{
                type: Input
            }], tone: [{
                type: Input
            }], size: [{
                type: Input
            }], content: [{
                type: ContentChild,
                args: [DropdownTriggerContentSlot]
            }], suffixIcon: [{
                type: ContentChild,
                args: [DropdownTriggerSuffixIconSlot]
            }], prefixIcon: [{
                type: ContentChild,
                args: [DropdownTriggerPrefixIconSlot]
            }] } });
const $DropdownPanel = css({
    display: 'block',
    py: '$medium',
    m: '$xsmall',
    boxShadow: '$small',
    backgroundColor: '$panel',
    borderRadius: '$small',
});
let DropdownPanelComponent = class DropdownPanelComponent {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: DropdownPanelComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: DropdownPanelComponent, selector: "kp-dropdown-panel", host: { properties: { "class": "this.class" } }, ngImport: i0, template: ` <ng-content></ng-content> `, isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
};
DropdownPanelComponent = __decorate$d([
    Styled($DropdownPanel)
], DropdownPanelComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: DropdownPanelComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-dropdown-panel',
                    template: ` <ng-content></ng-content> `,
                    changeDetection: ChangeDetectionStrategy.OnPush,
                }]
        }], propDecorators: { class: [{
                type: HostBinding,
                args: ['class']
            }] } });
class DropdownBehavior extends PopoverPrimitive {
    constructor() {
        super(...arguments);
        this.keepOpenOnContentClick = false;
        this.fixed = false;
        this.zIndex = '$max';
        this.shown = new EventEmitter();
        this.hidden = new EventEmitter();
    }
    async show() {
        await super.show();
        this.shown.emit();
    }
    async hide() {
        await super.hide();
        this.hidden.emit();
    }
    createPopoverComponent(value) {
        return this.componentFactory.create(DropdownPanelComponent, {
            content: { value },
        });
    }
    getOptions() {
        const options = super.getOptions();
        options.strategy = this.fixed ? 'fixed' : 'absolute';
        const modifiers = [
            {
                name: 'arrow',
                options: {
                    element: null,
                },
            },
            {
                name: 'apply-zindex',
                enabled: true,
                phase: 'write',
                fn: ({ state }) => {
                    const { popper } = state.elements;
                    if (!popper)
                        return;
                    const $zIndex = css({
                        zIndex: this.zIndex,
                    });
                    popper.classList.add($zIndex);
                },
            },
            {
                name: 'preventOverflow',
                options: {
                    boundary: this.boundary,
                },
            },
            {
                name: 'flip',
                options: {
                    boundary: this.boundary,
                },
            },
        ];
        options.modifiers = !!options.modifiers
            ? options.modifiers.concat(modifiers)
            : modifiers;
        return options;
    }
    getShowAnimation() {
        return {
            keyframes: [
                { opacity: 0, transform: 'translate3d(0, -8px, 0) scale(0.9)' },
                { opacity: 1, transform: 'translate3d(0, 0, 0) scale(1)' },
            ],
            options: { duration: 200, easing: 'ease-out' },
        };
    }
    getHideAnimation() {
        return {
            keyframes: [
                { opacity: 1, transform: 'translate3d(0, 0, 0) scale(1)' },
                { opacity: 0, transform: 'translate3d(0, -8px, 0) scale(0.9)' },
            ],
            options: { duration: 200, easing: 'ease-in' },
        };
    }
    getKeepOpenOnContentClick() {
        return this.keepOpenOnContentClick;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: DropdownBehavior, deps: null, target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.1.4", type: DropdownBehavior, selector: "[kp-dropdown-panel]", inputs: { content: ["kp-dropdown-panel", "content"], trigger: ["kp-dropdown-trigger", "trigger"], placement: ["kp-dropdown-placement", "placement"], keepOpenOnContentClick: ["kp-dropdown-keep-open-on-click", "keepOpenOnContentClick"], fixed: "fixed", zIndex: "zIndex", boundary: ["kp-dropdown-boundary", "boundary"] }, outputs: { shown: "shown", hidden: "hidden" }, providers: [POPOVER_PROVIDERS, TEXT_CONTENT_PROVIDERS], exportAs: ["dropdown-content"], usesInheritance: true, ngImport: i0 }); }
}
__decorate$d([
    CoerceBoolean()
], DropdownBehavior.prototype, "keepOpenOnContentClick", void 0);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: DropdownBehavior, decorators: [{
            type: Directive,
            args: [{
                    selector: '[kp-dropdown-panel]',
                    exportAs: 'dropdown-content',
                    providers: [POPOVER_PROVIDERS, TEXT_CONTENT_PROVIDERS],
                }]
        }], propDecorators: { content: [{
                type: Input,
                args: ['kp-dropdown-panel']
            }], trigger: [{
                type: Input,
                args: ['kp-dropdown-trigger']
            }], placement: [{
                type: Input,
                args: ['kp-dropdown-placement']
            }], keepOpenOnContentClick: [{
                type: Input,
                args: ['kp-dropdown-keep-open-on-click']
            }], fixed: [{
                type: Input
            }], zIndex: [{
                type: Input
            }], boundary: [{
                type: Input,
                args: ['kp-dropdown-boundary']
            }], shown: [{
                type: Output
            }], hidden: [{
                type: Output
            }] } });
class DropdownContentDirective {
    constructor(behavior) {
        this.behavior = behavior;
    }
    show() {
        return this.behavior.show();
    }
    hide() {
        return this.behavior.hide();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: DropdownContentDirective, deps: [{ token: DropdownBehavior, host: true }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.1.4", type: DropdownContentDirective, selector: "[kp-dropdown-panel]", exportAs: ["dropdown-content"], ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: DropdownContentDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[kp-dropdown-panel]',
                    exportAs: 'dropdown-content',
                }]
        }], ctorParameters: () => [{ type: DropdownBehavior, decorators: [{
                    type: Host
                }] }] });
const $Dropdown = css({
    display: 'block',
});
let DropdownComponent = class DropdownComponent {
    onBlur() {
        this.blured.emit();
    }
    onFocus() {
        this.focused.emit();
    }
    constructor(detector) {
        this.detector = detector;
        this.tabindex = '0';
        this.opened = false;
        this.disabled = false;
        this.fixed = false;
        this.keepOpenOnContentClick = false;
        this.boundary = 'clippingParents';
        this.zIndex = '$max';
        this.placement = 'bottom';
        this.shown = new EventEmitter();
        this.hidden = new EventEmitter();
        this.blured = new EventEmitter();
        this.focused = new EventEmitter();
    }
    onShown() {
        this.opened = true;
        this.detector.detectChanges();
        this.shown.emit();
    }
    onHidden() {
        this.opened = false;
        this.detector.detectChanges();
        this.hidden.emit();
    }
    async show() {
        if (!!this.panelContent)
            return this.panelContent.show();
        if (!!this.panelChild)
            return this.panelChild.show();
    }
    async hide() {
        if (!!this.panelContent)
            return this.panelContent.hide();
        if (!!this.panelChild)
            return this.panelChild.hide();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: DropdownComponent, deps: [{ token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: DropdownComponent, selector: "kp-dropdown", inputs: { text: "text", disabled: "disabled", fixed: "fixed", keepOpenOnContentClick: "keepOpenOnContentClick", boundary: "boundary", zIndex: "zIndex", placement: "placement", size: "size", tone: "tone" }, outputs: { shown: "shown", hidden: "hidden", blured: "blured", focused: "focused" }, host: { listeners: { "blur": "onBlur()", "focus": "onFocus()" }, properties: { "class": "this.class", "attr.tabindex": "this.tabindex" } }, queries: [{ propertyName: "trigger", first: true, predicate: DropdownTriggerSlot, descendants: true }, { propertyName: "triggerSuffixIcon", first: true, predicate: DropdownTriggerSuffixIconSlot, descendants: true }, { propertyName: "triggerPrefixIcon", first: true, predicate: DropdownTriggerPrefixIconSlot, descendants: true }, { propertyName: "triggerContent", first: true, predicate: DropdownTriggerContentSlot, descendants: true }, { propertyName: "panelContent", first: true, predicate: DropdownBehavior, descendants: true }], viewQueries: [{ propertyName: "panelChild", first: true, predicate: DropdownBehavior, descendants: true }], exportAs: ["dropdown"], ngImport: i0, template: `
    <ng-container *ngIf="!!trigger">
      <ng-content select="[kp-dropdown-trigger]"></ng-content>
    </ng-container>
    <ng-container *ngIf="!trigger && !!triggerContent">
      <kp-dropdown-trigger
        [active]="opened"
        [attr.disabled]="disabled || null"
        [attr.aria-disabled]="disabled || null"
      >
        <ng-container
          *ngIf="!!triggerPrefixIcon"
          kp-dropdown-trigger-prefix-icon
        >
          <ng-content select="[kp-dropdown-trigger-prefix-icon]"></ng-content>
        </ng-container>

        <ng-container kp-dropdown-trigger-content>
          <ng-content select="[kp-dropdown-trigger-content]"></ng-content>
        </ng-container>
        <ng-container
          *ngIf="!!triggerSuffixIcon"
          kp-dropdown-trigger-suffix-icon
        >
          <ng-content select="[kp-dropdown-trigger-suffix-icon]"></ng-content>
        </ng-container>
      </kp-dropdown-trigger>
    </ng-container>
    <ng-container *ngIf="!trigger && !triggerContent">
      <kp-dropdown-trigger
        [kp-dropdown-panel]="content"
        [kp-dropdown-placement]="placement"
        [kp-dropdown-boundary]="boundary"
        kp-dropdown-trigger="click"
        [kp-dropdown-keep-open-on-click]="keepOpenOnContentClick"
        [text]="text"
        [fixed]="fixed"
        [zIndex]="zIndex"
        [active]="opened"
        [tone]="tone"
        [size]="size"
        [attr.disabled]="disabled || null"
        [attr.aria-disabled]="disabled || null"
        (shown)="onShown()"
        (hidden)="onHidden()"
      >
        <ng-container
          *ngIf="!!triggerPrefixIcon"
          kp-dropdown-trigger-prefix-icon
        >
          <ng-content select="[kp-dropdown-trigger-prefix-icon]"></ng-content>
        </ng-container>

        <ng-container
          *ngIf="!!triggerSuffixIcon"
          kp-dropdown-trigger-suffix-icon
        >
          <ng-content select="[kp-dropdown-trigger-suffix-icon]"></ng-content>
        </ng-container>
      </kp-dropdown-trigger>
    </ng-container>

    <ng-template #content>
      <ng-content></ng-content>
    </ng-template>
  `, isInline: true, dependencies: [{ kind: "directive", type: i1$1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: DropdownTriggerComponent, selector: "kp-dropdown-trigger", inputs: ["text", "active", "tone", "size"] }, { kind: "directive", type: DropdownTriggerSlot, selector: "[kp-dropdown-trigger]" }, { kind: "directive", type: DropdownTriggerContentSlot, selector: "[kp-dropdown-trigger-content]" }, { kind: "directive", type: DropdownTriggerPrefixIconSlot, selector: "[kp-dropdown-trigger-prefix-icon]" }, { kind: "directive", type: DropdownTriggerSuffixIconSlot, selector: "[kp-dropdown-trigger-suffix-icon]" }, { kind: "directive", type: DropdownBehavior, selector: "[kp-dropdown-panel]", inputs: ["kp-dropdown-panel", "kp-dropdown-trigger", "kp-dropdown-placement", "kp-dropdown-keep-open-on-click", "fixed", "zIndex", "kp-dropdown-boundary"], outputs: ["shown", "hidden"], exportAs: ["dropdown-content"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
};
__decorate$d([
    CoerceBoolean()
], DropdownComponent.prototype, "disabled", void 0);
__decorate$d([
    CoerceBoolean()
], DropdownComponent.prototype, "fixed", void 0);
__decorate$d([
    CoerceBoolean()
], DropdownComponent.prototype, "keepOpenOnContentClick", void 0);
DropdownComponent = __decorate$d([
    Styled($Dropdown)
], DropdownComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: DropdownComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-dropdown',
                    exportAs: 'dropdown',
                    template: `
    <ng-container *ngIf="!!trigger">
      <ng-content select="[kp-dropdown-trigger]"></ng-content>
    </ng-container>
    <ng-container *ngIf="!trigger && !!triggerContent">
      <kp-dropdown-trigger
        [active]="opened"
        [attr.disabled]="disabled || null"
        [attr.aria-disabled]="disabled || null"
      >
        <ng-container
          *ngIf="!!triggerPrefixIcon"
          kp-dropdown-trigger-prefix-icon
        >
          <ng-content select="[kp-dropdown-trigger-prefix-icon]"></ng-content>
        </ng-container>

        <ng-container kp-dropdown-trigger-content>
          <ng-content select="[kp-dropdown-trigger-content]"></ng-content>
        </ng-container>
        <ng-container
          *ngIf="!!triggerSuffixIcon"
          kp-dropdown-trigger-suffix-icon
        >
          <ng-content select="[kp-dropdown-trigger-suffix-icon]"></ng-content>
        </ng-container>
      </kp-dropdown-trigger>
    </ng-container>
    <ng-container *ngIf="!trigger && !triggerContent">
      <kp-dropdown-trigger
        [kp-dropdown-panel]="content"
        [kp-dropdown-placement]="placement"
        [kp-dropdown-boundary]="boundary"
        kp-dropdown-trigger="click"
        [kp-dropdown-keep-open-on-click]="keepOpenOnContentClick"
        [text]="text"
        [fixed]="fixed"
        [zIndex]="zIndex"
        [active]="opened"
        [tone]="tone"
        [size]="size"
        [attr.disabled]="disabled || null"
        [attr.aria-disabled]="disabled || null"
        (shown)="onShown()"
        (hidden)="onHidden()"
      >
        <ng-container
          *ngIf="!!triggerPrefixIcon"
          kp-dropdown-trigger-prefix-icon
        >
          <ng-content select="[kp-dropdown-trigger-prefix-icon]"></ng-content>
        </ng-container>

        <ng-container
          *ngIf="!!triggerSuffixIcon"
          kp-dropdown-trigger-suffix-icon
        >
          <ng-content select="[kp-dropdown-trigger-suffix-icon]"></ng-content>
        </ng-container>
      </kp-dropdown-trigger>
    </ng-container>

    <ng-template #content>
      <ng-content></ng-content>
    </ng-template>
  `,
                    changeDetection: ChangeDetectionStrategy.OnPush,
                }]
        }], ctorParameters: () => [{ type: i0.ChangeDetectorRef }], propDecorators: { class: [{
                type: HostBinding,
                args: ['class']
            }], tabindex: [{
                type: HostBinding,
                args: ['attr.tabindex']
            }], onBlur: [{
                type: HostListener,
                args: ['blur']
            }], onFocus: [{
                type: HostListener,
                args: ['focus']
            }], text: [{
                type: Input
            }], disabled: [{
                type: Input
            }], fixed: [{
                type: Input
            }], keepOpenOnContentClick: [{
                type: Input
            }], boundary: [{
                type: Input
            }], zIndex: [{
                type: Input
            }], placement: [{
                type: Input
            }], size: [{
                type: Input
            }], tone: [{
                type: Input
            }], shown: [{
                type: Output
            }], hidden: [{
                type: Output
            }], blured: [{
                type: Output
            }], focused: [{
                type: Output
            }], trigger: [{
                type: ContentChild,
                args: [DropdownTriggerSlot]
            }], triggerSuffixIcon: [{
                type: ContentChild,
                args: [DropdownTriggerSuffixIconSlot]
            }], triggerPrefixIcon: [{
                type: ContentChild,
                args: [DropdownTriggerPrefixIconSlot]
            }], triggerContent: [{
                type: ContentChild,
                args: [DropdownTriggerContentSlot]
            }], panelContent: [{
                type: ContentChild,
                args: [DropdownBehavior]
            }], panelChild: [{
                type: ViewChild,
                args: [DropdownBehavior]
            }] } });
const DIRECTIVES$d = [
    DropdownPanelComponent,
    DropdownComponent,
    DropdownTriggerComponent,
    DropdownTriggerSlot,
    DropdownTriggerContentSlot,
    DropdownTriggerPrefixIconSlot,
    DropdownTriggerSuffixIconSlot,
    DropdownBehavior,
    SuffixDropdownIconComponent,
];
class DropdownModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: DropdownModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: DropdownModule, declarations: [DropdownPanelComponent, DropdownComponent, DropdownTriggerComponent, DropdownTriggerSlot, DropdownTriggerContentSlot, DropdownTriggerPrefixIconSlot, DropdownTriggerSuffixIconSlot, DropdownBehavior, SuffixDropdownIconComponent], imports: [CommonModule,
            TextRendererModule,
            ColumnsModule,
            TextModule,
            BoxModule], exports: [DropdownPanelComponent, DropdownComponent, DropdownTriggerComponent, DropdownTriggerSlot, DropdownTriggerContentSlot, DropdownTriggerPrefixIconSlot, DropdownTriggerSuffixIconSlot, DropdownBehavior, SuffixDropdownIconComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: DropdownModule, imports: [CommonModule,
            TextRendererModule,
            ColumnsModule,
            TextModule,
            BoxModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: DropdownModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        TextRendererModule,
                        ColumnsModule,
                        TextModule,
                        BoxModule,
                    ],
                    declarations: DIRECTIVES$d,
                    exports: DIRECTIVES$d,
                }]
        }] });

var __decorate$c = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
const HEIGHT = {
    small: '2rem',
    medium: '2.75rem',
    large: '3.5rem',
};
const PADDING$2 = {
    small: '1rem',
    medium: '1.25rem',
    large: '1.75rem',
};
function compoundAddon(size, side) {
    const padding = side === 'suffix' ? 'pr' : 'pl';
    const margin = side === 'suffix' ? 'mr' : 'ml';
    const buttonSelector = side === 'suffix' ? ':last-child' : ':first-child';
    return {
        size,
        side,
        css: {
            [padding]: PADDING$2[size],
            [`& > ${SetComponent} > ${SetContentComponent} > ${ButtonDirective}`]: {
                [`&${buttonSelector}`]: {
                    [margin]: `calc(-${PADDING$2[size]} - $borderWidths$thick)`,
                    '&[variant="ghost"]': {
                        [margin]: `-${PADDING$2[size]}`,
                    },
                },
            },
            [`& > ${SetComponent} > ${SetContentComponent} > ${DropdownComponent}`]: {
                [`&${buttonSelector}`]: {
                    [`${ButtonDirective}`]: {
                        [margin]: `calc(-${PADDING$2[size]} - $borderWidths$thick)`,
                        '&[variant="ghost"]': {
                            [margin]: `-${PADDING$2[size]}`,
                        },
                    },
                },
            },
        },
    };
}
function addonSize(size) {
    return {
        [`& > ${SetComponent} > ${SetContentComponent} ${ButtonDirective}`]: {
            '&[variant="ghost"]': {
                h: `calc(${HEIGHT[size]} - $borderWidths$thick * 2)`,
            },
        },
    };
}
class InputPrefixSlot {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: InputPrefixSlot, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.1.4", type: InputPrefixSlot, selector: "[kp-input-prefix]", ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: InputPrefixSlot, decorators: [{
            type: Directive,
            args: [{ selector: '[kp-input-prefix]' }]
        }] });
class InputSuffixSlot {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: InputSuffixSlot, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.1.4", type: InputSuffixSlot, selector: "[kp-input-suffix]", ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: InputSuffixSlot, decorators: [{
            type: Directive,
            args: [{ selector: '[kp-input-suffix]' }]
        }] });
const $InputAddon = css({
    display: 'flex',
    alignItems: 'center',
    position: 'relative',
    flexShrink: 0,
    color: '$foreground-subtle',
    h: '$full',
    bg: '$panel',
    borderRadius: '$pill',
    borderWidth: '$thick',
    borderStyle: 'solid',
    borderColor: '$border',
    transitionDuration: '300ms',
    transitionTimingFunction: 'ease',
    transitionProperty: 'border-color, color, background-color',
    userSelect: 'none',
    variants: {
        side: {
            prefix: {
                pr: '0.5rem',
                order: 0,
                borderRight: 0,
                borderTopRightRadius: 0,
                borderBottomRightRadius: 0,
                [`& > ${SetComponent} > ${SetContentComponent} > ${ButtonDirective}`]: {
                    '&:first-child': {
                        borderBottomRightRadius: '$none',
                        borderTopRightRadius: '$none',
                    },
                    '&:not(:first-child)': {
                        borderRadius: '$none',
                    },
                },
                [`& > ${SetComponent} > ${SetContentComponent} > ${DropdownComponent}`]: {
                    '&:first-child': {
                        [`${ButtonDirective}`]: {
                            borderBottomRightRadius: '$none',
                            borderTopRightRadius: '$none',
                        },
                    },
                    '&:not(:first-child)': {
                        [`${ButtonDirective}`]: {
                            borderRadius: '$none',
                        },
                    },
                },
            },
            suffix: {
                pl: '0.5rem',
                order: 2,
                borderLeft: 0,
                borderTopLeftRadius: 0,
                borderBottomLeftRadius: 0,
                [`& > ${SetComponent} > ${SetContentComponent} > ${ButtonDirective}`]: {
                    '&:last-child': {
                        borderBottomLeftRadius: '$none',
                        borderTopLeftRadius: '$none',
                    },
                    '&:not(:last-child)': {
                        borderRadius: '$none',
                    },
                },
                [`& > ${SetComponent} > ${SetContentComponent} > ${DropdownComponent}`]: {
                    '&:last-child': {
                        [`${ButtonDirective}`]: {
                            borderBottomLeftRadius: '$none',
                            borderTopLeftRadius: '$none',
                        },
                    },
                    '&:not(:last-child)': {
                        [`${ButtonDirective}`]: {
                            borderRadius: '$none',
                        },
                    },
                },
            },
        },
        size: {
            small: addonSize('small'),
            medium: addonSize('medium'),
            large: addonSize('large'),
        },
        disabled: {
            true: {
                pointerEvents: 'none',
                cursor: 'not-allowed',
                backgroundColor: '$neutral-subtle',
                opacity: 0.4,
            },
        },
    },
    defaultVariants: {
        size: 'medium',
    },
    compoundVariants: compoundVariants(compoundAddon, ['small', 'medium', 'large'], ['prefix', 'suffix']),
});
let InputAddonComponent = class InputAddonComponent {
    constructor() {
        this.disabled = false;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: InputAddonComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: InputAddonComponent, selector: "kp-input-addon", inputs: { size: "size", side: "side", disabled: "disabled" }, host: { properties: { "class": "this.class" } }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
};
InputAddonComponent = __decorate$c([
    Styled($InputAddon)
], InputAddonComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: InputAddonComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-input-addon',
                    template: '<ng-content></ng-content>',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                }]
        }], propDecorators: { class: [{
                type: HostBinding,
                args: ['class']
            }], size: [{
                type: Input
            }], side: [{
                type: Input
            }], disabled: [{
                type: Input
            }] } });
class InputBehavior extends TextInputPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: InputBehavior, deps: null, target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.1.4", type: InputBehavior, selector: "kp-input", usesInheritance: true, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: InputBehavior, decorators: [{
            type: Directive,
            args: [{ selector: 'kp-input' }]
        }] });
function tone$2(color) {
    const border = color === 'none' ? 'border' : `${color}-border`;
    return {
        '& > input': {
            borderColor: `$${border}`,
            '&:hover': {
                borderColor: `$${border}-hover`,
            },
            '&:focus': {
                borderColor: `$${border}-active`,
            },
            '&:disabled': {
                borderColor: `$${border}`,
            },
        },
    };
}
function compoundAddonTone(tone, side) {
    const border = tone === 'none' ? 'border' : `${tone}-border`;
    const foreground = tone === 'none' ? 'primary' : tone;
    return {
        tone,
        [`${side}`]: true,
        css: {
            [`& > [side="${side}"]`]: {
                color: tone === 'none' ? undefined : `$${foreground}-foreground`,
                borderColor: `$${border}`,
                backgroundColor: '$none',
            },
            '& > input': {
                [`&:hover ~ [side="${side}"]`]: {
                    color: `$${foreground}-foreground`,
                    borderColor: `$${border}-hover`,
                },
                [`&:focus ~ [side="${side}"]`]: {
                    color: `$${foreground}-foreground`,
                    borderColor: `$${border}-active`,
                },
            },
        },
    };
}
function compoundAddonOnInput(size, side) {
    const padding = side === 'suffix' ? 'pr' : 'pl';
    return {
        size,
        [side]: true,
        css: {
            '& > input': {
                [padding]: 0,
            },
        },
    };
}
const $Input = css({
    display: 'flex',
    alignItems: 'center',
    lineHeight: '$none',
    maxW: '$full',
    '& > input': {
        fontSize: 'inherit',
        display: 'inline-flex',
        w: '$full',
        h: '$full',
        bg: '$panel',
        py: '0',
        borderRadius: '$pill',
        borderWidth: '$thick',
        borderStyle: 'solid',
        color: '$foreground',
        order: 1,
        outline: 'none',
        appearance: 'none',
        caretColor: '$brand',
        transitionDuration: '300ms',
        transitionTimingFunction: 'ease',
        transitionProperty: 'border-color, background-color',
        '&::placeholder': {
            color: '$placeholder',
            opacity: '0.6',
        },
        '&:disabled': {
            pointerEvents: 'none',
            userSelect: 'none',
            cursor: 'not-allowed',
            backgroundColor: '$neutral-subtle',
            color: '$foreground-subtle',
            opacity: 0.4,
        },
    },
    variants: {
        size: {
            small: {
                h: HEIGHT['small'],
                fontSize: '$small',
                '& > input': {
                    px: PADDING$2.small,
                },
            },
            medium: {
                h: HEIGHT['medium'],
                fontSize: '$medium',
                '& > input': {
                    px: PADDING$2.medium,
                },
            },
            large: {
                h: HEIGHT['large'],
                fontSize: '$large',
                '& > input': {
                    px: PADDING$2.large,
                },
            },
        },
        tone: {
            none: tone$2('none'),
            success: tone$2('success'),
            warning: tone$2('warning'),
            danger: tone$2('danger'),
        },
        prefix: {
            true: {
                '& > input': {
                    borderLeft: 0,
                    borderTopLeftRadius: '$none',
                    borderBottomLeftRadius: '$none',
                    '&:hover ~ [side="prefix"]': {
                        borderColor: '$border-hover',
                    },
                    '&:focus ~ [side="prefix"]': {
                        color: '$primary-foreground',
                        borderColor: '$border-active',
                    },
                },
            },
        },
        suffix: {
            true: {
                '& > input': {
                    borderRight: 0,
                    borderTopRightRadius: '$none',
                    borderBottomRightRadius: '$none',
                    '&:hover ~ [side="suffix"]': {
                        borderColor: '$border-hover',
                    },
                    '&:focus ~ [side="suffix"]': {
                        color: '$primary-foreground',
                        borderColor: '$border-active',
                    },
                },
            },
        },
    },
    defaultVariants: {
        size: 'medium',
        tone: 'none',
    },
    compoundVariants: [
        ...compoundVariants(compoundAddonOnInput, ['small', 'medium', 'large'], ['prefix', 'suffix']),
        ...compoundVariants(compoundAddonTone, ['danger', 'warning', 'success'], ['prefix', 'suffix']),
    ],
});
let InputComponent = class InputComponent {
    constructor(behavior, detector) {
        this.behavior = behavior;
        this.detector = detector;
        this.type = 'text';
    }
    get prefix() {
        return !!this.prefixSlot;
    }
    get suffix() {
        return !!this.suffixSlot;
    }
    get input() {
        return coerceElement(this.inputRef);
    }
    ngAfterContentChecked() {
        this.class = $Input(this).toString();
        this.detector.detectChanges();
    }
    focus() {
        if (!this.input?.focus)
            return;
        this.input.focus();
    }
    blur() {
        if (!this.input?.focus)
            return;
        this.input.blur();
    }
    select() {
        this.input?.select();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: InputComponent, deps: [{ token: InputBehavior, host: true }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: InputComponent, selector: "kp-input", inputs: { size: "size", tone: "tone", type: "type" }, host: { properties: { "class": "this.class" } }, queries: [{ propertyName: "prefixSlot", first: true, predicate: InputPrefixSlot, descendants: true }, { propertyName: "suffixSlot", first: true, predicate: InputSuffixSlot, descendants: true }], viewQueries: [{ propertyName: "inputRef", first: true, predicate: ["input"], descendants: true }], ngImport: i0, template: `
    <input
      #input
      [attr.id]="behavior.id || null"
      [attr.name]="behavior.name || null"
      [attr.type]="type || 'text'"
      [attr.placeholder]="behavior.placeholder || null"
      [attr.disabled]="behavior.disabled || null"
      [disabled]="behavior.disabled"
      [attr.aria-disabled]="behavior.disabled || null"
      [attr.tabindex]="behavior.tabIndex"
      [attr.autofocus]="behavior.autofocus || null"
      [attr.readonly]="behavior.readonly || null"
      [attr.autocomplete]="behavior.autocomplete || null"
      [attr.minlength]="behavior.minlength || null"
      [attr.maxlength]="behavior.maxlength || null"
      [ngModel]="behavior.value || ''"
      (ngModelChange)="behavior.setValue($event)"
      (blur)="behavior.notifyBlur()"
      (focus)="behavior.notifyFocus()"
    />
    <kp-input-addon
      *ngIf="prefix"
      side="prefix"
      [size]="size"
      [disabled]="behavior.disabled"
    >
      <ng-content select="[kp-input-prefix]"></ng-content>
    </kp-input-addon>
    <kp-input-addon
      *ngIf="suffix"
      side="suffix"
      [size]="size"
      [disabled]="behavior.disabled"
    >
      <ng-content select="[kp-input-suffix]"></ng-content>
    </kp-input-addon>
  `, isInline: true, dependencies: [{ kind: "directive", type: i1$1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i2.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { kind: "directive", type: i2.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i2.NgModel, selector: "[ngModel]:not([formControlName]):not([formControl])", inputs: ["name", "disabled", "ngModel", "ngModelOptions"], outputs: ["ngModelChange"], exportAs: ["ngModel"] }, { kind: "component", type: InputAddonComponent, selector: "kp-input-addon", inputs: ["size", "side", "disabled"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
};
InputComponent = __decorate$c([
    Styled($Input)
], InputComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: InputComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-input',
                    template: `
    <input
      #input
      [attr.id]="behavior.id || null"
      [attr.name]="behavior.name || null"
      [attr.type]="type || 'text'"
      [attr.placeholder]="behavior.placeholder || null"
      [attr.disabled]="behavior.disabled || null"
      [disabled]="behavior.disabled"
      [attr.aria-disabled]="behavior.disabled || null"
      [attr.tabindex]="behavior.tabIndex"
      [attr.autofocus]="behavior.autofocus || null"
      [attr.readonly]="behavior.readonly || null"
      [attr.autocomplete]="behavior.autocomplete || null"
      [attr.minlength]="behavior.minlength || null"
      [attr.maxlength]="behavior.maxlength || null"
      [ngModel]="behavior.value || ''"
      (ngModelChange)="behavior.setValue($event)"
      (blur)="behavior.notifyBlur()"
      (focus)="behavior.notifyFocus()"
    />
    <kp-input-addon
      *ngIf="prefix"
      side="prefix"
      [size]="size"
      [disabled]="behavior.disabled"
    >
      <ng-content select="[kp-input-prefix]"></ng-content>
    </kp-input-addon>
    <kp-input-addon
      *ngIf="suffix"
      side="suffix"
      [size]="size"
      [disabled]="behavior.disabled"
    >
      <ng-content select="[kp-input-suffix]"></ng-content>
    </kp-input-addon>
  `,
                    changeDetection: ChangeDetectionStrategy.OnPush,
                }]
        }], ctorParameters: () => [{ type: InputBehavior, decorators: [{
                    type: Host
                }] }, { type: i0.ChangeDetectorRef }], propDecorators: { class: [{
                type: HostBinding,
                args: ['class']
            }], size: [{
                type: Input
            }], tone: [{
                type: Input
            }], type: [{
                type: Input
            }], prefixSlot: [{
                type: ContentChild,
                args: [InputPrefixSlot]
            }], suffixSlot: [{
                type: ContentChild,
                args: [InputSuffixSlot]
            }], inputRef: [{
                type: ViewChild,
                args: ['input']
            }] } });
const DIRECTIVES$c = [
    InputComponent,
    InputBehavior,
    InputPrefixSlot,
    InputSuffixSlot,
    InputAddonComponent,
];
class InputModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: InputModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: InputModule, declarations: [InputComponent, InputBehavior, InputPrefixSlot, InputSuffixSlot, InputAddonComponent], imports: [CommonModule, FormsModule], exports: [InputComponent, InputBehavior, InputPrefixSlot, InputSuffixSlot, InputAddonComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: InputModule, imports: [CommonModule, FormsModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: InputModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, FormsModule],
                    declarations: DIRECTIVES$c,
                    exports: DIRECTIVES$c,
                }]
        }] });

class FieldPrimitive extends Control {
    constructor() {
        super(...arguments);
        this.autofocus = false;
    }
    get hasAction() {
        return !!this.actionSlot;
    }
    get hasPrefix() {
        return !!this.prefixSlot;
    }
    get hasSuffix() {
        return !!this.suffixSlot;
    }
    ngAfterContentInit() {
        this.detector.detectChanges();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: FieldPrimitive, deps: null, target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.1.4", type: FieldPrimitive, inputs: { id: ["html-id", "id"], name: "name", placeholder: "placeholder", tabIndex: "tabIndex", autofocus: "autofocus", autocomplete: "autocomplete", size: "size", tone: "tone", label: "label", secondary: "secondary", description: "description", message: "message", minlength: "minlength", maxlength: "maxlength" }, queries: [{ propertyName: "actionSlot", first: true, predicate: LabelActionSlot, descendants: true }, { propertyName: "prefixSlot", first: true, predicate: InputPrefixSlot, descendants: true }, { propertyName: "suffixSlot", first: true, predicate: InputSuffixSlot, descendants: true }], usesInheritance: true, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: FieldPrimitive, decorators: [{
            type: Directive
        }], propDecorators: { id: [{
                type: Input,
                args: ['html-id']
            }], name: [{
                type: Input
            }], placeholder: [{
                type: Input
            }], tabIndex: [{
                type: Input
            }], autofocus: [{
                type: Input
            }], autocomplete: [{
                type: Input
            }], size: [{
                type: Input
            }], tone: [{
                type: Input
            }], label: [{
                type: Input
            }], secondary: [{
                type: Input
            }], description: [{
                type: Input
            }], message: [{
                type: Input
            }], minlength: [{
                type: Input
            }], maxlength: [{
                type: Input
            }], actionSlot: [{
                type: ContentChild,
                args: [LabelActionSlot]
            }], prefixSlot: [{
                type: ContentChild,
                args: [InputPrefixSlot]
            }], suffixSlot: [{
                type: ContentChild,
                args: [InputSuffixSlot]
            }] } });

var __decorate$b = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
class TextFieldBehavior extends FieldPrimitive {
    constructor() {
        super(...arguments);
        this.readonly = false;
    }
    getDefaultValue() {
        return '';
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TextFieldBehavior, deps: null, target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.1.4", type: TextFieldBehavior, selector: "kp-text-field", inputs: { readonly: "readonly" }, usesInheritance: true, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TextFieldBehavior, decorators: [{
            type: Directive,
            args: [{ selector: 'kp-text-field' }]
        }], propDecorators: { readonly: [{
                type: Input
            }] } });
const $TextField = css({
    display: 'block',
});
let TextFieldComponent = class TextFieldComponent {
    constructor(behavior) {
        this.behavior = behavior;
    }
    focus() {
        if (!this.input)
            return;
        this.input.focus();
    }
    blur() {
        if (!this.input)
            return;
        this.input.blur();
    }
    select() {
        if (!this.input)
            return;
        this.input.select();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TextFieldComponent, deps: [{ token: TextFieldBehavior, host: true }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: TextFieldComponent, selector: "kp-text-field", host: { properties: { "class": "this.class" } }, viewQueries: [{ propertyName: "input", first: true, predicate: InputComponent, descendants: true }], ngImport: i0, template: `
    <kp-stack space="small">
      <kp-label
        *ngIf="!!behavior.label || behavior.hasAction"
        [for]="behavior.id"
        [text]="behavior.label"
        [secondary]="behavior.secondary"
        [description]="behavior.description"
      >
        <ng-container *ngIf="behavior.hasAction" kp-label-action>
          <ng-content select="[kp-label-action]"></ng-content>
        </ng-container>
      </kp-label>
      <kp-input
        type="text"
        [html-id]="behavior.id"
        [value]="behavior.value"
        [name]="behavior.name"
        [placeholder]="behavior.placeholder"
        [disabled]="behavior.disabled"
        [tabIndex]="behavior.tabIndex"
        [autofocus]="behavior.autofocus"
        [readonly]="behavior.readonly"
        [autocomplete]="behavior.autocomplete"
        [size]="behavior.size"
        [tone]="behavior.tone"
        [minlength]="behavior.minlength"
        [maxlength]="behavior.maxlength"
        (changed)="behavior.setValue($event)"
        (blured)="behavior.notifyBlur()"
        (focused)="behavior.notifyFocus()"
      >
        <ng-container *ngIf="behavior.hasPrefix" kp-input-prefix>
          <ng-content select="[kp-input-prefix]"></ng-content>
        </ng-container>
        <ng-container *ngIf="behavior.hasSuffix" kp-input-suffix>
          <ng-content select="[kp-input-suffix]"></ng-content>
        </ng-container>
      </kp-input>
      <kp-text-block
        *ngIf="behavior.message"
        size="small"
        [tone]="
          !behavior.tone || behavior.tone === 'none' ? 'neutral' : behavior.tone
        "
      >
        {{ behavior.message }}
      </kp-text-block>
    </kp-stack>
  `, isInline: true, dependencies: [{ kind: "directive", type: i1$1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: StackComponent, selector: "kp-stack", inputs: ["space", "alignment", "grow"] }, { kind: "component", type: InputComponent, selector: "kp-input", inputs: ["size", "tone", "type"] }, { kind: "directive", type: InputBehavior, selector: "kp-input" }, { kind: "directive", type: InputPrefixSlot, selector: "[kp-input-prefix]" }, { kind: "directive", type: InputSuffixSlot, selector: "[kp-input-suffix]" }, { kind: "component", type: LabelComponent, selector: "kp-label", inputs: ["html-id", "for", "text", "secondary", "description"] }, { kind: "directive", type: LabelActionSlot, selector: "[kp-label-action]" }, { kind: "component", type: TextBlockComponent, selector: "kp-text-block, div[kp-text-block], p[kp-text-block], a[kp-text-block]", inputs: ["tone", "weight", "size", "alignment", "contrast", "truncate", "clamp"] }, { kind: "directive", type: TextBackgroundBehavior, selector: "kp-text, span[kp-text], a[kp-text], kp-text-block, div[kp-text-block], p[kp-text-block], a[kp-text-block]" }], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
};
TextFieldComponent = __decorate$b([
    Styled($TextField)
], TextFieldComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TextFieldComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-text-field',
                    template: `
    <kp-stack space="small">
      <kp-label
        *ngIf="!!behavior.label || behavior.hasAction"
        [for]="behavior.id"
        [text]="behavior.label"
        [secondary]="behavior.secondary"
        [description]="behavior.description"
      >
        <ng-container *ngIf="behavior.hasAction" kp-label-action>
          <ng-content select="[kp-label-action]"></ng-content>
        </ng-container>
      </kp-label>
      <kp-input
        type="text"
        [html-id]="behavior.id"
        [value]="behavior.value"
        [name]="behavior.name"
        [placeholder]="behavior.placeholder"
        [disabled]="behavior.disabled"
        [tabIndex]="behavior.tabIndex"
        [autofocus]="behavior.autofocus"
        [readonly]="behavior.readonly"
        [autocomplete]="behavior.autocomplete"
        [size]="behavior.size"
        [tone]="behavior.tone"
        [minlength]="behavior.minlength"
        [maxlength]="behavior.maxlength"
        (changed)="behavior.setValue($event)"
        (blured)="behavior.notifyBlur()"
        (focused)="behavior.notifyFocus()"
      >
        <ng-container *ngIf="behavior.hasPrefix" kp-input-prefix>
          <ng-content select="[kp-input-prefix]"></ng-content>
        </ng-container>
        <ng-container *ngIf="behavior.hasSuffix" kp-input-suffix>
          <ng-content select="[kp-input-suffix]"></ng-content>
        </ng-container>
      </kp-input>
      <kp-text-block
        *ngIf="behavior.message"
        size="small"
        [tone]="
          !behavior.tone || behavior.tone === 'none' ? 'neutral' : behavior.tone
        "
      >
        {{ behavior.message }}
      </kp-text-block>
    </kp-stack>
  `,
                    changeDetection: ChangeDetectionStrategy.OnPush,
                }]
        }], ctorParameters: () => [{ type: TextFieldBehavior, decorators: [{
                    type: Host
                }] }], propDecorators: { class: [{
                type: HostBinding,
                args: ['class']
            }], input: [{
                type: ViewChild,
                args: [InputComponent]
            }] } });
const DIRECTIVES$b = [TextFieldComponent, TextFieldBehavior];
class TextFieldModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TextFieldModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: TextFieldModule, declarations: [TextFieldComponent, TextFieldBehavior], imports: [CommonModule, StackModule, InputModule, LabelModule, TextModule], exports: [TextFieldComponent, TextFieldBehavior] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TextFieldModule, imports: [CommonModule, StackModule, InputModule, LabelModule, TextModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TextFieldModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, StackModule, InputModule, LabelModule, TextModule],
                    declarations: DIRECTIVES$b,
                    exports: DIRECTIVES$b,
                }]
        }] });

var __decorate$a = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
class ToastContentRef {
    constructor(toast, component, view) {
        this.toast = toast;
        this.component = component;
        this.view = view;
    }
    get element() {
        return this.toast.location.nativeElement;
    }
}
class ToastResultResolver {
    constructor() {
        this.promise = new Promise(resolve => {
            this.res = resolve;
        });
    }
    resolve(result) {
        this.res([false, result]);
    }
    reject(reason) {
        this.rej(reason);
    }
    dismiss() {
        this.res([true, void 0]);
    }
}
class ToastContext {
    constructor(resolver, data, duration) {
        this.resolver = resolver;
        this.data = data;
        this.duration = duration;
    }
    resolve(result) {
        this.resolver.resolve(result);
    }
    reject(reason) {
        this.resolver.reject(reason);
    }
    dismiss() {
        this.resolver.dismiss();
    }
}
class ToastRef {
    constructor(resolver, ref, destroy) {
        this.resolver = resolver;
        this.ref = ref;
        this.destroy$ = new Subject();
        this.result.finally(() => {
            destroy(this.ref);
            this.destroy$.next();
            this.destroy$.complete();
        });
    }
    get content() {
        return this.ref.component?.instance;
    }
    get result() {
        return this.resolver.promise;
    }
    close(result) {
        this.resolver.resolve(result);
    }
    dismiss() {
        this.resolver.dismiss();
    }
}
class ToastContentComponent {
    constructor(context) {
        this.context = context;
    }
    get data() {
        return this.context.data;
    }
    resolve(result) {
        this.context.resolve(result);
    }
    reject(reason) {
        this.context.reject(reason);
    }
    dismiss() {
        this.context.dismiss();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ToastContentComponent, deps: [{ token: ToastContext }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.1.4", type: ToastContentComponent, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ToastContentComponent, decorators: [{
            type: Directive
        }], ctorParameters: () => [{ type: ToastContext }] });
const $Toast = css({
    display: 'block',
    borderRadius: '$small',
    color: '$foreground',
    background: '$panel',
    height: 'var(--kp-toast-height)',
    boxSizing: 'border-box',
});
let ToastComponent = class ToastComponent {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ToastComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: ToastComponent, selector: "kp-toast", host: { properties: { "class": "this.class" } }, ngImport: i0, template: ` <ng-content></ng-content>`, isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
};
ToastComponent = __decorate$a([
    Styled($Toast)
], ToastComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ToastComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-toast',
                    template: ` <ng-content></ng-content>`,
                    changeDetection: ChangeDetectionStrategy.OnPush,
                }]
        }], propDecorators: { class: [{
                type: HostBinding,
                args: ['class']
            }] } });
const TOAST_WIDTH = '29.25rem';
const $ToastHost = css({
    display: 'block',
    position: 'absolute',
    bottom: 0,
    left: 0,
    w: '90vw',
    opacity: 0,
    transition: 'all 400ms ease',
    transform: 'translate3d(0, 5.375rem, 0)',
    '&::after': {
        content: "''",
        position: 'absolute',
        insetX: 0,
        top: 'calc(100% + 1px)',
        w: '$full',
        h: '1000px',
        background: 'transparent',
    },
    '@tablet': {
        w: TOAST_WIDTH,
    },
});
let ToastHostComponent = class ToastHostComponent {
    constructor(context) {
        this.context = context;
    }
    ngAfterViewInit() {
        this.start();
    }
    ngOnDestroy() {
        this.stop();
    }
    start() {
        if (!this.context.duration ||
            !!this.timer ||
            this.context.duration === Infinity)
            return;
        this.timer = setTimeout(() => this.context.dismiss(), this.context.duration);
    }
    stop() {
        if (!this.timer)
            return;
        clearTimeout(this.timer);
        this.timer = void 0;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ToastHostComponent, deps: [{ token: ToastContext }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: ToastHostComponent, selector: "kp-toast-host", host: { properties: { "class": "this.class" } }, ngImport: i0, template: `
    <kp-toast>
      <ng-content></ng-content>
    </kp-toast>
  `, isInline: true, dependencies: [{ kind: "component", type: ToastComponent, selector: "kp-toast" }], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
};
ToastHostComponent = __decorate$a([
    Styled($ToastHost)
], ToastHostComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ToastHostComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-toast-host',
                    template: `
    <kp-toast>
      <ng-content></ng-content>
    </kp-toast>
  `,
                    changeDetection: ChangeDetectionStrategy.OnPush,
                }]
        }], ctorParameters: () => [{ type: ToastContext }], propDecorators: { class: [{
                type: HostBinding,
                args: ['class']
            }] } });
const $ToastStack = css({
    display: 'block',
    position: 'fixed',
    maxW: '90vw',
    bottom: '$small',
    left: '5vw',
    zIndex: '$5',
    transition: 'all 400ms ease',
    [`& > ${ToastHostComponent}`]: {
        [`& > ${ToastComponent}`]: {
            boxShadow: '$small',
            '&::after': {
                content: '',
                borderRadius: '$small',
                position: 'absolute',
                top: 0,
                left: 0,
                zIndex: -1,
                w: '$full',
                h: '$full',
                boxShadow: '$medium',
                opacity: 0,
                transition: 'all 400ms ease',
            },
        },
        '&.kp-toast-front': {
            transform: 'translate3d(0, 0, 0)',
            opacity: 1,
        },
        '&:not(:last-child)': {
            '--i': 'calc(var(--kp-toast-index) - 1)',
            h: 'var(--kp-toast-front-height)',
            opacity: '1',
            // --kp-space-medium is part of the design system css variables
            transform: 'translate3d(0, calc(1px - (var(--kp-space-medium) * var(--i))), 0) scale(calc(1 - .05 * var(--i)))',
            [`& > ${ToastComponent} > *`]: {
                transition: 'all 300ms ease-in',
                opacity: 0,
            },
        },
        '&:nth-last-child(n+4)': {
            opacity: '0 !important',
        },
    },
    '&:hover': {
        bottom: '$medium',
        [`& > ${ToastHostComponent}`]: {
            h: 'var(--kp-toast-height)',
            transform: 'translate3d(0, calc(var(--kp-toast-hover-offset-y) - var(--kp-space-medium) * (var(--kp-toast-index) - 1)), 0)',
            [`& > ${ToastComponent}`]: {
                '& > *, &::after': { opacity: 1 },
            },
        },
    },
    '@tablet': {
        maxW: TOAST_WIDTH,
        left: '$medium',
    },
});
const DEFAULT_DURATION = 5000;
let ToastStackComponent = class ToastStackComponent {
    constructor(view, resolver, app, injector, renderer) {
        this.view = view;
        this.resolver = resolver;
        this.app = app;
        this.injector = injector;
        this.renderer = renderer;
        this.toasts = [];
    }
    stopTimers() {
        this.toasts.forEach(([t]) => t.toast.instance.stop());
    }
    startTimers() {
        this.toasts.forEach(([t]) => t.toast.instance.start());
    }
    dismiss() {
        this.toasts.forEach(([, t]) => t.dismiss());
    }
    open(options) {
        const resolver = new ToastResultResolver();
        const context = new ToastContext(resolver, options.data ?? {}, options.duration ?? DEFAULT_DURATION);
        const toast = this.createToastContent(context, options.content);
        this.toasts.push([toast, context]);
        // execute sync in the next event loop tick
        setTimeout(() => this.sync());
        return new ToastRef(resolver, toast, this.destroy.bind(this));
    }
    sync() {
        const heights = [];
        [...this.toasts]
            .reverse()
            .slice(0, 4)
            .forEach(([toast], i) => {
            const index = i + 1;
            const element = toast.element;
            const height = +(element.dataset['height'] ?? element.clientHeight);
            heights.push(height);
            if (index === 1)
                this.renderer.addClass(element, 'kp-toast-front');
            else
                this.renderer.removeClass(element, 'kp-toast-front');
            this.renderer.setAttribute(element, 'data-height', `${height}`);
            element.style.setProperty('--kp-toast-index', `${index}`);
            element.style.setProperty('--kp-toast-height', `${height}px`);
            element.style.setProperty('--kp-toast-front-height', `${heights[0]}px`);
            if (index <= 1) {
                element.style.removeProperty('--kp-toast-hover-offset-y');
                return;
            }
            const offset = heights.slice(0, i).reduce((xs, x) => xs + x, 0);
            element.style.setProperty('--kp-toast-hover-offset-y', `-${offset}px`);
        });
    }
    createToastContent(context, content) {
        const [nodes, component, view] = this.getNodes(context, content);
        const toast = this.view.createComponent(this.resolver.resolveComponentFactory(ToastHostComponent), this.view.length, Injector.create({
            providers: [{ provide: ToastContext, useValue: context }],
            parent: this.injector,
        }), [nodes]);
        this.renderer.appendChild(this.view.element.nativeElement, toast.location.nativeElement);
        return new ToastContentRef(toast, component, view);
    }
    destroy(content) {
        if (!content)
            return;
        if (content.toast) {
            this.renderer.removeChild(content.element.parentElement, content.element);
            this.app.detachView(content.toast.hostView);
            content.toast.destroy();
        }
        if (content.component) {
            this.app.detachView(content.component.hostView);
            content.component.destroy();
        }
        if (content.view) {
            this.app.detachView(content.view);
            content.view.destroy();
        }
        this.toasts = this.toasts.filter(([t]) => t !== content);
        this.sync();
    }
    getNodes(context, content) {
        if (!content)
            return [[]];
        if (typeof content === 'string') {
            return [[this.renderer.createText(content)]];
        }
        if (content instanceof Element)
            return [[content]];
        if (content instanceof TemplateRef) {
            const ctx = {
                $implicit: context,
                resolve(result) {
                    context.resolve(result);
                },
                reject(reason) {
                    context.reject(reason);
                },
                dismiss() {
                    context.dismiss();
                },
            };
            const view = content.createEmbeddedView(ctx);
            view.detectChanges();
            this.app.attachView(view);
            return [view.rootNodes, void 0, view];
        }
        const factory = this.resolver.resolveComponentFactory(content);
        const injector = Injector.create({
            providers: [{ provide: ToastContext, useValue: context }],
            parent: this.injector,
        });
        const component = factory.create(injector);
        this.app.attachView(component.hostView);
        return [[component.location.nativeElement], component];
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ToastStackComponent, deps: [{ token: i0.ViewContainerRef }, { token: i0.ComponentFactoryResolver }, { token: i0.ApplicationRef }, { token: i0.Injector }, { token: i0.Renderer2 }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: ToastStackComponent, selector: "kp-toast-stack", host: { listeners: { "mouseenter": "stopTimers()", "mouseleave": "startTimers()" }, properties: { "class": "this.class" } }, ngImport: i0, template: ``, isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
};
ToastStackComponent = __decorate$a([
    Styled($ToastStack)
], ToastStackComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ToastStackComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-toast-stack',
                    template: ``,
                    changeDetection: ChangeDetectionStrategy.OnPush,
                }]
        }], ctorParameters: () => [{ type: i0.ViewContainerRef }, { type: i0.ComponentFactoryResolver }, { type: i0.ApplicationRef }, { type: i0.Injector }, { type: i0.Renderer2 }], propDecorators: { class: [{
                type: HostBinding,
                args: ['class']
            }], stopTimers: [{
                type: HostListener,
                args: ['mouseenter']
            }], startTimers: [{
                type: HostListener,
                args: ['mouseleave']
            }] } });
const $ToastMessage = css({
    display: 'block',
    '.sb-toast-message__header-dismiss': {
        visibility: 'hidden',
    },
    '.sb-toast-message__header': {
        py: '$xsmall',
        px: '$medium',
        borderTopLeftRadius: '$small',
        borderTopRightRadius: '$small',
    },
    '.sb-toast-message__content': {
        p: '$medium',
        pb: '$large',
    },
    variants: {
        tone: {
            primary: {
                '.sb-toast-message__header': {
                    background: '$primary-subtle',
                    color: '$primary-foreground',
                },
            },
            accent: {
                '.sb-toast-message__header': {
                    background: '$accent-subtle',
                    color: '$accent-foreground',
                },
            },
            promote: {
                '.sb-toast-message__header': {
                    background: '$promote-subtle',
                    color: '$promote-foreground',
                },
            },
            success: {
                '.sb-toast-message__header': {
                    background: '$success-subtle',
                    color: '$success-foreground',
                },
            },
            warning: {
                '.sb-toast-message__header': {
                    background: '$warning-subtle',
                    color: '$warning-foreground',
                },
            },
            danger: {
                '.sb-toast-message__header': {
                    background: '$danger-subtle',
                    color: '$danger-foreground',
                },
            },
        },
        showDismiss: {
            true: {
                '.sb-toast-message__header-dismiss': {
                    visibility: 'visible',
                },
            },
        },
    },
    defaultVariants: {
        tone: 'primary',
        showDismiss: true,
    },
});
let ToastMessageComponent = class ToastMessageComponent extends ToastContentComponent {
    get showDismiss() {
        return !!this.data.showDismiss;
    }
    get tone() {
        return this.data.tone ?? 'primary';
    }
    get hasActions() {
        return (!!this.data.actions &&
            (!!this.data.actions.showCancel || !!this.data.actions.showAccept));
    }
    get headerIcon() {
        if (!!this.data.icon)
            return this.data.icon;
        const type = this.tone;
        if (type === 'success')
            return coerceIcon(CheckCircleAltIconComponent);
        if (type === 'danger')
            return coerceIcon(TimesCircleIconComponent);
        if (type === 'warning')
            return coerceIcon(TriangleExclamationIconComponent);
        if (type === 'primary')
            return coerceIcon(InfoCircleIconComponent);
        if (type === 'accent' || type === 'promote')
            return coerceIcon(MagicSparklesIconComponent);
    }
    constructor(context) {
        super(context);
        this.icon = coerceIcon(TimesIconComponent);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ToastMessageComponent, deps: [{ token: ToastContext }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: ToastMessageComponent, selector: "sb-toast-message", host: { properties: { "class": "this.class" } }, usesInheritance: true, ngImport: i0, template: `
    <kp-stack space="none">
      <kp-set space="small" class="sb-toast-message__header">
        <kp-icon *ngIf="!!headerIcon" [icon]="headerIcon" />
        <kp-text-block weight="bold">{{ data.title }}</kp-text-block>
        <kp-spacer />
        <button
          class="sb-toast-message__header-dismiss"
          kp-icon-button
          [icon]="icon"
          size="small"
          [tone]="tone"
          variant="ghost"
          (click)="dismiss()"
        ></button>
      </kp-set>

      <div class="sb-toast-message__content">
        <kp-stack space="medium">
          <kp-text>{{ data.message }}</kp-text>

          <kp-set
            space="medium"
            *ngIf="hasActions"
            alignmentX="center"
            [grow]="true"
          >
            <button
              *ngIf="!!data.actions?.showCancel"
              kp-button
              variant="subtle"
              [tone]="tone"
              size="small"
              (click)="resolve({ action: 'cancel' })"
            >
              {{ data.actions?.cancelLabel ?? 'Cancel' }}
            </button>
            <button
              *ngIf="!!data.actions?.showAccept"
              kp-button
              [tone]="tone"
              size="small"
              (click)="resolve({ action: 'accept' })"
            >
              {{ data.actions?.acceptLabel ?? 'Accept' }}
            </button>
          </kp-set>
        </kp-stack>
      </div>
    </kp-stack>
  `, isInline: true, dependencies: [{ kind: "directive", type: i1$1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: TextComponent, selector: "kp-text, span[kp-text], a[kp-text]", inputs: ["tone", "weight", "size", "contrast"] }, { kind: "component", type: TextBlockComponent, selector: "kp-text-block, div[kp-text-block], p[kp-text-block], a[kp-text-block]", inputs: ["tone", "weight", "size", "alignment", "contrast", "truncate", "clamp"] }, { kind: "directive", type: TextBackgroundBehavior, selector: "kp-text, span[kp-text], a[kp-text], kp-text-block, div[kp-text-block], p[kp-text-block], a[kp-text-block]" }, { kind: "component", type: StackComponent, selector: "kp-stack", inputs: ["space", "alignment", "grow"] }, { kind: "component", type: SetComponent, selector: "kp-set", inputs: ["space", "alignmentX", "alignmentY", "collapseBelow", "wrap", "reverse", "grow"] }, { kind: "component", type: SpacerComponent, selector: "kp-spacer" }, { kind: "directive", type: ButtonDirective, selector: "button[kp-button], a[kp-button]", inputs: ["tone", "size", "variant", "wrap", "grow"] }, { kind: "component", type: IconButtonComponent, selector: "button[kp-icon-button], a[kp-icon-button]", inputs: ["icon", "spin", "tone", "size", "variant"] }, { kind: "component", type: IconComponent, selector: "kp-icon", inputs: ["icon"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
};
ToastMessageComponent = __decorate$a([
    Styled($ToastMessage)
], ToastMessageComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ToastMessageComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'sb-toast-message',
                    template: `
    <kp-stack space="none">
      <kp-set space="small" class="sb-toast-message__header">
        <kp-icon *ngIf="!!headerIcon" [icon]="headerIcon" />
        <kp-text-block weight="bold">{{ data.title }}</kp-text-block>
        <kp-spacer />
        <button
          class="sb-toast-message__header-dismiss"
          kp-icon-button
          [icon]="icon"
          size="small"
          [tone]="tone"
          variant="ghost"
          (click)="dismiss()"
        ></button>
      </kp-set>

      <div class="sb-toast-message__content">
        <kp-stack space="medium">
          <kp-text>{{ data.message }}</kp-text>

          <kp-set
            space="medium"
            *ngIf="hasActions"
            alignmentX="center"
            [grow]="true"
          >
            <button
              *ngIf="!!data.actions?.showCancel"
              kp-button
              variant="subtle"
              [tone]="tone"
              size="small"
              (click)="resolve({ action: 'cancel' })"
            >
              {{ data.actions?.cancelLabel ?? 'Cancel' }}
            </button>
            <button
              *ngIf="!!data.actions?.showAccept"
              kp-button
              [tone]="tone"
              size="small"
              (click)="resolve({ action: 'accept' })"
            >
              {{ data.actions?.acceptLabel ?? 'Accept' }}
            </button>
          </kp-set>
        </kp-stack>
      </div>
    </kp-stack>
  `,
                    changeDetection: ChangeDetectionStrategy.OnPush,
                }]
        }], ctorParameters: () => [{ type: ToastContext }], propDecorators: { class: [{
                type: HostBinding,
                args: ['class']
            }] } });
class Toast {
    constructor(app, injector, resolver, document, factory) {
        this.app = app;
        this.injector = injector;
        this.resolver = resolver;
        this.document = document;
        this.renderer = factory.createRenderer(void 0, null);
    }
    ngOnDestroy() {
        this.destroyStack(this.stack);
    }
    dismissAll() {
        if (!this.stack)
            return;
        this.stack.instance.dismiss();
    }
    open(content, data, duration) {
        if (!this.stack)
            this.stack = this.createStack();
        const toastContent = typeof content === 'string' ? ToastMessageComponent : content;
        const toastData = typeof content === 'string'
            ? {
                message: content,
                ...data,
            }
            : data;
        return this.stack.instance.open({
            content: toastContent,
            data: toastData,
            duration,
        });
    }
    destroyStack(stack) {
        if (!stack)
            return;
        const element = stack.location.nativeElement;
        if (!element)
            return;
        this.renderer.removeChild(element.parentElement, element);
        this.app.detachView(stack.hostView);
        stack.destroy();
    }
    createStack() {
        const factory = this.resolver.resolveComponentFactory(ToastStackComponent);
        const component = factory.create(this.injector);
        this.app.attachView(component.hostView);
        this.renderer.appendChild(this.document.body, component.location.nativeElement);
        return component;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: Toast, deps: [{ token: i0.ApplicationRef }, { token: i0.Injector }, { token: i0.ComponentFactoryResolver }, { token: DOCUMENT }, { token: i0.RendererFactory2 }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: Toast, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: Toast, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: () => [{ type: i0.ApplicationRef }, { type: i0.Injector }, { type: i0.ComponentFactoryResolver }, { type: Document, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }, { type: i0.RendererFactory2 }] });
const DIRECTIVES$a = [
    ToastComponent,
    ToastHostComponent,
    ToastStackComponent,
    ToastMessageComponent,
];
class ToastModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ToastModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: ToastModule, declarations: [ToastComponent, ToastHostComponent, ToastStackComponent, ToastMessageComponent], imports: [CommonModule,
            TextModule,
            StackModule,
            SetModule,
            SpacerModule,
            ButtonModule,
            IconButtonModule,
            IconModule], exports: [ToastComponent, ToastHostComponent, ToastStackComponent, ToastMessageComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ToastModule, imports: [CommonModule,
            TextModule,
            StackModule,
            SetModule,
            SpacerModule,
            ButtonModule,
            IconButtonModule,
            IconModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: ToastModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        TextModule,
                        StackModule,
                        SetModule,
                        SpacerModule,
                        ButtonModule,
                        IconButtonModule,
                        IconModule,
                    ],
                    declarations: DIRECTIVES$a,
                    exports: DIRECTIVES$a,
                }]
        }] });

var __decorate$9 = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
class CheckboxBehavior extends CheckInputPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: CheckboxBehavior, deps: null, target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.1.4", type: CheckboxBehavior, selector: "kp-checkbox", usesInheritance: true, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: CheckboxBehavior, decorators: [{
            type: Directive,
            args: [{ selector: 'kp-checkbox' }]
        }] });
const $Checkbox = css({
    display: 'inline-flex',
    alignItems: 'center',
    verticalAlign: 'top',
    position: 'relative',
    '& > input[type="checkbox"]': {
        appearance: 'none',
        margin: '-1px',
        '&:checked + span': {
            borderColor: '$primary-solid',
            backgroundColor: '$primary-solid',
            [`& > div > ${CheckComponent}`]: {
                display: 'block',
            },
        },
        '&:disabled': {
            '& + span': {
                cursor: 'not-allowed',
                pointerEvents: 'none',
                opacity: 0.4,
            },
        },
        '&:focus': {
            '& + span': {
                borderColor: '$border-hover',
            },
            '&:checked + span': {
                borderColor: '$primary-solid',
            },
        },
    },
    '& > span': {
        color: '$white',
        backgroundColor: '$white',
        borderStyle: 'solid',
        borderWidth: '$thick',
        borderColor: '$border',
        borderRadius: '0.375rem',
        boxSizing: 'border-box',
        cursor: 'pointer',
        display: 'inline-flex',
        justifyContent: 'center',
        alignItems: 'center',
        userSelect: 'none',
        '&:hover': {
            borderColor: '$border-hover',
        },
        '& > div': {
            lineHeight: '$none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '$full',
            width: '$full',
            [`& > ${CheckComponent}`]: {
                display: 'none',
            },
        },
    },
    variants: {
        size: {
            small: {
                '& > span': {
                    size: '1.25rem',
                    minSize: '1.25rem',
                    [`& > div > ${CheckComponent} > svg`]: {
                        width: '12px',
                        height: '9px',
                    },
                },
            },
            medium: {
                '& > span': {
                    size: '1.75rem',
                    minSize: '1.75rem',
                    [`& > div > ${CheckComponent} > svg`]: {
                        width: '16px',
                        height: '12px',
                    },
                },
            },
        },
    },
});
let CheckboxComponent = class CheckboxComponent {
    constructor(behavior) {
        this.behavior = behavior;
    }
    focus() {
        const input = coerceElement(this.inputRef);
        if (!input?.focus)
            return;
        input.focus();
    }
    blur() {
        const input = coerceElement(this.inputRef);
        if (!input?.blur)
            return;
        input.blur();
    }
    onClick() {
        if (this.labeled)
            return;
        this.behavior.setValue(!this.behavior.checked);
        this.focus();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: CheckboxComponent, deps: [{ token: CheckboxBehavior, host: true }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: CheckboxComponent, selector: "kp-checkbox", inputs: { size: "size", labeled: "labeled" }, host: { properties: { "class": "this.class" } }, viewQueries: [{ propertyName: "inputRef", first: true, predicate: ["checkbox"], descendants: true }], ngImport: i0, template: `
    <input
      #checkbox
      type="checkbox"
      [attr.id]="behavior.id || null"
      [attr.name]="behavior.name || null"
      [attr.tabindex]="behavior.tabIndex"
      [attr.disabled]="behavior.disabled || null"
      [attr.aria-disabled]="behavior.disabled || null"
      [ngModel]="behavior.checked"
      (ngModelChange)="behavior.setValue($event)"
      (blur)="behavior.notifyBlur()"
      (focus)="behavior.notifyFocus()"
    />
    <span (click)="onClick()">
      <div>
        <hy-check></hy-check>
      </div>
    </span>
  `, isInline: true, dependencies: [{ kind: "component", type: i1$4.CheckComponent, selector: "hy-check", inputs: ["width", "height"] }, { kind: "directive", type: i2.CheckboxControlValueAccessor, selector: "input[type=checkbox][formControlName],input[type=checkbox][formControl],input[type=checkbox][ngModel]" }, { kind: "directive", type: i2.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i2.NgModel, selector: "[ngModel]:not([formControlName]):not([formControl])", inputs: ["name", "disabled", "ngModel", "ngModelOptions"], outputs: ["ngModelChange"], exportAs: ["ngModel"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
};
__decorate$9([
    CoerceBoolean()
], CheckboxComponent.prototype, "labeled", void 0);
CheckboxComponent = __decorate$9([
    Styled($Checkbox)
], CheckboxComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: CheckboxComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-checkbox',
                    template: `
    <input
      #checkbox
      type="checkbox"
      [attr.id]="behavior.id || null"
      [attr.name]="behavior.name || null"
      [attr.tabindex]="behavior.tabIndex"
      [attr.disabled]="behavior.disabled || null"
      [attr.aria-disabled]="behavior.disabled || null"
      [ngModel]="behavior.checked"
      (ngModelChange)="behavior.setValue($event)"
      (blur)="behavior.notifyBlur()"
      (focus)="behavior.notifyFocus()"
    />
    <span (click)="onClick()">
      <div>
        <hy-check></hy-check>
      </div>
    </span>
  `,
                    changeDetection: ChangeDetectionStrategy.OnPush,
                }]
        }], ctorParameters: () => [{ type: CheckboxBehavior, decorators: [{
                    type: Host
                }] }], propDecorators: { class: [{
                type: HostBinding,
                args: ['class']
            }], size: [{
                type: Input
            }], labeled: [{
                type: Input
            }], inputRef: [{
                type: ViewChild,
                args: ['checkbox']
            }] } });
const DIRECTIVES$9 = [CheckboxComponent, CheckboxBehavior];
class CheckboxModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: CheckboxModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: CheckboxModule, declarations: [CheckboxComponent, CheckboxBehavior], imports: [CommonModule, CheckModule, FormsModule], exports: [CheckboxComponent, CheckboxBehavior] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: CheckboxModule, imports: [CommonModule, CheckModule, FormsModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: CheckboxModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, CheckModule, FormsModule],
                    declarations: DIRECTIVES$9,
                    exports: DIRECTIVES$9,
                }]
        }] });

var __decorate$8 = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
class CheckboxFieldBehavior extends FieldPrimitive {
    constructor(control, detector) {
        super(control, detector);
        this.disabled$.pipe(takeUntil(this.destroy$)).subscribe(() => {
            if (!this.instance || !this.instance?.['ngOnChanges'])
                return;
            this.instance['ngOnChanges']({});
        });
    }
    for(instance) {
        this.instance = instance;
    }
    getDefaultValue() {
        return false;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: CheckboxFieldBehavior, deps: [{ token: NgControl, optional: true, self: true }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.1.4", type: CheckboxFieldBehavior, selector: "kp-checkbox-field", inputs: { size: "size" }, usesInheritance: true, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: CheckboxFieldBehavior, decorators: [{
            type: Directive,
            args: [{ selector: 'kp-checkbox-field' }]
        }], ctorParameters: () => [{ type: i2.NgControl, decorators: [{
                    type: Self
                }, {
                    type: Optional
                }, {
                    type: Inject,
                    args: [NgControl]
                }] }, { type: i0.ChangeDetectorRef }], propDecorators: { size: [{
                type: Input
            }] } });
const $CheckboxField = css({
    display: 'block',
    '& > label': {
        all: 'unset',
        cursor: 'pointer',
        '&:hover': {
            [`${CheckboxComponent}`]: {
                '& > input[type="checkbox"]:checked + span': {
                    borderColor: '$primary-solid',
                },
                '& > span': {
                    borderColor: '$border-hover',
                },
            },
        },
    },
    variants: {
        isDisabled: {
            true: {
                '& > label': {
                    cursor: 'not-allowed',
                    pointerEvents: 'none',
                },
            },
        },
    },
});
let CheckboxFieldComponent = class CheckboxFieldComponent {
    constructor(behavior) {
        this.behavior = behavior;
        this.behavior.for(this);
    }
    get isDisabled() {
        return this.behavior.disabled;
    }
    focus() {
        if (!this.checkbox)
            return;
        this.checkbox.focus();
    }
    blur() {
        if (!this.checkbox)
            return;
        this.checkbox.blur();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: CheckboxFieldComponent, deps: [{ token: CheckboxFieldBehavior, host: true }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: CheckboxFieldComponent, selector: "kp-checkbox-field", host: { properties: { "class": "this.class" } }, viewQueries: [{ propertyName: "checkbox", first: true, predicate: CheckboxComponent, descendants: true }], ngImport: i0, template: `
    <label>
      <kp-set space="small">
        <kp-checkbox
          [labeled]="true"
          [html-id]="behavior.id"
          [checked]="behavior.value"
          [name]="behavior.name"
          [disabled]="behavior.disabled"
          [tabIndex]="behavior.tabIndex"
          [autofocus]="behavior.autofocus"
          [size]="behavior.size"
          (changed)="behavior.setValue($event)"
          (blured)="behavior.notifyBlur()"
          (focused)="behavior.notifyFocus()"
        ></kp-checkbox>
        <ng-content></ng-content
      ></kp-set>
    </label>
  `, isInline: true, dependencies: [{ kind: "component", type: CheckboxComponent, selector: "kp-checkbox", inputs: ["size", "labeled"] }, { kind: "directive", type: CheckboxBehavior, selector: "kp-checkbox" }, { kind: "component", type: SetComponent, selector: "kp-set", inputs: ["space", "alignmentX", "alignmentY", "collapseBelow", "wrap", "reverse", "grow"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
};
CheckboxFieldComponent = __decorate$8([
    Styled($CheckboxField)
], CheckboxFieldComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: CheckboxFieldComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-checkbox-field',
                    template: `
    <label>
      <kp-set space="small">
        <kp-checkbox
          [labeled]="true"
          [html-id]="behavior.id"
          [checked]="behavior.value"
          [name]="behavior.name"
          [disabled]="behavior.disabled"
          [tabIndex]="behavior.tabIndex"
          [autofocus]="behavior.autofocus"
          [size]="behavior.size"
          (changed)="behavior.setValue($event)"
          (blured)="behavior.notifyBlur()"
          (focused)="behavior.notifyFocus()"
        ></kp-checkbox>
        <ng-content></ng-content
      ></kp-set>
    </label>
  `,
                    changeDetection: ChangeDetectionStrategy.OnPush,
                }]
        }], ctorParameters: () => [{ type: CheckboxFieldBehavior, decorators: [{
                    type: Host
                }] }], propDecorators: { class: [{
                type: HostBinding,
                args: ['class']
            }], checkbox: [{
                type: ViewChild,
                args: [CheckboxComponent]
            }] } });
const DIRECTIVES$8 = [CheckboxFieldComponent, CheckboxFieldBehavior];
class CheckboxFieldModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: CheckboxFieldModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: CheckboxFieldModule, declarations: [CheckboxFieldComponent, CheckboxFieldBehavior], imports: [CommonModule, CheckboxModule, SetModule], exports: [CheckboxFieldComponent, CheckboxFieldBehavior] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: CheckboxFieldModule, imports: [CommonModule, CheckboxModule, SetModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: CheckboxFieldModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, CheckboxModule, SetModule],
                    declarations: DIRECTIVES$8,
                    exports: DIRECTIVES$8,
                }]
        }] });

var __decorate$7 = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
const ARROW_SVG = '%3Csvg%20width%3D%2210%22%20height%3D%226%22%20viewBox%3D%220%200%2010%206%22%20fill%3D%22none%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%0A%3Cpath%20d%3D%22M9%201L5%205L1%201%22%20stroke%3D%22%23677594%22%20stroke-width%3D%222%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%2F%3E%0A%3C%2Fsvg%3E%0A';
const PADDING$1 = {
    small: '1rem',
    medium: '1.25rem',
    large: '1.75rem',
};
class SelectBehavior extends TextInputPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: SelectBehavior, deps: null, target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.1.4", type: SelectBehavior, selector: "kp-select", usesInheritance: true, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: SelectBehavior, decorators: [{
            type: Directive,
            args: [{ selector: 'kp-select' }]
        }] });
function tone$1(color) {
    const border = color === 'none' ? 'border' : `${color}-border`;
    return {
        '& > select': {
            borderColor: `$${border}`,
            '&:hover': {
                borderColor: `$${border}-hover`,
            },
            '&:focus': {
                borderColor: `$${border}-active`,
            },
        },
    };
}
const $Select = css({
    display: 'flex',
    alignItems: 'center',
    lineHeight: '$none',
    maxW: '$full',
    '& > select': {
        cursor: 'pointer',
        fontSize: 'inherit',
        w: '$full',
        h: '$full',
        pr: '2.75rem',
        backgroundColor: '$panel',
        backgroundPosition: 'bottom 50% right 1.5rem',
        backgroundRepeat: 'no-repeat',
        backgroundImage: `url("data:image/svg+xml,${ARROW_SVG}")`,
        borderRadius: '$pill',
        borderWidth: '$thick',
        borderStyle: 'solid',
        color: '$foreground',
        outline: 'none',
        appearance: 'none',
        transitionDuration: '300ms',
        transitionTimingFunction: 'ease',
        transitionProperty: 'border-color, background-color',
        '&:disabled': {
            pointerEvents: 'none',
            cursor: 'not-allowed',
            backgroundColor: '$neutral-subtle',
            borderColor: '$neutral-subtle',
            color: '$foreground-subtle',
            opacity: 0.4,
        },
    },
    variants: {
        size: {
            small: {
                h: '2rem',
                fontSize: '$small',
                '& > select': {
                    pl: PADDING$1.small,
                },
            },
            medium: {
                h: '2.75rem',
                fontSize: '$medium',
                '& > select': {
                    pl: PADDING$1.medium,
                },
            },
            large: {
                h: '3.5rem',
                fontSize: '$large',
                '& > select': {
                    pl: PADDING$1.large,
                },
            },
        },
        tone: {
            none: tone$1('none'),
            success: tone$1('success'),
            warning: tone$1('warning'),
            danger: tone$1('danger'),
        },
    },
    defaultVariants: {
        size: 'medium',
        tone: 'none',
    },
});
let SelectComponent = class SelectComponent {
    constructor(behavior) {
        this.behavior = behavior;
    }
    get select() {
        return coerceElement(this.selectRef);
    }
    focus() {
        if (!this.select?.focus)
            return;
        this.select.focus();
    }
    blur() {
        if (!this.select?.focus)
            return;
        this.select.blur();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: SelectComponent, deps: [{ token: SelectBehavior, host: true }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: SelectComponent, selector: "kp-select", inputs: { size: "size", tone: "tone" }, host: { properties: { "class": "this.class" } }, viewQueries: [{ propertyName: "selectRef", first: true, predicate: ["select"], descendants: true }], ngImport: i0, template: `
    <select
      #select
      [attr.id]="behavior.id || null"
      [attr.name]="behavior.name || null"
      [attr.disabled]="behavior.disabled || null"
      [attr.aria-disabled]="behavior.disabled || null"
      [attr.tabindex]="behavior.tabIndex"
      [attr.autofocus]="behavior.autofocus || null"
      [attr.autocomplete]="behavior.autocomplete || null"
      [ngModel]="behavior.value || ''"
      (ngModelChange)="behavior.setValue($event)"
      (blur)="behavior.notifyBlur()"
      (focus)="behavior.notifyFocus()"
    >
      <option *ngIf="behavior.placeholder" value="" hidden>
        {{ behavior.placeholder }}
      </option>
      <ng-content></ng-content>
    </select>
  `, isInline: true, dependencies: [{ kind: "directive", type: i2.NgSelectOption, selector: "option", inputs: ["ngValue", "value"] }, { kind: "directive", type: i2.ɵNgSelectMultipleOption, selector: "option", inputs: ["ngValue", "value"] }, { kind: "directive", type: i2.SelectControlValueAccessor, selector: "select:not([multiple])[formControlName],select:not([multiple])[formControl],select:not([multiple])[ngModel]", inputs: ["compareWith"] }, { kind: "directive", type: i2.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i2.NgModel, selector: "[ngModel]:not([formControlName]):not([formControl])", inputs: ["name", "disabled", "ngModel", "ngModelOptions"], outputs: ["ngModelChange"], exportAs: ["ngModel"] }, { kind: "directive", type: i1$1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
};
SelectComponent = __decorate$7([
    Styled($Select)
], SelectComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: SelectComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-select',
                    template: `
    <select
      #select
      [attr.id]="behavior.id || null"
      [attr.name]="behavior.name || null"
      [attr.disabled]="behavior.disabled || null"
      [attr.aria-disabled]="behavior.disabled || null"
      [attr.tabindex]="behavior.tabIndex"
      [attr.autofocus]="behavior.autofocus || null"
      [attr.autocomplete]="behavior.autocomplete || null"
      [ngModel]="behavior.value || ''"
      (ngModelChange)="behavior.setValue($event)"
      (blur)="behavior.notifyBlur()"
      (focus)="behavior.notifyFocus()"
    >
      <option *ngIf="behavior.placeholder" value="" hidden>
        {{ behavior.placeholder }}
      </option>
      <ng-content></ng-content>
    </select>
  `,
                    changeDetection: ChangeDetectionStrategy.OnPush,
                }]
        }], ctorParameters: () => [{ type: SelectBehavior, decorators: [{
                    type: Host
                }] }], propDecorators: { class: [{
                type: HostBinding,
                args: ['class']
            }], size: [{
                type: Input
            }], tone: [{
                type: Input
            }], selectRef: [{
                type: ViewChild,
                args: ['select']
            }] } });
const DIRECTIVES$7 = [SelectComponent, SelectBehavior];
class SelectModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: SelectModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: SelectModule, declarations: [SelectComponent, SelectBehavior], imports: [FormsModule, CommonModule], exports: [SelectComponent, SelectBehavior] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: SelectModule, imports: [FormsModule, CommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: SelectModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [FormsModule, CommonModule],
                    declarations: DIRECTIVES$7,
                    exports: DIRECTIVES$7,
                }]
        }] });

var __decorate$6 = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
class SelectFieldBehavior extends FieldPrimitive {
    getDefaultValue() {
        return '';
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: SelectFieldBehavior, deps: null, target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.1.4", type: SelectFieldBehavior, selector: "kp-select-field", usesInheritance: true, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: SelectFieldBehavior, decorators: [{
            type: Directive,
            args: [{ selector: 'kp-select-field' }]
        }] });
const $SelectField = css({
    display: 'block',
});
let SelectFieldComponent = class SelectFieldComponent {
    constructor(behavior) {
        this.behavior = behavior;
    }
    focus() {
        if (!this.select)
            return;
        this.select.focus();
    }
    blur() {
        if (!this.select)
            return;
        this.select.blur();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: SelectFieldComponent, deps: [{ token: SelectFieldBehavior, host: true }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: SelectFieldComponent, selector: "kp-select-field", host: { properties: { "class": "this.class" } }, viewQueries: [{ propertyName: "select", first: true, predicate: SelectComponent, descendants: true }], ngImport: i0, template: `
    <kp-stack space="small">
      <kp-label
        *ngIf="!!behavior.label || behavior.hasAction"
        [for]="behavior.id"
        [text]="behavior.label"
        [secondary]="behavior.secondary"
        [description]="behavior.description"
      >
        <ng-container *ngIf="behavior.hasAction" kp-label-action>
          <ng-content select="[kp-label-action]"></ng-content>
        </ng-container>
      </kp-label>
      <kp-select
        [html-id]="behavior.id"
        [value]="behavior.value"
        [name]="behavior.name"
        [disabled]="behavior.disabled"
        [tabIndex]="behavior.tabIndex"
        [autofocus]="behavior.autofocus"
        [autocomplete]="behavior.autocomplete"
        [size]="behavior.size"
        [tone]="behavior.tone"
        [placeholder]="behavior.placeholder"
        (changed)="behavior.setValue($event)"
        (blured)="behavior.notifyBlur()"
        (focused)="behavior.notifyFocus()"
      >
        <ng-content></ng-content>
      </kp-select>
      <kp-text-block
        *ngIf="behavior.message"
        size="small"
        [tone]="
          !behavior.tone || behavior.tone === 'none' ? 'neutral' : behavior.tone
        "
      >
        {{ behavior.message }}
      </kp-text-block>
    </kp-stack>
  `, isInline: true, dependencies: [{ kind: "directive", type: i1$1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: StackComponent, selector: "kp-stack", inputs: ["space", "alignment", "grow"] }, { kind: "component", type: SelectComponent, selector: "kp-select", inputs: ["size", "tone"] }, { kind: "directive", type: SelectBehavior, selector: "kp-select" }, { kind: "component", type: LabelComponent, selector: "kp-label", inputs: ["html-id", "for", "text", "secondary", "description"] }, { kind: "directive", type: LabelActionSlot, selector: "[kp-label-action]" }, { kind: "component", type: TextBlockComponent, selector: "kp-text-block, div[kp-text-block], p[kp-text-block], a[kp-text-block]", inputs: ["tone", "weight", "size", "alignment", "contrast", "truncate", "clamp"] }, { kind: "directive", type: TextBackgroundBehavior, selector: "kp-text, span[kp-text], a[kp-text], kp-text-block, div[kp-text-block], p[kp-text-block], a[kp-text-block]" }], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
};
SelectFieldComponent = __decorate$6([
    Styled($SelectField)
], SelectFieldComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: SelectFieldComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-select-field',
                    template: `
    <kp-stack space="small">
      <kp-label
        *ngIf="!!behavior.label || behavior.hasAction"
        [for]="behavior.id"
        [text]="behavior.label"
        [secondary]="behavior.secondary"
        [description]="behavior.description"
      >
        <ng-container *ngIf="behavior.hasAction" kp-label-action>
          <ng-content select="[kp-label-action]"></ng-content>
        </ng-container>
      </kp-label>
      <kp-select
        [html-id]="behavior.id"
        [value]="behavior.value"
        [name]="behavior.name"
        [disabled]="behavior.disabled"
        [tabIndex]="behavior.tabIndex"
        [autofocus]="behavior.autofocus"
        [autocomplete]="behavior.autocomplete"
        [size]="behavior.size"
        [tone]="behavior.tone"
        [placeholder]="behavior.placeholder"
        (changed)="behavior.setValue($event)"
        (blured)="behavior.notifyBlur()"
        (focused)="behavior.notifyFocus()"
      >
        <ng-content></ng-content>
      </kp-select>
      <kp-text-block
        *ngIf="behavior.message"
        size="small"
        [tone]="
          !behavior.tone || behavior.tone === 'none' ? 'neutral' : behavior.tone
        "
      >
        {{ behavior.message }}
      </kp-text-block>
    </kp-stack>
  `,
                    changeDetection: ChangeDetectionStrategy.OnPush,
                }]
        }], ctorParameters: () => [{ type: SelectFieldBehavior, decorators: [{
                    type: Host
                }] }], propDecorators: { class: [{
                type: HostBinding,
                args: ['class']
            }], select: [{
                type: ViewChild,
                args: [SelectComponent]
            }] } });
const DIRECTIVES$6 = [SelectFieldComponent, SelectFieldBehavior];
class SelectFieldModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: SelectFieldModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: SelectFieldModule, declarations: [SelectFieldComponent, SelectFieldBehavior], imports: [CommonModule, StackModule, SelectModule, LabelModule, TextModule], exports: [SelectFieldComponent, SelectFieldBehavior] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: SelectFieldModule, imports: [CommonModule, StackModule, SelectModule, LabelModule, TextModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: SelectFieldModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, StackModule, SelectModule, LabelModule, TextModule],
                    declarations: DIRECTIVES$6,
                    exports: DIRECTIVES$6,
                }]
        }] });

var __decorate$5 = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
class DropdownFieldBehavior extends FieldPrimitive {
    getDefaultValue() {
        return '';
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: DropdownFieldBehavior, deps: null, target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.1.4", type: DropdownFieldBehavior, selector: "kp-dropdown-field", usesInheritance: true, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: DropdownFieldBehavior, decorators: [{
            type: Directive,
            args: [{ selector: 'kp-dropdown-field' }]
        }] });
const $DropdownField = css({
    display: 'block',
    [`& > * ${ColumnsComponent}`]: { w: '$full', h: '$full' },
});
let DropdownFieldComponent = class DropdownFieldComponent {
    get text() {
        const option = (this.options ?? []).find(o => this.behavior.value === o.value);
        return (option?.label || this.placeholder) ?? '';
    }
    constructor(behavior) {
        this.behavior = behavior;
        this.shown = new EventEmitter();
        this.options = [];
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: DropdownFieldComponent, deps: [{ token: DropdownFieldBehavior, host: true }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: DropdownFieldComponent, selector: "kp-dropdown-field", inputs: { placeholder: "placeholder", size: "size", tone: "tone", options: "options" }, outputs: { shown: "shown" }, host: { properties: { "class": "this.class" } }, ngImport: i0, template: `
    <kp-stack space="small">
      <kp-label
        *ngIf="!!behavior.label || behavior.hasAction"
        [for]="behavior.id"
        [text]="behavior.label"
        [secondary]="behavior.secondary"
        [description]="behavior.description"
      >
        <ng-container *ngIf="behavior.hasAction" kp-label-action>
          <ng-content select="[kp-label-action]"></ng-content>
        </ng-container>
      </kp-label>
      <kp-dropdown
        [text]="text"
        [tone]="tone"
        [size]="size"
        [disabled]="behavior.disabled"
        (shown)="shown.emit()"
        (blured)="behavior.notifyBlur()"
        (focused)="behavior.notifyFocus()"
      >
        <kp-menu>
          <kp-menu-item
            *ngFor="let option of options"
            [kp-menu-active]="behavior.value === option.value"
            [attr.kp-menu-active]="behavior.value === option.value"
            (click)="behavior.setValue(option.value)"
          >
            {{ option.label }}
          </kp-menu-item>
        </kp-menu>
      </kp-dropdown>
      <kp-text-block
        *ngIf="behavior.message"
        size="small"
        [tone]="
          !behavior.tone || behavior.tone === 'none' ? 'neutral' : behavior.tone
        "
      >
        {{ behavior.message }}
      </kp-text-block>
    </kp-stack>
  `, isInline: true, dependencies: [{ kind: "directive", type: i1$1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1$1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: TextBlockComponent, selector: "kp-text-block, div[kp-text-block], p[kp-text-block], a[kp-text-block]", inputs: ["tone", "weight", "size", "alignment", "contrast", "truncate", "clamp"] }, { kind: "directive", type: TextBackgroundBehavior, selector: "kp-text, span[kp-text], a[kp-text], kp-text-block, div[kp-text-block], p[kp-text-block], a[kp-text-block]" }, { kind: "component", type: DropdownComponent, selector: "kp-dropdown", inputs: ["text", "disabled", "fixed", "keepOpenOnContentClick", "boundary", "zIndex", "placement", "size", "tone"], outputs: ["shown", "hidden", "blured", "focused"], exportAs: ["dropdown"] }, { kind: "component", type: StackComponent, selector: "kp-stack", inputs: ["space", "alignment", "grow"] }, { kind: "component", type: MenuComponent, selector: "kp-menu, [kp-menu]", inputs: ["space"] }, { kind: "directive", type: MenuActiveDirective, selector: "kp-menu-item[kp-menu-active], [kp-menu-item][kp-menu-active]", inputs: ["kp-menu-active", "tone"] }, { kind: "component", type: MenuItemComponent, selector: "kp-menu-item, [kp-menu-item]", inputs: ["disabled", "tone", "space"] }, { kind: "component", type: LabelComponent, selector: "kp-label", inputs: ["html-id", "for", "text", "secondary", "description"] }, { kind: "directive", type: LabelActionSlot, selector: "[kp-label-action]" }], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
};
DropdownFieldComponent = __decorate$5([
    Styled($DropdownField)
], DropdownFieldComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: DropdownFieldComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-dropdown-field',
                    template: `
    <kp-stack space="small">
      <kp-label
        *ngIf="!!behavior.label || behavior.hasAction"
        [for]="behavior.id"
        [text]="behavior.label"
        [secondary]="behavior.secondary"
        [description]="behavior.description"
      >
        <ng-container *ngIf="behavior.hasAction" kp-label-action>
          <ng-content select="[kp-label-action]"></ng-content>
        </ng-container>
      </kp-label>
      <kp-dropdown
        [text]="text"
        [tone]="tone"
        [size]="size"
        [disabled]="behavior.disabled"
        (shown)="shown.emit()"
        (blured)="behavior.notifyBlur()"
        (focused)="behavior.notifyFocus()"
      >
        <kp-menu>
          <kp-menu-item
            *ngFor="let option of options"
            [kp-menu-active]="behavior.value === option.value"
            [attr.kp-menu-active]="behavior.value === option.value"
            (click)="behavior.setValue(option.value)"
          >
            {{ option.label }}
          </kp-menu-item>
        </kp-menu>
      </kp-dropdown>
      <kp-text-block
        *ngIf="behavior.message"
        size="small"
        [tone]="
          !behavior.tone || behavior.tone === 'none' ? 'neutral' : behavior.tone
        "
      >
        {{ behavior.message }}
      </kp-text-block>
    </kp-stack>
  `,
                    changeDetection: ChangeDetectionStrategy.OnPush,
                }]
        }], ctorParameters: () => [{ type: DropdownFieldBehavior, decorators: [{
                    type: Host
                }] }], propDecorators: { class: [{
                type: HostBinding,
                args: ['class']
            }], placeholder: [{
                type: Input
            }], shown: [{
                type: Output
            }], size: [{
                type: Input
            }], tone: [{
                type: Input
            }], options: [{
                type: Input
            }] } });
class MultiDropdownFieldBehavior extends FieldPrimitive {
    getDefaultValue() {
        return [];
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: MultiDropdownFieldBehavior, deps: null, target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.1.4", type: MultiDropdownFieldBehavior, selector: "kp-multi-dropdown-field", usesInheritance: true, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: MultiDropdownFieldBehavior, decorators: [{
            type: Directive,
            args: [{ selector: 'kp-multi-dropdown-field' }]
        }] });
let MultiDropdownFieldComponent = class MultiDropdownFieldComponent {
    get text() {
        const selectedOptions = (this.options ?? []).filter(o => this.behavior.value.includes(o.value));
        if (selectedOptions.length === 0 && !!this.placeholder)
            return this.placeholder;
        return selectedOptions.map(o => o.label).join(', ');
    }
    constructor(behavior) {
        this.behavior = behavior;
        this.shown = new EventEmitter();
        this.options = [];
    }
    selectValue(value) {
        let newValue = [];
        if (this.behavior.value.includes(value)) {
            newValue = this.behavior.value.filter(v => v !== value);
        }
        else {
            newValue = [...this.behavior.value, value];
        }
        this.behavior.setValue(newValue);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: MultiDropdownFieldComponent, deps: [{ token: MultiDropdownFieldBehavior, host: true }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: MultiDropdownFieldComponent, selector: "kp-multi-dropdown-field", inputs: { placeholder: "placeholder", size: "size", tone: "tone", options: "options" }, outputs: { shown: "shown" }, host: { properties: { "class": "this.class" } }, ngImport: i0, template: `
    <kp-stack space="small">
      <kp-label
        *ngIf="!!behavior.label || behavior.hasAction"
        [for]="behavior.id"
        [text]="behavior.label"
        [secondary]="behavior.secondary"
        [description]="behavior.description"
      >
        <ng-container *ngIf="behavior.hasAction" kp-label-action>
          <ng-content select="[kp-label-action]"></ng-content>
        </ng-container>
      </kp-label>
      <kp-dropdown
        [text]="text"
        [tone]="tone"
        [size]="size"
        [disabled]="behavior.disabled"
        (shown)="shown.emit()"
        (blur)="behavior.notifyBlur()"
        (focus)="behavior.notifyFocus()"
      >
        <kp-menu>
          <kp-menu-item
            *ngFor="let option of options"
            [kp-menu-active]="behavior.value.includes(option.value)"
            [attr.kp-menu-active]="behavior.value.includes(option.value)"
            (click)="selectValue(option.value)"
          >
            {{ option.label }}
          </kp-menu-item>
        </kp-menu>
      </kp-dropdown>
      <kp-text-block
        *ngIf="behavior.message"
        size="small"
        [tone]="
          !behavior.tone || behavior.tone === 'none' ? 'neutral' : behavior.tone
        "
      >
        {{ behavior.message }}
      </kp-text-block>
    </kp-stack>
  `, isInline: true, dependencies: [{ kind: "directive", type: i1$1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1$1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: TextBlockComponent, selector: "kp-text-block, div[kp-text-block], p[kp-text-block], a[kp-text-block]", inputs: ["tone", "weight", "size", "alignment", "contrast", "truncate", "clamp"] }, { kind: "directive", type: TextBackgroundBehavior, selector: "kp-text, span[kp-text], a[kp-text], kp-text-block, div[kp-text-block], p[kp-text-block], a[kp-text-block]" }, { kind: "component", type: DropdownComponent, selector: "kp-dropdown", inputs: ["text", "disabled", "fixed", "keepOpenOnContentClick", "boundary", "zIndex", "placement", "size", "tone"], outputs: ["shown", "hidden", "blured", "focused"], exportAs: ["dropdown"] }, { kind: "component", type: StackComponent, selector: "kp-stack", inputs: ["space", "alignment", "grow"] }, { kind: "component", type: MenuComponent, selector: "kp-menu, [kp-menu]", inputs: ["space"] }, { kind: "directive", type: MenuActiveDirective, selector: "kp-menu-item[kp-menu-active], [kp-menu-item][kp-menu-active]", inputs: ["kp-menu-active", "tone"] }, { kind: "component", type: MenuItemComponent, selector: "kp-menu-item, [kp-menu-item]", inputs: ["disabled", "tone", "space"] }, { kind: "component", type: LabelComponent, selector: "kp-label", inputs: ["html-id", "for", "text", "secondary", "description"] }, { kind: "directive", type: LabelActionSlot, selector: "[kp-label-action]" }], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
};
MultiDropdownFieldComponent = __decorate$5([
    Styled($DropdownField)
], MultiDropdownFieldComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: MultiDropdownFieldComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-multi-dropdown-field',
                    template: `
    <kp-stack space="small">
      <kp-label
        *ngIf="!!behavior.label || behavior.hasAction"
        [for]="behavior.id"
        [text]="behavior.label"
        [secondary]="behavior.secondary"
        [description]="behavior.description"
      >
        <ng-container *ngIf="behavior.hasAction" kp-label-action>
          <ng-content select="[kp-label-action]"></ng-content>
        </ng-container>
      </kp-label>
      <kp-dropdown
        [text]="text"
        [tone]="tone"
        [size]="size"
        [disabled]="behavior.disabled"
        (shown)="shown.emit()"
        (blur)="behavior.notifyBlur()"
        (focus)="behavior.notifyFocus()"
      >
        <kp-menu>
          <kp-menu-item
            *ngFor="let option of options"
            [kp-menu-active]="behavior.value.includes(option.value)"
            [attr.kp-menu-active]="behavior.value.includes(option.value)"
            (click)="selectValue(option.value)"
          >
            {{ option.label }}
          </kp-menu-item>
        </kp-menu>
      </kp-dropdown>
      <kp-text-block
        *ngIf="behavior.message"
        size="small"
        [tone]="
          !behavior.tone || behavior.tone === 'none' ? 'neutral' : behavior.tone
        "
      >
        {{ behavior.message }}
      </kp-text-block>
    </kp-stack>
  `,
                    changeDetection: ChangeDetectionStrategy.OnPush,
                }]
        }], ctorParameters: () => [{ type: MultiDropdownFieldBehavior, decorators: [{
                    type: Host
                }] }], propDecorators: { class: [{
                type: HostBinding,
                args: ['class']
            }], placeholder: [{
                type: Input
            }], shown: [{
                type: Output
            }], size: [{
                type: Input
            }], tone: [{
                type: Input
            }], options: [{
                type: Input
            }] } });
const DIRECTIVES$5 = [
    DropdownFieldComponent,
    DropdownFieldBehavior,
    MultiDropdownFieldBehavior,
    MultiDropdownFieldComponent,
];
class DropdownFieldModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: DropdownFieldModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: DropdownFieldModule, declarations: [DropdownFieldComponent, DropdownFieldBehavior, MultiDropdownFieldBehavior, MultiDropdownFieldComponent], imports: [CommonModule,
            TextModule,
            DropdownModule,
            StackModule,
            MenuModule,
            LabelModule], exports: [DropdownFieldComponent, DropdownFieldBehavior, MultiDropdownFieldBehavior, MultiDropdownFieldComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: DropdownFieldModule, imports: [CommonModule,
            TextModule,
            DropdownModule,
            StackModule,
            MenuModule,
            LabelModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: DropdownFieldModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        TextModule,
                        DropdownModule,
                        StackModule,
                        MenuModule,
                        LabelModule,
                    ],
                    declarations: DIRECTIVES$5,
                    exports: DIRECTIVES$5,
                }]
        }] });

var __decorate$4 = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
const $Note = css({
    display: 'block',
});
let NoteComponent = class NoteComponent {
    constructor() {
        this.iconSize = 'large';
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: NoteComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: NoteComponent, selector: "kp-note", inputs: { icon: "icon", iconSize: "iconSize", tone: "tone", alignmentY: "alignmentY" }, host: { properties: { "class": "this.class" } }, ngImport: i0, template: `
    <kp-columns [alignmentY]="alignmentY" space="small">
      <kp-column *ngIf="icon" width="content">
        <kp-icon [icon]="icon" [tone]="tone" [size]="iconSize" />
      </kp-column>
      <kp-column>
        <ng-content />
      </kp-column>
    </kp-columns>
  `, isInline: true, dependencies: [{ kind: "directive", type: i1$1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: ColumnComponent, selector: "kp-column", inputs: ["width"] }, { kind: "component", type: ColumnsComponent, selector: "kp-columns", inputs: ["space", "alignmentX", "alignmentY", "collapseBelow", "wrap", "reverse"] }, { kind: "component", type: IconComponent, selector: "kp-icon", inputs: ["icon"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
};
NoteComponent = __decorate$4([
    Styled($Note)
], NoteComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: NoteComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-note',
                    template: `
    <kp-columns [alignmentY]="alignmentY" space="small">
      <kp-column *ngIf="icon" width="content">
        <kp-icon [icon]="icon" [tone]="tone" [size]="iconSize" />
      </kp-column>
      <kp-column>
        <ng-content />
      </kp-column>
    </kp-columns>
  `,
                    changeDetection: ChangeDetectionStrategy.OnPush,
                }]
        }], propDecorators: { class: [{
                type: HostBinding,
                args: ['class']
            }], icon: [{
                type: Input
            }], iconSize: [{
                type: Input
            }], tone: [{
                type: Input
            }], alignmentY: [{
                type: Input
            }] } });
const DIRECTIVES$4 = [NoteComponent];
class NoteModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: NoteModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: NoteModule, declarations: [NoteComponent], imports: [CommonModule, ColumnsModule, IconsModule], exports: [NoteComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: NoteModule, imports: [CommonModule, ColumnsModule, IconsModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: NoteModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, ColumnsModule, IconsModule],
                    declarations: [...DIRECTIVES$4],
                    exports: DIRECTIVES$4,
                }]
        }] });

var __decorate$3 = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
const $Alert = css({
    display: 'block',
    position: 'relative',
    paddingBottom: '$medium',
    paddingTop: '$medium',
    paddingRight: '$large',
    paddingLeft: '$large',
    borderRadius: '$small',
    overflow: 'hidden',
    variants: {
        tone: {
            info: { bg: '$info-subtle' },
            success: { bg: '$success-subtle' },
            warning: { bg: '$warning-subtle' },
            danger: { bg: '$danger-subtle' },
            promote: { bg: '$promote-subtle' },
        },
    },
    defaultVariants: {
        tone: 'info',
    },
});
let AlertComponent = class AlertComponent {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: AlertComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: AlertComponent, selector: "kp-alert", inputs: { icon: "icon", iconSize: "iconSize", tone: "tone" }, host: { properties: { "class": "this.class" } }, ngImport: i0, template: `
    <kp-note
      [tone]="tone || 'info'"
      alignmentY="center"
      [icon]="icon"
      [iconSize]="iconSize"
    >
      <ng-content></ng-content>
    </kp-note>
  `, isInline: true, dependencies: [{ kind: "component", type: NoteComponent, selector: "kp-note", inputs: ["icon", "iconSize", "tone", "alignmentY"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
};
AlertComponent = __decorate$3([
    Styled($Alert)
], AlertComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: AlertComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-alert',
                    template: `
    <kp-note
      [tone]="tone || 'info'"
      alignmentY="center"
      [icon]="icon"
      [iconSize]="iconSize"
    >
      <ng-content></ng-content>
    </kp-note>
  `,
                    changeDetection: ChangeDetectionStrategy.OnPush,
                }]
        }], propDecorators: { class: [{
                type: HostBinding,
                args: ['class']
            }], icon: [{
                type: Input
            }], iconSize: [{
                type: Input
            }], tone: [{
                type: Input
            }] } });
const DIRECTIVES$3 = [AlertComponent];
class AlertModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: AlertModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: AlertModule, declarations: [AlertComponent], imports: [CommonModule,
            ColumnsModule,
            NoteModule,
            IconButtonModule,
            IconsModule,
            CenterModule], exports: [AlertComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: AlertModule, imports: [CommonModule,
            ColumnsModule,
            NoteModule,
            IconButtonModule,
            IconsModule,
            CenterModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: AlertModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        ColumnsModule,
                        NoteModule,
                        IconButtonModule,
                        IconsModule,
                        CenterModule,
                    ],
                    declarations: DIRECTIVES$3,
                    exports: DIRECTIVES$3,
                }]
        }] });

var __decorate$2 = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
class RadioBehavior extends TextInputPrimitive {
    constructor() {
        super(...arguments);
        this.match = '';
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: RadioBehavior, deps: null, target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.1.4", type: RadioBehavior, selector: "kp-radio", inputs: { match: "match" }, usesInheritance: true, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: RadioBehavior, decorators: [{
            type: Directive,
            args: [{ selector: 'kp-radio' }]
        }], propDecorators: { match: [{
                type: Input
            }] } });
const $Radio = css({
    display: 'inline-flex',
    '& > input[type="radio"]': {
        appearance: 'none',
        '&:disabled': {
            '& + span': {
                cursor: 'not-allowed',
                pointerEvents: 'none',
                opacity: 0.4,
            },
        },
    },
    '& > span': {
        color: '$white',
        backgroundColor: '$white',
        borderStyle: 'solid',
        borderWidth: '$thick',
        borderColor: '$border',
        borderRadius: '$round',
        boxSizing: 'border-box',
        cursor: 'pointer',
        display: 'inline-flex',
        justifyContent: 'center',
        alignItems: 'center',
        userSelect: 'none',
        '&:hover': {
            borderColor: '$primary-solid',
        },
        '&[active=true]': {
            borderColor: '$primary-solid',
        },
        '& > div': {
            lineHeight: '$none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '$full',
            width: '$full',
            '& > svg': {
                color: '$primary-solid',
            },
        },
    },
    variants: {
        size: {
            small: {
                '& > span': {
                    size: '1.25rem',
                    minSize: '1.25rem',
                    [`& > div > svg`]: {
                        width: '12px',
                        height: '9px',
                    },
                },
            },
            medium: {
                '& > span': {
                    size: '1.75rem',
                    minSize: '1.75rem',
                    [`& > div > svg`]: {
                        width: '18px',
                        height: '14px',
                    },
                },
            },
        },
    },
    defaultVariants: {
        size: 'small',
    },
});
let RadioComponent = class RadioComponent {
    constructor(behavior) {
        this.behavior = behavior;
    }
    focus() {
        const input = coerceElement(this.inputRef);
        if (!input?.focus)
            return;
        input.focus();
    }
    blur() {
        const input = coerceElement(this.inputRef);
        if (!input?.blur)
            return;
        input.blur();
    }
    onClick() {
        if (this.labeled)
            return;
        if (this.behavior.value === this.behavior.match) {
            this.behavior.setValue('');
        }
        else {
            this.behavior.setValue(this.behavior.match);
        }
        this.focus();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: RadioComponent, deps: [{ token: RadioBehavior, host: true }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: RadioComponent, selector: "kp-radio", inputs: { size: "size", labeled: "labeled" }, host: { properties: { "class": "this.class" } }, viewQueries: [{ propertyName: "inputRef", first: true, predicate: ["radio"], descendants: true }], ngImport: i0, template: `
    <input
      #radio
      type="radio"
      [attr.id]="behavior.id || null"
      [attr.name]="behavior.name || null"
      [attr.tabindex]="behavior.tabIndex"
      [attr.disabled]="behavior.disabled || null"
      [attr.aria-disabled]="behavior.disabled || null"
      [value]="behavior.match"
      [ngModel]="behavior.value"
      (ngModelChange)="behavior.setValue($event)"
      (blur)="behavior.notifyBlur()"
      (focus)="behavior.notifyFocus()"
    />
    <span (click)="onClick()" [attr.active]="behavior.value === behavior.match">
      <div>
        <svg
          *ngIf="behavior.value === behavior.match"
          height="12"
          width="9"
          viewBox="0 0 16 12"
        >
          <circle cx="8" cy="6" r="6" fill="currentColor" />
        </svg>
      </div>
    </span>
  `, isInline: true, dependencies: [{ kind: "directive", type: i1$1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i2.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { kind: "directive", type: i2.RadioControlValueAccessor, selector: "input[type=radio][formControlName],input[type=radio][formControl],input[type=radio][ngModel]", inputs: ["name", "formControlName", "value"] }, { kind: "directive", type: i2.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i2.NgModel, selector: "[ngModel]:not([formControlName]):not([formControl])", inputs: ["name", "disabled", "ngModel", "ngModelOptions"], outputs: ["ngModelChange"], exportAs: ["ngModel"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
};
__decorate$2([
    CoerceBoolean()
], RadioComponent.prototype, "labeled", void 0);
RadioComponent = __decorate$2([
    Styled($Radio)
], RadioComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: RadioComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-radio',
                    template: `
    <input
      #radio
      type="radio"
      [attr.id]="behavior.id || null"
      [attr.name]="behavior.name || null"
      [attr.tabindex]="behavior.tabIndex"
      [attr.disabled]="behavior.disabled || null"
      [attr.aria-disabled]="behavior.disabled || null"
      [value]="behavior.match"
      [ngModel]="behavior.value"
      (ngModelChange)="behavior.setValue($event)"
      (blur)="behavior.notifyBlur()"
      (focus)="behavior.notifyFocus()"
    />
    <span (click)="onClick()" [attr.active]="behavior.value === behavior.match">
      <div>
        <svg
          *ngIf="behavior.value === behavior.match"
          height="12"
          width="9"
          viewBox="0 0 16 12"
        >
          <circle cx="8" cy="6" r="6" fill="currentColor" />
        </svg>
      </div>
    </span>
  `,
                    changeDetection: ChangeDetectionStrategy.OnPush,
                }]
        }], ctorParameters: () => [{ type: RadioBehavior, decorators: [{
                    type: Host
                }] }], propDecorators: { class: [{
                type: HostBinding,
                args: ['class']
            }], size: [{
                type: Input
            }], labeled: [{
                type: Input
            }], inputRef: [{
                type: ViewChild,
                args: ['radio']
            }] } });
const DIRECTIVES$2 = [RadioComponent, RadioBehavior];
class RadioModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: RadioModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: RadioModule, declarations: [RadioComponent, RadioBehavior], imports: [CommonModule, FormsModule], exports: [RadioComponent, RadioBehavior] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: RadioModule, imports: [CommonModule, FormsModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: RadioModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, FormsModule],
                    declarations: DIRECTIVES$2,
                    exports: DIRECTIVES$2,
                }]
        }] });

var __decorate$1 = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
const PADDING = {
    small: '1rem',
    medium: '1.25rem',
    large: '1.75rem',
};
function tone(color) {
    const border = color === 'none' ? 'border' : `${color}-border`;
    return {
        '& > textarea': {
            borderColor: `$${border}`,
            '&:hover': {
                borderColor: `$${border}-hover`,
            },
            '&:focus, &:focus-visible': {
                borderColor: `$${border}-active`,
            },
        },
    };
}
class TextareaBehavior extends TextInputPrimitive {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TextareaBehavior, deps: null, target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.1.4", type: TextareaBehavior, selector: "kp-textarea", usesInheritance: true, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TextareaBehavior, decorators: [{
            type: Directive,
            args: [{ selector: 'kp-textarea' }]
        }] });
const $Textarea = css({
    position: 'relative',
    width: '$full',
    maxHeight: '60vh',
    'textarea, pre': {
        fontWeight: '$regular',
        wordWrap: 'break-word',
        whiteSpace: 'pre-wrap',
        width: '$full',
        maxWidth: '$full',
        fontFamily: '$body',
        fontSize: 'inherit',
    },
    'textarea': {
        height: '$full',
        borderRadius: '$small',
        borderWidth: '$thick',
        borderStyle: 'solid',
        color: '$foreground',
        backgroundColor: '$panel',
        position: 'absolute',
        top: 0,
        left: 0,
        resize: 'none',
        boxSizing: 'border-box',
        transitionDuration: '300ms',
        transitionTimingFunction: 'ease',
        transitionProperty: 'border-color, background-color',
        py: '$small',
        outline: 'none',
        '&:disabled': {
            cursor: 'not-allowed',
            userSelect: 'none',
            pointerEvents: 'none',
            backgroundColor: '$neutral-subtle',
            borderColor: '$neutral-subtle',
            color: '$foreground-subtle',
            opacity: 0.4,
        },
        '&::placeholder': {
            color: '$placeholder',
            opacity: '0.6',
        },
    },
    'pre': {
        display: 'block',
        visibility: 'hidden',
        paddingTop: '3rem',
        margin: 0,
    },
    variants: {
        size: {
            small: {
                minHeight: '$24',
                'textarea, pre': {
                    fontSize: '$small',
                    px: PADDING['small'],
                },
            },
            medium: {
                minHeight: '$48',
                'textarea, pre': {
                    fontSize: '$medium',
                    px: PADDING['medium'],
                },
            },
            large: {
                minHeight: '$72',
                'textarea, pre': {
                    fontSize: '$large',
                    px: PADDING['large'],
                },
            },
        },
        tone: {
            none: tone('none'),
            success: tone('success'),
            warning: tone('warning'),
            danger: tone('danger'),
        },
    },
    defaultVariants: {
        size: 'medium',
        tone: 'none',
    },
});
let TextareaComponent = class TextareaComponent {
    constructor(behavior) {
        this.behavior = behavior;
        this.maxHeight = '60vh';
    }
    updateMirrorElement() {
        if (!this.textarea || !this.span)
            return;
        this.span.textContent = this.textarea.value;
    }
    get textarea() {
        return coerceElement(this.textareaRef);
    }
    get span() {
        return coerceElement(this.spanRef);
    }
    focus() {
        if (!this.textarea?.focus)
            return;
        this.textarea.focus();
    }
    blur() {
        if (!this.textarea?.focus)
            return;
        this.textarea.blur();
    }
    get css() {
        return propsToCss({
            maxHeight: this.maxHeight,
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TextareaComponent, deps: [{ token: TextareaBehavior, host: true }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: TextareaComponent, selector: "kp-textarea", inputs: { size: "size", tone: "tone", maxHeight: "maxHeight" }, host: { properties: { "class": "this.class" } }, viewQueries: [{ propertyName: "textareaRef", first: true, predicate: ["textarea"], descendants: true }, { propertyName: "spanRef", first: true, predicate: ["mirror"], descendants: true }], ngImport: i0, template: `
    <pre><span #mirror></span><br></pre>
    <textarea
      #textarea
      [attr.id]="behavior.id || null"
      [attr.name]="behavior.name || null"
      [attr.disabled]="behavior.disabled || null"
      [attr.aria-disabled]="behavior.disabled || null"
      [attr.tabindex]="behavior.tabIndex"
      [attr.autofocus]="behavior.autofocus || null"
      [attr.autocomplete]="behavior.autocomplete || null"
      [attr.placeholder]="behavior.placeholder"
      [attr.readonly]="behavior.readonly || null"
      [attr.minlength]="behavior.minlength || null"
      [attr.maxlength]="behavior.maxlength || null"
      (blur)="behavior.notifyBlur()"
      (focus)="behavior.notifyFocus()"
      (input)="updateMirrorElement()"
      [ngModel]="behavior.value"
      (ngModelChange)="behavior.setValue($event)"
    ></textarea>
  `, isInline: true, dependencies: [{ kind: "directive", type: i2.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { kind: "directive", type: i2.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i2.NgModel, selector: "[ngModel]:not([formControlName]):not([formControl])", inputs: ["name", "disabled", "ngModel", "ngModelOptions"], outputs: ["ngModelChange"], exportAs: ["ngModel"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
};
TextareaComponent = __decorate$1([
    Styled($Textarea)
], TextareaComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TextareaComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-textarea',
                    template: `
    <pre><span #mirror></span><br></pre>
    <textarea
      #textarea
      [attr.id]="behavior.id || null"
      [attr.name]="behavior.name || null"
      [attr.disabled]="behavior.disabled || null"
      [attr.aria-disabled]="behavior.disabled || null"
      [attr.tabindex]="behavior.tabIndex"
      [attr.autofocus]="behavior.autofocus || null"
      [attr.autocomplete]="behavior.autocomplete || null"
      [attr.placeholder]="behavior.placeholder"
      [attr.readonly]="behavior.readonly || null"
      [attr.minlength]="behavior.minlength || null"
      [attr.maxlength]="behavior.maxlength || null"
      (blur)="behavior.notifyBlur()"
      (focus)="behavior.notifyFocus()"
      (input)="updateMirrorElement()"
      [ngModel]="behavior.value"
      (ngModelChange)="behavior.setValue($event)"
    ></textarea>
  `,
                    changeDetection: ChangeDetectionStrategy.OnPush,
                }]
        }], ctorParameters: () => [{ type: TextareaBehavior, decorators: [{
                    type: Host
                }] }], propDecorators: { class: [{
                type: HostBinding,
                args: ['class']
            }], textareaRef: [{
                type: ViewChild,
                args: ['textarea']
            }], spanRef: [{
                type: ViewChild,
                args: ['mirror']
            }], size: [{
                type: Input
            }], tone: [{
                type: Input
            }], maxHeight: [{
                type: Input
            }] } });
const DIRECTIVES$1 = [TextareaComponent, TextareaBehavior];
class TextareaModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TextareaModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: TextareaModule, declarations: [TextareaComponent, TextareaBehavior], imports: [FormsModule], exports: [TextareaComponent, TextareaBehavior] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TextareaModule, imports: [FormsModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TextareaModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [FormsModule],
                    declarations: DIRECTIVES$1,
                    exports: DIRECTIVES$1,
                }]
        }] });

var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
class TextareaFieldBehavior extends FieldPrimitive {
    constructor() {
        super(...arguments);
        this.readonly = false;
    }
    getDefaultValue() {
        return '';
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TextareaFieldBehavior, deps: null, target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.1.4", type: TextareaFieldBehavior, selector: "kp-textarea-field", inputs: { readonly: "readonly" }, usesInheritance: true, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TextareaFieldBehavior, decorators: [{
            type: Directive,
            args: [{ selector: 'kp-textarea-field' }]
        }], propDecorators: { readonly: [{
                type: Input
            }] } });
const $TextareaField = css({
    display: 'block',
});
let TextareaFieldComponent = class TextareaFieldComponent {
    constructor(behavior) {
        this.behavior = behavior;
    }
    focus() {
        if (!this.textarea)
            return;
        this.textarea.focus();
    }
    blur() {
        if (!this.textarea)
            return;
        this.textarea.blur();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TextareaFieldComponent, deps: [{ token: TextareaFieldBehavior, host: true }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.1.4", type: TextareaFieldComponent, selector: "kp-textarea-field", host: { properties: { "class": "this.class" } }, viewQueries: [{ propertyName: "textarea", first: true, predicate: TextareaComponent, descendants: true }], ngImport: i0, template: `
    <kp-stack space="small">
      <kp-label
        *ngIf="!!behavior.label || behavior.hasAction"
        [for]="behavior.id"
        [text]="behavior.label"
        [secondary]="behavior.secondary"
        [description]="behavior.description"
      >
        <ng-container *ngIf="behavior.hasAction" kp-label-action>
          <ng-content select="[kp-label-action]"></ng-content>
        </ng-container>
      </kp-label>
      <kp-textarea
        type="text"
        [html-id]="behavior.id"
        [value]="behavior.value"
        [name]="behavior.name"
        [placeholder]="behavior.placeholder"
        [disabled]="behavior.disabled"
        [tabIndex]="behavior.tabIndex"
        [autofocus]="behavior.autofocus"
        [readonly]="behavior.readonly"
        [autocomplete]="behavior.autocomplete"
        [size]="behavior.size"
        [tone]="behavior.tone"
        [minlength]="behavior.minlength"
        [maxlength]="behavior.maxlength"
        (changed)="behavior.setValue($event)"
        (blured)="behavior.notifyBlur()"
        (focused)="behavior.notifyFocus()"
      ></kp-textarea>
      <kp-text-block
        *ngIf="behavior.message"
        size="small"
        [tone]="
          !behavior.tone || behavior.tone === 'none' ? 'neutral' : behavior.tone
        "
      >
        {{ behavior.message }}
      </kp-text-block>
    </kp-stack>
  `, isInline: true, dependencies: [{ kind: "directive", type: i1$1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: StackComponent, selector: "kp-stack", inputs: ["space", "alignment", "grow"] }, { kind: "component", type: LabelComponent, selector: "kp-label", inputs: ["html-id", "for", "text", "secondary", "description"] }, { kind: "directive", type: LabelActionSlot, selector: "[kp-label-action]" }, { kind: "component", type: TextareaComponent, selector: "kp-textarea", inputs: ["size", "tone", "maxHeight"] }, { kind: "directive", type: TextareaBehavior, selector: "kp-textarea" }, { kind: "component", type: TextBlockComponent, selector: "kp-text-block, div[kp-text-block], p[kp-text-block], a[kp-text-block]", inputs: ["tone", "weight", "size", "alignment", "contrast", "truncate", "clamp"] }, { kind: "directive", type: TextBackgroundBehavior, selector: "kp-text, span[kp-text], a[kp-text], kp-text-block, div[kp-text-block], p[kp-text-block], a[kp-text-block]" }], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
};
TextareaFieldComponent = __decorate([
    Styled($TextareaField)
], TextareaFieldComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TextareaFieldComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'kp-textarea-field',
                    template: `
    <kp-stack space="small">
      <kp-label
        *ngIf="!!behavior.label || behavior.hasAction"
        [for]="behavior.id"
        [text]="behavior.label"
        [secondary]="behavior.secondary"
        [description]="behavior.description"
      >
        <ng-container *ngIf="behavior.hasAction" kp-label-action>
          <ng-content select="[kp-label-action]"></ng-content>
        </ng-container>
      </kp-label>
      <kp-textarea
        type="text"
        [html-id]="behavior.id"
        [value]="behavior.value"
        [name]="behavior.name"
        [placeholder]="behavior.placeholder"
        [disabled]="behavior.disabled"
        [tabIndex]="behavior.tabIndex"
        [autofocus]="behavior.autofocus"
        [readonly]="behavior.readonly"
        [autocomplete]="behavior.autocomplete"
        [size]="behavior.size"
        [tone]="behavior.tone"
        [minlength]="behavior.minlength"
        [maxlength]="behavior.maxlength"
        (changed)="behavior.setValue($event)"
        (blured)="behavior.notifyBlur()"
        (focused)="behavior.notifyFocus()"
      ></kp-textarea>
      <kp-text-block
        *ngIf="behavior.message"
        size="small"
        [tone]="
          !behavior.tone || behavior.tone === 'none' ? 'neutral' : behavior.tone
        "
      >
        {{ behavior.message }}
      </kp-text-block>
    </kp-stack>
  `,
                    changeDetection: ChangeDetectionStrategy.OnPush,
                }]
        }], ctorParameters: () => [{ type: TextareaFieldBehavior, decorators: [{
                    type: Host
                }] }], propDecorators: { class: [{
                type: HostBinding,
                args: ['class']
            }], textarea: [{
                type: ViewChild,
                args: [TextareaComponent]
            }] } });
const DIRECTIVES = [TextareaFieldComponent, TextareaFieldBehavior];
class TextareaFieldModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TextareaFieldModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: TextareaFieldModule, declarations: [TextareaFieldComponent, TextareaFieldBehavior], imports: [CommonModule, StackModule, LabelModule, TextareaModule, TextModule], exports: [TextareaFieldComponent, TextareaFieldBehavior] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TextareaFieldModule, imports: [CommonModule, StackModule, LabelModule, TextareaModule, TextModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: TextareaFieldModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, StackModule, LabelModule, TextareaModule, TextModule],
                    declarations: DIRECTIVES,
                    exports: DIRECTIVES,
                }]
        }] });

class KappaModule {
    static forRoot() {
        return {
            ngModule: KappaModule,
            providers: [GLOBAL_STYLES_INITIALIZER, FA_CONFIG_INITIALIZER],
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: KappaModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.1.4", ngImport: i0, type: KappaModule, exports: [IconModule,
            AspectRatioModule,
            CenterModule,
            VisuallyHiddenModule,
            FilePickerModule,
            AvatarModule,
            BoxModule,
            TextModule,
            HeadingModule,
            ColumnsModule,
            ContentBlockModule,
            DividerModule,
            TooltipModule,
            CardModule,
            SetModule,
            HideModule,
            StackModule,
            MenuModule,
            LinkModule,
            LabelModule,
            ContentCardModule,
            SkeletonModule,
            DialogModule,
            BackdropModule,
            IconButtonModule,
            InputModule,
            TextFieldModule,
            ToastModule,
            ButtonModule,
            CheckboxModule,
            CheckboxFieldModule,
            SpacerModule,
            PopoverModule,
            SelectModule,
            SelectFieldModule,
            DropdownModule,
            NoteModule,
            AlertModule,
            RadioModule,
            TextareaModule,
            TextareaFieldModule,
            DropdownFieldModule] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: KappaModule, imports: [IconModule,
            AspectRatioModule,
            CenterModule,
            VisuallyHiddenModule,
            FilePickerModule,
            AvatarModule,
            BoxModule,
            TextModule,
            HeadingModule,
            ColumnsModule,
            ContentBlockModule,
            DividerModule,
            TooltipModule,
            CardModule,
            SetModule,
            HideModule,
            StackModule,
            MenuModule,
            LinkModule,
            LabelModule,
            ContentCardModule,
            SkeletonModule,
            DialogModule,
            BackdropModule,
            IconButtonModule,
            InputModule,
            TextFieldModule,
            ToastModule,
            ButtonModule,
            CheckboxModule,
            CheckboxFieldModule,
            SpacerModule,
            PopoverModule,
            SelectModule,
            SelectFieldModule,
            DropdownModule,
            NoteModule,
            AlertModule,
            RadioModule,
            TextareaModule,
            TextareaFieldModule,
            DropdownFieldModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.1.4", ngImport: i0, type: KappaModule, decorators: [{
            type: NgModule,
            args: [{
                    exports: [
                        IconModule,
                        AspectRatioModule,
                        CenterModule,
                        VisuallyHiddenModule,
                        FilePickerModule,
                        AvatarModule,
                        BoxModule,
                        TextModule,
                        HeadingModule,
                        ColumnsModule,
                        ContentBlockModule,
                        DividerModule,
                        TooltipModule,
                        CardModule,
                        SetModule,
                        HideModule,
                        StackModule,
                        MenuModule,
                        LinkModule,
                        LabelModule,
                        ContentCardModule,
                        SkeletonModule,
                        DialogModule,
                        BackdropModule,
                        IconButtonModule,
                        InputModule,
                        TextFieldModule,
                        ToastModule,
                        ButtonModule,
                        CheckboxModule,
                        CheckboxFieldModule,
                        SpacerModule,
                        PopoverModule,
                        SelectModule,
                        SelectFieldModule,
                        DropdownModule,
                        NoteModule,
                        AlertModule,
                        RadioModule,
                        TextareaModule,
                        TextareaFieldModule,
                        DropdownFieldModule,
                    ],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { $Icon, $SuffixDropdownIcon, AlertComponent, AlertModule, AlignCenterIconComponent, AlignCenterIconModule, AlignLeftIconComponent, AlignLeftIconModule, AlignRightIconComponent, AlignRightIconModule, ArrowCircleRightIconComponent, ArrowCircleRightIconModule, ArrowDownToLineIconComponent, ArrowDownToLineIconModule, ArrowLeftIconComponent, ArrowLeftIconModule, ArrowLeftToLineIconComponent, ArrowLeftToLineIconModule, ArrowRightIconComponent, ArrowRightIconModule, ArrowRightToLineIconComponent, ArrowRightToLineIconModule, ArrowUpToLineIconComponent, ArrowUpToLineIconModule, AvatarBehavior, AvatarComponent, AvatarGroupComponent, AvatarModule, AwardIconComponent, AwardIconModule, Backdrop, BackdropComponent, BackdropModule, BackgroundContext, BackgroundPrimitive, BackwardIconComponent, BackwardIconModule, BellAltIconComponent, BellAltIconModule, BlogsNewsIconComponent, BlogsNewsIconModule, BoldIconComponent, BoldIconModule, BoxBackgroundBehavior, BoxComponent, BoxModule, Breakpoints, BringFrontIconComponent, BringFrontIconModule, BrushAltIconComponent, BrushAltIconModule, ButtonDirective, ButtonModule, CalendarIconComponent, CalendarIconModule, CardComponent, CardModule, ChartAreaIconComponent, ChartAreaIconModule, ChartBarIconComponent, ChartBarIconModule, ChartColumnIconComponent, ChartColumnIconModule, ChartLineIconComponent, ChartLineIconModule, ChartMixedUpCircleDollarAltIconComponent, ChartMixedUpCircleDollarAltIconModule, ChartMixedUpCircleDollarIconComponent, ChartMixedUpCircleDollarIconModule, ChartPieIconComponent, ChartPieIconModule, CheckCircleAltIconComponent, CheckCircleAltIconModule, CheckCircleIconComponent, CheckCircleIconModule, CheckIconComponent, CheckIconModule, CheckboxBehavior, CheckboxComponent, CheckboxFieldBehavior, CheckboxFieldComponent, CheckboxFieldModule, CheckboxModule, ChevronDownIconComponent, ChevronDownIconModule, ChevronLeftIconComponent, ChevronLeftIconModule, ChevronRightIconComponent, ChevronRightIconModule, ChevronUpIconComponent, ChevronUpIconModule, CircleAltIconComponent, CircleAltIconModule, CircleDashedIconComponent, CircleDashedIconModule, CircleNotchIconComponent, CircleNotchIconModule, ClipboardCheckIconComponent, ClipboardCheckIconModule, ClipboardIconComponent, ClipboardIconModule, ClockIconComponent, ClockIconModule, CloneIconComponent, CloneIconModule, CloudCheckIconComponent, CloudCheckIconModule, CloudDownloadIconComponent, CloudDownloadIconModule, CloudTimesIconComponent, CloudTimesIconModule, CloudUploadIconComponent, CloudUploadIconModule, CodeIconComponent, CodeIconModule, CoerceIcon, ColumnComponent, ColumnsComponent, ColumnsContentComponent, ColumnsModule, ContentBlockComponent, ContentBlockDirective, ContentBlockModule, ContentCardComponent, ContentCardHeaderActionSlot, ContentCardHeaderContentSlot, ContentCardModule, ContextualHeadingPrimitive, ContextualTextPrimitive, CopyAltIconComponent, CopyAltIconModule, CopyIconComponent, CopyIconModule, CreditCardIconComponent, CreditCardIconModule, CurvedLineIconComponent, CurvedLineIconModule, CustomIconStyle, DashboardIconComponent, DashboardIconModule, DiagonalDashedRegularLineIconComponent, DiagonalDashedRegularLineIconModule, DiagonalSolidRegularLineIconComponent, DiagonalSolidRegularLineIconModule, DiagonalSolidThickLineIconComponent, DiagonalSolidThickLineIconModule, DiagonalSolidThinLineIconComponent, DiagonalSolidThinLineIconModule, DiagramVennIconComponent, DiagramVennIconModule, Dialog, DialogComponent, DialogContent, DialogLayoutComponent, DialogLayoutContentComponent, DialogLayoutHeaderComponent, DialogModule, DividerComponent, DividerModule, DollarIconComponent, DollarIconModule, DotCircleIconComponent, DotCircleIconModule, DropdownBehavior, DropdownComponent, DropdownContentDirective, DropdownFieldBehavior, DropdownFieldComponent, DropdownFieldModule, DropdownModule, DropdownPanelComponent, DropdownTriggerComponent, DropdownTriggerContentSlot, DropdownTriggerPrefixIconSlot, DropdownTriggerSlot, DropdownTriggerSuffixIconSlot, EditAltIconComponent, EditAltIconModule, EditIconComponent, EditIconModule, EllipsisIconComponent, EllipsisIconModule, EmptySetIconComponent, EmptySetIconModule, EnvelopeIconComponent, EnvelopeIconModule, ExchangeAltIconComponent, ExchangeAltIconModule, ExclamationCircleAltIconComponent, ExclamationCircleAltIconModule, ExclamationCircleIconComponent, ExclamationCircleIconModule, ExpandWideIconComponent, ExpandWideIconModule, ExternalLinkIconComponent, ExternalLinkIconModule, EyeAltIconComponent, EyeAltIconModule, EyeIconComponent, EyeIconModule, EyeSlashIconComponent, EyeSlashIconModule, FA_CONFIG_INITIALIZER, FaceLaughWinkIconComponent, FaceLaughWinkIconModule, FieldPrimitive, FileCheckIconComponent, FileCheckIconModule, FileEditIconComponent, FileEditIconModule, FileIconComponent, FileIconModule, FileInvoiceIconComponent, FileInvoiceIconModule, FilePlusIconComponent, FilePlusIconModule, FileTextIconComponent, FileTextIconModule, FillDripIconComponent, FillDripIconModule, FilmTapeIconComponent, FilmTapeIconModule, FilterAltIconComponent, FilterAltIconModule, FilterIconComponent, FilterIconModule, FlagIconComponent, FlagIconModule, FolderAltIconComponent, FolderAltIconModule, FolderIconComponent, FolderIconModule, FolderOpenIconComponent, FolderOpenIconModule, FolderPlusIconComponent, FolderPlusIconModule, FolderTimesIconComponent, FolderTimesIconModule, FontIconComponent, FontIconModule, GLOBAL_STYLES_INITIALIZER, GoogleDriveIconComponent, GoogleDriveIconModule, GridIconComponent, GridIconModule, HandPointUpIconComponent, HandPointUpIconModule, HeadingBackgroundBehavior, HeadingComponent, HeadingIconComponent, HeadingIconModule, HeadingModule, HeartAltIconComponent, HeartAltIconModule, HeartIconComponent, HeartIconModule, HideComponent, HideDirective, HideModule, HorizontalLineIconComponent, HorizontalLineIconModule, ICON_PROVIDERS, Icon, IconButtonComponent, IconButtonModule, IconComponent, IconModule, IconPrimitive, IconSpaceComponent, IconsModule, ImageIconComponent, ImageIconModule, InfoCircleIconComponent, InfoCircleIconModule, InputAddonComponent, InputBehavior, InputComponent, InputModule, InputPrefixSlot, InputSuffixSlot, ItalicIconComponent, ItalicIconModule, KappaModule, KappaTextContentRenderer, LabelActionSlot, LabelComponent, LabelModule, LaptopIconComponent, LaptopIconModule, LevelDownIconComponent, LevelDownIconIconModule, LevelUpIconComponent, LevelUpIconIconModule, LineCirclePointerLeftIconComponent, LineCirclePointerLeftIconModule, LineCirclePointerRightIconComponent, LineCirclePointerRightIconModule, LinkComponent, LinkIconComponent, LinkIconModule, LinkModule, ListOlIconComponent, ListOlIconModule, ListUlIconComponent, ListUlIconModule, LockAltIconComponent, LockAltIconModule, LockIconComponent, LockIconModule, LongArrowRightIconComponent, LongArrowRightIconModule, MagicSparklesIconComponent, MagicSparklesIconModule, MagnifyingGlassChartIconComponent, MagnifyingGlassChartIconModule, MarkdownIconComponent, MarkdownIconModule, MegaphoneIconComponent, MegaphoneIconModule, MenuActiveDirective, MenuActiveModule, MenuComponent, MenuDividerComponent, MenuDividerModule, MenuGroupComponent, MenuGroupHeaderComponent, MenuGroupHeaderSlot, MenuGroupModule, MenuIconComponent, MenuIconModule, MenuItemComponent, MenuItemContentSlot, MenuItemModule, MenuItemPrefixSlot, MenuItemSuffixSlot, MenuModule, MinusIconComponent, MinusIconModule, MultiDropdownFieldBehavior, MultiDropdownFieldComponent, NoteComponent, NoteModule, ObjectGroupIconComponent, ObjectGroupIconModule, PaintBrushPencilIconComponent, PaintBrushPencilIconModule, PaletteIconComponent, PaletteIconModule, ParagraphIconComponent, ParagraphIconModule, PasteIconComponent, PasteIconModule, PauseIconComponent, PauseIconModule, PdfIconComponent, PdfIconModule, PersonChalkboardAltIconComponent, PersonChalkboardAltIconModule, PersonChalkboardIconComponent, PersonChalkboardIconModule, PhoneCallIconComponent, PhoneCallIconModule, PhotoVideoIconComponent, PhotoVideoIconModule, PlayAltIconComponent, PlayAltIconModule, PlayIconComponent, PlayIconModule, PlusCircleIconComponent, PlusCircleIconModule, PlusIconComponent, PlusIconModule, PlusSquareIconComponent, PlusSquareIconModule, PopoverBehavior, PopoverComponent, PopoverContentComponent, PopoverDirective, PopoverModule, PptIconComponent, PptIconModule, PresentationIconComponent, PresentationIconModule, ProjectorIconComponent, ProjectorIconModule, QuestionCircleAltIconComponent, QuestionCircleAltIconModule, QuestionCircleIconComponent, QuestionCircleIconModule, QuoteIconComponent, QuoteIconModule, RadioBehavior, RadioComponent, RadioModule, RedoIconComponent, RedoIconModule, RocketIconComponent, RocketIconModule, SaveIconComponent, SaveIconModule, ScissorsIconComponent, ScissorsIconModule, SearchIconComponent, SearchIconModule, SelectBehavior, SelectComponent, SelectFieldBehavior, SelectFieldComponent, SelectFieldModule, SelectModule, SendBackIconComponent, SendBackIconModule, SetComponent, SetContentComponent, SetModule, ShapesIconComponent, ShapesIconModule, ShareIconComponent, ShareIconModule, SignOutIconComponent, SignOutIconModule, SkeletonComponent, SkeletonModule, SkeletonParagraphComponent, SliderVerticalIconComponent, SliderVerticalIconModule, SortIconComponent, SortIconModule, SpacerComponent, SpacerModule, SpinnerThirdIconComponent, SpinnerThirdIconModule, SquareIconComponent, SquareIconModule, StackComponent, StackModule, StarIconComponent, StarIconModule, StraightLineIconComponent, StraightLineIconModule, StrikethroughIconComponent, StrikethroughIconModule, Styled, SubscriptIconComponent, SubscriptIconModule, SuffixDropdownIconComponent, SuperscriptIconComponent, SuperscriptIconModule, SwatchbookIconComponent, SwatchbookIconModule, TEXT_CONTENT_PROVIDERS, TableIconComponent, TableIconModule, TagIconComponent, TagIconModule, TallyIconComponent, TallyIconModule, TaxiIconComponent, TaxiIconModule, TeamAltIconComponent, TeamAltIconModule, TeamIconComponent, TeamIconModule, TextBackgroundBehavior, TextBlockComponent, TextComponent, TextContext, TextFieldBehavior, TextFieldComponent, TextFieldModule, TextHeightIconComponent, TextHeightIconModule, TextHostComponent, TextIconComponent, TextIconModule, TextModule, TextRendererModule, TextSlashIconComponent, TextSlashIconModule, TextareaBehavior, TextareaComponent, TextareaFieldBehavior, TextareaFieldComponent, TextareaFieldModule, TextareaModule, TimelineAlternateDistributionIconComponent, TimelineAlternateDistributionIconModule, TimelineBottomDistributionIconComponent, TimelineBottomDistributionIconModule, TimelineIconComponent, TimelineIconModule, TimelineTopDistributionIconComponent, TimelineTopDistributionIconModule, TimesCircleIconComponent, TimesCircleIconModule, TimesIconComponent, TimesIconModule, Toast, ToastComponent, ToastContentComponent, ToastContentRef, ToastContext, ToastHostComponent, ToastMessageComponent, ToastModule, ToastRef, ToastResultResolver, ToastStackComponent, TooltipArrowComponent, TooltipBackgroundBehavior, TooltipBehavior, TooltipComponent, TooltipContentComponent, TooltipDirective, TooltipModule, TrademarkIconComponent, TrademarkIconModule, TrashAltIconComponent, TrashAltIconModule, TrashIconComponent, TrashIconModule, TriangleArrowLeftIconComponent, TriangleArrowLeftIconModule, TriangleArrowRightIconComponent, TriangleArrowRightIconModule, TriangleExclamationIconComponent, TriangleExclamationIconModule, TriangleIconComponent, TriangleIconModule, UnderlineIconComponent, UnderlineIconModule, UndoIconComponent, UndoIconModule, UnlinkIconComponent, UnlinkIconModule, UserIconComponent, UserIconModule, UserPlusIconComponent, UserPlusIconModule, UsersIconComponent, UsersIconModule, WalletIconComponent, WalletIconModule, WalletPlusIconComponent, WalletPlusIconModule, WaveformIconComponent, WaveformIconModule, coerceIcon, compoundVariants, config, createTheme, css, defineCustomIcon, globalCss, keyframes, kpAward, kpBellAlt, kpBrushAlt, kpClipboard, kpClipboardCheck, kpClock, kpClone, kpCloudCheck, kpCloudDownload, kpCloudTimes, kpCloudUpload, kpCode, kpCopy, kpCopyAlt, kpCurvedLine, kpDashboard, kpDiagonalDashedRegularLine, kpDiagonalSolidRegularLine, kpDiagonalSolidThickLine, kpDiagonalSolidThinLine, kpEllipsis, kpEnvelope, kpExternalLink, kpEye, kpEyeAlt, kpEyeSlash, kpFile, kpFilePlus, kpFilmTape, kpFlag, kpFolder, kpFolderOpen, kpGoogleDrive, kpGrid, kpHorizontalLine, kpImage, kpLineCirclePointerLeft, kpLineCirclePointerRight, kpLock, kpLockAlt, kpMarkdown, kpMegaphone, kpPhoneCall, kpPlusSquare, kpPresentation, kpRedo, kpSearch, kpSignOut, kpSliderVertical, kpStar, kpStraightLine, kpSwatchbook, kpTag, kpTimelineAlternateDistribution, kpTimelineBottomDistribution, kpTimelineTopDistribution, kpTriangleArrowLeft, kpTriangleArrowRight, kpUndo, kpWallet, kpWalletPlus, propsToCss, registerGlobalStyles, shallowEqual, theme };
